import { Logger as t, LogLevel as e } from "@firebase/logger";

import { isMobileCordova as n, isReactNative as s, isElectron as i, isIE as r, isUWP as o, isBrowserExtension as c, getUA as a } from "@firebase/util";

import { XhrIo as u, EventType as h, ErrorCode as l, createWebChannelTransport as _, getStatEventTarget as f, WebChannel as d, Event as w, Stat as E } from "@firebase/webchannel-wrapper";

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Converts a Base64 encoded string to a binary string. */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const T = new t("@firebase/firestore");

// Helper methods are needed because variables can't be exported as read/write
function I() {
    return T.logLevel;
}

/**
 * Sets the verbosity of Cloud Firestore logs (debug, error, or silent).
 *
 * @param logLevel - The verbosity you set for activity and error logging. Can
 *   be any of the following values:
 *
 *   <ul>
 *     <li>`debug` for the most verbose logging level, primarily for
 *     debugging.</li>
 *     <li>`error` to log errors only.</li>
 *     <li><code>`silent` to turn off logging.</li>
 *   </ul>
 */ function m(t, ...n) {
    if (T.logLevel <= e.DEBUG) {
        const e = n.map(P);
        T.debug("Firestore (8.2.0): " + t, ...e);
    }
}

function A(t, ...n) {
    if (T.logLevel <= e.ERROR) {
        const e = n.map(P);
        T.error("Firestore (8.2.0): " + t, ...e);
    }
}

function R(t, ...n) {
    if (T.logLevel <= e.WARN) {
        const e = n.map(P);
        T.warn("Firestore (8.2.0): " + t, ...e);
    }
}

/**
 * Converts an additional log parameter to a string representation.
 */ function P(t) {
    if ("string" == typeof t) return t;
    try {
        return e = t, JSON.stringify(e);
    } catch (e) {
        // Converting to JSON failed, just log the object directly
        return t;
    }
    /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
    /** Formats an object as a JSON string, suitable for logging. */
    var e;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Unconditionally fails, throwing an Error with the given message.
 * Messages are stripped in production builds.
 *
 * Returns `never` and can be used in expressions:
 * @example
 * let futureVar = fail('not implemented yet');
 */ function y(t = "Unexpected state") {
    // Log the failure in addition to throw an exception, just in case the
    // exception is swallowed.
    const e = "FIRESTORE (8.2.0) INTERNAL ASSERTION FAILED: " + t;
    // NOTE: We don't use FirestoreError here because these are internal failures
    // that cannot be handled by the user. (Also it would create a circular
    // dependency between the error and assert modules which doesn't work.)
    throw A(e), new Error(e);
}

/**
 * Fails if the given assertion condition is false, throwing an Error with the
 * given message if it did.
 *
 * Messages are stripped in production builds.
 */ function g(t, e) {
    t || y();
}

/**
 * Casts `obj` to `T`. In non-production builds, verifies that `obj` is an
 * instance of `T` before casting.
 */ function V(t, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
e) {
    return t;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Generates `nBytes` of random bytes.
 *
 * If `nBytes < 0` , an error will be thrown.
 */ function p(t) {
    // Polyfills for IE and WebWorker by using `self` and `msCrypto` when `crypto` is not available.
    const e = 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    "undefined" != typeof self && (self.crypto || self.msCrypto), n = new Uint8Array(t);
    if (e && "function" == typeof e.getRandomValues) e.getRandomValues(n); else 
    // Falls back to Math.random
    for (let e = 0; e < t; e++) n[e] = Math.floor(256 * Math.random());
    return n;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class b {
    static t() {
        // Alphanumeric characters
        const t = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789", e = Math.floor(256 / t.length) * t.length;
        // The largest byte value that is a multiple of `char.length`.
                let n = "";
        for (;n.length < 20; ) {
            const s = p(40);
            for (let i = 0; i < s.length; ++i) 
            // Only accept values that are [0, maxMultiple), this ensures they can
            // be evenly mapped to indices of `chars` via a modulo operation.
            n.length < 20 && s[i] < e && (n += t.charAt(s[i] % t.length));
        }
        return n;
    }
}

function v(t, e) {
    return t < e ? -1 : t > e ? 1 : 0;
}

/** Helper to compare arrays using isEqual(). */ function S(t, e, n) {
    return t.length === e.length && t.every(((t, s) => n(t, e[s])));
}

/**
 * Returns the immediate lexicographically-following string. This is useful to
 * construct an inclusive range for indexeddb iterators.
 */ function D(t) {
    // Return the input string, with an additional NUL byte appended.
    return t + "\0";
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Immutable class that represents a "proto" byte string.
 *
 * Proto byte strings can either be Base64-encoded strings or Uint8Arrays when
 * sent on the wire. This class abstracts away this differentiation by holding
 * the proto byte string in a common class that must be converted into a string
 * before being sent as a proto.
 */ class C {
    constructor(t) {
        this.i = t;
    }
    static fromBase64String(t) {
        const e = atob(t);
        return new C(e);
    }
    static fromUint8Array(t) {
        const e = 
        /**
 * Helper function to convert an Uint8array to a binary string.
 */
        function(t) {
            let e = "";
            for (let n = 0; n < t.length; ++n) e += String.fromCharCode(t[n]);
            return e;
        }
        /**
 * Helper function to convert a binary string to an Uint8Array.
 */ (t);
        return new C(e);
    }
    toBase64() {
        return t = this.i, btoa(t);
        /** Converts a binary string to a Base64 encoded string. */
        var t;
        /** True if and only if the Base64 conversion functions are available. */    }
    toUint8Array() {
        return function(t) {
            const e = new Uint8Array(t.length);
            for (let n = 0; n < t.length; n++) e[n] = t.charCodeAt(n);
            return e;
        }
        /**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (this.i);
    }
    o() {
        return 2 * this.i.length;
    }
    u(t) {
        return v(this.i, t.i);
    }
    isEqual(t) {
        return this.i === t.i;
    }
}

C.h = new C("");

const x = {
    // Causes are copied from:
    // https://github.com/grpc/grpc/blob/bceec94ea4fc5f0085d81235d8e1c06798dc341a/include/grpc%2B%2B/impl/codegen/status_code_enum.h
    /** Not an error; returned on success. */
    OK: "ok",
    /** The operation was cancelled (typically by the caller). */
    CANCELLED: "cancelled",
    /** Unknown error or an error from a different error domain. */
    UNKNOWN: "unknown",
    /**
     * Client specified an invalid argument. Note that this differs from
     * FAILED_PRECONDITION. INVALID_ARGUMENT indicates arguments that are
     * problematic regardless of the state of the system (e.g., a malformed file
     * name).
     */
    INVALID_ARGUMENT: "invalid-argument",
    /**
     * Deadline expired before operation could complete. For operations that
     * change the state of the system, this error may be returned even if the
     * operation has completed successfully. For example, a successful response
     * from a server could have been delayed long enough for the deadline to
     * expire.
     */
    DEADLINE_EXCEEDED: "deadline-exceeded",
    /** Some requested entity (e.g., file or directory) was not found. */
    NOT_FOUND: "not-found",
    /**
     * Some entity that we attempted to create (e.g., file or directory) already
     * exists.
     */
    ALREADY_EXISTS: "already-exists",
    /**
     * The caller does not have permission to execute the specified operation.
     * PERMISSION_DENIED must not be used for rejections caused by exhausting
     * some resource (use RESOURCE_EXHAUSTED instead for those errors).
     * PERMISSION_DENIED must not be used if the caller can not be identified
     * (use UNAUTHENTICATED instead for those errors).
     */
    PERMISSION_DENIED: "permission-denied",
    /**
     * The request does not have valid authentication credentials for the
     * operation.
     */
    UNAUTHENTICATED: "unauthenticated",
    /**
     * Some resource has been exhausted, perhaps a per-user quota, or perhaps the
     * entire file system is out of space.
     */
    RESOURCE_EXHAUSTED: "resource-exhausted",
    /**
     * Operation was rejected because the system is not in a state required for
     * the operation's execution. For example, directory to be deleted may be
     * non-empty, an rmdir operation is applied to a non-directory, etc.
     *
     * A litmus test that may help a service implementor in deciding
     * between FAILED_PRECONDITION, ABORTED, and UNAVAILABLE:
     *  (a) Use UNAVAILABLE if the client can retry just the failing call.
     *  (b) Use ABORTED if the client should retry at a higher-level
     *      (e.g., restarting a read-modify-write sequence).
     *  (c) Use FAILED_PRECONDITION if the client should not retry until
     *      the system state has been explicitly fixed. E.g., if an "rmdir"
     *      fails because the directory is non-empty, FAILED_PRECONDITION
     *      should be returned since the client should not retry unless
     *      they have first fixed up the directory by deleting files from it.
     *  (d) Use FAILED_PRECONDITION if the client performs conditional
     *      REST Get/Update/Delete on a resource and the resource on the
     *      server does not match the condition. E.g., conflicting
     *      read-modify-write on the same resource.
     */
    FAILED_PRECONDITION: "failed-precondition",
    /**
     * The operation was aborted, typically due to a concurrency issue like
     * sequencer check failures, transaction aborts, etc.
     *
     * See litmus test above for deciding between FAILED_PRECONDITION, ABORTED,
     * and UNAVAILABLE.
     */
    ABORTED: "aborted",
    /**
     * Operation was attempted past the valid range. E.g., seeking or reading
     * past end of file.
     *
     * Unlike INVALID_ARGUMENT, this error indicates a problem that may be fixed
     * if the system state changes. For example, a 32-bit file system will
     * generate INVALID_ARGUMENT if asked to read at an offset that is not in the
     * range [0,2^32-1], but it will generate OUT_OF_RANGE if asked to read from
     * an offset past the current file size.
     *
     * There is a fair bit of overlap between FAILED_PRECONDITION and
     * OUT_OF_RANGE. We recommend using OUT_OF_RANGE (the more specific error)
     * when it applies so that callers who are iterating through a space can
     * easily look for an OUT_OF_RANGE error to detect when they are done.
     */
    OUT_OF_RANGE: "out-of-range",
    /** Operation is not implemented or not supported/enabled in this service. */
    UNIMPLEMENTED: "unimplemented",
    /**
     * Internal errors. Means some invariants expected by underlying System has
     * been broken. If you see one of these errors, Something is very broken.
     */
    INTERNAL: "internal",
    /**
     * The service is currently unavailable. This is a most likely a transient
     * condition and may be corrected by retrying with a backoff.
     *
     * See litmus test above for deciding between FAILED_PRECONDITION, ABORTED,
     * and UNAVAILABLE.
     */
    UNAVAILABLE: "unavailable",
    /** Unrecoverable data loss or corruption. */
    DATA_LOSS: "data-loss"
};

/** An error returned by a Firestore operation. */ class N extends Error {
    /** @hideconstructor */
    constructor(t, e) {
        super(e), this.code = t, this.message = e, this.name = "FirebaseError", 
        // HACK: We write a toString property directly because Error is not a real
        // class and so inheritance does not work correctly. We could alternatively
        // do the same "back-door inheritance" trick that FirebaseError does.
        this.toString = () => `${this.name}: [code=${this.code}]: ${this.message}`;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An immutable object representing an array of bytes.
 */ class F {
    /** @hideconstructor */
    constructor(t) {
        this.l = t;
    }
    /**
     * Creates a new `Bytes` object from the given Base64 string, converting it to
     * bytes.
     *
     * @param base64 - The Base64 string used to create the `Bytes` object.
     */    static fromBase64String(t) {
        try {
            return new F(C.fromBase64String(t));
        } catch (t) {
            throw new N(x.INVALID_ARGUMENT, "Failed to construct data from Base64 string: " + t);
        }
    }
    /**
     * Creates a new `Bytes` object from the given Uint8Array.
     *
     * @param array - The Uint8Array used to create the `Bytes` object.
     */    static fromUint8Array(t) {
        return new F(C.fromUint8Array(t));
    }
    /**
     * Returns the underlying bytes as a Base64-encoded string.
     *
     * @returns The Base64-encoded string created from the `Bytes` object.
     */    toBase64() {
        return this.l.toBase64();
    }
    /**
     * Returns the underlying bytes in a new `Uint8Array`.
     *
     * @returns The Uint8Array created from the `Bytes` object.
     */    toUint8Array() {
        return this.l.toUint8Array();
    }
    /**
     * Returns a string representation of the `Bytes` object.
     *
     * @returns A string representation of the `Bytes` object.
     */    toString() {
        return "Bytes(base64: " + this.toBase64() + ")";
    }
    /**
     * Returns true if this `Bytes` object is equal to the provided one.
     *
     * @param other - The `Bytes` object to compare against.
     * @returns true if this `Bytes` object is equal to the provided one.
     */    isEqual(t) {
        return this.l.isEqual(t.l);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A class implemented by all API types of the legacy Firestore API which
 * contains a reference to the API type in the firestore-exp API. All internal
 * code unwraps these references, which allows us to only use firestore-exp
 * types in the SDK.
 */ class O {
    constructor(t) {
        this._ = t;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Helper function to assert Uint8Array is available at runtime. */ function k() {
    if ("undefined" == typeof Uint8Array) throw new N(x.UNIMPLEMENTED, "Uint8Arrays are not available in this environment.");
}

/** Helper function to assert Base64 functions are available at runtime. */ function M() {
    if ("undefined" == typeof atob) throw new N(x.UNIMPLEMENTED, "Blobs are unavailable in Firestore in this environment.");
}

/** Immutable class holding a blob (binary data) */ class L extends O {
    static fromBase64String(t) {
        return M(), new L(F.fromBase64String(t));
    }
    static fromUint8Array(t) {
        return k(), new L(F.fromUint8Array(t));
    }
    toBase64() {
        return M(), this._.toBase64();
    }
    toUint8Array() {
        return k(), this._.toUint8Array();
    }
    isEqual(t) {
        return this._.isEqual(t._);
    }
    toString() {
        return "Blob(base64: " + this.toBase64() + ")";
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Simple wrapper around a nullable UID. Mostly exists to make code more
 * readable.
 */ class $ {
    constructor(t) {
        this.uid = t;
    }
    T() {
        return null != this.uid;
    }
    /**
     * Returns a key representing this user, suitable for inclusion in a
     * dictionary.
     */    I() {
        return this.T() ? "uid:" + this.uid : "anonymous-user";
    }
    isEqual(t) {
        return t.uid === this.uid;
    }
}

/** A user with a null UID. */ $.UNAUTHENTICATED = new $(null), 
// TODO(mikelehen): Look into getting a proper uid-equivalent for
// non-FirebaseAuth providers.
$.m = new $("google-credentials-uid"), $.A = new $("first-party-uid");

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class B {
    constructor(t, e) {
        this.user = e, this.type = "OAuth", this.R = {}, 
        // Set the headers using Object Literal notation to avoid minification
        this.R.Authorization = "Bearer " + t;
    }
}

/** A CredentialsProvider that always yields an empty token. */ class q {
    constructor() {
        /**
         * Stores the listener registered with setChangeListener()
         * This isn't actually necessary since the UID never changes, but we use this
         * to verify the listen contract is adhered to in tests.
         */
        this.P = null;
    }
    getToken() {
        return Promise.resolve(null);
    }
    g() {}
    V(t) {
        this.P = t, 
        // Fire with initial user.
        t($.UNAUTHENTICATED);
    }
    p() {
        this.P = null;
    }
}

class U {
    constructor(t) {
        /**
         * The auth token listener registered with FirebaseApp, retained here so we
         * can unregister it.
         */
        this.v = null, 
        /** Tracks the current User. */
        this.currentUser = $.UNAUTHENTICATED, this.S = !1, 
        /**
         * Counter used to detect if the token changed while a getToken request was
         * outstanding.
         */
        this.D = 0, 
        /** The listener registered with setChangeListener(). */
        this.P = null, this.forceRefresh = !1, this.v = () => {
            this.D++, this.currentUser = this.C(), this.S = !0, this.P && this.P(this.currentUser);
        }, this.D = 0, this.auth = t.getImmediate({
            optional: !0
        }), this.auth ? this.auth.addAuthTokenListener(this.v) : (
        // if auth is not available, invoke tokenListener once with null token
        this.v(null), t.get().then((t => {
            this.auth = t, this.v && 
            // tokenListener can be removed by removeChangeListener()
            this.auth.addAuthTokenListener(this.v);
        }), (() => {})));
    }
    getToken() {
        // Take note of the current value of the tokenCounter so that this method
        // can fail (with an ABORTED error) if there is a token change while the
        // request is outstanding.
        const t = this.D, e = this.forceRefresh;
        return this.forceRefresh = !1, this.auth ? this.auth.getToken(e).then((e => 
        // Cancel the request since the token changed while the request was
        // outstanding so the response is potentially for a previous user (which
        // user, we can't be sure).
        this.D !== t ? (m("FirebaseCredentialsProvider", "getToken aborted due to token change."), 
        this.getToken()) : e ? (g("string" == typeof e.accessToken), new B(e.accessToken, this.currentUser)) : null)) : Promise.resolve(null);
    }
    g() {
        this.forceRefresh = !0;
    }
    V(t) {
        this.P = t, 
        // Fire the initial event
        this.S && t(this.currentUser);
    }
    p() {
        this.auth && this.auth.removeAuthTokenListener(this.v), this.v = null, this.P = null;
    }
    // Auth.getUid() can return null even with a user logged in. It is because
    // getUid() is synchronous, but the auth code populating Uid is asynchronous.
    // This method should only be called in the AuthTokenListener callback
    // to guarantee to get the actual user.
    C() {
        const t = this.auth && this.auth.getUid();
        return g(null === t || "string" == typeof t), new $(t);
    }
}

/*
 * FirstPartyToken provides a fresh token each time its value
 * is requested, because if the token is too old, requests will be rejected.
 * Technically this may no longer be necessary since the SDK should gracefully
 * recover from unauthenticated errors (see b/33147818 for context), but it's
 * safer to keep the implementation as-is.
 */ class Q {
    constructor(t, e) {
        this.N = t, this.F = e, this.type = "FirstParty", this.user = $.A;
    }
    get R() {
        const t = {
            "X-Goog-AuthUser": this.F
        }, e = this.N.auth.getAuthHeaderValueForFirstParty([]);
        // Use array notation to prevent minification
                return e && (t.Authorization = e), t;
    }
}

/*
 * Provides user credentials required for the Firestore JavaScript SDK
 * to authenticate the user, using technique that is only available
 * to applications hosted by Google.
 */ class K {
    constructor(t, e) {
        this.N = t, this.F = e;
    }
    getToken() {
        return Promise.resolve(new Q(this.N, this.F));
    }
    V(t) {
        // Fire with initial uid.
        t($.A);
    }
    p() {}
    g() {}
}

/**
 * Builds a CredentialsProvider depending on the type of
 * the credentials passed in.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class W {
    /**
     * Constructs a DatabaseInfo using the provided host, databaseId and
     * persistenceKey.
     *
     * @param databaseId - The database to use.
     * @param persistenceKey - A unique identifier for this Firestore's local
     * storage (used in conjunction with the databaseId).
     * @param host - The Firestore backend host to connect to.
     * @param ssl - Whether to use SSL when connecting.
     * @param forceLongPolling - Whether to use the forceLongPolling option
     * when using WebChannel as the network transport.
     * @param autoDetectLongPolling - Whether to use the detectBufferingProxy
     * option when using WebChannel as the network transport.
     */
    constructor(t, e, n, s, i, r) {
        this.O = t, this.persistenceKey = e, this.host = n, this.ssl = s, this.forceLongPolling = i, 
        this.k = r;
    }
}

/** The default database name for a project. */
/** Represents the database ID a Firestore client is associated with. */
class j {
    constructor(t, e) {
        this.projectId = t, this.database = e || "(default)";
    }
    get M() {
        return "(default)" === this.database;
    }
    isEqual(t) {
        return t instanceof j && t.projectId === this.projectId && t.database === this.database;
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class G {
    L(t) {
        // No-op.
    }
    $() {
        // No-op.
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// References to `window` are guarded by BrowserConnectivityMonitor.isAvailable()
/* eslint-disable no-restricted-globals */
/**
 * Browser implementation of ConnectivityMonitor.
 */
class z {
    constructor() {
        this.B = () => this.q(), this.U = () => this.K(), this.W = [], this.j();
    }
    L(t) {
        this.W.push(t);
    }
    $() {
        window.removeEventListener("online", this.B), window.removeEventListener("offline", this.U);
    }
    j() {
        window.addEventListener("online", this.B), window.addEventListener("offline", this.U);
    }
    q() {
        m("ConnectivityMonitor", "Network connectivity changed: AVAILABLE");
        for (const t of this.W) t(0 /* AVAILABLE */);
    }
    K() {
        m("ConnectivityMonitor", "Network connectivity changed: UNAVAILABLE");
        for (const t of this.W) t(1 /* UNAVAILABLE */);
    }
    // TODO(chenbrian): Consider passing in window either into this component or
    // here for testing via FakeWindow.
    /** Checks that all used attributes of window are available. */
    static G() {
        return "undefined" != typeof window && void 0 !== window.addEventListener && void 0 !== window.removeEventListener;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Sentinel value that sorts before any Mutation Batch ID. */
/**
 * Returns whether a variable is either undefined or null.
 */
function H(t) {
    return null == t;
}

/** Returns whether the value represents -0. */ function J(t) {
    // Detect if the value is -0.0. Based on polyfill from
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
    return 0 === t && 1 / t == -1 / 0;
}

/**
 * Returns whether a value is an integer and in the safe integer range
 * @param value - The value to test for being an integer and in the safe range
 */ function Y(t) {
    return "number" == typeof t && Number.isInteger(t) && !J(t) && t <= Number.MAX_SAFE_INTEGER && t >= Number.MIN_SAFE_INTEGER;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const X = {
    BatchGetDocuments: "batchGet",
    Commit: "commit",
    RunQuery: "runQuery"
};

/**
 * Maps RPC names to the corresponding REST endpoint name.
 *
 * We use array notation to avoid mangling.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Error Codes describing the different ways GRPC can fail. These are copied
 * directly from GRPC's sources here:
 *
 * https://github.com/grpc/grpc/blob/bceec94ea4fc5f0085d81235d8e1c06798dc341a/include/grpc%2B%2B/impl/codegen/status_code_enum.h
 *
 * Important! The names of these identifiers matter because the string forms
 * are used for reverse lookups from the webchannel stream. Do NOT change the
 * names of these identifiers or change this into a const enum.
 */
var Z, tt;

/**
 * Determines whether an error code represents a permanent error when received
 * in response to a non-write operation.
 *
 * See isPermanentWriteError for classifying write errors.
 */
function et(t) {
    switch (t) {
      case x.OK:
        return y();

      case x.CANCELLED:
      case x.UNKNOWN:
      case x.DEADLINE_EXCEEDED:
      case x.RESOURCE_EXHAUSTED:
      case x.INTERNAL:
      case x.UNAVAILABLE:
 // Unauthenticated means something went wrong with our token and we need
        // to retry with new credentials which will happen automatically.
              case x.UNAUTHENTICATED:
        return !1;

      case x.INVALID_ARGUMENT:
      case x.NOT_FOUND:
      case x.ALREADY_EXISTS:
      case x.PERMISSION_DENIED:
      case x.FAILED_PRECONDITION:
 // Aborted might be retried in some scenarios, but that is dependant on
        // the context and should handled individually by the calling code.
        // See https://cloud.google.com/apis/design/errors.
              case x.ABORTED:
      case x.OUT_OF_RANGE:
      case x.UNIMPLEMENTED:
      case x.DATA_LOSS:
        return !0;

      default:
        return y();
    }
}

/**
 * Determines whether an error code represents a permanent error when received
 * in response to a write operation.
 *
 * Write operations must be handled specially because as of b/119437764, ABORTED
 * errors on the write stream should be retried too (even though ABORTED errors
 * are not generally retryable).
 *
 * Note that during the initial handshake on the write stream an ABORTED error
 * signals that we should discard our stream token (i.e. it is permanent). This
 * means a handshake error should be classified with isPermanentError, above.
 */
/**
 * Maps an error Code from GRPC status code number, like 0, 1, or 14. These
 * are not the same as HTTP status codes.
 *
 * @returns The Code equivalent to the given GRPC status code. Fails if there
 *     is no match.
 */
function nt(t) {
    if (void 0 === t) 
    // This shouldn't normally happen, but in certain error cases (like trying
    // to send invalid proto messages) we may get an error with no GRPC code.
    return A("GRPC error has no .code"), x.UNKNOWN;
    switch (t) {
      case Z.OK:
        return x.OK;

      case Z.CANCELLED:
        return x.CANCELLED;

      case Z.UNKNOWN:
        return x.UNKNOWN;

      case Z.DEADLINE_EXCEEDED:
        return x.DEADLINE_EXCEEDED;

      case Z.RESOURCE_EXHAUSTED:
        return x.RESOURCE_EXHAUSTED;

      case Z.INTERNAL:
        return x.INTERNAL;

      case Z.UNAVAILABLE:
        return x.UNAVAILABLE;

      case Z.UNAUTHENTICATED:
        return x.UNAUTHENTICATED;

      case Z.INVALID_ARGUMENT:
        return x.INVALID_ARGUMENT;

      case Z.NOT_FOUND:
        return x.NOT_FOUND;

      case Z.ALREADY_EXISTS:
        return x.ALREADY_EXISTS;

      case Z.PERMISSION_DENIED:
        return x.PERMISSION_DENIED;

      case Z.FAILED_PRECONDITION:
        return x.FAILED_PRECONDITION;

      case Z.ABORTED:
        return x.ABORTED;

      case Z.OUT_OF_RANGE:
        return x.OUT_OF_RANGE;

      case Z.UNIMPLEMENTED:
        return x.UNIMPLEMENTED;

      case Z.DATA_LOSS:
        return x.DATA_LOSS;

      default:
        return y();
    }
}

/**
 * Converts an HTTP response's error status to the equivalent error code.
 *
 * @param status - An HTTP error response status ("FAILED_PRECONDITION",
 * "UNKNOWN", etc.)
 * @returns The equivalent Code. Non-matching responses are mapped to
 *     Code.UNKNOWN.
 */ (tt = Z || (Z = {}))[tt.OK = 0] = "OK", tt[tt.CANCELLED = 1] = "CANCELLED", 
tt[tt.UNKNOWN = 2] = "UNKNOWN", tt[tt.INVALID_ARGUMENT = 3] = "INVALID_ARGUMENT", 
tt[tt.DEADLINE_EXCEEDED = 4] = "DEADLINE_EXCEEDED", tt[tt.NOT_FOUND = 5] = "NOT_FOUND", 
tt[tt.ALREADY_EXISTS = 6] = "ALREADY_EXISTS", tt[tt.PERMISSION_DENIED = 7] = "PERMISSION_DENIED", 
tt[tt.UNAUTHENTICATED = 16] = "UNAUTHENTICATED", tt[tt.RESOURCE_EXHAUSTED = 8] = "RESOURCE_EXHAUSTED", 
tt[tt.FAILED_PRECONDITION = 9] = "FAILED_PRECONDITION", tt[tt.ABORTED = 10] = "ABORTED", 
tt[tt.OUT_OF_RANGE = 11] = "OUT_OF_RANGE", tt[tt.UNIMPLEMENTED = 12] = "UNIMPLEMENTED", 
tt[tt.INTERNAL = 13] = "INTERNAL", tt[tt.UNAVAILABLE = 14] = "UNAVAILABLE", tt[tt.DATA_LOSS = 15] = "DATA_LOSS";

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Provides a simple helper class that implements the Stream interface to
 * bridge to other implementations that are streams but do not implement the
 * interface. The stream callbacks are invoked with the callOn... methods.
 */
class st {
    constructor(t) {
        this.H = t.H, this.J = t.J;
    }
    Y(t) {
        this.X = t;
    }
    Z(t) {
        this.tt = t;
    }
    onMessage(t) {
        this.et = t;
    }
    close() {
        this.J();
    }
    send(t) {
        this.H(t);
    }
    nt() {
        this.X();
    }
    st(t) {
        this.tt(t);
    }
    it(t) {
        this.et(t);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class it {
    constructor() {
        this.promise = new Promise(((t, e) => {
            this.resolve = t, this.reject = e;
        }));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class rt extends 
/**
 * Base class for all Rest-based connections to the backend (WebChannel and
 * HTTP).
 */
class {
    constructor(t) {
        this.rt = t, this.O = t.O;
        const e = t.ssl ? "https" : "http";
        this.ot = e + "://" + t.host, this.ct = "projects/" + this.O.projectId + "/databases/" + this.O.database + "/documents";
    }
    at(t, e, n, s) {
        const i = this.ut(t, e);
        m("RestConnection", "Sending: ", i, n);
        const r = {};
        return this.ht(r, s), this.lt(t, i, r, n).then((t => (m("RestConnection", "Received: ", t), 
        t)), (e => {
            throw R("RestConnection", t + " failed with error: ", e, "url: ", i, "request:", n), 
            e;
        }));
    }
    _t(t, e, n, s) {
        // The REST API automatically aggregates all of the streamed results, so we
        // can just use the normal invoke() method.
        return this.at(t, e, n, s);
    }
    /**
     * Modifies the headers for a request, adding any authorization token if
     * present and any additional headers for the request.
     */    ht(t, e) {
        if (t["X-Goog-Api-Client"] = "gl-js/ fire/8.2.0", 
        // Content-Type: text/plain will avoid preflight requests which might
        // mess with CORS and redirects by proxies. If we add custom headers
        // we will need to change this code to potentially use the $httpOverwrite
        // parameter supported by ESF to avoid triggering preflight requests.
        t["Content-Type"] = "text/plain", e) for (const n in e.R) e.R.hasOwnProperty(n) && (t[n] = e.R[n]);
    }
    ut(t, e) {
        const n = X[t];
        return `${this.ot}/v1/${e}:${n}`;
    }
} {
    constructor(t) {
        super(t), this.forceLongPolling = t.forceLongPolling, this.k = t.k;
    }
    lt(t, e, n, s) {
        return new Promise(((i, r) => {
            const o = new u;
            o.listenOnce(h.COMPLETE, (() => {
                try {
                    switch (o.getLastErrorCode()) {
                      case l.NO_ERROR:
                        const e = o.getResponseJson();
                        m("Connection", "XHR received:", JSON.stringify(e)), i(e);
                        break;

                      case l.TIMEOUT:
                        m("Connection", 'RPC "' + t + '" timed out'), r(new N(x.DEADLINE_EXCEEDED, "Request time out"));
                        break;

                      case l.HTTP_ERROR:
                        const n = o.getStatus();
                        if (m("Connection", 'RPC "' + t + '" failed with status:', n, "response text:", o.getResponseText()), 
                        n > 0) {
                            const t = o.getResponseJson().error;
                            if (t && t.status && t.message) {
                                const e = function(t) {
                                    const e = t.toLowerCase().replace(/_/g, "-");
                                    return Object.values(x).indexOf(e) >= 0 ? e : x.UNKNOWN;
                                }(t.status);
                                r(new N(e, t.message));
                            } else r(new N(x.UNKNOWN, "Server responded with status " + o.getStatus()));
                        } else 
                        // If we received an HTTP_ERROR but there's no status code,
                        // it's most probably a connection issue
                        r(new N(x.UNAVAILABLE, "Connection failed."));
                        break;

                      default:
                        y();
                    }
                } finally {
                    m("Connection", 'RPC "' + t + '" completed.');
                }
            }));
            const c = JSON.stringify(s);
            o.send(e, "POST", c, n, 15);
        }));
    }
    ft(t, e) {
        const a = [ this.ot, "/", "google.firestore.v1.Firestore", "/", t, "/channel" ], u = _(), h = f(), l = {
            // Required for backend stickiness, routing behavior is based on this
            // parameter.
            httpSessionIdParam: "gsessionid",
            initMessageHeaders: {},
            messageUrlParams: {
                // This param is used to improve routing and project isolation by the
                // backend and must be included in every request.
                database: `projects/${this.O.projectId}/databases/${this.O.database}`
            },
            sendRawJson: !0,
            supportsCrossDomainXhr: !0,
            internalChannelParams: {
                // Override the default timeout (randomized between 10-20 seconds) since
                // a large write batch on a slow internet connection may take a long
                // time to send to the backend. Rather than have WebChannel impose a
                // tight timeout which could lead to infinite timeouts and retries, we
                // set it very large (5-10 minutes) and rely on the browser's builtin
                // timeouts to kick in if the request isn't working.
                forwardChannelRequestTimeoutMs: 6e5
            },
            forceLongPolling: this.forceLongPolling,
            detectBufferingProxy: this.k
        };
        this.ht(l.initMessageHeaders, e), 
        // Sending the custom headers we just added to request.initMessageHeaders
        // (Authorization, etc.) will trigger the browser to make a CORS preflight
        // request because the XHR will no longer meet the criteria for a "simple"
        // CORS request:
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#Simple_requests
        // Therefore to avoid the CORS preflight request (an extra network
        // roundtrip), we use the httpHeadersOverwriteParam option to specify that
        // the headers should instead be encoded into a special "$httpHeaders" query
        // parameter, which is recognized by the webchannel backend. This is
        // formally defined here:
        // https://github.com/google/closure-library/blob/b0e1815b13fb92a46d7c9b3c30de5d6a396a3245/closure/goog/net/rpc/httpcors.js#L32
        // TODO(b/145624756): There is a backend bug where $httpHeaders isn't respected if the request
        // doesn't have an Origin header. So we have to exclude a few browser environments that are
        // known to (sometimes) not include an Origin. See
        // https://github.com/firebase/firebase-js-sdk/issues/1491.
        n() || s() || i() || r() || o() || c() || (l.httpHeadersOverwriteParam = "$httpHeaders");
        const T = a.join("");
        m("Connection", "Creating WebChannel: " + T, l);
        const I = u.createWebChannel(T, l);
        // WebChannel supports sending the first message with the handshake - saving
        // a network round trip. However, it will have to call send in the same
        // JS event loop as open. In order to enforce this, we delay actually
        // opening the WebChannel until send is called. Whether we have called
        // open is tracked with this variable.
                let A = !1, P = !1;
        // A flag to determine whether the stream was closed (by us or through an
        // error/close event) to avoid delivering multiple close events or sending
        // on a closed stream
                const y = new st({
            H: t => {
                P ? m("Connection", "Not sending because WebChannel is closed:", t) : (A || (m("Connection", "Opening WebChannel transport."), 
                I.open(), A = !0), m("Connection", "WebChannel sending:", t), I.send(t));
            },
            J: () => I.close()
        }), V = (t, e, n) => {
            // TODO(dimond): closure typing seems broken because WebChannel does
            // not implement goog.events.Listenable
            t.listen(e, (t => {
                try {
                    n(t);
                } catch (t) {
                    setTimeout((() => {
                        throw t;
                    }), 0);
                }
            }));
        };
        // Closure events are guarded and exceptions are swallowed, so catch any
        // exception and rethrow using a setTimeout so they become visible again.
        // Note that eventually this function could go away if we are confident
        // enough the code is exception free.
                return V(I, d.EventType.OPEN, (() => {
            P || m("Connection", "WebChannel transport opened.");
        })), V(I, d.EventType.CLOSE, (() => {
            P || (P = !0, m("Connection", "WebChannel transport closed"), y.st());
        })), V(I, d.EventType.ERROR, (t => {
            P || (P = !0, R("Connection", "WebChannel transport errored:", t), y.st(new N(x.UNAVAILABLE, "The operation could not be completed")));
        })), V(I, d.EventType.MESSAGE, (t => {
            var e;
            if (!P) {
                const n = t.data[0];
                g(!!n);
                // TODO(b/35143891): There is a bug in One Platform that caused errors
                // (and only errors) to be wrapped in an extra array. To be forward
                // compatible with the bug we need to check either condition. The latter
                // can be removed once the fix has been rolled out.
                // Use any because msgData.error is not typed.
                const s = n, i = s.error || (null === (e = s[0]) || void 0 === e ? void 0 : e.error);
                if (i) {
                    m("Connection", "WebChannel received error:", i);
                    // error.status will be a string like 'OK' or 'NOT_FOUND'.
                    const t = i.status;
                    let e = 
                    /**
 * Maps an error Code from a GRPC status identifier like 'NOT_FOUND'.
 *
 * @returns The Code equivalent to the given status string or undefined if
 *     there is no match.
 */
                    function(t) {
                        // lookup by string
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const e = Z[t];
                        if (void 0 !== e) return nt(e);
                    }(t), n = i.message;
                    void 0 === e && (e = x.INTERNAL, n = "Unknown error status: " + t + " with message " + i.message), 
                    // Mark closed so no further events are propagated
                    P = !0, y.st(new N(e, n)), I.close();
                } else m("Connection", "WebChannel received:", n), y.it(n);
            }
        })), V(h, w.STAT_EVENT, (t => {
            t.stat === E.PROXY ? m("Connection", "Detected buffering proxy") : t.stat === E.NOPROXY && m("Connection", "Detected no buffering proxy");
        })), setTimeout((() => {
            // Technically we could/should wait for the WebChannel opened event,
            // but because we want to send the first message with the WebChannel
            // handshake we pretend the channel opened here (asynchronously), and
            // then delay the actual open until the first message is sent.
            y.nt();
        }), 0), y;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Initializes the WebChannelConnection for the browser. */
/**
 * A `Timestamp` represents a point in time independent of any time zone or
 * calendar, represented as seconds and fractions of seconds at nanosecond
 * resolution in UTC Epoch time.
 *
 * It is encoded using the Proleptic Gregorian Calendar which extends the
 * Gregorian calendar backwards to year one. It is encoded assuming all minutes
 * are 60 seconds long, i.e. leap seconds are "smeared" so that no leap second
 * table is needed for interpretation. Range is from 0001-01-01T00:00:00Z to
 * 9999-12-31T23:59:59.999999999Z.
 *
 * @see https://github.com/google/protobuf/blob/master/src/google/protobuf/timestamp.proto
 */
class ot {
    /**
     * Creates a new timestamp.
     *
     * @param seconds - The number of seconds of UTC time since Unix epoch
     *     1970-01-01T00:00:00Z. Must be from 0001-01-01T00:00:00Z to
     *     9999-12-31T23:59:59Z inclusive.
     * @param nanoseconds - The non-negative fractions of a second at nanosecond
     *     resolution. Negative second values with fractions must still have
     *     non-negative nanoseconds values that count forward in time. Must be
     *     from 0 to 999,999,999 inclusive.
     */
    constructor(t, e) {
        if (this.seconds = t, this.nanoseconds = e, e < 0) throw new N(x.INVALID_ARGUMENT, "Timestamp nanoseconds out of range: " + e);
        if (e >= 1e9) throw new N(x.INVALID_ARGUMENT, "Timestamp nanoseconds out of range: " + e);
        if (t < -62135596800) throw new N(x.INVALID_ARGUMENT, "Timestamp seconds out of range: " + t);
        // This will break in the year 10,000.
                if (t >= 253402300800) throw new N(x.INVALID_ARGUMENT, "Timestamp seconds out of range: " + t);
    }
    /**
     * Creates a new timestamp with the current date, with millisecond precision.
     *
     * @returns a new timestamp representing the current date.
     */    static now() {
        return ot.fromMillis(Date.now());
    }
    /**
     * Creates a new timestamp from the given date.
     *
     * @param date - The date to initialize the `Timestamp` from.
     * @returns A new `Timestamp` representing the same point in time as the given
     *     date.
     */    static fromDate(t) {
        return ot.fromMillis(t.getTime());
    }
    /**
     * Creates a new timestamp from the given number of milliseconds.
     *
     * @param milliseconds - Number of milliseconds since Unix epoch
     *     1970-01-01T00:00:00Z.
     * @returns A new `Timestamp` representing the same point in time as the given
     *     number of milliseconds.
     */    static fromMillis(t) {
        const e = Math.floor(t / 1e3);
        return new ot(e, 1e6 * (t - 1e3 * e));
    }
    /**
     * Converts a `Timestamp` to a JavaScript `Date` object. This conversion causes
     * a loss of precision since `Date` objects only support millisecond precision.
     *
     * @returns JavaScript `Date` object representing the same point in time as
     *     this `Timestamp`, with millisecond precision.
     */    toDate() {
        return new Date(this.toMillis());
    }
    /**
     * Converts a `Timestamp` to a numeric timestamp (in milliseconds since
     * epoch). This operation causes a loss of precision.
     *
     * @returns The point in time corresponding to this timestamp, represented as
     *     the number of milliseconds since Unix epoch 1970-01-01T00:00:00Z.
     */    toMillis() {
        return 1e3 * this.seconds + this.nanoseconds / 1e6;
    }
    dt(t) {
        return this.seconds === t.seconds ? v(this.nanoseconds, t.nanoseconds) : v(this.seconds, t.seconds);
    }
    /**
     * Returns true if this `Timestamp` is equal to the provided one.
     *
     * @param other - The `Timestamp` to compare against.
     * @returns true if this `Timestamp` is equal to the provided one.
     */    isEqual(t) {
        return t.seconds === this.seconds && t.nanoseconds === this.nanoseconds;
    }
    toString() {
        return "Timestamp(seconds=" + this.seconds + ", nanoseconds=" + this.nanoseconds + ")";
    }
    toJSON() {
        return {
            seconds: this.seconds,
            nanoseconds: this.nanoseconds
        };
    }
    /**
     * Converts this object to a primitive string, which allows Timestamp objects to be compared
     * using the `>`, `<=`, `>=` and `>` operators.
     */    valueOf() {
        // This method returns a string of the form <seconds>.<nanoseconds> where <seconds> is
        // translated to have a non-negative value and both <seconds> and <nanoseconds> are left-padded
        // with zeroes to be a consistent length. Strings with this format then have a lexiographical
        // ordering that matches the expected ordering. The <seconds> translation is done to avoid
        // having a leading negative sign (i.e. a leading '-' character) in its string representation,
        // which would affect its lexiographical ordering.
        const t = this.seconds - -62135596800;
        // Note: Up to 12 decimal digits are required to represent all valid 'seconds' values.
                return String(t).padStart(12, "0") + "." + String(this.nanoseconds).padStart(9, "0");
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A version of a document in Firestore. This corresponds to the version
 * timestamp, such as update_time or read_time.
 */ class ct {
    constructor(t) {
        this.timestamp = t;
    }
    static wt(t) {
        return new ct(t);
    }
    static min() {
        return new ct(new ot(0, 0));
    }
    u(t) {
        return this.timestamp.dt(t.timestamp);
    }
    isEqual(t) {
        return this.timestamp.isEqual(t.timestamp);
    }
    /** Returns a number representation of the version for use in spec tests. */    Et() {
        // Convert to microseconds.
        return 1e6 * this.timestamp.seconds + this.timestamp.nanoseconds / 1e3;
    }
    toString() {
        return "SnapshotVersion(" + this.timestamp.toString() + ")";
    }
    Tt() {
        return this.timestamp;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Path represents an ordered sequence of string segments.
 */
class at {
    constructor(t, e, n) {
        void 0 === e ? e = 0 : e > t.length && y(), void 0 === n ? n = t.length - e : n > t.length - e && y(), 
        this.segments = t, this.offset = e, this.It = n;
    }
    get length() {
        return this.It;
    }
    isEqual(t) {
        return 0 === at.At(this, t);
    }
    child(t) {
        const e = this.segments.slice(this.offset, this.limit());
        return t instanceof at ? t.forEach((t => {
            e.push(t);
        })) : e.push(t), this.Rt(e);
    }
    /** The index of one past the last segment of the path. */    limit() {
        return this.offset + this.length;
    }
    Pt(t) {
        return t = void 0 === t ? 1 : t, this.Rt(this.segments, this.offset + t, this.length - t);
    }
    yt() {
        return this.Rt(this.segments, this.offset, this.length - 1);
    }
    gt() {
        return this.segments[this.offset];
    }
    Vt() {
        return this.get(this.length - 1);
    }
    get(t) {
        return this.segments[this.offset + t];
    }
    bt() {
        return 0 === this.length;
    }
    vt(t) {
        if (t.length < this.length) return !1;
        for (let e = 0; e < this.length; e++) if (this.get(e) !== t.get(e)) return !1;
        return !0;
    }
    St(t) {
        if (this.length + 1 !== t.length) return !1;
        for (let e = 0; e < this.length; e++) if (this.get(e) !== t.get(e)) return !1;
        return !0;
    }
    forEach(t) {
        for (let e = this.offset, n = this.limit(); e < n; e++) t(this.segments[e]);
    }
    Dt() {
        return this.segments.slice(this.offset, this.limit());
    }
    static At(t, e) {
        const n = Math.min(t.length, e.length);
        for (let s = 0; s < n; s++) {
            const n = t.get(s), i = e.get(s);
            if (n < i) return -1;
            if (n > i) return 1;
        }
        return t.length < e.length ? -1 : t.length > e.length ? 1 : 0;
    }
}

/**
 * A slash-separated path for navigating resources (documents and collections)
 * within Firestore.
 */ class ut extends at {
    Rt(t, e, n) {
        return new ut(t, e, n);
    }
    Ct() {
        // NOTE: The client is ignorant of any path segments containing escape
        // sequences (e.g. __id123__) and just passes them through raw (they exist
        // for legacy reasons and should not be used frequently).
        return this.Dt().join("/");
    }
    toString() {
        return this.Ct();
    }
    /**
     * Creates a resource path from the given slash-delimited string. If multiple
     * arguments are provided, all components are combined. Leading and trailing
     * slashes from all components are ignored.
     */    static xt(...t) {
        // NOTE: The client is ignorant of any path segments containing escape
        // sequences (e.g. __id123__) and just passes them through raw (they exist
        // for legacy reasons and should not be used frequently).
        const e = [];
        for (const n of t) {
            if (n.indexOf("//") >= 0) throw new N(x.INVALID_ARGUMENT, `Invalid segment (${n}). Paths must not contain // in them.`);
            // Strip leading and traling slashed.
                        e.push(...n.split("/").filter((t => t.length > 0)));
        }
        return new ut(e);
    }
    static Nt() {
        return new ut([]);
    }
}

const ht = /^[_a-zA-Z][_a-zA-Z0-9]*$/;

/** A dot-separated path for navigating sub-objects within a document. */ class lt extends at {
    Rt(t, e, n) {
        return new lt(t, e, n);
    }
    /**
     * Returns true if the string could be used as a segment in a field path
     * without escaping.
     */    static Ft(t) {
        return ht.test(t);
    }
    Ct() {
        return this.Dt().map((t => (t = t.replace(/\\/g, "\\\\").replace(/`/g, "\\`"), lt.Ft(t) || (t = "`" + t + "`"), 
        t))).join(".");
    }
    toString() {
        return this.Ct();
    }
    /**
     * Returns true if this field references the key of a document.
     */    Ot() {
        return 1 === this.length && "__name__" === this.get(0);
    }
    /**
     * The field designating the key of a document.
     */    static kt() {
        return new lt([ "__name__" ]);
    }
    /**
     * Parses a field string from the given server-formatted string.
     *
     * - Splitting the empty string is not allowed (for now at least).
     * - Empty segments within the string (e.g. if there are two consecutive
     *   separators) are not allowed.
     *
     * TODO(b/37244157): we should make this more strict. Right now, it allows
     * non-identifier path components, even if they aren't escaped.
     */    static Mt(t) {
        const e = [];
        let n = "", s = 0;
        const i = () => {
            if (0 === n.length) throw new N(x.INVALID_ARGUMENT, `Invalid field path (${t}). Paths must not be empty, begin with '.', end with '.', or contain '..'`);
            e.push(n), n = "";
        };
        let r = !1;
        for (;s < t.length; ) {
            const e = t[s];
            if ("\\" === e) {
                if (s + 1 === t.length) throw new N(x.INVALID_ARGUMENT, "Path has trailing escape character: " + t);
                const e = t[s + 1];
                if ("\\" !== e && "." !== e && "`" !== e) throw new N(x.INVALID_ARGUMENT, "Path has invalid escape sequence: " + t);
                n += e, s += 2;
            } else "`" === e ? (r = !r, s++) : "." !== e || r ? (n += e, s++) : (i(), s++);
        }
        if (i(), r) throw new N(x.INVALID_ARGUMENT, "Unterminated ` in path: " + t);
        return new lt(e);
    }
    static Nt() {
        return new lt([]);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class _t {
    constructor(t) {
        this.path = t;
    }
    static Lt(t) {
        return new _t(ut.xt(t));
    }
    static $t(t) {
        return new _t(ut.xt(t).Pt(5));
    }
    /** Returns true if the document is in the specified collectionId. */    Bt(t) {
        return this.path.length >= 2 && this.path.get(this.path.length - 2) === t;
    }
    isEqual(t) {
        return null !== t && 0 === ut.At(this.path, t.path);
    }
    toString() {
        return this.path.toString();
    }
    static At(t, e) {
        return ut.At(t.path, e.path);
    }
    static qt(t) {
        return t.length % 2 == 0;
    }
    /**
     * Creates and returns a new document key with the given segments.
     *
     * @param segments - The segments of the path to the document
     * @returns A new instance of DocumentKey
     */    static Ut(t) {
        return new _t(new ut(t.slice()));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function ft(t) {
    let e = 0;
    for (const n in t) Object.prototype.hasOwnProperty.call(t, n) && e++;
    return e;
}

function dt(t, e) {
    for (const n in t) Object.prototype.hasOwnProperty.call(t, n) && e(n, t[n]);
}

function wt(t) {
    for (const e in t) if (Object.prototype.hasOwnProperty.call(t, e)) return !1;
    return !0;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Provides a set of fields that can be used to partially patch a document.
 * FieldMask is used in conjunction with ObjectValue.
 * Examples:
 *   foo - Overwrites foo entirely with the provided value. If foo is not
 *         present in the companion ObjectValue, the field is deleted.
 *   foo.bar - Overwrites only the field bar of the object foo.
 *             If foo is not an object, foo is replaced with an object
 *             containing foo
 */ class Et {
    constructor(t) {
        this.fields = t, 
        // TODO(dimond): validation of FieldMask
        // Sort the field mask to support `FieldMask.isEqual()` and assert below.
        t.sort(lt.At);
    }
    /**
     * Verifies that `fieldPath` is included by at least one field in this field
     * mask.
     *
     * This is an O(n) operation, where `n` is the size of the field mask.
     */    Qt(t) {
        for (const e of this.fields) if (e.vt(t)) return !0;
        return !1;
    }
    isEqual(t) {
        return S(this.fields, t.fields, ((t, e) => t.isEqual(e)));
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// A RegExp matching ISO 8601 UTC timestamps with optional fraction.
const Tt = new RegExp(/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.(\d+))?Z$/);

/**
 * Converts the possible Proto values for a timestamp value into a "seconds and
 * nanos" representation.
 */ function It(t) {
    // The json interface (for the browser) will return an iso timestamp string,
    // while the proto js library (for node) will return a
    // google.protobuf.Timestamp instance.
    if (g(!!t), "string" == typeof t) {
        // The date string can have higher precision (nanos) than the Date class
        // (millis), so we do some custom parsing here.
        // Parse the nanos right out of the string.
        let e = 0;
        const n = Tt.exec(t);
        if (g(!!n), n[1]) {
            // Pad the fraction out to 9 digits (nanos).
            let t = n[1];
            t = (t + "000000000").substr(0, 9), e = Number(t);
        }
        // Parse the date to get the seconds.
                const s = new Date(t);
        return {
            seconds: Math.floor(s.getTime() / 1e3),
            nanos: e
        };
    }
    return {
        seconds: mt(t.seconds),
        nanos: mt(t.nanos)
    };
}

/**
 * Converts the possible Proto types for numbers into a JavaScript number.
 * Returns 0 if the value is not numeric.
 */ function mt(t) {
    // TODO(bjornick): Handle int64 greater than 53 bits.
    return "number" == typeof t ? t : "string" == typeof t ? Number(t) : 0;
}

/** Converts the possible Proto types for Blobs into a ByteString. */ function At(t) {
    return "string" == typeof t ? C.fromBase64String(t) : C.fromUint8Array(t);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Represents a locally-applied ServerTimestamp.
 *
 * Server Timestamps are backed by MapValues that contain an internal field
 * `__type__` with a value of `server_timestamp`. The previous value and local
 * write time are stored in its `__previous_value__` and `__local_write_time__`
 * fields respectively.
 *
 * Notes:
 * - ServerTimestampValue instances are created as the result of applying a
 *   TransformMutation (see TransformMutation.applyTo()). They can only exist in
 *   the local view of a document. Therefore they do not need to be parsed or
 *   serialized.
 * - When evaluated locally (e.g. for snapshot.data()), they by default
 *   evaluate to `null`. This behavior can be configured by passing custom
 *   FieldValueOptions to value().
 * - With respect to other ServerTimestampValues, they sort by their
 *   localWriteTime.
 */ function Rt(t) {
    var e, n;
    return "server_timestamp" === (null === (n = ((null === (e = null == t ? void 0 : t.mapValue) || void 0 === e ? void 0 : e.fields) || {}).__type__) || void 0 === n ? void 0 : n.stringValue);
}

/**
 * Creates a new ServerTimestamp proto value (using the internal format).
 */
/**
 * Returns the value of the field before this ServerTimestamp was set.
 *
 * Preserving the previous values allows the user to display the last resoled
 * value until the backend responds with the timestamp.
 */
function Pt(t) {
    const e = t.mapValue.fields.__previous_value__;
    return Rt(e) ? Pt(e) : e;
}

/**
 * Returns the local time at which this timestamp was first set.
 */ function yt(t) {
    const e = It(t.mapValue.fields.__local_write_time__.timestampValue);
    return new ot(e.seconds, e.nanos);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Extracts the backend's type order for the provided value. */ function gt(t) {
    return "nullValue" in t ? 0 /* NullValue */ : "booleanValue" in t ? 1 /* BooleanValue */ : "integerValue" in t || "doubleValue" in t ? 2 /* NumberValue */ : "timestampValue" in t ? 3 /* TimestampValue */ : "stringValue" in t ? 5 /* StringValue */ : "bytesValue" in t ? 6 /* BlobValue */ : "referenceValue" in t ? 7 /* RefValue */ : "geoPointValue" in t ? 8 /* GeoPointValue */ : "arrayValue" in t ? 9 /* ArrayValue */ : "mapValue" in t ? Rt(t) ? 4 /* ServerTimestampValue */ : 10 /* ObjectValue */ : y();
}

/** Tests `left` and `right` for equality based on the backend semantics. */ function Vt(t, e) {
    const n = gt(t);
    if (n !== gt(e)) return !1;
    switch (n) {
      case 0 /* NullValue */ :
        return !0;

      case 1 /* BooleanValue */ :
        return t.booleanValue === e.booleanValue;

      case 4 /* ServerTimestampValue */ :
        return yt(t).isEqual(yt(e));

      case 3 /* TimestampValue */ :
        return function(t, e) {
            if ("string" == typeof t.timestampValue && "string" == typeof e.timestampValue && t.timestampValue.length === e.timestampValue.length) 
            // Use string equality for ISO 8601 timestamps
            return t.timestampValue === e.timestampValue;
            const n = It(t.timestampValue), s = It(e.timestampValue);
            return n.seconds === s.seconds && n.nanos === s.nanos;
        }(t, e);

      case 5 /* StringValue */ :
        return t.stringValue === e.stringValue;

      case 6 /* BlobValue */ :
        return function(t, e) {
            return At(t.bytesValue).isEqual(At(e.bytesValue));
        }(t, e);

      case 7 /* RefValue */ :
        return t.referenceValue === e.referenceValue;

      case 8 /* GeoPointValue */ :
        return function(t, e) {
            return mt(t.geoPointValue.latitude) === mt(e.geoPointValue.latitude) && mt(t.geoPointValue.longitude) === mt(e.geoPointValue.longitude);
        }(t, e);

      case 2 /* NumberValue */ :
        return function(t, e) {
            if ("integerValue" in t && "integerValue" in e) return mt(t.integerValue) === mt(e.integerValue);
            if ("doubleValue" in t && "doubleValue" in e) {
                const n = mt(t.doubleValue), s = mt(e.doubleValue);
                return n === s ? J(n) === J(s) : isNaN(n) && isNaN(s);
            }
            return !1;
        }(t, e);

      case 9 /* ArrayValue */ :
        return S(t.arrayValue.values || [], e.arrayValue.values || [], Vt);

      case 10 /* ObjectValue */ :
        return function(t, e) {
            const n = t.mapValue.fields || {}, s = e.mapValue.fields || {};
            if (ft(n) !== ft(s)) return !1;
            for (const t in n) if (n.hasOwnProperty(t) && (void 0 === s[t] || !Vt(n[t], s[t]))) return !1;
            return !0;
        }
        /** Returns true if the ArrayValue contains the specified element. */ (t, e);

      default:
        return y();
    }
}

function pt(t, e) {
    return void 0 !== (t.values || []).find((t => Vt(t, e)));
}

function bt(t, e) {
    const n = gt(t), s = gt(e);
    if (n !== s) return v(n, s);
    switch (n) {
      case 0 /* NullValue */ :
        return 0;

      case 1 /* BooleanValue */ :
        return v(t.booleanValue, e.booleanValue);

      case 2 /* NumberValue */ :
        return function(t, e) {
            const n = mt(t.integerValue || t.doubleValue), s = mt(e.integerValue || e.doubleValue);
            return n < s ? -1 : n > s ? 1 : n === s ? 0 : 
            // one or both are NaN.
            isNaN(n) ? isNaN(s) ? 0 : -1 : 1;
        }(t, e);

      case 3 /* TimestampValue */ :
        return vt(t.timestampValue, e.timestampValue);

      case 4 /* ServerTimestampValue */ :
        return vt(yt(t), yt(e));

      case 5 /* StringValue */ :
        return v(t.stringValue, e.stringValue);

      case 6 /* BlobValue */ :
        return function(t, e) {
            const n = At(t), s = At(e);
            return n.u(s);
        }(t.bytesValue, e.bytesValue);

      case 7 /* RefValue */ :
        return function(t, e) {
            const n = t.split("/"), s = e.split("/");
            for (let t = 0; t < n.length && t < s.length; t++) {
                const e = v(n[t], s[t]);
                if (0 !== e) return e;
            }
            return v(n.length, s.length);
        }(t.referenceValue, e.referenceValue);

      case 8 /* GeoPointValue */ :
        return function(t, e) {
            const n = v(mt(t.latitude), mt(e.latitude));
            if (0 !== n) return n;
            return v(mt(t.longitude), mt(e.longitude));
        }(t.geoPointValue, e.geoPointValue);

      case 9 /* ArrayValue */ :
        return function(t, e) {
            const n = t.values || [], s = e.values || [];
            for (let t = 0; t < n.length && t < s.length; ++t) {
                const e = bt(n[t], s[t]);
                if (e) return e;
            }
            return v(n.length, s.length);
        }(t.arrayValue, e.arrayValue);

      case 10 /* ObjectValue */ :
        return function(t, e) {
            const n = t.fields || {}, s = Object.keys(n), i = e.fields || {}, r = Object.keys(i);
            // Even though MapValues are likely sorted correctly based on their insertion
            // order (e.g. when received from the backend), local modifications can bring
            // elements out of order. We need to re-sort the elements to ensure that
            // canonical IDs are independent of insertion order.
            s.sort(), r.sort();
            for (let t = 0; t < s.length && t < r.length; ++t) {
                const e = v(s[t], r[t]);
                if (0 !== e) return e;
                const o = bt(n[s[t]], i[r[t]]);
                if (0 !== o) return o;
            }
            return v(s.length, r.length);
        }
        /**
 * Generates the canonical ID for the provided field value (as used in Target
 * serialization).
 */ (t.mapValue, e.mapValue);

      default:
        throw y();
    }
}

function vt(t, e) {
    if ("string" == typeof t && "string" == typeof e && t.length === e.length) return v(t, e);
    const n = It(t), s = It(e), i = v(n.seconds, s.seconds);
    return 0 !== i ? i : v(n.nanos, s.nanos);
}

function St(t) {
    return Dt(t);
}

function Dt(t) {
    return "nullValue" in t ? "null" : "booleanValue" in t ? "" + t.booleanValue : "integerValue" in t ? "" + t.integerValue : "doubleValue" in t ? "" + t.doubleValue : "timestampValue" in t ? function(t) {
        const e = It(t);
        return `time(${e.seconds},${e.nanos})`;
    }(t.timestampValue) : "stringValue" in t ? t.stringValue : "bytesValue" in t ? At(t.bytesValue).toBase64() : "referenceValue" in t ? (n = t.referenceValue, 
    _t.$t(n).toString()) : "geoPointValue" in t ? `geo(${(e = t.geoPointValue).latitude},${e.longitude})` : "arrayValue" in t ? function(t) {
        let e = "[", n = !0;
        for (const s of t.values || []) n ? n = !1 : e += ",", e += Dt(s);
        return e + "]";
    }
    /** Returns a reference value for the provided database and key. */ (t.arrayValue) : "mapValue" in t ? function(t) {
        // Iteration order in JavaScript is not guaranteed. To ensure that we generate
        // matching canonical IDs for identical maps, we need to sort the keys.
        const e = Object.keys(t.fields || {}).sort();
        let n = "{", s = !0;
        for (const i of e) s ? s = !1 : n += ",", n += `${i}:${Dt(t.fields[i])}`;
        return n + "}";
    }(t.mapValue) : y();
    var e, n;
}

function Ct(t, e) {
    return {
        referenceValue: `projects/${t.projectId}/databases/${t.database}/documents/${e.path.Ct()}`
    };
}

/** Returns true if `value` is an IntegerValue . */ function xt(t) {
    return !!t && "integerValue" in t;
}

/** Returns true if `value` is a DoubleValue. */
/** Returns true if `value` is an ArrayValue. */
function Nt(t) {
    return !!t && "arrayValue" in t;
}

/** Returns true if `value` is a NullValue. */ function Ft(t) {
    return !!t && "nullValue" in t;
}

/** Returns true if `value` is NaN. */ function Ot(t) {
    return !!t && "doubleValue" in t && isNaN(Number(t.doubleValue));
}

/** Returns true if `value` is a MapValue. */ function kt(t) {
    return !!t && "mapValue" in t;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An ObjectValue represents a MapValue in the Firestore Proto and offers the
 * ability to add and remove fields (via the ObjectValueBuilder).
 */ class Mt {
    constructor(t) {
        this.proto = t;
    }
    static empty() {
        return new Mt({
            mapValue: {}
        });
    }
    /**
     * Returns the value at the given path or null.
     *
     * @param path - the path to search
     * @returns The value at the path or if there it doesn't exist.
     */    field(t) {
        if (t.bt()) return this.proto;
        {
            let e = this.proto;
            for (let n = 0; n < t.length - 1; ++n) {
                if (!e.mapValue.fields) return null;
                if (e = e.mapValue.fields[t.get(n)], !kt(e)) return null;
            }
            return e = (e.mapValue.fields || {})[t.Vt()], e || null;
        }
    }
    isEqual(t) {
        return Vt(this.proto, t.proto);
    }
}

/**
 * An ObjectValueBuilder provides APIs to set and delete fields from an
 * ObjectValue.
 */ class Lt {
    /**
     * @param baseObject - The object to mutate.
     */
    constructor(t = Mt.empty()) {
        this.Kt = t, 
        /** A map that contains the accumulated changes in this builder. */
        this.Wt = new Map;
    }
    /**
     * Sets the field to the provided value.
     *
     * @param path - The field path to set.
     * @param value - The value to set.
     * @returns The current Builder instance.
     */    set(t, e) {
        return this.jt(t, e), this;
    }
    /**
     * Removes the field at the specified path. If there is no field at the
     * specified path, nothing is changed.
     *
     * @param path - The field path to remove.
     * @returns The current Builder instance.
     */    delete(t) {
        return this.jt(t, null), this;
    }
    /**
     * Adds `value` to the overlay map at `path`. Creates nested map entries if
     * needed.
     */    jt(t, e) {
        let n = this.Wt;
        for (let e = 0; e < t.length - 1; ++e) {
            const s = t.get(e);
            let i = n.get(s);
            i instanceof Map ? 
            // Re-use a previously created map
            n = i : i && 10 /* ObjectValue */ === gt(i) ? (
            // Convert the existing Protobuf MapValue into a map
            i = new Map(Object.entries(i.mapValue.fields || {})), n.set(s, i), n = i) : (
            // Create an empty map to represent the current nesting level
            i = new Map, n.set(s, i), n = i);
        }
        n.set(t.Vt(), e);
    }
    /** Returns an ObjectValue with all mutations applied. */    Gt() {
        const t = this.zt(lt.Nt(), this.Wt);
        return null != t ? new Mt(t) : this.Kt;
    }
    /**
     * Applies any overlays from `currentOverlays` that exist at `currentPath`
     * and returns the merged data at `currentPath` (or null if there were no
     * changes).
     *
     * @param currentPath - The path at the current nesting level. Can be set to
     * FieldValue.emptyPath() to represent the root.
     * @param currentOverlays - The overlays at the current nesting level in the
     * same format as `overlayMap`.
     * @returns The merged data at `currentPath` or null if no modifications
     * were applied.
     */    zt(t, e) {
        let n = !1;
        const s = this.Kt.field(t), i = kt(s) ? // If there is already data at the current path, base our
        Object.assign({}, s.mapValue.fields) : {};
        return e.forEach(((e, s) => {
            if (e instanceof Map) {
                const r = this.zt(t.child(s), e);
                null != r && (i[s] = r, n = !0);
            } else null !== e ? (i[s] = e, n = !0) : i.hasOwnProperty(s) && (delete i[s], n = !0);
        })), n ? {
            mapValue: {
                fields: i
            }
        } : null;
    }
}

/**
 * Returns a FieldMask built from all fields in a MapValue.
 */ function $t(t) {
    const e = [];
    return dt(t.fields || {}, ((t, n) => {
        const s = new lt([ t ]);
        if (kt(n)) {
            const t = $t(n.mapValue).fields;
            if (0 === t.length) 
            // Preserve the empty map by adding it to the FieldMask.
            e.push(s); else 
            // For nested and non-empty ObjectValues, add the FieldPath of the
            // leaf nodes.
            for (const n of t) e.push(s.child(n));
        } else 
        // For nested and non-empty ObjectValues, add the FieldPath of the leaf
        // nodes.
        e.push(s);
    })), new Et(e);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * The result of a lookup for a given path may be an existing document or a
 * marker that this document does not exist at a given version.
 */ class Bt {
    constructor(t, e) {
        this.key = t, this.version = e;
    }
}

/**
 * Represents a document in Firestore with a key, version, data and whether the
 * data has local mutations applied to it.
 */ class qt extends Bt {
    constructor(t, e, n, s) {
        super(t, e), this.Ht = n, this.Jt = !!s.Jt, this.hasCommittedMutations = !!s.hasCommittedMutations;
    }
    field(t) {
        return this.Ht.field(t);
    }
    data() {
        return this.Ht;
    }
    Yt() {
        return this.Ht.proto;
    }
    isEqual(t) {
        return t instanceof qt && this.key.isEqual(t.key) && this.version.isEqual(t.version) && this.Jt === t.Jt && this.hasCommittedMutations === t.hasCommittedMutations && this.Ht.isEqual(t.Ht);
    }
    toString() {
        return `Document(${this.key}, ${this.version}, ${this.Ht.toString()}, {hasLocalMutations: ${this.Jt}}), {hasCommittedMutations: ${this.hasCommittedMutations}})`;
    }
    get hasPendingWrites() {
        return this.Jt || this.hasCommittedMutations;
    }
}

/**
 * Compares the value for field `field` in the provided documents. Throws if
 * the field does not exist in both documents.
 */
/**
 * A class representing a deleted document.
 * Version is set to 0 if we don't point to any specific time, otherwise it
 * denotes time we know it didn't exist at.
 */
class Ut extends Bt {
    constructor(t, e, n) {
        super(t, e), this.hasCommittedMutations = !(!n || !n.hasCommittedMutations);
    }
    toString() {
        return `NoDocument(${this.key}, ${this.version})`;
    }
    get hasPendingWrites() {
        return this.hasCommittedMutations;
    }
    isEqual(t) {
        return t instanceof Ut && t.hasCommittedMutations === this.hasCommittedMutations && t.version.isEqual(this.version) && t.key.isEqual(this.key);
    }
}

/**
 * A class representing an existing document whose data is unknown (e.g. a
 * document that was updated without a known base document).
 */ class Qt extends Bt {
    toString() {
        return `UnknownDocument(${this.key}, ${this.version})`;
    }
    get hasPendingWrites() {
        return !0;
    }
    isEqual(t) {
        return t instanceof Qt && t.version.isEqual(this.version) && t.key.isEqual(this.key);
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// Visible for testing
class Kt {
    constructor(t, e = null, n = [], s = [], i = null, r = null, o = null) {
        this.path = t, this.collectionGroup = e, this.orderBy = n, this.filters = s, this.limit = i, 
        this.startAt = r, this.endAt = o, this.Xt = null;
    }
}

/**
 * Initializes a Target with a path and optional additional query constraints.
 * Path must currently be empty if this is a collection group query.
 *
 * NOTE: you should always construct `Target` from `Query.toTarget` instead of
 * using this factory method, because `Query` provides an implicit `orderBy`
 * property.
 */ function Wt(t, e = null, n = [], s = [], i = null, r = null, o = null) {
    return new Kt(t, e, n, s, i, r, o);
}

function jt(t) {
    const e = V(t);
    if (null === e.Xt) {
        let t = e.path.Ct();
        null !== e.collectionGroup && (t += "|cg:" + e.collectionGroup), t += "|f:", t += e.filters.map((t => Yt(t))).join(","), 
        t += "|ob:", t += e.orderBy.map((t => function(t) {
            // TODO(b/29183165): Make this collision robust.
            return t.field.Ct() + t.dir;
        }(t))).join(","), H(e.limit) || (t += "|l:", t += e.limit), e.startAt && (t += "|lb:", 
        t += ce(e.startAt)), e.endAt && (t += "|ub:", t += ce(e.endAt)), e.Xt = t;
    }
    return e.Xt;
}

function Gt(t) {
    let e = t.path.Ct();
    return null !== t.collectionGroup && (e += " collectionGroup=" + t.collectionGroup), 
    t.filters.length > 0 && (e += `, filters: [${t.filters.map((t => {
        return `${(e = t).field.Ct()} ${e.op} ${St(e.value)}`;
        /** Returns a debug description for `filter`. */
        var e;
        /** Filter that matches on key fields (i.e. '__name__'). */    })).join(", ")}]`), 
    H(t.limit) || (e += ", limit: " + t.limit), t.orderBy.length > 0 && (e += `, orderBy: [${t.orderBy.map((t => function(t) {
        return `${t.field.Ct()} (${t.dir})`;
    }(t))).join(", ")}]`), t.startAt && (e += ", startAt: " + ce(t.startAt)), t.endAt && (e += ", endAt: " + ce(t.endAt)), 
    `Target(${e})`;
}

function zt(t, e) {
    if (t.limit !== e.limit) return !1;
    if (t.orderBy.length !== e.orderBy.length) return !1;
    for (let n = 0; n < t.orderBy.length; n++) if (!ue(t.orderBy[n], e.orderBy[n])) return !1;
    if (t.filters.length !== e.filters.length) return !1;
    for (let i = 0; i < t.filters.length; i++) if (n = t.filters[i], s = e.filters[i], 
    n.op !== s.op || !n.field.isEqual(s.field) || !Vt(n.value, s.value)) return !1;
    var n, s;
    return t.collectionGroup === e.collectionGroup && (!!t.path.isEqual(e.path) && (!!le(t.startAt, e.startAt) && le(t.endAt, e.endAt)));
}

function Ht(t) {
    return _t.qt(t.path) && null === t.collectionGroup && 0 === t.filters.length;
}

class Jt extends class {} {
    constructor(t, e, n) {
        super(), this.field = t, this.op = e, this.value = n;
    }
    /**
     * Creates a filter based on the provided arguments.
     */    static create(t, e, n) {
        return t.Ot() ? "in" /* IN */ === e || "not-in" /* NOT_IN */ === e ? this.Zt(t, e, n) : new Xt(t, e, n) : "array-contains" /* ARRAY_CONTAINS */ === e ? new ne(t, n) : "in" /* IN */ === e ? new se(t, n) : "not-in" /* NOT_IN */ === e ? new ie(t, n) : "array-contains-any" /* ARRAY_CONTAINS_ANY */ === e ? new re(t, n) : new Jt(t, e, n);
    }
    static Zt(t, e, n) {
        return "in" /* IN */ === e ? new Zt(t, n) : new te(t, n);
    }
    matches(t) {
        const e = t.field(this.field);
        // Types do not have to match in NOT_EQUAL filters.
                return "!=" /* NOT_EQUAL */ === this.op ? null !== e && this.te(bt(e, this.value)) : null !== e && gt(this.value) === gt(e) && this.te(bt(e, this.value));
        // Only compare types with matching backend order (such as double and int).
        }
    te(t) {
        switch (this.op) {
          case "<" /* LESS_THAN */ :
            return t < 0;

          case "<=" /* LESS_THAN_OR_EQUAL */ :
            return t <= 0;

          case "==" /* EQUAL */ :
            return 0 === t;

          case "!=" /* NOT_EQUAL */ :
            return 0 !== t;

          case ">" /* GREATER_THAN */ :
            return t > 0;

          case ">=" /* GREATER_THAN_OR_EQUAL */ :
            return t >= 0;

          default:
            return y();
        }
    }
    ee() {
        return [ "<" /* LESS_THAN */ , "<=" /* LESS_THAN_OR_EQUAL */ , ">" /* GREATER_THAN */ , ">=" /* GREATER_THAN_OR_EQUAL */ , "!=" /* NOT_EQUAL */ , "not-in" /* NOT_IN */ ].indexOf(this.op) >= 0;
    }
}

function Yt(t) {
    // TODO(b/29183165): Technically, this won't be unique if two values have
    // the same description, such as the int 3 and the string "3". So we should
    // add the types in here somehow, too.
    return t.field.Ct() + t.op.toString() + St(t.value);
}

class Xt extends Jt {
    constructor(t, e, n) {
        super(t, e, n), this.key = _t.$t(n.referenceValue);
    }
    matches(t) {
        const e = _t.At(t.key, this.key);
        return this.te(e);
    }
}

/** Filter that matches on key fields within an array. */ class Zt extends Jt {
    constructor(t, e) {
        super(t, "in" /* IN */ , e), this.keys = ee("in" /* IN */ , e);
    }
    matches(t) {
        return this.keys.some((e => e.isEqual(t.key)));
    }
}

/** Filter that matches on key fields not present within an array. */ class te extends Jt {
    constructor(t, e) {
        super(t, "not-in" /* NOT_IN */ , e), this.keys = ee("not-in" /* NOT_IN */ , e);
    }
    matches(t) {
        return !this.keys.some((e => e.isEqual(t.key)));
    }
}

function ee(t, e) {
    var n;
    return ((null === (n = e.arrayValue) || void 0 === n ? void 0 : n.values) || []).map((t => _t.$t(t.referenceValue)));
}

/** A Filter that implements the array-contains operator. */ class ne extends Jt {
    constructor(t, e) {
        super(t, "array-contains" /* ARRAY_CONTAINS */ , e);
    }
    matches(t) {
        const e = t.field(this.field);
        return Nt(e) && pt(e.arrayValue, this.value);
    }
}

/** A Filter that implements the IN operator. */ class se extends Jt {
    constructor(t, e) {
        super(t, "in" /* IN */ , e);
    }
    matches(t) {
        const e = t.field(this.field);
        return null !== e && pt(this.value.arrayValue, e);
    }
}

/** A Filter that implements the not-in operator. */ class ie extends Jt {
    constructor(t, e) {
        super(t, "not-in" /* NOT_IN */ , e);
    }
    matches(t) {
        if (pt(this.value.arrayValue, {
            nullValue: "NULL_VALUE"
        })) return !1;
        const e = t.field(this.field);
        return null !== e && !pt(this.value.arrayValue, e);
    }
}

/** A Filter that implements the array-contains-any operator. */ class re extends Jt {
    constructor(t, e) {
        super(t, "array-contains-any" /* ARRAY_CONTAINS_ANY */ , e);
    }
    matches(t) {
        const e = t.field(this.field);
        return !(!Nt(e) || !e.arrayValue.values) && e.arrayValue.values.some((t => pt(this.value.arrayValue, t)));
    }
}

/**
 * Represents a bound of a query.
 *
 * The bound is specified with the given components representing a position and
 * whether it's just before or just after the position (relative to whatever the
 * query order is).
 *
 * The position represents a logical index position for a query. It's a prefix
 * of values for the (potentially implicit) order by clauses of a query.
 *
 * Bound provides a function to determine whether a document comes before or
 * after a bound. This is influenced by whether the position is just before or
 * just after the provided values.
 */ class oe {
    constructor(t, e) {
        this.position = t, this.before = e;
    }
}

function ce(t) {
    // TODO(b/29183165): Make this collision robust.
    return `${t.before ? "b" : "a"}:${t.position.map((t => St(t))).join(",")}`;
}

/**
 * An ordering on a field, in some Direction. Direction defaults to ASCENDING.
 */ class ae {
    constructor(t, e = "asc" /* ASCENDING */) {
        this.field = t, this.dir = e;
    }
}

function ue(t, e) {
    return t.dir === e.dir && t.field.isEqual(e.field);
}

/**
 * Returns true if a document sorts before a bound using the provided sort
 * order.
 */ function he(t, e, n) {
    let s = 0;
    for (let i = 0; i < t.position.length; i++) {
        const r = e[i], o = t.position[i];
        if (r.field.Ot()) s = _t.At(_t.$t(o.referenceValue), n.key); else {
            s = bt(o, n.field(r.field));
        }
        if ("desc" /* DESCENDING */ === r.dir && (s *= -1), 0 !== s) break;
    }
    return t.before ? s <= 0 : s < 0;
}

function le(t, e) {
    if (null === t) return null === e;
    if (null === e) return !1;
    if (t.before !== e.before || t.position.length !== e.position.length) return !1;
    for (let n = 0; n < t.position.length; n++) {
        if (!Vt(t.position[n], e.position[n])) return !1;
    }
    return !0;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Query encapsulates all the query attributes we support in the SDK. It can
 * be run against the LocalStore, as well as be converted to a `Target` to
 * query the RemoteStore results.
 *
 * Visible for testing.
 */ class _e {
    /**
     * Initializes a Query with a path and optional additional query constraints.
     * Path must currently be empty if this is a collection group query.
     */
    constructor(t, e = null, n = [], s = [], i = null, r = "F" /* First */ , o = null, c = null) {
        this.path = t, this.collectionGroup = e, this.ne = n, this.filters = s, this.limit = i, 
        this.limitType = r, this.startAt = o, this.endAt = c, this.se = null, 
        // The corresponding `Target` of this `Query` instance.
        this.ie = null, this.startAt, this.endAt;
    }
}

/** Creates a new Query instance with the options provided. */ function fe(t, e, n, s, i, r, o, c) {
    return new _e(t, e, n, s, i, r, o, c);
}

/** Creates a new Query for a query that matches all documents at `path` */ function de(t) {
    return new _e(t);
}

/**
 * Helper to convert a collection group query into a collection query at a
 * specific path. This is used when executing collection group queries, since
 * we have to split the query into a set of collection queries at multiple
 * paths.
 */ function we(t) {
    return !H(t.limit) && "F" /* First */ === t.limitType;
}

function Ee(t) {
    return !H(t.limit) && "L" /* Last */ === t.limitType;
}

function Te(t) {
    return t.ne.length > 0 ? t.ne[0].field : null;
}

function Ie(t) {
    for (const e of t.filters) if (e.ee()) return e.field;
    return null;
}

/**
 * Checks if any of the provided Operators are included in the query and
 * returns the first one that is, or null if none are.
 */
/**
 * Returns whether the query matches a collection group rather than a specific
 * collection.
 */
function me(t) {
    return null !== t.collectionGroup;
}

/**
 * Returns the implicit order by constraint that is used to execute the Query,
 * which can be different from the order by constraints the user provided (e.g.
 * the SDK and backend always orders by `__name__`).
 */ function Ae(t) {
    const e = V(t);
    if (null === e.se) {
        e.se = [];
        const t = Ie(e), n = Te(e);
        if (null !== t && null === n) 
        // In order to implicitly add key ordering, we must also add the
        // inequality filter field for it to be a valid query.
        // Note that the default inequality field and key ordering is ascending.
        t.Ot() || e.se.push(new ae(t)), e.se.push(new ae(lt.kt(), "asc" /* ASCENDING */)); else {
            let t = !1;
            for (const n of e.ne) e.se.push(n), n.field.Ot() && (t = !0);
            if (!t) {
                // The order of the implicit key ordering always matches the last
                // explicit order by
                const t = e.ne.length > 0 ? e.ne[e.ne.length - 1].dir : "asc" /* ASCENDING */;
                e.se.push(new ae(lt.kt(), t));
            }
        }
    }
    return e.se;
}

/**
 * Converts this `Query` instance to it's corresponding `Target` representation.
 */ function Re(t) {
    const e = V(t);
    if (!e.ie) if ("F" /* First */ === e.limitType) e.ie = Wt(e.path, e.collectionGroup, Ae(e), e.filters, e.limit, e.startAt, e.endAt); else {
        // Flip the orderBy directions since we want the last results
        const t = [];
        for (const n of Ae(e)) {
            const e = "desc" /* DESCENDING */ === n.dir ? "asc" /* ASCENDING */ : "desc" /* DESCENDING */;
            t.push(new ae(n.field, e));
        }
        // We need to swap the cursors to match the now-flipped query ordering.
                const n = e.endAt ? new oe(e.endAt.position, !e.endAt.before) : null, s = e.startAt ? new oe(e.startAt.position, !e.startAt.before) : null;
        // Now return as a LimitType.First query.
        e.ie = Wt(e.path, e.collectionGroup, t, e.filters, e.limit, n, s);
    }
    return e.ie;
}

function Pe(t, e, n) {
    return new _e(t.path, t.collectionGroup, t.ne.slice(), t.filters.slice(), e, n, t.startAt, t.endAt);
}

function ye(t, e) {
    return zt(Re(t), Re(e)) && t.limitType === e.limitType;
}

// TODO(b/29183165): This is used to get a unique string from a query to, for
// example, use as a dictionary key, but the implementation is subject to
// collisions. Make it collision-free.
function ge(t) {
    return `${jt(Re(t))}|lt:${t.limitType}`;
}

function Ve(t) {
    return `Query(target=${Gt(Re(t))}; limitType=${t.limitType})`;
}

/** Returns whether `doc` matches the constraints of `query`. */ function pe(t, e) {
    return function(t, e) {
        const n = e.key.path;
        return null !== t.collectionGroup ? e.key.Bt(t.collectionGroup) && t.path.vt(n) : _t.qt(t.path) ? t.path.isEqual(n) : t.path.St(n);
    }
    /**
 * A document must have a value for every ordering clause in order to show up
 * in the results.
 */ (t, e) && function(t, e) {
        for (const n of t.ne) 
        // order by key always matches
        if (!n.field.Ot() && null === e.field(n.field)) return !1;
        return !0;
    }(t, e) && function(t, e) {
        for (const n of t.filters) if (!n.matches(e)) return !1;
        return !0;
    }
    /** Makes sure a document is within the bounds, if provided. */ (t, e) && function(t, e) {
        if (t.startAt && !he(t.startAt, Ae(t), e)) return !1;
        if (t.endAt && he(t.endAt, Ae(t), e)) return !1;
        return !0;
    }
    /**
 * Returns a new comparator function that can be used to compare two documents
 * based on the Query's ordering constraint.
 */ (t, e);
}

function be(t) {
    return (e, n) => {
        let s = !1;
        for (const i of Ae(t)) {
            const t = ve(i, e, n);
            if (0 !== t) return t;
            s = s || i.field.Ot();
        }
        return 0;
    };
}

function ve(t, e, n) {
    const s = t.field.Ot() ? _t.At(e.key, n.key) : function(t, e, n) {
        const s = e.field(t), i = n.field(t);
        return null !== s && null !== i ? bt(s, i) : y();
    }(t.field, e, n);
    switch (t.dir) {
      case "asc" /* ASCENDING */ :
        return s;

      case "desc" /* DESCENDING */ :
        return -1 * s;

      default:
        return y();
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An immutable set of metadata that the local store tracks for each target.
 */ class Se {
    constructor(
    /** The target being listened to. */
    t, 
    /**
     * The target ID to which the target corresponds; Assigned by the
     * LocalStore for user listens and by the SyncEngine for limbo watches.
     */
    e, 
    /** The purpose of the target. */
    n, 
    /**
     * The sequence number of the last transaction during which this target data
     * was modified.
     */
    s, 
    /** The latest snapshot version seen for this target. */
    i = ct.min()
    /**
     * The maximum snapshot version at which the associated view
     * contained no limbo documents.
     */ , r = ct.min()
    /**
     * An opaque, server-assigned token that allows watching a target to be
     * resumed after disconnecting without retransmitting all the data that
     * matches the target. The resume token essentially identifies a point in
     * time from which the server should resume sending results.
     */ , o = C.h) {
        this.target = t, this.targetId = e, this.re = n, this.sequenceNumber = s, this.oe = i, 
        this.lastLimboFreeSnapshotVersion = r, this.resumeToken = o;
    }
    /** Creates a new target data instance with an updated sequence number. */    ce(t) {
        return new Se(this.target, this.targetId, this.re, t, this.oe, this.lastLimboFreeSnapshotVersion, this.resumeToken);
    }
    /**
     * Creates a new target data instance with an updated resume token and
     * snapshot version.
     */    ae(t, e) {
        return new Se(this.target, this.targetId, this.re, this.sequenceNumber, e, this.lastLimboFreeSnapshotVersion, t);
    }
    /**
     * Creates a new target data instance with an updated last limbo free
     * snapshot version number.
     */    ue(t) {
        return new Se(this.target, this.targetId, this.re, this.sequenceNumber, this.oe, t, this.resumeToken);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Returns an DoubleValue for `value` that is encoded based the serializer's
 * `useProto3Json` setting.
 */ function De(t, e) {
    if (t.he) {
        if (isNaN(e)) return {
            doubleValue: "NaN"
        };
        if (e === 1 / 0) return {
            doubleValue: "Infinity"
        };
        if (e === -1 / 0) return {
            doubleValue: "-Infinity"
        };
    }
    return {
        doubleValue: J(e) ? "-0" : e
    };
}

/**
 * Returns an IntegerValue for `value`.
 */ function Ce(t) {
    return {
        integerValue: "" + t
    };
}

/**
 * Returns a value for a number that's appropriate to put into a proto.
 * The return value is an IntegerValue if it can safely represent the value,
 * otherwise a DoubleValue is returned.
 */ function xe(t, e) {
    return Y(e) ? Ce(e) : De(t, e);
}

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Represents a transform within a TransformMutation. */ class Ne {
    constructor() {
        // Make sure that the structural type of `TransformOperation` is unique.
        // See https://github.com/microsoft/TypeScript/issues/5451
        this.le = void 0;
    }
}

/**
 * Computes the local transform result against the provided `previousValue`,
 * optionally using the provided localWriteTime.
 */ function Fe(t, e, n) {
    return t instanceof Me ? function(t, e) {
        const n = {
            fields: {
                __type__: {
                    stringValue: "server_timestamp"
                },
                __local_write_time__: {
                    timestampValue: {
                        seconds: t.seconds,
                        nanos: t.nanoseconds
                    }
                }
            }
        };
        return e && (n.fields.__previous_value__ = e), {
            mapValue: n
        };
    }(n, e) : t instanceof Le ? $e(t, e) : t instanceof Be ? qe(t, e) : function(t, e) {
        // PORTING NOTE: Since JavaScript's integer arithmetic is limited to 53 bit
        // precision and resolves overflows by reducing precision, we do not
        // manually cap overflows at 2^63.
        const n = ke(t, e), s = Qe(n) + Qe(t._e);
        return xt(n) && xt(t._e) ? Ce(s) : De(t.fe, s);
    }(t, e);
}

/**
 * Computes a final transform result after the transform has been acknowledged
 * by the server, potentially using the server-provided transformResult.
 */ function Oe(t, e, n) {
    // The server just sends null as the transform result for array operations,
    // so we have to calculate a result the same as we do for local
    // applications.
    return t instanceof Le ? $e(t, e) : t instanceof Be ? qe(t, e) : n;
}

/**
 * If this transform operation is not idempotent, returns the base value to
 * persist for this transform. If a base value is returned, the transform
 * operation is always applied to this base value, even if document has
 * already been updated.
 *
 * Base values provide consistent behavior for non-idempotent transforms and
 * allow us to return the same latency-compensated value even if the backend
 * has already applied the transform operation. The base value is null for
 * idempotent transforms, as they can be re-played even if the backend has
 * already applied them.
 *
 * @returns a base value to store along with the mutation, or null for
 * idempotent transforms.
 */ function ke(t, e) {
    return t instanceof Ue ? xt(n = e) || function(t) {
        return !!t && "doubleValue" in t;
    }
    /** Returns true if `value` is either an IntegerValue or a DoubleValue. */ (n) ? e : {
        integerValue: 0
    } : null;
    var n;
}

/** Transforms a value into a server-generated timestamp. */
class Me extends Ne {}

/** Transforms an array value via a union operation. */ class Le extends Ne {
    constructor(t) {
        super(), this.elements = t;
    }
}

function $e(t, e) {
    const n = Ke(e);
    for (const e of t.elements) n.some((t => Vt(t, e))) || n.push(e);
    return {
        arrayValue: {
            values: n
        }
    };
}

/** Transforms an array value via a remove operation. */ class Be extends Ne {
    constructor(t) {
        super(), this.elements = t;
    }
}

function qe(t, e) {
    let n = Ke(e);
    for (const e of t.elements) n = n.filter((t => !Vt(t, e)));
    return {
        arrayValue: {
            values: n
        }
    };
}

/**
 * Implements the backend semantics for locally computed NUMERIC_ADD (increment)
 * transforms. Converts all field values to integers or doubles, but unlike the
 * backend does not cap integer values at 2^63. Instead, JavaScript number
 * arithmetic is used and precision loss can occur for values greater than 2^53.
 */ class Ue extends Ne {
    constructor(t, e) {
        super(), this.fe = t, this._e = e;
    }
}

function Qe(t) {
    return mt(t.integerValue || t.doubleValue);
}

function Ke(t) {
    return Nt(t) && t.arrayValue.values ? t.arrayValue.values.slice() : [];
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** A field path and the TransformOperation to perform upon it. */ class We {
    constructor(t, e) {
        this.field = t, this.transform = e;
    }
}

function je(t, e) {
    return t.field.isEqual(e.field) && function(t, e) {
        return t instanceof Le && e instanceof Le || t instanceof Be && e instanceof Be ? S(t.elements, e.elements, Vt) : t instanceof Ue && e instanceof Ue ? Vt(t._e, e._e) : t instanceof Me && e instanceof Me;
    }(t.transform, e.transform);
}

/** The result of successfully applying a mutation to the backend. */
class Ge {
    constructor(
    /**
     * The version at which the mutation was committed:
     *
     * - For most operations, this is the updateTime in the WriteResult.
     * - For deletes, the commitTime of the WriteResponse (because deletes are
     *   not stored and have no updateTime).
     *
     * Note that these versions can be different: No-op writes will not change
     * the updateTime even though the commitTime advances.
     */
    t, 
    /**
     * The resulting fields returned from the backend after a
     * TransformMutation has been committed. Contains one FieldValue for each
     * FieldTransform that was in the mutation.
     *
     * Will be null if the mutation was not a TransformMutation.
     */
    e) {
        this.version = t, this.transformResults = e;
    }
}

/**
 * Encodes a precondition for a mutation. This follows the model that the
 * backend accepts with the special case of an explicit "empty" precondition
 * (meaning no precondition).
 */ class ze {
    constructor(t, e) {
        this.updateTime = t, this.exists = e;
    }
    /** Creates a new empty Precondition. */    static de() {
        return new ze;
    }
    /** Creates a new Precondition with an exists flag. */    static exists(t) {
        return new ze(void 0, t);
    }
    /** Creates a new Precondition based on a version a document exists at. */    static updateTime(t) {
        return new ze(t);
    }
    /** Returns whether this Precondition is empty. */    get we() {
        return void 0 === this.updateTime && void 0 === this.exists;
    }
    isEqual(t) {
        return this.exists === t.exists && (this.updateTime ? !!t.updateTime && this.updateTime.isEqual(t.updateTime) : !t.updateTime);
    }
}

/**
 * Returns true if the preconditions is valid for the given document
 * (or null if no document is available).
 */ function He(t, e) {
    return void 0 !== t.updateTime ? e instanceof qt && e.version.isEqual(t.updateTime) : void 0 === t.exists || t.exists === e instanceof qt;
}

/**
 * A mutation describes a self-contained change to a document. Mutations can
 * create, replace, delete, and update subsets of documents.
 *
 * Mutations not only act on the value of the document but also its version.
 *
 * For local mutations (mutations that haven't been committed yet), we preserve
 * the existing version for Set, Patch, and Transform mutations. For Delete
 * mutations, we reset the version to 0.
 *
 * Here's the expected transition table.
 *
 * MUTATION           APPLIED TO            RESULTS IN
 *
 * SetMutation        Document(v3)          Document(v3)
 * SetMutation        NoDocument(v3)        Document(v0)
 * SetMutation        null                  Document(v0)
 * PatchMutation      Document(v3)          Document(v3)
 * PatchMutation      NoDocument(v3)        NoDocument(v3)
 * PatchMutation      null                  null
 * TransformMutation  Document(v3)          Document(v3)
 * TransformMutation  NoDocument(v3)        NoDocument(v3)
 * TransformMutation  null                  null
 * DeleteMutation     Document(v3)          NoDocument(v0)
 * DeleteMutation     NoDocument(v3)        NoDocument(v0)
 * DeleteMutation     null                  NoDocument(v0)
 *
 * For acknowledged mutations, we use the updateTime of the WriteResponse as
 * the resulting version for Set, Patch, and Transform mutations. As deletes
 * have no explicit update time, we use the commitTime of the WriteResponse for
 * Delete mutations.
 *
 * If a mutation is acknowledged by the backend but fails the precondition check
 * locally, we return an `UnknownDocument` and rely on Watch to send us the
 * updated version.
 *
 * Note that TransformMutations don't create Documents (in the case of being
 * applied to a NoDocument), even though they would on the backend. This is
 * because the client always combines the TransformMutation with a SetMutation
 * or PatchMutation and we only want to apply the transform if the prior
 * mutation resulted in a Document (always true for a SetMutation, but not
 * necessarily for a PatchMutation).
 *
 * ## Subclassing Notes
 *
 * Subclasses of Mutation need to implement applyToRemoteDocument() and
 * applyToLocalView() to implement the actual behavior of applying the mutation
 * to some source document.
 */ class Je {}

/**
 * Applies this mutation to the given MaybeDocument or null for the purposes
 * of computing a new remote document. If the input document doesn't match the
 * expected state (e.g. it is null or outdated), an `UnknownDocument` can be
 * returned.
 *
 * @param mutation - The mutation to apply.
 * @param maybeDoc - The document to mutate. The input document can be null if
 *     the client has no knowledge of the pre-mutation state of the document.
 * @param mutationResult - The result of applying the mutation from the backend.
 * @returns The mutated document. The returned document may be an
 *     UnknownDocument if the mutation could not be applied to the locally
 *     cached base document.
 */ function Ye(t, e, n) {
    return t instanceof nn ? function(t, e, n) {
        // Unlike applySetMutationToLocalView, if we're applying a mutation to a
        // remote document the server has accepted the mutation so the precondition
        // must have held.
        let s = t.value;
        if (n.transformResults) {
            const i = on(t.fieldTransforms, e, n.transformResults);
            s = an(t.fieldTransforms, s, i);
        }
        return new qt(t.key, n.version, s, {
            hasCommittedMutations: !0
        });
    }(t, e, n) : t instanceof sn ? function(t, e, n) {
        if (!He(t.Ee, e)) 
        // Since the mutation was not rejected, we know that the precondition
        // matched on the backend. We therefore must not have the expected version
        // of the document in our cache and return an UnknownDocument with the
        // known updateTime.
        return new Qt(t.key, n.version);
        const s = n.transformResults ? on(t.fieldTransforms, e, n.transformResults) : [], i = rn(t, e, s);
        return new qt(t.key, n.version, i, {
            hasCommittedMutations: !0
        });
    }(t, e, n) : function(t, e, n) {
        // Unlike applyToLocalView, if we're applying a mutation to a remote
        // document the server has accepted the mutation so the precondition must
        // have held.
        return new Ut(t.key, n.version, {
            hasCommittedMutations: !0
        });
    }(t, 0, n);
}

/**
 * Applies this mutation to the given MaybeDocument or null for the purposes
 * of computing the new local view of a document. Both the input and returned
 * documents can be null.
 *
 * @param mutation - The mutation to apply.
 * @param maybeDoc - The document to mutate. The input document can be null if
 *     the client has no knowledge of the pre-mutation state of the document.
 * @param baseDoc - The state of the document prior to this mutation batch. The
 *     input document can be null if the client has no knowledge of the
 *     pre-mutation state of the document.
 * @param localWriteTime - A timestamp indicating the local write time of the
 *     batch this mutation is a part of.
 * @returns The mutated document. The returned document may be null, but only
 *     if maybeDoc was null and the mutation would not create a new document.
 */ function Xe(t, e, n, s) {
    return t instanceof nn ? function(t, e, n, s) {
        if (!He(t.Ee, e)) return e;
        let i = t.value;
        if (t.fieldTransforms) {
            const r = cn(t.fieldTransforms, n, e, s);
            i = an(t.fieldTransforms, i, r);
        }
        const r = en(e);
        return new qt(t.key, r, i, {
            Jt: !0
        });
    }
    /**
 * A mutation that modifies fields of the document at the given key with the
 * given values. The values are applied through a field mask:
 *
 *  * When a field is in both the mask and the values, the corresponding field
 *    is updated.
 *  * When a field is in neither the mask nor the values, the corresponding
 *    field is unmodified.
 *  * When a field is in the mask but not in the values, the corresponding field
 *    is deleted.
 *  * When a field is not in the mask but is in the values, the values map is
 *    ignored.
 */ (t, e, s, n) : t instanceof sn ? function(t, e, n, s) {
        if (!He(t.Ee, e)) return e;
        const i = en(e), r = cn(t.fieldTransforms, n, e, s), o = rn(t, e, r);
        return new qt(t.key, i, o, {
            Jt: !0
        });
    }
    /**
 * Patches the data of document if available or creates a new document. Note
 * that this does not check whether or not the precondition of this patch
 * holds.
 */ (t, e, s, n) : function(t, e) {
        if (!He(t.Ee, e)) return e;
        return new Ut(t.key, ct.min());
    }
    /**
 * A mutation that verifies the existence of the document at the given key with
 * the provided precondition.
 *
 * The `verify` operation is only used in Transactions, and this class serves
 * primarily to facilitate serialization into protos.
 */ (t, e);
}

/**
 * If this mutation is not idempotent, returns the base value to persist with
 * this mutation. If a base value is returned, the mutation is always applied
 * to this base value, even if document has already been updated.
 *
 * The base value is a sparse object that consists of only the document
 * fields for which this mutation contains a non-idempotent transformation
 * (e.g. a numeric increment). The provided value guarantees consistent
 * behavior for non-idempotent transforms and allow us to return the same
 * latency-compensated value even if the backend has already applied the
 * mutation. The base value is null for idempotent mutations, as they can be
 * re-played even if the backend has already applied them.
 *
 * @returns a base value to store along with the mutation, or null for
 * idempotent mutations.
 */ function Ze(t, e) {
    return void 0 !== t.fieldTransforms ? function(t, e) {
        let n = null;
        for (const s of t) {
            const t = e instanceof qt ? e.field(s.field) : void 0, i = ke(s.transform, t || null);
            null != i && (n = null == n ? (new Lt).set(s.field, i) : n.set(s.field, i));
        }
        return n ? n.Gt() : null;
    }(t.fieldTransforms, e) : null;
}

function tn(t, e) {
    return t.type === e.type && (!!t.key.isEqual(e.key) && (!!t.Ee.isEqual(e.Ee) && (!!function(t, e) {
        return void 0 === t && void 0 === e || !(!t || !e) && S(t, e, ((t, e) => je(t, e)));
    }(t.fieldTransforms, e.fieldTransforms) && (0 /* Set */ === t.type ? t.value.isEqual(e.value) : 1 /* Patch */ !== t.type || t.data.isEqual(e.data) && t.Te.isEqual(e.Te)))));
}

/**
 * Returns the version from the given document for use as the result of a
 * mutation. Mutations are defined to return the version of the base document
 * only if it is an existing document. Deleted and unknown documents have a
 * post-mutation version of SnapshotVersion.min().
 */ function en(t) {
    return t instanceof qt ? t.version : ct.min();
}

/**
 * A mutation that creates or replaces the document at the given key with the
 * object value contents.
 */ class nn extends Je {
    constructor(t, e, n, s = []) {
        super(), this.key = t, this.value = e, this.Ee = n, this.fieldTransforms = s, this.type = 0 /* Set */;
    }
}

class sn extends Je {
    constructor(t, e, n, s, i = []) {
        super(), this.key = t, this.data = e, this.Te = n, this.Ee = s, this.fieldTransforms = i, 
        this.type = 1 /* Patch */;
    }
}

function rn(t, e, n) {
    let s;
    return s = e instanceof qt ? e.data() : Mt.empty(), s = function(t, e) {
        const n = new Lt(e);
        return t.Te.fields.forEach((e => {
            if (!e.bt()) {
                const s = t.data.field(e);
                null !== s ? n.set(e, s) : n.delete(e);
            }
        })), n.Gt();
    }
    /**
 * Creates a list of "transform results" (a transform result is a field value
 * representing the result of applying a transform) for use after a
 * TransformMutation has been acknowledged by the server.
 *
 * @param fieldTransforms - The field transforms to apply the result to.
 * @param baseDoc - The document prior to applying this mutation batch.
 * @param serverTransformResults - The transform results received by the server.
 * @returns The transform results list.
 */ (t, s), s = an(t.fieldTransforms, s, n), s;
}

function on(t, e, n) {
    const s = [];
    g(t.length === n.length);
    for (let i = 0; i < n.length; i++) {
        const r = t[i], o = r.transform;
        let c = null;
        e instanceof qt && (c = e.field(r.field)), s.push(Oe(o, c, n[i]));
    }
    return s;
}

/**
 * Creates a list of "transform results" (a transform result is a field value
 * representing the result of applying a transform) for use when applying a
 * TransformMutation locally.
 *
 * @param fieldTransforms - The field transforms to apply the result to.
 * @param localWriteTime - The local time of the transform mutation (used to
 *     generate ServerTimestampValues).
 * @param maybeDoc - The current state of the document after applying all
 *     previous mutations.
 * @param baseDoc - The document prior to applying this mutation batch.
 * @returns The transform results list.
 */ function cn(t, e, n, s) {
    const i = [];
    for (const r of t) {
        const t = r.transform;
        let o = null;
        n instanceof qt && (o = n.field(r.field)), null === o && s instanceof qt && (
        // If the current document does not contain a value for the mutated
        // field, use the value that existed before applying this mutation
        // batch. This solves an edge case where a PatchMutation clears the
        // values in a nested map before the TransformMutation is applied.
        o = s.field(r.field)), i.push(Fe(t, o, e));
    }
    return i;
}

function an(t, e, n) {
    const s = new Lt(e);
    for (let e = 0; e < t.length; e++) {
        const i = t[e];
        s.set(i.field, n[e]);
    }
    return s.Gt();
}

/** A mutation that deletes the document at the given key. */ class un extends Je {
    constructor(t, e) {
        super(), this.key = t, this.Ee = e, this.type = 2 /* Delete */ , this.fieldTransforms = [];
    }
}

class hn extends Je {
    constructor(t, e) {
        super(), this.key = t, this.Ee = e, this.type = 3 /* Verify */ , this.fieldTransforms = [];
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class ln {
    // TODO(b/33078163): just use simplest form of existence filter for now
    constructor(t) {
        this.count = t;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// An immutable sorted map implementation, based on a Left-leaning Red-Black
// tree.
class _n {
    constructor(t, e) {
        this.At = t, this.root = e || dn.EMPTY;
    }
    // Returns a copy of the map, with the specified key/value added or replaced.
    Ie(t, e) {
        return new _n(this.At, this.root.Ie(t, e, this.At).copy(null, null, dn.me, null, null));
    }
    // Returns a copy of the map, with the specified key removed.
    remove(t) {
        return new _n(this.At, this.root.remove(t, this.At).copy(null, null, dn.me, null, null));
    }
    // Returns the value of the node with the given key, or null.
    get(t) {
        let e = this.root;
        for (;!e.bt(); ) {
            const n = this.At(t, e.key);
            if (0 === n) return e.value;
            n < 0 ? e = e.left : n > 0 && (e = e.right);
        }
        return null;
    }
    // Returns the index of the element in this sorted map, or -1 if it doesn't
    // exist.
    indexOf(t) {
        // Number of nodes that were pruned when descending right
        let e = 0, n = this.root;
        for (;!n.bt(); ) {
            const s = this.At(t, n.key);
            if (0 === s) return e + n.left.size;
            s < 0 ? n = n.left : (
            // Count all nodes left of the node plus the node itself
            e += n.left.size + 1, n = n.right);
        }
        // Node not found
                return -1;
    }
    bt() {
        return this.root.bt();
    }
    // Returns the total number of nodes in the map.
    get size() {
        return this.root.size;
    }
    // Returns the minimum key in the map.
    Ae() {
        return this.root.Ae();
    }
    // Returns the maximum key in the map.
    Re() {
        return this.root.Re();
    }
    // Traverses the map in key order and calls the specified action function
    // for each key/value pair. If action returns true, traversal is aborted.
    // Returns the first truthy value returned by action, or the last falsey
    // value returned by action.
    Pe(t) {
        return this.root.Pe(t);
    }
    forEach(t) {
        this.Pe(((e, n) => (t(e, n), !1)));
    }
    toString() {
        const t = [];
        return this.Pe(((e, n) => (t.push(`${e}:${n}`), !1))), `{${t.join(", ")}}`;
    }
    // Traverses the map in reverse key order and calls the specified action
    // function for each key/value pair. If action returns true, traversal is
    // aborted.
    // Returns the first truthy value returned by action, or the last falsey
    // value returned by action.
    ye(t) {
        return this.root.ye(t);
    }
    // Returns an iterator over the SortedMap.
    ge() {
        return new fn(this.root, null, this.At, !1);
    }
    Ve(t) {
        return new fn(this.root, t, this.At, !1);
    }
    pe() {
        return new fn(this.root, null, this.At, !0);
    }
    be(t) {
        return new fn(this.root, t, this.At, !0);
    }
}

 // end SortedMap
// An iterator over an LLRBNode.
class fn {
    constructor(t, e, n, s) {
        this.ve = s, this.Se = [];
        let i = 1;
        for (;!t.bt(); ) if (i = e ? n(t.key, e) : 1, 
        // flip the comparison if we're going in reverse
        s && (i *= -1), i < 0) 
        // This node is less than our start key. ignore it
        t = this.ve ? t.left : t.right; else {
            if (0 === i) {
                // This node is exactly equal to our start key. Push it on the stack,
                // but stop iterating;
                this.Se.push(t);
                break;
            }
            // This node is greater than our start key, add it to the stack and move
            // to the next one
            this.Se.push(t), t = this.ve ? t.right : t.left;
        }
    }
    De() {
        let t = this.Se.pop();
        const e = {
            key: t.key,
            value: t.value
        };
        if (this.ve) for (t = t.left; !t.bt(); ) this.Se.push(t), t = t.right; else for (t = t.right; !t.bt(); ) this.Se.push(t), 
        t = t.left;
        return e;
    }
    Ce() {
        return this.Se.length > 0;
    }
    xe() {
        if (0 === this.Se.length) return null;
        const t = this.Se[this.Se.length - 1];
        return {
            key: t.key,
            value: t.value
        };
    }
}

 // end SortedMapIterator
// Represents a node in a Left-leaning Red-Black tree.
class dn {
    constructor(t, e, n, s, i) {
        this.key = t, this.value = e, this.color = null != n ? n : dn.RED, this.left = null != s ? s : dn.EMPTY, 
        this.right = null != i ? i : dn.EMPTY, this.size = this.left.size + 1 + this.right.size;
    }
    // Returns a copy of the current node, optionally replacing pieces of it.
    copy(t, e, n, s, i) {
        return new dn(null != t ? t : this.key, null != e ? e : this.value, null != n ? n : this.color, null != s ? s : this.left, null != i ? i : this.right);
    }
    bt() {
        return !1;
    }
    // Traverses the tree in key order and calls the specified action function
    // for each node. If action returns true, traversal is aborted.
    // Returns the first truthy value returned by action, or the last falsey
    // value returned by action.
    Pe(t) {
        return this.left.Pe(t) || t(this.key, this.value) || this.right.Pe(t);
    }
    // Traverses the tree in reverse key order and calls the specified action
    // function for each node. If action returns true, traversal is aborted.
    // Returns the first truthy value returned by action, or the last falsey
    // value returned by action.
    ye(t) {
        return this.right.ye(t) || t(this.key, this.value) || this.left.ye(t);
    }
    // Returns the minimum node in the tree.
    min() {
        return this.left.bt() ? this : this.left.min();
    }
    // Returns the maximum key in the tree.
    Ae() {
        return this.min().key;
    }
    // Returns the maximum key in the tree.
    Re() {
        return this.right.bt() ? this.key : this.right.Re();
    }
    // Returns new tree, with the key/value added.
    Ie(t, e, n) {
        let s = this;
        const i = n(t, s.key);
        return s = i < 0 ? s.copy(null, null, null, s.left.Ie(t, e, n), null) : 0 === i ? s.copy(null, e, null, null, null) : s.copy(null, null, null, null, s.right.Ie(t, e, n)), 
        s.Ne();
    }
    Fe() {
        if (this.left.bt()) return dn.EMPTY;
        let t = this;
        return t.left.Oe() || t.left.left.Oe() || (t = t.ke()), t = t.copy(null, null, null, t.left.Fe(), null), 
        t.Ne();
    }
    // Returns new tree, with the specified item removed.
    remove(t, e) {
        let n, s = this;
        if (e(t, s.key) < 0) s.left.bt() || s.left.Oe() || s.left.left.Oe() || (s = s.ke()), 
        s = s.copy(null, null, null, s.left.remove(t, e), null); else {
            if (s.left.Oe() && (s = s.Me()), s.right.bt() || s.right.Oe() || s.right.left.Oe() || (s = s.Le()), 
            0 === e(t, s.key)) {
                if (s.right.bt()) return dn.EMPTY;
                n = s.right.min(), s = s.copy(n.key, n.value, null, null, s.right.Fe());
            }
            s = s.copy(null, null, null, null, s.right.remove(t, e));
        }
        return s.Ne();
    }
    Oe() {
        return this.color;
    }
    // Returns new tree after performing any needed rotations.
    Ne() {
        let t = this;
        return t.right.Oe() && !t.left.Oe() && (t = t.$e()), t.left.Oe() && t.left.left.Oe() && (t = t.Me()), 
        t.left.Oe() && t.right.Oe() && (t = t.Be()), t;
    }
    ke() {
        let t = this.Be();
        return t.right.left.Oe() && (t = t.copy(null, null, null, null, t.right.Me()), t = t.$e(), 
        t = t.Be()), t;
    }
    Le() {
        let t = this.Be();
        return t.left.left.Oe() && (t = t.Me(), t = t.Be()), t;
    }
    $e() {
        const t = this.copy(null, null, dn.RED, null, this.right.left);
        return this.right.copy(null, null, this.color, t, null);
    }
    Me() {
        const t = this.copy(null, null, dn.RED, this.left.right, null);
        return this.left.copy(null, null, this.color, null, t);
    }
    Be() {
        const t = this.left.copy(null, null, !this.left.color, null, null), e = this.right.copy(null, null, !this.right.color, null, null);
        return this.copy(null, null, !this.color, t, e);
    }
    // For testing.
    qe() {
        const t = this.Ue();
        return Math.pow(2, t) <= this.size + 1;
    }
    // In a balanced RB tree, the black-depth (number of black nodes) from root to
    // leaves is equal on both sides.  This function verifies that or asserts.
    Ue() {
        if (this.Oe() && this.left.Oe()) throw y();
        if (this.right.Oe()) throw y();
        const t = this.left.Ue();
        if (t !== this.right.Ue()) throw y();
        return t + (this.Oe() ? 0 : 1);
    }
}

 // end LLRBNode
// Empty node is shared between all LLRB trees.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
dn.EMPTY = null, dn.RED = !0, dn.me = !1;

// end LLRBEmptyNode
dn.EMPTY = new 
// Represents an empty node (a leaf node in the Red-Black Tree).
class {
    constructor() {
        this.size = 0;
    }
    get key() {
        throw y();
    }
    get value() {
        throw y();
    }
    get color() {
        throw y();
    }
    get left() {
        throw y();
    }
    get right() {
        throw y();
    }
    // Returns a copy of the current node.
    copy(t, e, n, s, i) {
        return this;
    }
    // Returns a copy of the tree, with the specified key/value added.
    Ie(t, e, n) {
        return new dn(t, e);
    }
    // Returns a copy of the tree, with the specified key removed.
    remove(t, e) {
        return this;
    }
    bt() {
        return !0;
    }
    Pe(t) {
        return !1;
    }
    ye(t) {
        return !1;
    }
    Ae() {
        return null;
    }
    Re() {
        return null;
    }
    Oe() {
        return !1;
    }
    // For testing.
    qe() {
        return !0;
    }
    Ue() {
        return 0;
    }
};

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * SortedSet is an immutable (copy-on-write) collection that holds elements
 * in order specified by the provided comparator.
 *
 * NOTE: if provided comparator returns 0 for two elements, we consider them to
 * be equal!
 */
class wn {
    constructor(t) {
        this.At = t, this.data = new _n(this.At);
    }
    has(t) {
        return null !== this.data.get(t);
    }
    first() {
        return this.data.Ae();
    }
    last() {
        return this.data.Re();
    }
    get size() {
        return this.data.size;
    }
    indexOf(t) {
        return this.data.indexOf(t);
    }
    /** Iterates elements in order defined by "comparator" */    forEach(t) {
        this.data.Pe(((e, n) => (t(e), !1)));
    }
    /** Iterates over `elem`s such that: range[0] &lt;= elem &lt; range[1]. */    Qe(t, e) {
        const n = this.data.Ve(t[0]);
        for (;n.Ce(); ) {
            const s = n.De();
            if (this.At(s.key, t[1]) >= 0) return;
            e(s.key);
        }
    }
    /**
     * Iterates over `elem`s such that: start &lt;= elem until false is returned.
     */    Ke(t, e) {
        let n;
        for (n = void 0 !== e ? this.data.Ve(e) : this.data.ge(); n.Ce(); ) {
            if (!t(n.De().key)) return;
        }
    }
    /** Finds the least element greater than or equal to `elem`. */    We(t) {
        const e = this.data.Ve(t);
        return e.Ce() ? e.De().key : null;
    }
    ge() {
        return new En(this.data.ge());
    }
    Ve(t) {
        return new En(this.data.Ve(t));
    }
    /** Inserts or updates an element */    add(t) {
        return this.copy(this.data.remove(t).Ie(t, !0));
    }
    /** Deletes an element */    delete(t) {
        return this.has(t) ? this.copy(this.data.remove(t)) : this;
    }
    bt() {
        return this.data.bt();
    }
    je(t) {
        let e = this;
        // Make sure `result` always refers to the larger one of the two sets.
                return e.size < t.size && (e = t, t = this), t.forEach((t => {
            e = e.add(t);
        })), e;
    }
    isEqual(t) {
        if (!(t instanceof wn)) return !1;
        if (this.size !== t.size) return !1;
        const e = this.data.ge(), n = t.data.ge();
        for (;e.Ce(); ) {
            const t = e.De().key, s = n.De().key;
            if (0 !== this.At(t, s)) return !1;
        }
        return !0;
    }
    Dt() {
        const t = [];
        return this.forEach((e => {
            t.push(e);
        })), t;
    }
    toString() {
        const t = [];
        return this.forEach((e => t.push(e))), "SortedSet(" + t.toString() + ")";
    }
    copy(t) {
        const e = new wn(this.At);
        return e.data = t, e;
    }
}

class En {
    constructor(t) {
        this.Ge = t;
    }
    De() {
        return this.Ge.De().key;
    }
    Ce() {
        return this.Ge.Ce();
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const Tn = new _n(_t.At);

function In() {
    return Tn;
}

function mn() {
    return In();
}

const An = new _n(_t.At);

function Rn() {
    return An;
}

const Pn = new _n(_t.At);

function yn() {
    return Pn;
}

const gn = new wn(_t.At);

function Vn(...t) {
    let e = gn;
    for (const n of t) e = e.add(n);
    return e;
}

const pn = new wn(v);

function bn() {
    return pn;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * DocumentSet is an immutable (copy-on-write) collection that holds documents
 * in order specified by the provided comparator. We always add a document key
 * comparator on top of what is provided to guarantee document equality based on
 * the key.
 */ class vn {
    /** The default ordering is by key if the comparator is omitted */
    constructor(t) {
        // We are adding document key comparator to the end as it's the only
        // guaranteed unique property of a document.
        this.At = t ? (e, n) => t(e, n) || _t.At(e.key, n.key) : (t, e) => _t.At(t.key, e.key), 
        this.ze = Rn(), this.He = new _n(this.At);
    }
    /**
     * Returns an empty copy of the existing DocumentSet, using the same
     * comparator.
     */    static Je(t) {
        return new vn(t.At);
    }
    has(t) {
        return null != this.ze.get(t);
    }
    get(t) {
        return this.ze.get(t);
    }
    first() {
        return this.He.Ae();
    }
    last() {
        return this.He.Re();
    }
    bt() {
        return this.He.bt();
    }
    /**
     * Returns the index of the provided key in the document set, or -1 if the
     * document key is not present in the set;
     */    indexOf(t) {
        const e = this.ze.get(t);
        return e ? this.He.indexOf(e) : -1;
    }
    get size() {
        return this.He.size;
    }
    /** Iterates documents in order defined by "comparator" */    forEach(t) {
        this.He.Pe(((e, n) => (t(e), !1)));
    }
    /** Inserts or updates a document with the same key */    add(t) {
        // First remove the element if we have it.
        const e = this.delete(t.key);
        return e.copy(e.ze.Ie(t.key, t), e.He.Ie(t, null));
    }
    /** Deletes a document with a given key */    delete(t) {
        const e = this.get(t);
        return e ? this.copy(this.ze.remove(t), this.He.remove(e)) : this;
    }
    isEqual(t) {
        if (!(t instanceof vn)) return !1;
        if (this.size !== t.size) return !1;
        const e = this.He.ge(), n = t.He.ge();
        for (;e.Ce(); ) {
            const t = e.De().key, s = n.De().key;
            if (!t.isEqual(s)) return !1;
        }
        return !0;
    }
    toString() {
        const t = [];
        return this.forEach((e => {
            t.push(e.toString());
        })), 0 === t.length ? "DocumentSet ()" : "DocumentSet (\n  " + t.join("  \n") + "\n)";
    }
    copy(t, e) {
        const n = new vn;
        return n.At = this.At, n.ze = t, n.He = e, n;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * DocumentChangeSet keeps track of a set of changes to docs in a query, merging
 * duplicate events for the same doc.
 */ class Sn {
    constructor() {
        this.Ye = new _n(_t.At);
    }
    track(t) {
        const e = t.doc.key, n = this.Ye.get(e);
        n ? 
        // Merge the new change with the existing change.
        0 /* Added */ !== t.type && 3 /* Metadata */ === n.type ? this.Ye = this.Ye.Ie(e, t) : 3 /* Metadata */ === t.type && 1 /* Removed */ !== n.type ? this.Ye = this.Ye.Ie(e, {
            type: n.type,
            doc: t.doc
        }) : 2 /* Modified */ === t.type && 2 /* Modified */ === n.type ? this.Ye = this.Ye.Ie(e, {
            type: 2 /* Modified */ ,
            doc: t.doc
        }) : 2 /* Modified */ === t.type && 0 /* Added */ === n.type ? this.Ye = this.Ye.Ie(e, {
            type: 0 /* Added */ ,
            doc: t.doc
        }) : 1 /* Removed */ === t.type && 0 /* Added */ === n.type ? this.Ye = this.Ye.remove(e) : 1 /* Removed */ === t.type && 2 /* Modified */ === n.type ? this.Ye = this.Ye.Ie(e, {
            type: 1 /* Removed */ ,
            doc: n.doc
        }) : 0 /* Added */ === t.type && 1 /* Removed */ === n.type ? this.Ye = this.Ye.Ie(e, {
            type: 2 /* Modified */ ,
            doc: t.doc
        }) : 
        // This includes these cases, which don't make sense:
        // Added->Added
        // Removed->Removed
        // Modified->Added
        // Removed->Modified
        // Metadata->Added
        // Removed->Metadata
        y() : this.Ye = this.Ye.Ie(e, t);
    }
    Xe() {
        const t = [];
        return this.Ye.Pe(((e, n) => {
            t.push(n);
        })), t;
    }
}

class Dn {
    constructor(t, e, n, s, i, r, o, c) {
        this.query = t, this.docs = e, this.Ze = n, this.docChanges = s, this.tn = i, this.fromCache = r, 
        this.en = o, this.nn = c;
    }
    /** Returns a view snapshot as if all documents in the snapshot were added. */    static sn(t, e, n, s) {
        const i = [];
        return e.forEach((t => {
            i.push({
                type: 0 /* Added */ ,
                doc: t
            });
        })), new Dn(t, e, vn.Je(e), i, n, s, 
        /* syncStateChanged= */ !0, 
        /* excludesMetadataChanges= */ !1);
    }
    get hasPendingWrites() {
        return !this.tn.bt();
    }
    isEqual(t) {
        if (!(this.fromCache === t.fromCache && this.en === t.en && this.tn.isEqual(t.tn) && ye(this.query, t.query) && this.docs.isEqual(t.docs) && this.Ze.isEqual(t.Ze))) return !1;
        const e = this.docChanges, n = t.docChanges;
        if (e.length !== n.length) return !1;
        for (let t = 0; t < e.length; t++) if (e[t].type !== n[t].type || !e[t].doc.isEqual(n[t].doc)) return !1;
        return !0;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An event from the RemoteStore. It is split into targetChanges (changes to the
 * state or the set of documents in our watched targets) and documentUpdates
 * (changes to the actual documents).
 */ class Cn {
    constructor(
    /**
     * The snapshot version this event brings us up to, or MIN if not set.
     */
    t, 
    /**
     * A map from target to changes to the target. See TargetChange.
     */
    e, 
    /**
     * A set of targets that is known to be inconsistent. Listens for these
     * targets should be re-established without resume tokens.
     */
    n, 
    /**
     * A set of which documents have changed or been deleted, along with the
     * doc's new values (if not deleted).
     */
    s, 
    /**
     * A set of which document updates are due only to limbo resolution targets.
     */
    i) {
        this.oe = t, this.rn = e, this.on = n, this.cn = s, this.an = i;
    }
    /**
     * HACK: Views require RemoteEvents in order to determine whether the view is
     * CURRENT, but secondary tabs don't receive remote events. So this method is
     * used to create a synthesized RemoteEvent that can be used to apply a
     * CURRENT status change to a View, for queries executed in a different tab.
     */
    // PORTING NOTE: Multi-tab only
    static un(t, e) {
        const n = new Map;
        return n.set(t, xn.hn(t, e)), new Cn(ct.min(), n, bn(), In(), Vn());
    }
}

/**
 * A TargetChange specifies the set of changes for a specific target as part of
 * a RemoteEvent. These changes track which documents are added, modified or
 * removed, as well as the target's resume token and whether the target is
 * marked CURRENT.
 * The actual changes *to* documents are not part of the TargetChange since
 * documents may be part of multiple targets.
 */ class xn {
    constructor(
    /**
     * An opaque, server-assigned token that allows watching a query to be resumed
     * after disconnecting without retransmitting all the data that matches the
     * query. The resume token essentially identifies a point in time from which
     * the server should resume sending results.
     */
    t, 
    /**
     * The "current" (synced) status of this target. Note that "current"
     * has special meaning in the RPC protocol that implies that a target is
     * both up-to-date and consistent with the rest of the watch stream.
     */
    e, 
    /**
     * The set of documents that were newly assigned to this target as part of
     * this remote event.
     */
    n, 
    /**
     * The set of documents that were already assigned to this target but received
     * an update during this remote event.
     */
    s, 
    /**
     * The set of documents that were removed from this target as part of this
     * remote event.
     */
    i) {
        this.resumeToken = t, this.ln = e, this._n = n, this.fn = s, this.dn = i;
    }
    /**
     * This method is used to create a synthesized TargetChanges that can be used to
     * apply a CURRENT status change to a View (for queries executed in a different
     * tab) or for new queries (to raise snapshots with correct CURRENT status).
     */    static hn(t, e) {
        return new xn(C.h, e, Vn(), Vn(), Vn());
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Represents a changed document and a list of target ids to which this change
 * applies.
 *
 * If document has been deleted NoDocument will be provided.
 */ class Nn {
    constructor(
    /** The new document applies to all of these targets. */
    t, 
    /** The new document is removed from all of these targets. */
    e, 
    /** The key of the document for this change. */
    n, 
    /**
     * The new document or NoDocument if it was deleted. Is null if the
     * document went out of view without the server sending a new document.
     */
    s) {
        this.wn = t, this.removedTargetIds = e, this.key = n, this.En = s;
    }
}

class Fn {
    constructor(t, e) {
        this.targetId = t, this.Tn = e;
    }
}

class On {
    constructor(
    /** What kind of change occurred to the watch target. */
    t, 
    /** The target IDs that were added/removed/set. */
    e, 
    /**
     * An opaque, server-assigned token that allows watching a target to be
     * resumed after disconnecting without retransmitting all the data that
     * matches the target. The resume token essentially identifies a point in
     * time from which the server should resume sending results.
     */
    n = C.h
    /** An RPC error indicating why the watch failed. */ , s = null) {
        this.state = t, this.targetIds = e, this.resumeToken = n, this.cause = s;
    }
}

/** Tracks the internal state of a Watch target. */ class kn {
    constructor() {
        /**
         * The number of pending responses (adds or removes) that we are waiting on.
         * We only consider targets active that have no pending responses.
         */
        this.In = 0, 
        /**
         * Keeps track of the document changes since the last raised snapshot.
         *
         * These changes are continuously updated as we receive document updates and
         * always reflect the current set of changes against the last issued snapshot.
         */
        this.mn = $n(), 
        /** See public getters for explanations of these fields. */
        this.An = C.h, this.Rn = !1, 
        /**
         * Whether this target state should be included in the next snapshot. We
         * initialize to true so that newly-added targets are included in the next
         * RemoteEvent.
         */
        this.Pn = !0;
    }
    /**
     * Whether this target has been marked 'current'.
     *
     * 'Current' has special meaning in the RPC protocol: It implies that the
     * Watch backend has sent us all changes up to the point at which the target
     * was added and that the target is consistent with the rest of the watch
     * stream.
     */    get ln() {
        return this.Rn;
    }
    /** The last resume token sent to us for this target. */    get resumeToken() {
        return this.An;
    }
    /** Whether this target has pending target adds or target removes. */    get yn() {
        return 0 !== this.In;
    }
    /** Whether we have modified any state that should trigger a snapshot. */    get gn() {
        return this.Pn;
    }
    /**
     * Applies the resume token to the TargetChange, but only when it has a new
     * value. Empty resumeTokens are discarded.
     */    Vn(t) {
        t.o() > 0 && (this.Pn = !0, this.An = t);
    }
    /**
     * Creates a target change from the current set of changes.
     *
     * To reset the document changes after raising this snapshot, call
     * `clearPendingChanges()`.
     */    pn() {
        let t = Vn(), e = Vn(), n = Vn();
        return this.mn.forEach(((s, i) => {
            switch (i) {
              case 0 /* Added */ :
                t = t.add(s);
                break;

              case 2 /* Modified */ :
                e = e.add(s);
                break;

              case 1 /* Removed */ :
                n = n.add(s);
                break;

              default:
                y();
            }
        })), new xn(this.An, this.Rn, t, e, n);
    }
    /**
     * Resets the document changes and sets `hasPendingChanges` to false.
     */    bn() {
        this.Pn = !1, this.mn = $n();
    }
    vn(t, e) {
        this.Pn = !0, this.mn = this.mn.Ie(t, e);
    }
    Sn(t) {
        this.Pn = !0, this.mn = this.mn.remove(t);
    }
    Dn() {
        this.In += 1;
    }
    Cn() {
        this.In -= 1;
    }
    xn() {
        this.Pn = !0, this.Rn = !0;
    }
}

/**
 * A helper class to accumulate watch changes into a RemoteEvent.
 */
class Mn {
    constructor(t) {
        this.Nn = t, 
        /** The internal state of all tracked targets. */
        this.Fn = new Map, 
        /** Keeps track of the documents to update since the last raised snapshot. */
        this.On = In(), 
        /** A mapping of document keys to their set of target IDs. */
        this.kn = Ln(), 
        /**
         * A list of targets with existence filter mismatches. These targets are
         * known to be inconsistent and their listens needs to be re-established by
         * RemoteStore.
         */
        this.Mn = new wn(v);
    }
    /**
     * Processes and adds the DocumentWatchChange to the current set of changes.
     */    Ln(t) {
        for (const e of t.wn) t.En instanceof qt ? this.$n(e, t.En) : t.En instanceof Ut && this.Bn(e, t.key, t.En);
        for (const e of t.removedTargetIds) this.Bn(e, t.key, t.En);
    }
    /** Processes and adds the WatchTargetChange to the current set of changes. */    qn(t) {
        this.Un(t, (e => {
            const n = this.Qn(e);
            switch (t.state) {
              case 0 /* NoChange */ :
                this.Kn(e) && n.Vn(t.resumeToken);
                break;

              case 1 /* Added */ :
                // We need to decrement the number of pending acks needed from watch
                // for this targetId.
                n.Cn(), n.yn || 
                // We have a freshly added target, so we need to reset any state
                // that we had previously. This can happen e.g. when remove and add
                // back a target for existence filter mismatches.
                n.bn(), n.Vn(t.resumeToken);
                break;

              case 2 /* Removed */ :
                // We need to keep track of removed targets to we can post-filter and
                // remove any target changes.
                // We need to decrement the number of pending acks needed from watch
                // for this targetId.
                n.Cn(), n.yn || this.removeTarget(e);
                break;

              case 3 /* Current */ :
                this.Kn(e) && (n.xn(), n.Vn(t.resumeToken));
                break;

              case 4 /* Reset */ :
                this.Kn(e) && (
                // Reset the target and synthesizes removes for all existing
                // documents. The backend will re-add any documents that still
                // match the target before it sends the next global snapshot.
                this.Wn(e), n.Vn(t.resumeToken));
                break;

              default:
                y();
            }
        }));
    }
    /**
     * Iterates over all targetIds that the watch change applies to: either the
     * targetIds explicitly listed in the change or the targetIds of all currently
     * active targets.
     */    Un(t, e) {
        t.targetIds.length > 0 ? t.targetIds.forEach(e) : this.Fn.forEach(((t, n) => {
            this.Kn(n) && e(n);
        }));
    }
    /**
     * Handles existence filters and synthesizes deletes for filter mismatches.
     * Targets that are invalidated by filter mismatches are added to
     * `pendingTargetResets`.
     */    jn(t) {
        const e = t.targetId, n = t.Tn.count, s = this.Gn(e);
        if (s) {
            const t = s.target;
            if (Ht(t)) if (0 === n) {
                // The existence filter told us the document does not exist. We deduce
                // that this document does not exist and apply a deleted document to
                // our updates. Without applying this deleted document there might be
                // another query that will raise this document as part of a snapshot
                // until it is resolved, essentially exposing inconsistency between
                // queries.
                const n = new _t(t.path);
                this.Bn(e, n, new Ut(n, ct.min()));
            } else g(1 === n); else {
                this.zn(e) !== n && (
                // Existence filter mismatch: We reset the mapping and raise a new
                // snapshot with `isFromCache:true`.
                this.Wn(e), this.Mn = this.Mn.add(e));
            }
        }
    }
    /**
     * Converts the currently accumulated state into a remote event at the
     * provided snapshot version. Resets the accumulated changes before returning.
     */    Hn(t) {
        const e = new Map;
        this.Fn.forEach(((n, s) => {
            const i = this.Gn(s);
            if (i) {
                if (n.ln && Ht(i.target)) {
                    // Document queries for document that don't exist can produce an empty
                    // result set. To update our local cache, we synthesize a document
                    // delete if we have not previously received the document. This
                    // resolves the limbo state of the document, removing it from
                    // limboDocumentRefs.
                    // TODO(dimond): Ideally we would have an explicit lookup target
                    // instead resulting in an explicit delete message and we could
                    // remove this special logic.
                    const e = new _t(i.target.path);
                    null !== this.On.get(e) || this.Jn(s, e) || this.Bn(s, e, new Ut(e, t));
                }
                n.gn && (e.set(s, n.pn()), n.bn());
            }
        }));
        let n = Vn();
        // We extract the set of limbo-only document updates as the GC logic
        // special-cases documents that do not appear in the target cache.
        
        // TODO(gsoltis): Expand on this comment once GC is available in the JS
        // client.
                this.kn.forEach(((t, e) => {
            let s = !0;
            e.Ke((t => {
                const e = this.Gn(t);
                return !e || 2 /* LimboResolution */ === e.re || (s = !1, !1);
            })), s && (n = n.add(t));
        }));
        const s = new Cn(t, e, this.Mn, this.On, n);
        return this.On = In(), this.kn = Ln(), this.Mn = new wn(v), s;
    }
    /**
     * Adds the provided document to the internal list of document updates and
     * its document key to the given target's mapping.
     */
    // Visible for testing.
    $n(t, e) {
        if (!this.Kn(t)) return;
        const n = this.Jn(t, e.key) ? 2 /* Modified */ : 0 /* Added */;
        this.Qn(t).vn(e.key, n), this.On = this.On.Ie(e.key, e), this.kn = this.kn.Ie(e.key, this.Yn(e.key).add(t));
    }
    /**
     * Removes the provided document from the target mapping. If the
     * document no longer matches the target, but the document's state is still
     * known (e.g. we know that the document was deleted or we received the change
     * that caused the filter mismatch), the new document can be provided
     * to update the remote document cache.
     */
    // Visible for testing.
    Bn(t, e, n) {
        if (!this.Kn(t)) return;
        const s = this.Qn(t);
        this.Jn(t, e) ? s.vn(e, 1 /* Removed */) : 
        // The document may have entered and left the target before we raised a
        // snapshot, so we can just ignore the change.
        s.Sn(e), this.kn = this.kn.Ie(e, this.Yn(e).delete(t)), n && (this.On = this.On.Ie(e, n));
    }
    removeTarget(t) {
        this.Fn.delete(t);
    }
    /**
     * Returns the current count of documents in the target. This includes both
     * the number of documents that the LocalStore considers to be part of the
     * target as well as any accumulated changes.
     */    zn(t) {
        const e = this.Qn(t).pn();
        return this.Nn.Xn(t).size + e._n.size - e.dn.size;
    }
    /**
     * Increment the number of acks needed from watch before we can consider the
     * server to be 'in-sync' with the client's active targets.
     */    Dn(t) {
        this.Qn(t).Dn();
    }
    Qn(t) {
        let e = this.Fn.get(t);
        return e || (e = new kn, this.Fn.set(t, e)), e;
    }
    Yn(t) {
        let e = this.kn.get(t);
        return e || (e = new wn(v), this.kn = this.kn.Ie(t, e)), e;
    }
    /**
     * Verifies that the user is still interested in this target (by calling
     * `getTargetDataForTarget()`) and that we are not waiting for pending ADDs
     * from watch.
     */    Kn(t) {
        const e = null !== this.Gn(t);
        return e || m("WatchChangeAggregator", "Detected inactive target", t), e;
    }
    /**
     * Returns the TargetData for an active target (i.e. a target that the user
     * is still interested in that has no outstanding target change requests).
     */    Gn(t) {
        const e = this.Fn.get(t);
        return e && e.yn ? null : this.Nn.Zn(t);
    }
    /**
     * Resets the state of a Watch target to its initial state (e.g. sets
     * 'current' to false, clears the resume token and removes its target mapping
     * from all documents).
     */    Wn(t) {
        this.Fn.set(t, new kn);
        this.Nn.Xn(t).forEach((e => {
            this.Bn(t, e, /*updatedDocument=*/ null);
        }));
    }
    /**
     * Returns whether the LocalStore considers the document to be part of the
     * specified target.
     */    Jn(t, e) {
        return this.Nn.Xn(t).has(e);
    }
}

function Ln() {
    return new _n(_t.At);
}

function $n() {
    return new _n(_t.At);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const Bn = (() => {
    const t = {
        asc: "ASCENDING",
        desc: "DESCENDING"
    };
    return t;
})(), qn = (() => {
    const t = {
        "<": "LESS_THAN",
        "<=": "LESS_THAN_OR_EQUAL",
        ">": "GREATER_THAN",
        ">=": "GREATER_THAN_OR_EQUAL",
        "==": "EQUAL",
        "!=": "NOT_EQUAL",
        "array-contains": "ARRAY_CONTAINS",
        in: "IN",
        "not-in": "NOT_IN",
        "array-contains-any": "ARRAY_CONTAINS_ANY"
    };
    return t;
})();

/**
 * This class generates JsonObject values for the Datastore API suitable for
 * sending to either GRPC stub methods or via the JSON/HTTP REST API.
 *
 * The serializer supports both Protobuf.js and Proto3 JSON formats. By
 * setting `useProto3Json` to true, the serializer will use the Proto3 JSON
 * format.
 *
 * For a description of the Proto3 JSON format check
 * https://developers.google.com/protocol-buffers/docs/proto3#json
 *
 * TODO(klimt): We can remove the databaseId argument if we keep the full
 * resource name in documents.
 */
class Un {
    constructor(t, e) {
        this.O = t, this.he = e;
    }
}

/**
 * Returns a value for a Date that's appropriate to put into a proto.
 */
function Qn(t, e) {
    if (t.he) {
        return `${new Date(1e3 * e.seconds).toISOString().replace(/\.\d*/, "").replace("Z", "")}.${("000000000" + e.nanoseconds).slice(-9)}Z`;
    }
    return {
        seconds: "" + e.seconds,
        nanos: e.nanoseconds
    };
}

/**
 * Returns a value for bytes that's appropriate to put in a proto.
 *
 * Visible for testing.
 */
function Kn(t, e) {
    return t.he ? e.toBase64() : e.toUint8Array();
}

/**
 * Returns a ByteString based on the proto string value.
 */ function Wn(t, e) {
    return Qn(t, e.Tt());
}

function jn(t) {
    return g(!!t), ct.wt(function(t) {
        const e = It(t);
        return new ot(e.seconds, e.nanos);
    }(t));
}

function Gn(t, e) {
    return function(t) {
        return new ut([ "projects", t.projectId, "databases", t.database ]);
    }(t).child("documents").child(e).Ct();
}

function zn(t) {
    const e = ut.xt(t);
    return g(Ps(e)), e;
}

function Hn(t, e) {
    return Gn(t.O, e.path);
}

function Jn(t, e) {
    const n = zn(e);
    if (n.get(1) !== t.O.projectId) throw new N(x.INVALID_ARGUMENT, "Tried to deserialize key from different project: " + n.get(1) + " vs " + t.O.projectId);
    if (n.get(3) !== t.O.database) throw new N(x.INVALID_ARGUMENT, "Tried to deserialize key from different database: " + n.get(3) + " vs " + t.O.database);
    return new _t(ts(n));
}

function Yn(t, e) {
    return Gn(t.O, e);
}

function Xn(t) {
    const e = zn(t);
    // In v1beta1 queries for collections at the root did not have a trailing
    // "/documents". In v1 all resource paths contain "/documents". Preserve the
    // ability to read the v1beta1 form for compatibility with queries persisted
    // in the local target cache.
        return 4 === e.length ? ut.Nt() : ts(e);
}

function Zn(t) {
    return new ut([ "projects", t.O.projectId, "databases", t.O.database ]).Ct();
}

function ts(t) {
    return g(t.length > 4 && "documents" === t.get(4)), t.Pt(5);
}

/** Creates a Document proto from key and fields (but no create/update time) */ function es(t, e, n) {
    return {
        name: Hn(t, e),
        fields: n.proto.mapValue.fields
    };
}

function ns(t, e, n) {
    const s = Jn(t, e.name), i = jn(e.updateTime), r = new Mt({
        mapValue: {
            fields: e.fields
        }
    });
    return new qt(s, i, r, {
        hasCommittedMutations: !!n
    });
}

function ss(t, e) {
    return "found" in e ? function(t, e) {
        g(!!e.found), e.found.name, e.found.updateTime;
        const n = Jn(t, e.found.name), s = jn(e.found.updateTime), i = new Mt({
            mapValue: {
                fields: e.found.fields
            }
        });
        return new qt(n, s, i, {});
    }(t, e) : "missing" in e ? function(t, e) {
        g(!!e.missing), g(!!e.readTime);
        const n = Jn(t, e.missing), s = jn(e.readTime);
        return new Ut(n, s);
    }(t, e) : y();
}

function is(t, e) {
    let n;
    if ("targetChange" in e) {
        e.targetChange;
        // proto3 default value is unset in JSON (undefined), so use 'NO_CHANGE'
        // if unset
        const s = function(t) {
            return "NO_CHANGE" === t ? 0 /* NoChange */ : "ADD" === t ? 1 /* Added */ : "REMOVE" === t ? 2 /* Removed */ : "CURRENT" === t ? 3 /* Current */ : "RESET" === t ? 4 /* Reset */ : y();
        }(e.targetChange.targetChangeType || "NO_CHANGE"), i = e.targetChange.targetIds || [], r = function(t, e) {
            return t.he ? (g(void 0 === e || "string" == typeof e), C.fromBase64String(e || "")) : (g(void 0 === e || e instanceof Uint8Array), 
            C.fromUint8Array(e || new Uint8Array));
        }(t, e.targetChange.resumeToken), o = e.targetChange.cause, c = o && function(t) {
            const e = void 0 === t.code ? x.UNKNOWN : nt(t.code);
            return new N(e, t.message || "");
        }
        /**
 * Returns a value for a number (or null) that's appropriate to put into
 * a google.protobuf.Int32Value proto.
 * DO NOT USE THIS FOR ANYTHING ELSE.
 * This method cheats. It's typed as returning "number" because that's what
 * our generated proto interfaces say Int32Value must be. But GRPC actually
 * expects a { value: <number> } struct.
 */ (o);
        n = new On(s, i, r, c || null);
    } else if ("documentChange" in e) {
        e.documentChange;
        const s = e.documentChange;
        s.document, s.document.name, s.document.updateTime;
        const i = Jn(t, s.document.name), r = jn(s.document.updateTime), o = new Mt({
            mapValue: {
                fields: s.document.fields
            }
        }), c = new qt(i, r, o, {}), a = s.targetIds || [], u = s.removedTargetIds || [];
        n = new Nn(a, u, c.key, c);
    } else if ("documentDelete" in e) {
        e.documentDelete;
        const s = e.documentDelete;
        s.document;
        const i = Jn(t, s.document), r = s.readTime ? jn(s.readTime) : ct.min(), o = new Ut(i, r), c = s.removedTargetIds || [];
        n = new Nn([], c, o.key, o);
    } else if ("documentRemove" in e) {
        e.documentRemove;
        const s = e.documentRemove;
        s.document;
        const i = Jn(t, s.document), r = s.removedTargetIds || [];
        n = new Nn([], r, i, null);
    } else {
        if (!("filter" in e)) return y();
        {
            e.filter;
            const t = e.filter;
            t.targetId;
            const s = t.count || 0, i = new ln(s), r = t.targetId;
            n = new Fn(r, i);
        }
    }
    return n;
}

function rs(t, e) {
    let n;
    if (e instanceof nn) n = {
        update: es(t, e.key, e.value)
    }; else if (e instanceof un) n = {
        delete: Hn(t, e.key)
    }; else if (e instanceof sn) n = {
        update: es(t, e.key, e.data),
        updateMask: Rs(e.Te)
    }; else {
        if (!(e instanceof hn)) return y();
        n = {
            verify: Hn(t, e.key)
        };
    }
    return e.fieldTransforms.length > 0 && (n.updateTransforms = e.fieldTransforms.map((t => function(t, e) {
        const n = e.transform;
        if (n instanceof Me) return {
            fieldPath: e.field.Ct(),
            setToServerValue: "REQUEST_TIME"
        };
        if (n instanceof Le) return {
            fieldPath: e.field.Ct(),
            appendMissingElements: {
                values: n.elements
            }
        };
        if (n instanceof Be) return {
            fieldPath: e.field.Ct(),
            removeAllFromArray: {
                values: n.elements
            }
        };
        if (n instanceof Ue) return {
            fieldPath: e.field.Ct(),
            increment: n._e
        };
        throw y();
    }(0, t)))), e.Ee.we || (n.currentDocument = function(t, e) {
        return void 0 !== e.updateTime ? {
            updateTime: Wn(t, e.updateTime)
        } : void 0 !== e.exists ? {
            exists: e.exists
        } : y();
    }(t, e.Ee)), n;
}

function os(t, e) {
    const n = e.currentDocument ? function(t) {
        return void 0 !== t.updateTime ? ze.updateTime(jn(t.updateTime)) : void 0 !== t.exists ? ze.exists(t.exists) : ze.de();
    }(e.currentDocument) : ze.de(), s = e.updateTransforms ? e.updateTransforms.map((e => function(t, e) {
        let n = null;
        if ("setToServerValue" in e) g("REQUEST_TIME" === e.setToServerValue), n = new Me; else if ("appendMissingElements" in e) {
            const t = e.appendMissingElements.values || [];
            n = new Le(t);
        } else if ("removeAllFromArray" in e) {
            const t = e.removeAllFromArray.values || [];
            n = new Be(t);
        } else "increment" in e ? n = new Ue(t, e.increment) : y();
        const s = lt.Mt(e.fieldPath);
        return new We(s, n);
    }(t, e))) : [];
    if (e.update) {
        e.update.name;
        const i = Jn(t, e.update.name), r = new Mt({
            mapValue: {
                fields: e.update.fields
            }
        });
        if (e.updateMask) {
            const t = function(t) {
                const e = t.fieldPaths || [];
                return new Et(e.map((t => lt.Mt(t))));
            }(e.updateMask);
            return new sn(i, r, t, n, s);
        }
        return new nn(i, r, n, s);
    }
    if (e.delete) {
        const s = Jn(t, e.delete);
        return new un(s, n);
    }
    if (e.verify) {
        const s = Jn(t, e.verify);
        return new hn(s, n);
    }
    return y();
}

function cs(t, e) {
    return t && t.length > 0 ? (g(void 0 !== e), t.map((t => function(t, e) {
        // NOTE: Deletes don't have an updateTime.
        let n = t.updateTime ? jn(t.updateTime) : jn(e);
        n.isEqual(ct.min()) && (
        // The Firestore Emulator currently returns an update time of 0 for
        // deletes of non-existing documents (rather than null). This breaks the
        // test "get deleted doc while offline with source=cache" as NoDocuments
        // with version 0 are filtered by IndexedDb's RemoteDocumentCache.
        // TODO(#2149): Remove this when Emulator is fixed
        n = jn(e));
        let s = null;
        return t.transformResults && t.transformResults.length > 0 && (s = t.transformResults), 
        new Ge(n, s);
    }(t, e)))) : [];
}

function as(t, e) {
    return {
        documents: [ Yn(t, e.path) ]
    };
}

function us(t, e) {
    // Dissect the path into parent, collectionId, and optional key filter.
    const n = {
        structuredQuery: {}
    }, s = e.path;
    null !== e.collectionGroup ? (n.parent = Yn(t, s), n.structuredQuery.from = [ {
        collectionId: e.collectionGroup,
        allDescendants: !0
    } ]) : (n.parent = Yn(t, s.yt()), n.structuredQuery.from = [ {
        collectionId: s.Vt()
    } ]);
    const i = function(t) {
        if (0 === t.length) return;
        const e = t.map((t => 
        // visible for testing
        function(t) {
            if ("==" /* EQUAL */ === t.op) {
                if (Ot(t.value)) return {
                    unaryFilter: {
                        field: Ts(t.field),
                        op: "IS_NAN"
                    }
                };
                if (Ft(t.value)) return {
                    unaryFilter: {
                        field: Ts(t.field),
                        op: "IS_NULL"
                    }
                };
            } else if ("!=" /* NOT_EQUAL */ === t.op) {
                if (Ot(t.value)) return {
                    unaryFilter: {
                        field: Ts(t.field),
                        op: "IS_NOT_NAN"
                    }
                };
                if (Ft(t.value)) return {
                    unaryFilter: {
                        field: Ts(t.field),
                        op: "IS_NOT_NULL"
                    }
                };
            }
            return {
                fieldFilter: {
                    field: Ts(t.field),
                    op: Es(t.op),
                    value: t.value
                }
            };
        }(t)));
        if (1 === e.length) return e[0];
        return {
            compositeFilter: {
                op: "AND",
                filters: e
            }
        };
    }(e.filters);
    i && (n.structuredQuery.where = i);
    const r = function(t) {
        if (0 === t.length) return;
        return t.map((t => 
        // visible for testing
        function(t) {
            return {
                field: Ts(t.field),
                direction: ws(t.dir)
            };
        }(t)));
    }(e.orderBy);
    r && (n.structuredQuery.orderBy = r);
    const o = function(t, e) {
        return t.he || H(e) ? e : {
            value: e
        };
    }
    /**
 * Returns a number (or null) from a google.protobuf.Int32Value proto.
 */ (t, e.limit);
    return null !== o && (n.structuredQuery.limit = o), e.startAt && (n.structuredQuery.startAt = fs(e.startAt)), 
    e.endAt && (n.structuredQuery.endAt = fs(e.endAt)), n;
}

function hs(t) {
    let e = Xn(t.parent);
    const n = t.structuredQuery, s = n.from ? n.from.length : 0;
    let i = null;
    if (s > 0) {
        g(1 === s);
        const t = n.from[0];
        t.allDescendants ? i = t.collectionId : e = e.child(t.collectionId);
    }
    let r = [];
    n.where && (r = _s(n.where));
    let o = [];
    n.orderBy && (o = n.orderBy.map((t => function(t) {
        return new ae(Is(t.field), 
        // visible for testing
        function(t) {
            switch (t) {
              case "ASCENDING":
                return "asc" /* ASCENDING */;

              case "DESCENDING":
                return "desc" /* DESCENDING */;

              default:
                return;
            }
        }
        // visible for testing
        (t.direction));
    }(t))));
    let c = null;
    n.limit && (c = function(t) {
        let e;
        return e = "object" == typeof t ? t.value : t, H(e) ? null : e;
    }(n.limit));
    let a = null;
    n.startAt && (a = ds(n.startAt));
    let u = null;
    return n.endAt && (u = ds(n.endAt)), fe(e, i, o, r, c, "F" /* First */ , a, u);
}

function ls(t, e) {
    const n = function(t, e) {
        switch (e) {
          case 0 /* Listen */ :
            return null;

          case 1 /* ExistenceFilterMismatch */ :
            return "existence-filter-mismatch";

          case 2 /* LimboResolution */ :
            return "limbo-document";

          default:
            return y();
        }
    }(0, e.re);
    return null == n ? null : {
        "goog-listen-tags": n
    };
}

function _s(t) {
    return t ? void 0 !== t.unaryFilter ? [ As(t) ] : void 0 !== t.fieldFilter ? [ ms(t) ] : void 0 !== t.compositeFilter ? t.compositeFilter.filters.map((t => _s(t))).reduce(((t, e) => t.concat(e))) : y() : [];
}

function fs(t) {
    return {
        before: t.before,
        values: t.position
    };
}

function ds(t) {
    const e = !!t.before, n = t.values || [];
    return new oe(n, e);
}

// visible for testing
function ws(t) {
    return Bn[t];
}

function Es(t) {
    return qn[t];
}

function Ts(t) {
    return {
        fieldPath: t.Ct()
    };
}

function Is(t) {
    return lt.Mt(t.fieldPath);
}

function ms(t) {
    return Jt.create(Is(t.fieldFilter.field), function(t) {
        switch (t) {
          case "EQUAL":
            return "==" /* EQUAL */;

          case "NOT_EQUAL":
            return "!=" /* NOT_EQUAL */;

          case "GREATER_THAN":
            return ">" /* GREATER_THAN */;

          case "GREATER_THAN_OR_EQUAL":
            return ">=" /* GREATER_THAN_OR_EQUAL */;

          case "LESS_THAN":
            return "<" /* LESS_THAN */;

          case "LESS_THAN_OR_EQUAL":
            return "<=" /* LESS_THAN_OR_EQUAL */;

          case "ARRAY_CONTAINS":
            return "array-contains" /* ARRAY_CONTAINS */;

          case "IN":
            return "in" /* IN */;

          case "NOT_IN":
            return "not-in" /* NOT_IN */;

          case "ARRAY_CONTAINS_ANY":
            return "array-contains-any" /* ARRAY_CONTAINS_ANY */;

          case "OPERATOR_UNSPECIFIED":
          default:
            return y();
        }
    }(t.fieldFilter.op), t.fieldFilter.value);
}

function As(t) {
    switch (t.unaryFilter.op) {
      case "IS_NAN":
        const e = Is(t.unaryFilter.field);
        return Jt.create(e, "==" /* EQUAL */ , {
            doubleValue: NaN
        });

      case "IS_NULL":
        const n = Is(t.unaryFilter.field);
        return Jt.create(n, "==" /* EQUAL */ , {
            nullValue: "NULL_VALUE"
        });

      case "IS_NOT_NAN":
        const s = Is(t.unaryFilter.field);
        return Jt.create(s, "!=" /* NOT_EQUAL */ , {
            doubleValue: NaN
        });

      case "IS_NOT_NULL":
        const i = Is(t.unaryFilter.field);
        return Jt.create(i, "!=" /* NOT_EQUAL */ , {
            nullValue: "NULL_VALUE"
        });

      case "OPERATOR_UNSPECIFIED":
      default:
        return y();
    }
}

function Rs(t) {
    const e = [];
    return t.fields.forEach((t => e.push(t.Ct()))), {
        fieldPaths: e
    };
}

function Ps(t) {
    // Resource names have at least 4 components (project ID, database ID)
    return t.length >= 4 && "projects" === t.get(0) && "databases" === t.get(2);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function ys(t) {
    return new Un(t, /* useProto3Json= */ !0);
}

/**
 * An instance of the Platform's 'TextEncoder' implementation.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * PersistencePromise is essentially a re-implementation of Promise except
 * it has a .next() method instead of .then() and .next() and .catch() callbacks
 * are executed synchronously when a PersistencePromise resolves rather than
 * asynchronously (Promise implementations use setImmediate() or similar).
 *
 * This is necessary to interoperate with IndexedDB which will automatically
 * commit transactions if control is returned to the event loop without
 * synchronously initiating another operation on the transaction.
 *
 * NOTE: .then() and .catch() only allow a single consumer, unlike normal
 * Promises.
 */
class gs {
    constructor(t) {
        // NOTE: next/catchCallback will always point to our own wrapper functions,
        // not the user's raw next() or catch() callbacks.
        this.ts = null, this.es = null, 
        // When the operation resolves, we'll set result or error and mark isDone.
        this.result = void 0, this.error = void 0, this.ns = !1, 
        // Set to true when .then() or .catch() are called and prevents additional
        // chaining.
        this.ss = !1, t((t => {
            this.ns = !0, this.result = t, this.ts && 
            // value should be defined unless T is Void, but we can't express
            // that in the type system.
            this.ts(t);
        }), (t => {
            this.ns = !0, this.error = t, this.es && this.es(t);
        }));
    }
    catch(t) {
        return this.next(void 0, t);
    }
    next(t, e) {
        return this.ss && y(), this.ss = !0, this.ns ? this.error ? this.rs(e, this.error) : this.os(t, this.result) : new gs(((n, s) => {
            this.ts = e => {
                this.os(t, e).next(n, s);
            }, this.es = t => {
                this.rs(e, t).next(n, s);
            };
        }));
    }
    cs() {
        return new Promise(((t, e) => {
            this.next(t, e);
        }));
    }
    us(t) {
        try {
            const e = t();
            return e instanceof gs ? e : gs.resolve(e);
        } catch (t) {
            return gs.reject(t);
        }
    }
    os(t, e) {
        return t ? this.us((() => t(e))) : gs.resolve(e);
    }
    rs(t, e) {
        return t ? this.us((() => t(e))) : gs.reject(e);
    }
    static resolve(t) {
        return new gs(((e, n) => {
            e(t);
        }));
    }
    static reject(t) {
        return new gs(((e, n) => {
            n(t);
        }));
    }
    static hs(
    // Accept all Promise types in waitFor().
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    t) {
        return new gs(((e, n) => {
            let s = 0, i = 0, r = !1;
            t.forEach((t => {
                ++s, t.next((() => {
                    ++i, r && i === s && e();
                }), (t => n(t)));
            })), r = !0, i === s && e();
        }));
    }
    /**
     * Given an array of predicate functions that asynchronously evaluate to a
     * boolean, implements a short-circuiting `or` between the results. Predicates
     * will be evaluated until one of them returns `true`, then stop. The final
     * result will be whether any of them returned `true`.
     */    static ls(t) {
        let e = gs.resolve(!1);
        for (const n of t) e = e.next((t => t ? gs.resolve(t) : n()));
        return e;
    }
    static forEach(t, e) {
        const n = [];
        return t.forEach(((t, s) => {
            n.push(e.call(this, t, s));
        })), this.hs(n);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// References to `window` are guarded by SimpleDb.isAvailable()
/* eslint-disable no-restricted-globals */
/**
 * Wraps an IDBTransaction and exposes a store() method to get a handle to a
 * specific object store.
 */
class Vs {
    constructor(t, e) {
        this.action = t, this.transaction = e, this.aborted = !1, 
        /**
         * A promise that resolves with the result of the IndexedDb transaction.
         */
        this._s = new it, this.transaction.oncomplete = () => {
            this._s.resolve();
        }, this.transaction.onabort = () => {
            e.error ? this._s.reject(new vs(t, e.error)) : this._s.resolve();
        }, this.transaction.onerror = e => {
            const n = Ns(e.target.error);
            this._s.reject(new vs(t, n));
        };
    }
    static open(t, e, n, s) {
        try {
            return new Vs(e, t.transaction(s, n));
        } catch (t) {
            throw new vs(e, t);
        }
    }
    get fs() {
        return this._s.promise;
    }
    abort(t) {
        t && this._s.reject(t), this.aborted || (m("SimpleDb", "Aborting transaction:", t ? t.message : "Client-initiated abort"), 
        this.aborted = !0, this.transaction.abort());
    }
    /**
     * Returns a SimpleDbStore<KeyType, ValueType> for the specified store. All
     * operations performed on the SimpleDbStore happen within the context of this
     * transaction and it cannot be used anymore once the transaction is
     * completed.
     *
     * Note that we can't actually enforce that the KeyType and ValueType are
     * correct, but they allow type safety through the rest of the consuming code.
     */    store(t) {
        const e = this.transaction.objectStore(t);
        return new Ds(e);
    }
}

/**
 * Provides a wrapper around IndexedDb with a simplified interface that uses
 * Promise-like return values to chain operations. Real promises cannot be used
 * since .then() continuations are executed asynchronously (e.g. via
 * .setImmediate), which would cause IndexedDB to end the transaction.
 * See PersistencePromise for more details.
 */ class ps {
    /*
     * Creates a new SimpleDb wrapper for IndexedDb database `name`.
     *
     * Note that `version` must not be a downgrade. IndexedDB does not support
     * downgrading the schema version. We currently do not support any way to do
     * versioning outside of IndexedDB's versioning mechanism, as only
     * version-upgrade transactions are allowed to do things like create
     * objectstores.
     */
    constructor(t, e, n) {
        this.name = t, this.version = e, this.ds = n;
        // NOTE: According to https://bugs.webkit.org/show_bug.cgi?id=197050, the
        // bug we're checking for should exist in iOS >= 12.2 and < 13, but for
        // whatever reason it's much harder to hit after 12.2 so we only proactively
        // log on 12.2.
        12.2 === ps.ws(a()) && A("Firestore persistence suffers from a bug in iOS 12.2 Safari that may cause your app to stop working. See https://stackoverflow.com/q/56496296/110915 for details and a potential workaround.");
    }
    /** Deletes the specified database. */    static delete(t) {
        return m("SimpleDb", "Removing database:", t), Cs(window.indexedDB.deleteDatabase(t)).cs();
    }
    /** Returns true if IndexedDB is available in the current environment. */    static G() {
        if ("undefined" == typeof indexedDB) return !1;
        if (ps.Es()) return !0;
        // We extensively use indexed array values and compound keys,
        // which IE and Edge do not support. However, they still have indexedDB
        // defined on the window, so we need to check for them here and make sure
        // to return that persistence is not enabled for those browsers.
        // For tracking support of this feature, see here:
        // https://developer.microsoft.com/en-us/microsoft-edge/platform/status/indexeddbarraysandmultientrysupport/
        // Check the UA string to find out the browser.
                const t = a(), e = ps.ws(t), n = 0 < e && e < 10, s = ps.Ts(t), i = 0 < s && s < 4.5;
        // IE 10
        // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';
        // IE 11
        // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';
        // Edge
        // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML,
        // like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';
        // iOS Safari: Disable for users running iOS version < 10.
                return !(t.indexOf("MSIE ") > 0 || t.indexOf("Trident/") > 0 || t.indexOf("Edge/") > 0 || n || i);
    }
    /**
     * Returns true if the backing IndexedDB store is the Node IndexedDBShim
     * (see https://github.com/axemclion/IndexedDBShim).
     */    static Es() {
        var t;
        return "undefined" != typeof process && "YES" === (null === (t = process.env) || void 0 === t ? void 0 : t.Is);
    }
    /** Helper to get a typed SimpleDbStore from a transaction. */    static As(t, e) {
        return t.store(e);
    }
    // visible for testing
    /** Parse User Agent to determine iOS version. Returns -1 if not found. */
    static ws(t) {
        const e = t.match(/i(?:phone|pad|pod) os ([\d_]+)/i), n = e ? e[1].split("_").slice(0, 2).join(".") : "-1";
        return Number(n);
    }
    // visible for testing
    /** Parse User Agent to determine Android version. Returns -1 if not found. */
    static Ts(t) {
        const e = t.match(/Android ([\d.]+)/i), n = e ? e[1].split(".").slice(0, 2).join(".") : "-1";
        return Number(n);
    }
    /**
     * Opens the specified database, creating or upgrading it if necessary.
     */    async Rs(t) {
        return this.db || (m("SimpleDb", "Opening database:", this.name), this.db = await new Promise(((e, n) => {
            // TODO(mikelehen): Investigate browser compatibility.
            // https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API/Using_IndexedDB
            // suggests IE9 and older WebKit browsers handle upgrade
            // differently. They expect setVersion, as described here:
            // https://developer.mozilla.org/en-US/docs/Web/API/IDBVersionChangeRequest/setVersion
            const s = indexedDB.open(this.name, this.version);
            s.onsuccess = t => {
                const n = t.target.result;
                e(n);
            }, s.onblocked = () => {
                n(new vs(t, "Cannot upgrade IndexedDB schema while another tab is open. Close all tabs that access Firestore and reload this page to proceed."));
            }, s.onerror = e => {
                const s = e.target.error;
                "VersionError" === s.name ? n(new N(x.FAILED_PRECONDITION, "A newer version of the Firestore SDK was previously used and so the persisted data is not compatible with the version of the SDK you are now using. The SDK will operate with persistence disabled. If you need persistence, please re-upgrade to a newer version of the SDK or else clear the persisted IndexedDB data for your app to start fresh.")) : n(new vs(t, s));
            }, s.onupgradeneeded = t => {
                m("SimpleDb", 'Database "' + this.name + '" requires upgrade from version:', t.oldVersion);
                const e = t.target.result;
                this.ds.Ps(e, s.transaction, t.oldVersion, this.version).next((() => {
                    m("SimpleDb", "Database upgrade to version " + this.version + " complete");
                }));
            };
        }))), this.ys && (this.db.onversionchange = t => this.ys(t)), this.db;
    }
    gs(t) {
        this.ys = t, this.db && (this.db.onversionchange = e => t(e));
    }
    async runTransaction(t, e, n, s) {
        const i = "readonly" === e;
        let r = 0;
        for (;;) {
            ++r;
            try {
                this.db = await this.Rs(t);
                const e = Vs.open(this.db, t, i ? "readonly" : "readwrite", n), r = s(e).catch((t => (
                // Abort the transaction if there was an error.
                e.abort(t), gs.reject(t)))).cs();
                // As noted above, errors are propagated by aborting the transaction. So
                // we swallow any error here to avoid the browser logging it as unhandled.
                return r.catch((() => {})), 
                // Wait for the transaction to complete (i.e. IndexedDb's onsuccess event to
                // fire), but still return the original transactionFnResult back to the
                // caller.
                await e.fs, r;
            } catch (t) {
                // TODO(schmidt-sebastian): We could probably be smarter about this and
                // not retry exceptions that are likely unrecoverable (such as quota
                // exceeded errors).
                // Note: We cannot use an instanceof check for FirestoreException, since the
                // exception is wrapped in a generic error by our async/await handling.
                const e = "FirebaseError" !== t.name && r < 3;
                if (m("SimpleDb", "Transaction failed with error:", t.message, "Retrying:", e), 
                this.close(), !e) return Promise.reject(t);
            }
        }
    }
    close() {
        this.db && this.db.close(), this.db = void 0;
    }
}

/**
 * A controller for iterating over a key range or index. It allows an iterate
 * callback to delete the currently-referenced object, or jump to a new key
 * within the key range or index.
 */ class bs {
    constructor(t) {
        this.Vs = t, this.ps = !1, this.bs = null;
    }
    get ns() {
        return this.ps;
    }
    get vs() {
        return this.bs;
    }
    set cursor(t) {
        this.Vs = t;
    }
    /**
     * This function can be called to stop iteration at any point.
     */    done() {
        this.ps = !0;
    }
    /**
     * This function can be called to skip to that next key, which could be
     * an index or a primary key.
     */    Ss(t) {
        this.bs = t;
    }
    /**
     * Delete the current cursor value from the object store.
     *
     * NOTE: You CANNOT do this with a keysOnly query.
     */    delete() {
        return Cs(this.Vs.delete());
    }
}

/** An error that wraps exceptions that thrown during IndexedDB execution. */ class vs extends N {
    constructor(t, e) {
        super(x.UNAVAILABLE, `IndexedDB transaction '${t}' failed: ${e}`), this.name = "IndexedDbTransactionError";
    }
}

/** Verifies whether `e` is an IndexedDbTransactionError. */ function Ss(t) {
    // Use name equality, as instanceof checks on errors don't work with errors
    // that wrap other errors.
    return "IndexedDbTransactionError" === t.name;
}

/**
 * A wrapper around an IDBObjectStore providing an API that:
 *
 * 1) Has generic KeyType / ValueType parameters to provide strongly-typed
 * methods for acting against the object store.
 * 2) Deals with IndexedDB's onsuccess / onerror event callbacks, making every
 * method return a PersistencePromise instead.
 * 3) Provides a higher-level API to avoid needing to do excessive wrapping of
 * intermediate IndexedDB types (IDBCursorWithValue, etc.)
 */ class Ds {
    constructor(t) {
        this.store = t;
    }
    put(t, e) {
        let n;
        return void 0 !== e ? (m("SimpleDb", "PUT", this.store.name, t, e), n = this.store.put(e, t)) : (m("SimpleDb", "PUT", this.store.name, "<auto-key>", t), 
        n = this.store.put(t)), Cs(n);
    }
    /**
     * Adds a new value into an Object Store and returns the new key. Similar to
     * IndexedDb's `add()`, this method will fail on primary key collisions.
     *
     * @param value - The object to write.
     * @returns The key of the value to add.
     */    add(t) {
        m("SimpleDb", "ADD", this.store.name, t, t);
        return Cs(this.store.add(t));
    }
    /**
     * Gets the object with the specified key from the specified store, or null
     * if no object exists with the specified key.
     *
     * @key The key of the object to get.
     * @returns The object with the specified key or null if no object exists.
     */    get(t) {
        // We're doing an unsafe cast to ValueType.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return Cs(this.store.get(t)).next((e => (
        // Normalize nonexistence to null.
        void 0 === e && (e = null), m("SimpleDb", "GET", this.store.name, t, e), e)));
    }
    delete(t) {
        m("SimpleDb", "DELETE", this.store.name, t);
        return Cs(this.store.delete(t));
    }
    /**
     * If we ever need more of the count variants, we can add overloads. For now,
     * all we need is to count everything in a store.
     *
     * Returns the number of rows in the store.
     */    count() {
        m("SimpleDb", "COUNT", this.store.name);
        return Cs(this.store.count());
    }
    Ds(t, e) {
        const n = this.cursor(this.options(t, e)), s = [];
        return this.Cs(n, ((t, e) => {
            s.push(e);
        })).next((() => s));
    }
    xs(t, e) {
        m("SimpleDb", "DELETE ALL", this.store.name);
        const n = this.options(t, e);
        n.Ns = !1;
        const s = this.cursor(n);
        return this.Cs(s, ((t, e, n) => n.delete()));
    }
    Fs(t, e) {
        let n;
        e ? n = t : (n = {}, e = t);
        const s = this.cursor(n);
        return this.Cs(s, e);
    }
    /**
     * Iterates over a store, but waits for the given callback to complete for
     * each entry before iterating the next entry. This allows the callback to do
     * asynchronous work to determine if this iteration should continue.
     *
     * The provided callback should return `true` to continue iteration, and
     * `false` otherwise.
     */    Os(t) {
        const e = this.cursor({});
        return new gs(((n, s) => {
            e.onerror = t => {
                const e = Ns(t.target.error);
                s(e);
            }, e.onsuccess = e => {
                const s = e.target.result;
                s ? t(s.primaryKey, s.value).next((t => {
                    t ? s.continue() : n();
                })) : n();
            };
        }));
    }
    Cs(t, e) {
        const n = [];
        return new gs(((s, i) => {
            t.onerror = t => {
                i(t.target.error);
            }, t.onsuccess = t => {
                const i = t.target.result;
                if (!i) return void s();
                const r = new bs(i), o = e(i.primaryKey, i.value, r);
                if (o instanceof gs) {
                    const t = o.catch((t => (r.done(), gs.reject(t))));
                    n.push(t);
                }
                r.ns ? s() : null === r.vs ? i.continue() : i.continue(r.vs);
            };
        })).next((() => gs.hs(n)));
    }
    options(t, e) {
        let n = void 0;
        return void 0 !== t && ("string" == typeof t ? n = t : e = t), {
            index: n,
            range: e
        };
    }
    cursor(t) {
        let e = "next";
        if (t.reverse && (e = "prev"), t.index) {
            const n = this.store.index(t.index);
            return t.Ns ? n.openKeyCursor(t.range, e) : n.openCursor(t.range, e);
        }
        return this.store.openCursor(t.range, e);
    }
}

/**
 * Wraps an IDBRequest in a PersistencePromise, using the onsuccess / onerror
 * handlers to resolve / reject the PersistencePromise as appropriate.
 */ function Cs(t) {
    return new gs(((e, n) => {
        t.onsuccess = t => {
            const n = t.target.result;
            e(n);
        }, t.onerror = t => {
            const e = Ns(t.target.error);
            n(e);
        };
    }));
}

// Guard so we only report the error once.
let xs = !1;

function Ns(t) {
    const e = ps.ws(a());
    if (e >= 12.2 && e < 13) {
        const e = "An internal error was encountered in the Indexed Database server";
        if (t.message.indexOf(e) >= 0) {
            // Wrap error in a more descriptive one.
            const t = new N("internal", `IOS_INDEXEDDB_BUG1: IndexedDb has thrown '${e}'. This is likely due to an unavoidable bug in iOS. See https://stackoverflow.com/q/56496296/110915 for details and a potential workaround.`);
            return xs || (xs = !0, 
            // Throw a global exception outside of this promise chain, for the user to
            // potentially catch.
            setTimeout((() => {
                throw t;
            }), 0)), t;
        }
    }
    return t;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Represents an operation scheduled to be run in the future on an AsyncQueue.
 *
 * It is created via DelayedOperation.createAndSchedule().
 *
 * Supports cancellation (via cancel()) and early execution (via skipDelay()).
 *
 * Note: We implement `PromiseLike` instead of `Promise`, as the `Promise` type
 * in newer versions of TypeScript defines `finally`, which is not available in
 * IE.
 */
class Fs {
    constructor(t, e, n, s, i) {
        this.ks = t, this.Ms = e, this.Ls = n, this.op = s, this.$s = i, this.Bs = new it, 
        this.then = this.Bs.promise.then.bind(this.Bs.promise), 
        // It's normal for the deferred promise to be canceled (due to cancellation)
        // and so we attach a dummy catch callback to avoid
        // 'UnhandledPromiseRejectionWarning' log spam.
        this.Bs.promise.catch((t => {}));
    }
    /**
     * Creates and returns a DelayedOperation that has been scheduled to be
     * executed on the provided asyncQueue after the provided delayMs.
     *
     * @param asyncQueue - The queue to schedule the operation on.
     * @param id - A Timer ID identifying the type of operation this is.
     * @param delayMs - The delay (ms) before the operation should be scheduled.
     * @param op - The operation to run.
     * @param removalCallback - A callback to be called synchronously once the
     *   operation is executed or canceled, notifying the AsyncQueue to remove it
     *   from its delayedOperations list.
     *   PORTING NOTE: This exists to prevent making removeDelayedOperation() and
     *   the DelayedOperation class public.
     */    static qs(t, e, n, s, i) {
        const r = Date.now() + n, o = new Fs(t, e, r, s, i);
        return o.start(n), o;
    }
    /**
     * Starts the timer. This is called immediately after construction by
     * createAndSchedule().
     */    start(t) {
        this.Us = setTimeout((() => this.Qs()), t);
    }
    /**
     * Queues the operation to run immediately (if it hasn't already been run or
     * canceled).
     */    Ks() {
        return this.Qs();
    }
    /**
     * Cancels the operation if it hasn't already been executed or canceled. The
     * promise will be rejected.
     *
     * As long as the operation has not yet been run, calling cancel() provides a
     * guarantee that the operation will not be run.
     */    cancel(t) {
        null !== this.Us && (this.clearTimeout(), this.Bs.reject(new N(x.CANCELLED, "Operation cancelled" + (t ? ": " + t : ""))));
    }
    Qs() {
        this.ks.Ws((() => null !== this.Us ? (this.clearTimeout(), this.op().then((t => this.Bs.resolve(t)))) : Promise.resolve()));
    }
    clearTimeout() {
        null !== this.Us && (this.$s(this), clearTimeout(this.Us), this.Us = null);
    }
}

/**
 * Returns a FirestoreError that can be surfaced to the user if the provided
 * error is an IndexedDbTransactionError. Re-throws the error otherwise.
 */ function Os(t, e) {
    if (A("AsyncQueue", `${e}: ${t}`), Ss(t)) return new N(x.UNAVAILABLE, `${e}: ${t}`);
    throw t;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A helper for running delayed tasks following an exponential backoff curve
 * between attempts.
 *
 * Each delay is made up of a "base" delay which follows the exponential
 * backoff curve, and a +/- 50% "jitter" that is calculated and added to the
 * base delay. This prevents clients from accidentally synchronizing their
 * delays causing spikes of load to the backend.
 */
class ks {
    constructor(
    /**
     * The AsyncQueue to run backoff operations on.
     */
    t, 
    /**
     * The ID to use when scheduling backoff operations on the AsyncQueue.
     */
    e, 
    /**
     * The initial delay (used as the base delay on the first retry attempt).
     * Note that jitter will still be applied, so the actual delay could be as
     * little as 0.5*initialDelayMs.
     */
    n = 1e3
    /**
     * The multiplier to use to determine the extended base delay after each
     * attempt.
     */ , s = 1.5
    /**
     * The maximum base delay after which no further backoff is performed.
     * Note that jitter will still be applied, so the actual delay could be as
     * much as 1.5*maxDelayMs.
     */ , i = 6e4) {
        this.js = t, this.Ms = e, this.Gs = n, this.zs = s, this.Hs = i, this.Js = 0, this.Ys = null, 
        /** The last backoff attempt, as epoch milliseconds. */
        this.Xs = Date.now(), this.reset();
    }
    /**
     * Resets the backoff delay.
     *
     * The very next backoffAndWait() will have no delay. If it is called again
     * (i.e. due to an error), initialDelayMs (plus jitter) will be used, and
     * subsequent ones will increase according to the backoffFactor.
     */    reset() {
        this.Js = 0;
    }
    /**
     * Resets the backoff delay to the maximum delay (e.g. for use after a
     * RESOURCE_EXHAUSTED error).
     */    Zs() {
        this.Js = this.Hs;
    }
    /**
     * Returns a promise that resolves after currentDelayMs, and increases the
     * delay for any subsequent attempts. If there was a pending backoff operation
     * already, it will be canceled.
     */    ti(t) {
        // Cancel any pending backoff operation.
        this.cancel();
        // First schedule using the current base (which may be 0 and should be
        // honored as such).
        const e = Math.floor(this.Js + this.ei()), n = Math.max(0, Date.now() - this.Xs), s = Math.max(0, e - n);
        // Guard against lastAttemptTime being in the future due to a clock change.
                s > 0 && m("ExponentialBackoff", `Backing off for ${s} ms (base delay: ${this.Js} ms, delay with jitter: ${e} ms, last attempt: ${n} ms ago)`), 
        this.Ys = this.js.ni(this.Ms, s, (() => (this.Xs = Date.now(), t()))), 
        // Apply backoff factor to determine next delay and ensure it is within
        // bounds.
        this.Js *= this.zs, this.Js < this.Gs && (this.Js = this.Gs), this.Js > this.Hs && (this.Js = this.Hs);
    }
    si() {
        null !== this.Ys && (this.Ys.Ks(), this.Ys = null);
    }
    cancel() {
        null !== this.Ys && (this.Ys.cancel(), this.Ys = null);
    }
    /** Returns a random value in the range [-currentBaseMs/2, currentBaseMs/2] */    ei() {
        return (Math.random() - .5) * this.Js;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A PersistentStream is an abstract base class that represents a streaming RPC
 * to the Firestore backend. It's built on top of the connections own support
 * for streaming RPCs, and adds several critical features for our clients:
 *
 *   - Exponential backoff on failure
 *   - Authentication via CredentialsProvider
 *   - Dispatching all callbacks into the shared worker queue
 *   - Closing idle streams after 60 seconds of inactivity
 *
 * Subclasses of PersistentStream implement serialization of models to and
 * from the JSON representation of the protocol buffers for a specific
 * streaming RPC.
 *
 * ## Starting and Stopping
 *
 * Streaming RPCs are stateful and need to be start()ed before messages can
 * be sent and received. The PersistentStream will call the onOpen() function
 * of the listener once the stream is ready to accept requests.
 *
 * Should a start() fail, PersistentStream will call the registered onClose()
 * listener with a FirestoreError indicating what went wrong.
 *
 * A PersistentStream can be started and stopped repeatedly.
 *
 * Generic types:
 *  SendType: The type of the outgoing message of the underlying
 *    connection stream
 *  ReceiveType: The type of the incoming message of the underlying
 *    connection stream
 *  ListenerType: The type of the listener that will be used for callbacks
 */
class Ms {
    constructor(t, e, n, s, i, r) {
        this.js = t, this.ii = n, this.ri = s, this.oi = i, this.listener = r, this.state = 0 /* Initial */ , 
        /**
         * A close count that's incremented every time the stream is closed; used by
         * getCloseGuardedDispatcher() to invalidate callbacks that happen after
         * close.
         */
        this.ci = 0, this.ai = null, this.stream = null, this.ui = new ks(t, e);
    }
    /**
     * Returns true if start() has been called and no error has occurred. True
     * indicates the stream is open or in the process of opening (which
     * encompasses respecting backoff, getting auth tokens, and starting the
     * actual RPC). Use isOpen() to determine if the stream is open and ready for
     * outbound requests.
     */    hi() {
        return 1 /* Starting */ === this.state || 2 /* Open */ === this.state || 4 /* Backoff */ === this.state;
    }
    /**
     * Returns true if the underlying RPC is open (the onOpen() listener has been
     * called) and the stream is ready for outbound requests.
     */    li() {
        return 2 /* Open */ === this.state;
    }
    /**
     * Starts the RPC. Only allowed if isStarted() returns false. The stream is
     * not immediately ready for use: onOpen() will be invoked when the RPC is
     * ready for outbound requests, at which point isOpen() will return true.
     *
     * When start returns, isStarted() will return true.
     */    start() {
        3 /* Error */ !== this.state ? this.auth() : this._i();
    }
    /**
     * Stops the RPC. This call is idempotent and allowed regardless of the
     * current isStarted() state.
     *
     * When stop returns, isStarted() and isOpen() will both return false.
     */    async stop() {
        this.hi() && await this.close(0 /* Initial */);
    }
    /**
     * After an error the stream will usually back off on the next attempt to
     * start it. If the error warrants an immediate restart of the stream, the
     * sender can use this to indicate that the receiver should not back off.
     *
     * Each error will call the onClose() listener. That function can decide to
     * inhibit backoff if required.
     */    fi() {
        this.state = 0 /* Initial */ , this.ui.reset();
    }
    /**
     * Marks this stream as idle. If no further actions are performed on the
     * stream for one minute, the stream will automatically close itself and
     * notify the stream's onClose() handler with Status.OK. The stream will then
     * be in a !isStarted() state, requiring the caller to start the stream again
     * before further use.
     *
     * Only streams that are in state 'Open' can be marked idle, as all other
     * states imply pending network operations.
     */    di() {
        // Starts the idle time if we are in state 'Open' and are not yet already
        // running a timer (in which case the previous idle timeout still applies).
        this.li() && null === this.ai && (this.ai = this.js.ni(this.ii, 6e4, (() => this.wi())));
    }
    /** Sends a message to the underlying stream. */    Ei(t) {
        this.Ti(), this.stream.send(t);
    }
    /** Called by the idle timer when the stream should close due to inactivity. */    async wi() {
        if (this.li()) 
        // When timing out an idle stream there's no reason to force the stream into backoff when
        // it restarts so set the stream state to Initial instead of Error.
        return this.close(0 /* Initial */);
    }
    /** Marks the stream as active again. */    Ti() {
        this.ai && (this.ai.cancel(), this.ai = null);
    }
    /**
     * Closes the stream and cleans up as necessary:
     *
     * * closes the underlying GRPC stream;
     * * calls the onClose handler with the given 'error';
     * * sets internal stream state to 'finalState';
     * * adjusts the backoff timer based on the error
     *
     * A new stream can be opened by calling start().
     *
     * @param finalState - the intended state of the stream after closing.
     * @param error - the error the connection was closed with.
     */    async close(t, e) {
        // Cancel any outstanding timers (they're guaranteed not to execute).
        this.Ti(), this.ui.cancel(), 
        // Invalidates any stream-related callbacks (e.g. from auth or the
        // underlying stream), guaranteeing they won't execute.
        this.ci++, 3 /* Error */ !== t ? 
        // If this is an intentional close ensure we don't delay our next connection attempt.
        this.ui.reset() : e && e.code === x.RESOURCE_EXHAUSTED ? (
        // Log the error. (Probably either 'quota exceeded' or 'max queue length reached'.)
        A(e.toString()), A("Using maximum backoff delay to prevent overloading the backend."), 
        this.ui.Zs()) : e && e.code === x.UNAUTHENTICATED && 
        // "unauthenticated" error means the token was rejected. Try force refreshing it in case it
        // just expired.
        this.oi.g(), 
        // Clean up the underlying stream because we are no longer interested in events.
        null !== this.stream && (this.Ii(), this.stream.close(), this.stream = null), 
        // This state must be assigned before calling onClose() to allow the callback to
        // inhibit backoff or otherwise manipulate the state in its non-started state.
        this.state = t, 
        // Notify the listener that the stream closed.
        await this.listener.Z(e);
    }
    /**
     * Can be overridden to perform additional cleanup before the stream is closed.
     * Calling super.tearDown() is not required.
     */    Ii() {}
    auth() {
        this.state = 1 /* Starting */;
        const t = this.mi(this.ci), e = this.ci;
        // TODO(mikelehen): Just use dispatchIfNotClosed, but see TODO below.
                this.oi.getToken().then((t => {
            // Stream can be stopped while waiting for authentication.
            // TODO(mikelehen): We really should just use dispatchIfNotClosed
            // and let this dispatch onto the queue, but that opened a spec test can
            // of worms that I don't want to deal with in this PR.
            this.ci === e && 
            // Normally we'd have to schedule the callback on the AsyncQueue.
            // However, the following calls are safe to be called outside the
            // AsyncQueue since they don't chain asynchronous calls
            this.Ai(t);
        }), (e => {
            t((() => {
                const t = new N(x.UNKNOWN, "Fetching auth token failed: " + e.message);
                return this.Ri(t);
            }));
        }));
    }
    Ai(t) {
        const e = this.mi(this.ci);
        this.stream = this.Pi(t), this.stream.Y((() => {
            e((() => (this.state = 2 /* Open */ , this.listener.Y())));
        })), this.stream.Z((t => {
            e((() => this.Ri(t)));
        })), this.stream.onMessage((t => {
            e((() => this.onMessage(t)));
        }));
    }
    _i() {
        this.state = 4 /* Backoff */ , this.ui.ti((async () => {
            this.state = 0 /* Initial */ , this.start();
        }));
    }
    // Visible for tests
    Ri(t) {
        // In theory the stream could close cleanly, however, in our current model
        // we never expect this to happen because if we stop a stream ourselves,
        // this callback will never be called. To prevent cases where we retry
        // without a backoff accidentally, we set the stream to error in all cases.
        return m("PersistentStream", "close with error: " + t), this.stream = null, this.close(3 /* Error */ , t);
    }
    /**
     * Returns a "dispatcher" function that dispatches operations onto the
     * AsyncQueue but only runs them if closeCount remains unchanged. This allows
     * us to turn auth / stream callbacks into no-ops if the stream is closed /
     * re-opened, etc.
     */    mi(t) {
        return e => {
            this.js.Ws((() => this.ci === t ? e() : (m("PersistentStream", "stream callback skipped by getCloseGuardedDispatcher."), 
            Promise.resolve())));
        };
    }
}

/**
 * A PersistentStream that implements the Listen RPC.
 *
 * Once the Listen stream has called the onOpen() listener, any number of
 * listen() and unlisten() calls can be made to control what changes will be
 * sent from the server for ListenResponses.
 */ class Ls extends Ms {
    constructor(t, e, n, s, i) {
        super(t, "listen_stream_connection_backoff" /* ListenStreamConnectionBackoff */ , "listen_stream_idle" /* ListenStreamIdle */ , e, n, i), 
        this.fe = s;
    }
    Pi(t) {
        return this.ri.ft("Listen", t);
    }
    onMessage(t) {
        // A successful response means the stream is healthy
        this.ui.reset();
        const e = is(this.fe, t), n = function(t) {
            // We have only reached a consistent snapshot for the entire stream if there
            // is a read_time set and it applies to all targets (i.e. the list of
            // targets is empty). The backend is guaranteed to send such responses.
            if (!("targetChange" in t)) return ct.min();
            const e = t.targetChange;
            return e.targetIds && e.targetIds.length ? ct.min() : e.readTime ? jn(e.readTime) : ct.min();
        }(t);
        return this.listener.yi(e, n);
    }
    /**
     * Registers interest in the results of the given target. If the target
     * includes a resumeToken it will be included in the request. Results that
     * affect the target will be streamed back as WatchChange messages that
     * reference the targetId.
     */    gi(t) {
        const e = {};
        e.database = Zn(this.fe), e.addTarget = function(t, e) {
            let n;
            const s = e.target;
            return n = Ht(s) ? {
                documents: as(t, s)
            } : {
                query: us(t, s)
            }, n.targetId = e.targetId, e.resumeToken.o() > 0 ? n.resumeToken = Kn(t, e.resumeToken) : e.oe.u(ct.min()) > 0 && (
            // TODO(wuandy): Consider removing above check because it is most likely true.
            // Right now, many tests depend on this behaviour though (leaving min() out
            // of serialization).
            n.readTime = Qn(t, e.oe.Tt())), n;
        }(this.fe, t);
        const n = ls(this.fe, t);
        n && (e.labels = n), this.Ei(e);
    }
    /**
     * Unregisters interest in the results of the target associated with the
     * given targetId.
     */    Vi(t) {
        const e = {};
        e.database = Zn(this.fe), e.removeTarget = t, this.Ei(e);
    }
}

/**
 * A Stream that implements the Write RPC.
 *
 * The Write RPC requires the caller to maintain special streamToken
 * state in between calls, to help the server understand which responses the
 * client has processed by the time the next request is made. Every response
 * will contain a streamToken; this value must be passed to the next
 * request.
 *
 * After calling start() on this stream, the next request must be a handshake,
 * containing whatever streamToken is on hand. Once a response to this
 * request is received, all pending mutations may be submitted. When
 * submitting multiple batches of mutations at the same time, it's
 * okay to use the same streamToken for the calls to writeMutations.
 *
 * TODO(b/33271235): Use proto types
 */ class $s extends Ms {
    constructor(t, e, n, s, i) {
        super(t, "write_stream_connection_backoff" /* WriteStreamConnectionBackoff */ , "write_stream_idle" /* WriteStreamIdle */ , e, n, i), 
        this.fe = s, this.pi = !1;
    }
    /**
     * Tracks whether or not a handshake has been successfully exchanged and
     * the stream is ready to accept mutations.
     */    get bi() {
        return this.pi;
    }
    // Override of PersistentStream.start
    start() {
        this.pi = !1, this.lastStreamToken = void 0, super.start();
    }
    Ii() {
        this.pi && this.vi([]);
    }
    Pi(t) {
        return this.ri.ft("Write", t);
    }
    onMessage(t) {
        if (
        // Always capture the last stream token.
        g(!!t.streamToken), this.lastStreamToken = t.streamToken, this.pi) {
            // A successful first write response means the stream is healthy,
            // Note, that we could consider a successful handshake healthy, however,
            // the write itself might be causing an error we want to back off from.
            this.ui.reset();
            const e = cs(t.writeResults, t.commitTime), n = jn(t.commitTime);
            return this.listener.Si(n, e);
        }
        // The first response is always the handshake response
        return g(!t.writeResults || 0 === t.writeResults.length), this.pi = !0, this.listener.Di();
    }
    /**
     * Sends an initial streamToken to the server, performing the handshake
     * required to make the StreamingWrite RPC work. Subsequent
     * calls should wait until onHandshakeComplete was called.
     */    Ci() {
        // TODO(dimond): Support stream resumption. We intentionally do not set the
        // stream token on the handshake, ignoring any stream token we might have.
        const t = {};
        t.database = Zn(this.fe), this.Ei(t);
    }
    /** Sends a group of mutations to the Firestore backend to apply. */    vi(t) {
        const e = {
            streamToken: this.lastStreamToken,
            writes: t.map((t => rs(this.fe, t)))
        };
        this.Ei(e);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Datastore and its related methods are a wrapper around the external Google
 * Cloud Datastore grpc API, which provides an interface that is more convenient
 * for the rest of the client SDK architecture to consume.
 */
/**
 * An implementation of Datastore that exposes additional state for internal
 * consumption.
 */
class Bs extends class {} {
    constructor(t, e, n) {
        super(), this.credentials = t, this.ri = e, this.fe = n, this.xi = !1;
    }
    Ni() {
        if (this.xi) throw new N(x.FAILED_PRECONDITION, "The client has already been terminated.");
    }
    /** Gets an auth token and invokes the provided RPC. */    at(t, e, n) {
        return this.Ni(), this.credentials.getToken().then((s => this.ri.at(t, e, n, s))).catch((t => {
            throw t.code === x.UNAUTHENTICATED && this.credentials.g(), t;
        }));
    }
    /** Gets an auth token and invokes the provided RPC with streamed results. */    _t(t, e, n) {
        return this.Ni(), this.credentials.getToken().then((s => this.ri._t(t, e, n, s))).catch((t => {
            throw t.code === x.UNAUTHENTICATED && this.credentials.g(), t;
        }));
    }
    terminate() {
        this.xi = !1;
    }
}

// TODO(firestorexp): Make sure there is only one Datastore instance per
// firestore-exp client.
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const qs = "The current tab is not in the required state to perform this operation. It might be necessary to refresh the browser tab.";

/**
 * A base class representing a persistence transaction, encapsulating both the
 * transaction's sequence numbers as well as a list of onCommitted listeners.
 *
 * When you call Persistence.runTransaction(), it will create a transaction and
 * pass it to your callback. You then pass it to any method that operates
 * on persistence.
 */ class Us {
    constructor() {
        this.Fi = [];
    }
    Oi(t) {
        this.Fi.push(t);
    }
    ki() {
        this.Fi.forEach((t => t()));
    }
}

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const Qs = {
    Mi: !1,
    Li: 0,
    $i: 0,
    Bi: 0
};

class Ks {
    constructor(
    // When we attempt to collect, we will only do so if the cache size is greater than this
    // threshold. Passing `COLLECTION_DISABLED` here will cause collection to always be skipped.
    t, 
    // The percentage of sequence numbers that we will attempt to collect
    e, 
    // A cap on the total number of sequence numbers that will be collected. This prevents
    // us from collecting a huge number of sequence numbers if the cache has grown very large.
    n) {
        this.qi = t, this.Ui = e, this.Qi = n;
    }
    static Ki(t) {
        return new Ks(t, Ks.Wi, Ks.ji);
    }
}

Ks.Wi = 10, Ks.ji = 1e3, Ks.Gi = new Ks(41943040, Ks.Wi, Ks.ji), Ks.zi = new Ks(-1, 0, 0);

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * `ListenSequence` is a monotonic sequence. It is initialized with a minimum value to
 * exceed. All subsequent calls to next will return increasing values. If provided with a
 * `SequenceNumberSyncer`, it will additionally bump its next value when told of a new value, as
 * well as write out sequence numbers that it produces via `next()`.
 */
class Ws {
    constructor(t, e) {
        this.previousValue = t, e && (e.Hi = t => this.Ji(t), this.Yi = t => e.Xi(t));
    }
    Ji(t) {
        return this.previousValue = Math.max(t, this.previousValue), this.previousValue;
    }
    next() {
        const t = ++this.previousValue;
        return this.Yi && this.Yi(t), t;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Verifies the error thrown by a LocalStore operation. If a LocalStore
 * operation fails because the primary lease has been taken by another client,
 * we ignore the error (the persistence layer will immediately call
 * `applyPrimaryLease` to propagate the primary state change). All other errors
 * are re-thrown.
 *
 * @param err - An error returned by a LocalStore operation.
 * @returns A Promise that resolves after we recovered, or the original error.
 */
async function js(t) {
    if (t.code !== x.FAILED_PRECONDITION || t.message !== qs) throw t;
    m("LocalStore", "Unexpectedly lost primary lease");
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ Ws.Zi = -1;

class Gs {
    constructor(t, e) {
        this.progress = t, this.tr = e;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A batch of mutations that will be sent as one unit to the backend.
 */ class zs {
    /**
     * @param batchId - The unique ID of this mutation batch.
     * @param localWriteTime - The original write time of this mutation.
     * @param baseMutations - Mutations that are used to populate the base
     * values when this mutation is applied locally. This can be used to locally
     * overwrite values that are persisted in the remote document cache. Base
     * mutations are never sent to the backend.
     * @param mutations - The user-provided mutations in this mutation batch.
     * User-provided mutations are applied both locally and remotely on the
     * backend.
     */
    constructor(t, e, n, s) {
        this.batchId = t, this.er = e, this.baseMutations = n, this.mutations = s;
    }
    /**
     * Applies all the mutations in this MutationBatch to the specified document
     * to create a new remote document
     *
     * @param docKey - The key of the document to apply mutations to.
     * @param maybeDoc - The document to apply mutations to.
     * @param batchResult - The result of applying the MutationBatch to the
     * backend.
     */    nr(t, e, n) {
        const s = n.sr;
        for (let n = 0; n < this.mutations.length; n++) {
            const i = this.mutations[n];
            if (i.key.isEqual(t)) {
                e = Ye(i, e, s[n]);
            }
        }
        return e;
    }
    /**
     * Computes the local view of a document given all the mutations in this
     * batch.
     *
     * @param docKey - The key of the document to apply mutations to.
     * @param maybeDoc - The document to apply mutations to.
     */    ir(t, e) {
        // First, apply the base state. This allows us to apply non-idempotent
        // transform against a consistent set of values.
        for (const n of this.baseMutations) n.key.isEqual(t) && (e = Xe(n, e, e, this.er));
        const n = e;
        // Second, apply all user-provided mutations.
                for (const s of this.mutations) s.key.isEqual(t) && (e = Xe(s, e, n, this.er));
        return e;
    }
    /**
     * Computes the local view for all provided documents given the mutations in
     * this batch.
     */    rr(t) {
        // TODO(mrschmidt): This implementation is O(n^2). If we apply the mutations
        // directly (as done in `applyToLocalView()`), we can reduce the complexity
        // to O(n).
        let e = t;
        return this.mutations.forEach((n => {
            const s = this.ir(n.key, t.get(n.key));
            s && (e = e.Ie(n.key, s));
        })), e;
    }
    keys() {
        return this.mutations.reduce(((t, e) => t.add(e.key)), Vn());
    }
    isEqual(t) {
        return this.batchId === t.batchId && S(this.mutations, t.mutations, ((t, e) => tn(t, e))) && S(this.baseMutations, t.baseMutations, ((t, e) => tn(t, e)));
    }
}

/** The result of applying a mutation batch to the backend. */ class Hs {
    constructor(t, e, n, 
    /**
     * A pre-computed mapping from each mutated document to the resulting
     * version.
     */
    s) {
        this.batch = t, this.cr = e, this.sr = n, this.ar = s;
    }
    /**
     * Creates a new MutationBatchResult for the given batch and results. There
     * must be one result for each mutation in the batch. This static factory
     * caches a document=&gt;version mapping (docVersions).
     */    static from(t, e, n) {
        g(t.mutations.length === n.length);
        let s = yn();
        const i = t.mutations;
        for (let t = 0; t < i.length; t++) s = s.Ie(i[t].key, n[t].version);
        return new Hs(t, e, n, s);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A map implementation that uses objects as keys. Objects must have an
 * associated equals function and must be immutable. Entries in the map are
 * stored together with the key being produced from the mapKeyFn. This map
 * automatically handles collisions of keys.
 */ class Js {
    constructor(t, e) {
        this.ur = t, this.hr = e, 
        /**
         * The inner map for a key/value pair. Due to the possibility of collisions we
         * keep a list of entries that we do a linear search through to find an actual
         * match. Note that collisions should be rare, so we still expect near
         * constant time lookups in practice.
         */
        this.lr = {};
    }
    /** Get a value for this key, or undefined if it does not exist. */    get(t) {
        const e = this.ur(t), n = this.lr[e];
        if (void 0 !== n) for (const [e, s] of n) if (this.hr(e, t)) return s;
    }
    has(t) {
        return void 0 !== this.get(t);
    }
    /** Put this key and value in the map. */    set(t, e) {
        const n = this.ur(t), s = this.lr[n];
        if (void 0 !== s) {
            for (let n = 0; n < s.length; n++) if (this.hr(s[n][0], t)) return void (s[n] = [ t, e ]);
            s.push([ t, e ]);
        } else this.lr[n] = [ [ t, e ] ];
    }
    /**
     * Remove this key from the map. Returns a boolean if anything was deleted.
     */    delete(t) {
        const e = this.ur(t), n = this.lr[e];
        if (void 0 === n) return !1;
        for (let s = 0; s < n.length; s++) if (this.hr(n[s][0], t)) return 1 === n.length ? delete this.lr[e] : n.splice(s, 1), 
        !0;
        return !1;
    }
    forEach(t) {
        dt(this.lr, ((e, n) => {
            for (const [e, s] of n) t(e, s);
        }));
    }
    bt() {
        return wt(this.lr);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An in-memory buffer of entries to be written to a RemoteDocumentCache.
 * It can be used to batch up a set of changes to be written to the cache, but
 * additionally supports reading entries back with the `getEntry()` method,
 * falling back to the underlying RemoteDocumentCache if no entry is
 * buffered.
 *
 * Entries added to the cache *must* be read first. This is to facilitate
 * calculating the size delta of the pending changes.
 *
 * PORTING NOTE: This class was implemented then removed from other platforms.
 * If byte-counting ends up being needed on the other platforms, consider
 * porting this class as part of that implementation work.
 */ class Ys {
    constructor() {
        // A mapping of document key to the new cache entry that should be written (or null if any
        // existing cache entry should be removed).
        this._r = new Js((t => t.toString()), ((t, e) => t.isEqual(e))), this.dr = !1;
    }
    wr(t) {
        const e = this._r.get(t);
        return e ? e.readTime : ct.min();
    }
    /**
     * Buffers a `RemoteDocumentCache.addEntry()` call.
     *
     * You can only modify documents that have already been retrieved via
     * `getEntry()/getEntries()` (enforced via IndexedDbs `apply()`).
     */    Er(t, e) {
        this.Tr(), this._r.set(t.key, {
            Ir: t,
            readTime: e
        });
    }
    /**
     * Buffers a `RemoteDocumentCache.removeEntry()` call.
     *
     * You can only remove documents that have already been retrieved via
     * `getEntry()/getEntries()` (enforced via IndexedDbs `apply()`).
     */    mr(t, e = null) {
        this.Tr(), this._r.set(t, {
            Ir: null,
            readTime: e
        });
    }
    /**
     * Looks up an entry in the cache. The buffered changes will first be checked,
     * and if no buffered change applies, this will forward to
     * `RemoteDocumentCache.getEntry()`.
     *
     * @param transaction - The transaction in which to perform any persistence
     *     operations.
     * @param documentKey - The key of the entry to look up.
     * @returns The cached Document or NoDocument entry, or null if we have
     *     nothing cached.
     */    Ar(t, e) {
        this.Tr();
        const n = this._r.get(e);
        return void 0 !== n ? gs.resolve(n.Ir) : this.Rr(t, e);
    }
    /**
     * Looks up several entries in the cache, forwarding to
     * `RemoteDocumentCache.getEntry()`.
     *
     * @param transaction - The transaction in which to perform any persistence
     *     operations.
     * @param documentKeys - The keys of the entries to look up.
     * @returns A map of cached `Document`s or `NoDocument`s, indexed by key. If
     *     an entry cannot be found, the corresponding key will be mapped to a
     *     null value.
     */    getEntries(t, e) {
        return this.Pr(t, e);
    }
    /**
     * Applies buffered changes to the underlying RemoteDocumentCache, using
     * the provided transaction.
     */    apply(t) {
        return this.Tr(), this.dr = !0, this.yr(t);
    }
    /** Helper to assert this.changes is not null  */    Tr() {}
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function Xs([t, e], [n, s]) {
    const i = v(t, n);
    return 0 === i ? v(e, s) : i;
}

/**
 * Used to calculate the nth sequence number. Keeps a rolling buffer of the
 * lowest n values passed to `addElement`, and finally reports the largest of
 * them in `maxValue`.
 */ class Zs {
    constructor(t) {
        this.gr = t, this.buffer = new wn(Xs), this.Vr = 0;
    }
    pr() {
        return ++this.Vr;
    }
    br(t) {
        const e = [ t, this.pr() ];
        if (this.buffer.size < this.gr) this.buffer = this.buffer.add(e); else {
            const t = this.buffer.last();
            Xs(e, t) < 0 && (this.buffer = this.buffer.delete(t).add(e));
        }
    }
    get maxValue() {
        // Guaranteed to be non-empty. If we decide we are not collecting any
        // sequence numbers, nthSequenceNumber below short-circuits. If we have
        // decided that we are collecting n sequence numbers, it's because n is some
        // percentage of the existing sequence numbers. That means we should never
        // be in a situation where we are collecting sequence numbers but don't
        // actually have any.
        return this.buffer.last()[0];
    }
}

/**
 * This class is responsible for the scheduling of LRU garbage collection. It handles checking
 * whether or not GC is enabled, as well as which delay to use before the next run.
 */ class ti {
    constructor(t, e) {
        this.vr = t, this.ks = e, this.Sr = !1, this.Dr = null;
    }
    start(t) {
        -1 !== this.vr.params.qi && this.Cr(t);
    }
    stop() {
        this.Dr && (this.Dr.cancel(), this.Dr = null);
    }
    get Nr() {
        return null !== this.Dr;
    }
    Cr(t) {
        const e = this.Sr ? 3e5 : 6e4;
        m("LruGarbageCollector", `Garbage collection scheduled in ${e}ms`), this.Dr = this.ks.ni("lru_garbage_collection" /* LruGarbageCollection */ , e, (async () => {
            this.Dr = null, this.Sr = !0;
            try {
                await t.Fr(this.vr);
            } catch (t) {
                Ss(t) ? m("LruGarbageCollector", "Ignoring IndexedDB error during garbage collection: ", t) : await js(t);
            }
            await this.Cr(t);
        }));
    }
}

/** Implements the steps for LRU garbage collection. */ class ei {
    constructor(t, e) {
        this.Or = t, this.params = e;
    }
    kr(t, e) {
        return this.Or.Mr(t).next((t => Math.floor(e / 100 * t)));
    }
    Lr(t, e) {
        if (0 === e) return gs.resolve(Ws.Zi);
        const n = new Zs(e);
        return this.Or.Un(t, (t => n.br(t.sequenceNumber))).next((() => this.Or.$r(t, (t => n.br(t))))).next((() => n.maxValue));
    }
    Br(t, e, n) {
        return this.Or.Br(t, e, n);
    }
    qr(t, e) {
        return this.Or.qr(t, e);
    }
    Ur(t, e) {
        return -1 === this.params.qi ? (m("LruGarbageCollector", "Garbage collection skipped; disabled"), 
        gs.resolve(Qs)) : this.Qr(t).next((n => n < this.params.qi ? (m("LruGarbageCollector", `Garbage collection skipped; Cache size ${n} is lower than threshold ` + this.params.qi), 
        Qs) : this.Kr(t, e)));
    }
    Qr(t) {
        return this.Or.Qr(t);
    }
    Kr(t, n) {
        let s, i, r, o, c, a, u;
        const h = Date.now();
        return this.kr(t, this.params.Ui).next((e => (
        // Cap at the configured max
        e > this.params.Qi ? (m("LruGarbageCollector", `Capping sequence numbers to collect down to the maximum of ${this.params.Qi} from ` + e), 
        i = this.params.Qi) : i = e, o = Date.now(), this.Lr(t, i)))).next((e => (s = e, 
        c = Date.now(), this.Br(t, s, n)))).next((e => (r = e, a = Date.now(), this.qr(t, s)))).next((t => {
            if (u = Date.now(), I() <= e.DEBUG) {
                m("LruGarbageCollector", `LRU Garbage Collection\n\tCounted targets in ${o - h}ms\n\tDetermined least recently used ${i} in ` + (c - o) + "ms\n" + `\tRemoved ${r} targets in ` + (a - c) + "ms\n" + `\tRemoved ${t} documents in ` + (u - a) + "ms\n" + `Total Duration: ${u - h}ms`);
            }
            return gs.resolve({
                Mi: !0,
                Li: i,
                $i: r,
                Bi: t
            });
        }));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function ni(t, e, n) {
    if (!n) throw new N(x.INVALID_ARGUMENT, `Function ${t}() cannot be called with an empty ${e}.`);
}

function si(t, e) {
    if (void 0 === e) return {
        merge: !1
    };
    if (void 0 !== e.mergeFields && void 0 !== e.merge) throw new N(x.INVALID_ARGUMENT, `Invalid options passed to function ${t}(): You cannot specify both "merge" and "mergeFields".`);
    return e;
}

/**
 * Validates that two boolean options are not set at the same time.
 */ function ii(t, e, n, s) {
    if (!0 === e && !0 === s) throw new N(x.INVALID_ARGUMENT, `${t} and ${n} cannot be used together.`);
}

/**
 * Validates that `path` refers to a document (indicated by the fact it contains
 * an even numbers of segments).
 */ function ri(t) {
    if (!_t.qt(t)) throw new N(x.INVALID_ARGUMENT, `Invalid document reference. Document references must have an even number of segments, but ${t} has ${t.length}.`);
}

/**
 * Validates that `path` refers to a collection (indicated by the fact it
 * contains an odd numbers of segments).
 */ function oi(t) {
    if (_t.qt(t)) throw new N(x.INVALID_ARGUMENT, `Invalid collection reference. Collection references must have an odd number of segments, but ${t} has ${t.length}.`);
}

/**
 * Returns true if it's a non-null object without a custom prototype
 * (i.e. excludes Array, Date, etc.).
 */
/** Returns a string describing the type / value of the provided input. */
function ci(t) {
    if (void 0 === t) return "undefined";
    if (null === t) return "null";
    if ("string" == typeof t) return t.length > 20 && (t = t.substring(0, 20) + "..."), 
    JSON.stringify(t);
    if ("number" == typeof t || "boolean" == typeof t) return "" + t;
    if ("object" == typeof t) {
        if (t instanceof Array) return "an array";
        {
            const e = 
            /** Hacky method to try to get the constructor name for an object. */
            function(t) {
                if (t.constructor) {
                    const e = /function\s+([^\s(]+)\s*\(/.exec(t.constructor.toString());
                    if (e && e.length > 1) return e[1];
                }
                return null;
            }
            /**
 * Casts `obj` to `T`, optionally unwrapping Compat types to expose the
 * underlying instance. Throws if  `obj` is not an instance of `T`.
 *
 * This cast is used in the Lite and Full SDK to verify instance types for
 * arguments passed to the public API.
 */ (t);
            return e ? `a custom ${e} object` : "an object";
        }
    }
    return "function" == typeof t ? "a function" : y();
}

function ai(t, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
e) {
    if ("_delegate" in t && (
    // Unwrap Compat types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    t = t._), !(t instanceof e)) {
        if (e.name === t.constructor.name) throw new N(x.INVALID_ARGUMENT, "Type does not match the expected instance. Did you pass a reference from a different Firestore SDK?");
        {
            const n = ci(t);
            throw new N(x.INVALID_ARGUMENT, `Expected type '${e.name}', but it was: ${n}`);
        }
    }
    return t;
}

function ui(t, e) {
    if (e <= 0) throw new N(x.INVALID_ARGUMENT, `Function ${t}() requires a positive number, but it was: ${e}.`);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// settings() defaults:
/**
 * A concrete type describing all the values that can be applied via a
 * user-supplied firestore.Settings object. This is a separate type so that
 * defaults can be supplied and the value can be checked for equality.
 */
class hi {
    constructor(t) {
        var e;
        if (void 0 === t.host) {
            if (void 0 !== t.ssl) throw new N(x.INVALID_ARGUMENT, "Can't provide ssl option if host option is not set");
            this.host = "firestore.googleapis.com", this.ssl = true;
        } else this.host = t.host, this.ssl = null === (e = t.ssl) || void 0 === e || e;
        if (this.credentials = t.credentials, this.ignoreUndefinedProperties = !!t.ignoreUndefinedProperties, 
        void 0 === t.cacheSizeBytes) this.cacheSizeBytes = 41943040; else {
            if (-1 !== t.cacheSizeBytes && t.cacheSizeBytes < 1048576) throw new N(x.INVALID_ARGUMENT, "cacheSizeBytes must be at least 1048576");
            this.cacheSizeBytes = t.cacheSizeBytes;
        }
        this.experimentalForceLongPolling = !!t.experimentalForceLongPolling, this.experimentalAutoDetectLongPolling = !!t.experimentalAutoDetectLongPolling, 
        ii("experimentalForceLongPolling", t.experimentalForceLongPolling, "experimentalAutoDetectLongPolling", t.experimentalAutoDetectLongPolling);
    }
    isEqual(t) {
        return this.host === t.host && this.ssl === t.ssl && this.credentials === t.credentials && this.cacheSizeBytes === t.cacheSizeBytes && this.experimentalForceLongPolling === t.experimentalForceLongPolling && this.experimentalAutoDetectLongPolling === t.experimentalAutoDetectLongPolling && this.ignoreUndefinedProperties === t.ignoreUndefinedProperties;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const li = new Map;

/**
 * An instance map that ensures only one Datastore exists per Firestore
 * instance.
 */
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * The Cloud Firestore service interface.
 *
 * Do not call this constructor directly. Instead, use {@link getFirestore}.
 */
class _i {
    /** @hideconstructor */
    constructor(t, e) {
        this.Wr = "(lite)", this.jr = new hi({}), this.Gr = !1, t instanceof j ? (this.zr = t, 
        this.Hr = new q) : (this.Jr = t, this.zr = function(t) {
            if (!Object.prototype.hasOwnProperty.apply(t.options, [ "projectId" ])) throw new N(x.INVALID_ARGUMENT, '"projectId" not provided in firebase.initializeApp.');
            return new j(t.options.projectId);
        }
        /**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (t), this.Hr = new U(e));
    }
    /**
     * The {@link FirebaseApp} associated with this `Firestore` service
     * instance.
     */    get app() {
        if (!this.Jr) throw new N(x.FAILED_PRECONDITION, "Firestore was not initialized using the Firebase SDK. 'app' is not available");
        return this.Jr;
    }
    get Yr() {
        return this.Gr;
    }
    get Xr() {
        return void 0 !== this.Zr;
    }
    eo(t) {
        if (this.Gr) throw new N(x.FAILED_PRECONDITION, "Firestore has already been started and its settings can no longer be changed. You can only modify settings before calling any other methods on a Firestore object.");
        this.jr = new hi(t), void 0 !== t.credentials && (this.Hr = function(t) {
            if (!t) return new q;
            switch (t.type) {
              case "gapi":
                const e = t.client;
                // Make sure this really is a Gapi client.
                                return g(!("object" != typeof e || null === e || !e.auth || !e.auth.getAuthHeaderValueForFirstParty)), 
                new K(e, t.sessionIndex || "0");

              case "provider":
                return t.client;

              default:
                throw new N(x.INVALID_ARGUMENT, "makeCredentialsProvider failed due to invalid credential type");
            }
        }(t.credentials));
    }
    no() {
        return this.jr;
    }
    so() {
        return this.Gr = !0, this.jr;
    }
    _delete() {
        return this.Zr || (this.Zr = this.io()), this.Zr;
    }
    /**
     * Terminates all components used by this client. Subclasses can override
     * this method to clean up their own dependencies, but must also call this
     * method.
     *
     * Only ever called once.
     */    io() {
        /**
 * Removes all components associated with the provided instance. Must be called
 * when the `Firestore` instance is terminated.
 */
        return function(t) {
            const e = li.get(t);
            e && (m("ComponentProvider", "Removing Datastore"), li.delete(t), e.terminate());
        }(this), Promise.resolve();
    }
}

/**
 * Encodes a resource path into a IndexedDb-compatible string form.
 */
function fi(t) {
    let e = "";
    for (let n = 0; n < t.length; n++) e.length > 0 && (e = wi(e)), e = di(t.get(n), e);
    return wi(e);
}

/** Encodes a single segment of a resource path into the given result */ function di(t, e) {
    let n = e;
    const s = t.length;
    for (let e = 0; e < s; e++) {
        const s = t.charAt(e);
        switch (s) {
          case "\0":
            n += "";
            break;

          case "":
            n += "";
            break;

          default:
            n += s;
        }
    }
    return n;
}

/** Encodes a path separator into the given result */ function wi(t) {
    return t + "";
}

/**
 * Decodes the given IndexedDb-compatible string form of a resource path into
 * a ResourcePath instance. Note that this method is not suitable for use with
 * decoding resource names from the server; those are One Platform format
 * strings.
 */ function Ei(t) {
    // Event the empty path must encode as a path of at least length 2. A path
    // with exactly 2 must be the empty path.
    const e = t.length;
    if (g(e >= 2), 2 === e) return g("" === t.charAt(0) && "" === t.charAt(1)), ut.Nt();
    // Escape characters cannot exist past the second-to-last position in the
    // source value.
        const n = e - 2, s = [];
    let i = "";
    for (let r = 0; r < e; ) {
        // The last two characters of a valid encoded path must be a separator, so
        // there must be an end to this segment.
        const e = t.indexOf("", r);
        (e < 0 || e > n) && y();
        switch (t.charAt(e + 1)) {
          case "":
            const n = t.substring(r, e);
            let o;
            0 === i.length ? 
            // Avoid copying for the common case of a segment that excludes \0
            // and \001
            o = n : (i += n, o = i, i = ""), s.push(o);
            break;

          case "":
            i += t.substring(r, e), i += "\0";
            break;

          case "":
            // The escape character can be used in the output to encode itself.
            i += t.substring(r, e + 1);
            break;

          default:
            y();
        }
        r = e + 2;
    }
    return new ut(s);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Schema Version for the Web client:
 * 1.  Initial version including Mutation Queue, Query Cache, and Remote
 *     Document Cache
 * 2.  Used to ensure a targetGlobal object exists and add targetCount to it. No
 *     longer required because migration 3 unconditionally clears it.
 * 3.  Dropped and re-created Query Cache to deal with cache corruption related
 *     to limbo resolution. Addresses
 *     https://github.com/firebase/firebase-ios-sdk/issues/1548
 * 4.  Multi-Tab Support.
 * 5.  Removal of held write acks.
 * 6.  Create document global for tracking document cache size.
 * 7.  Ensure every cached document has a sentinel row with a sequence number.
 * 8.  Add collection-parent index for Collection Group queries.
 * 9.  Change RemoteDocumentChanges store to be keyed by readTime rather than
 *     an auto-incrementing ID. This is required for Index-Free queries.
 * 10. Rewrite the canonical IDs to the explicit Protobuf-based format.
 * 11. Add bundles and named_queries for bundle support.
 */
/**
 * Wrapper class to store timestamps (seconds and nanos) in IndexedDb objects.
 */
class Ti {
    constructor(t, e) {
        this.seconds = t, this.nanoseconds = e;
    }
}

/**
 * A singleton object to be stored in the 'owner' store in IndexedDb.
 *
 * A given database can have a single primary tab assigned at a given time. That
 * tab must validate that it is still holding the primary lease before every
 * operation that requires locked access. The primary tab should regularly
 * write an updated timestamp to this lease to prevent other tabs from
 * "stealing" the primary lease
 */ class Ii {
    constructor(t, 
    /** Whether to allow shared access from multiple tabs. */
    e, n) {
        this.ownerId = t, this.allowTabSynchronization = e, this.leaseTimestampMs = n;
    }
}

/**
 * Name of the IndexedDb object store.
 *
 * Note that the name 'owner' is chosen to ensure backwards compatibility with
 * older clients that only supported single locked access to the persistence
 * layer.
 */ Ii.store = "owner", 
/**
 * The key string used for the single object that exists in the
 * DbPrimaryClient store.
 */
Ii.key = "owner";

/**
 * An object to be stored in the 'mutationQueues' store in IndexedDb.
 *
 * Each user gets a single queue of MutationBatches to apply to the server.
 * DbMutationQueue tracks the metadata about the queue.
 */
class mi {
    constructor(
    /**
     * The normalized user ID to which this queue belongs.
     */
    t, 
    /**
     * An identifier for the highest numbered batch that has been acknowledged
     * by the server. All MutationBatches in this queue with batchIds less
     * than or equal to this value are considered to have been acknowledged by
     * the server.
     *
     * NOTE: this is deprecated and no longer used by the code.
     */
    e, 
    /**
     * A stream token that was previously sent by the server.
     *
     * See StreamingWriteRequest in datastore.proto for more details about
     * usage.
     *
     * After sending this token, earlier tokens may not be used anymore so
     * only a single stream token is retained.
     *
     * NOTE: this is deprecated and no longer used by the code.
     */
    n) {
        this.userId = t, this.lastAcknowledgedBatchId = e, this.lastStreamToken = n;
    }
}

/** Name of the IndexedDb object store.  */ mi.store = "mutationQueues", 
/** Keys are automatically assigned via the userId property. */
mi.keyPath = "userId";

/**
 * An object to be stored in the 'mutations' store in IndexedDb.
 *
 * Represents a batch of user-level mutations intended to be sent to the server
 * in a single write. Each user-level batch gets a separate DbMutationBatch
 * with a new batchId.
 */
class Ai {
    constructor(
    /**
     * The normalized user ID to which this batch belongs.
     */
    t, 
    /**
     * An identifier for this batch, allocated using an auto-generated key.
     */
    e, 
    /**
     * The local write time of the batch, stored as milliseconds since the
     * epoch.
     */
    n, 
    /**
     * A list of "mutations" that represent a partial base state from when this
     * write batch was initially created. During local application of the write
     * batch, these baseMutations are applied prior to the real writes in order
     * to override certain document fields from the remote document cache. This
     * is necessary in the case of non-idempotent writes (e.g. `increment()`
     * transforms) to make sure that the local view of the modified documents
     * doesn't flicker if the remote document cache receives the result of the
     * non-idempotent write before the write is removed from the queue.
     *
     * These mutations are never sent to the backend.
     */
    s, 
    /**
     * A list of mutations to apply. All mutations will be applied atomically.
     *
     * Mutations are serialized via toMutation().
     */
    i) {
        this.userId = t, this.batchId = e, this.localWriteTimeMs = n, this.baseMutations = s, 
        this.mutations = i;
    }
}

/** Name of the IndexedDb object store.  */ Ai.store = "mutations", 
/** Keys are automatically assigned via the userId, batchId properties. */
Ai.keyPath = "batchId", 
/** The index name for lookup of mutations by user. */
Ai.userMutationsIndex = "userMutationsIndex", 
/** The user mutations index is keyed by [userId, batchId] pairs. */
Ai.userMutationsKeyPath = [ "userId", "batchId" ];

/**
 * An object to be stored in the 'documentMutations' store in IndexedDb.
 *
 * A manually maintained index of all the mutation batches that affect a given
 * document key. The rows in this table are references based on the contents of
 * DbMutationBatch.mutations.
 */
class Ri {
    constructor() {}
    /**
     * Creates a [userId] key for use in the DbDocumentMutations index to iterate
     * over all of a user's document mutations.
     */    static prefixForUser(t) {
        return [ t ];
    }
    /**
     * Creates a [userId, encodedPath] key for use in the DbDocumentMutations
     * index to iterate over all at document mutations for a given path or lower.
     */    static prefixForPath(t, e) {
        return [ t, fi(e) ];
    }
    /**
     * Creates a full index key of [userId, encodedPath, batchId] for inserting
     * and deleting into the DbDocumentMutations index.
     */    static key(t, e, n) {
        return [ t, fi(e), n ];
    }
}

Ri.store = "documentMutations", 
/**
 * Because we store all the useful information for this store in the key,
 * there is no useful information to store as the value. The raw (unencoded)
 * path cannot be stored because IndexedDb doesn't store prototype
 * information.
 */
Ri.PLACEHOLDER = new Ri;

/**
 * Represents the known absence of a document at a particular version.
 * Stored in IndexedDb as part of a DbRemoteDocument object.
 */
class Pi {
    constructor(t, e) {
        this.path = t, this.readTime = e;
    }
}

/**
 * Represents a document that is known to exist but whose data is unknown.
 * Stored in IndexedDb as part of a DbRemoteDocument object.
 */ class yi {
    constructor(t, e) {
        this.path = t, this.version = e;
    }
}

/**
 * An object to be stored in the 'remoteDocuments' store in IndexedDb.
 * It represents either:
 *
 * - A complete document.
 * - A "no document" representing a document that is known not to exist (at
 * some version).
 * - An "unknown document" representing a document that is known to exist (at
 * some version) but whose contents are unknown.
 *
 * Note: This is the persisted equivalent of a MaybeDocument and could perhaps
 * be made more general if necessary.
 */ class gi {
    // TODO: We are currently storing full document keys almost three times
    // (once as part of the primary key, once - partly - as `parentPath` and once
    // inside the encoded documents). During our next migration, we should
    // rewrite the primary key as parentPath + document ID which would allow us
    // to drop one value.
    constructor(
    /**
     * Set to an instance of DbUnknownDocument if the data for a document is
     * not known, but it is known that a document exists at the specified
     * version (e.g. it had a successful update applied to it)
     */
    t, 
    /**
     * Set to an instance of a DbNoDocument if it is known that no document
     * exists.
     */
    e, 
    /**
     * Set to an instance of a Document if there's a cached version of the
     * document.
     */
    n, 
    /**
     * Documents that were written to the remote document store based on
     * a write acknowledgment are marked with `hasCommittedMutations`. These
     * documents are potentially inconsistent with the backend's copy and use
     * the write's commit version as their document version.
     */
    s, 
    /**
     * When the document was read from the backend. Undefined for data written
     * prior to schema version 9.
     */
    i, 
    /**
     * The path of the collection this document is part of. Undefined for data
     * written prior to schema version 9.
     */
    r) {
        this.unknownDocument = t, this.noDocument = e, this.document = n, this.hasCommittedMutations = s, 
        this.readTime = i, this.parentPath = r;
    }
}

gi.store = "remoteDocuments", 
/**
 * An index that provides access to all entries sorted by read time (which
 * corresponds to the last modification time of each row).
 *
 * This index is used to provide a changelog for Multi-Tab.
 */
gi.readTimeIndex = "readTimeIndex", gi.readTimeIndexPath = "readTime", 
/**
 * An index that provides access to documents in a collection sorted by read
 * time.
 *
 * This index is used to allow the RemoteDocumentCache to fetch newly changed
 * documents in a collection.
 */
gi.collectionReadTimeIndex = "collectionReadTimeIndex", gi.collectionReadTimeIndexPath = [ "parentPath", "readTime" ];

/**
 * Contains a single entry that has metadata about the remote document cache.
 */
class Vi {
    /**
     * @param byteSize - Approximately the total size in bytes of all the
     * documents in the document cache.
     */
    constructor(t) {
        this.byteSize = t;
    }
}

Vi.store = "remoteDocumentGlobal", Vi.key = "remoteDocumentGlobalKey";

/**
 * An object to be stored in the 'targets' store in IndexedDb.
 *
 * This is based on and should be kept in sync with the proto used in the iOS
 * client.
 *
 * Each query the client listens to against the server is tracked on disk so
 * that the query can be efficiently resumed on restart.
 */
class pi {
    constructor(
    /**
     * An auto-generated sequential numeric identifier for the query.
     *
     * Queries are stored using their canonicalId as the key, but these
     * canonicalIds can be quite long so we additionally assign a unique
     * queryId which can be used by referenced data structures (e.g.
     * indexes) to minimize the on-disk cost.
     */
    t, 
    /**
     * The canonical string representing this query. This is not unique.
     */
    e, 
    /**
     * The last readTime received from the Watch Service for this query.
     *
     * This is the same value as TargetChange.read_time in the protos.
     */
    n, 
    /**
     * An opaque, server-assigned token that allows watching a query to be
     * resumed after disconnecting without retransmitting all the data
     * that matches the query. The resume token essentially identifies a
     * point in time from which the server should resume sending results.
     *
     * This is related to the snapshotVersion in that the resumeToken
     * effectively also encodes that value, but the resumeToken is opaque
     * and sometimes encodes additional information.
     *
     * A consequence of this is that the resumeToken should be used when
     * asking the server to reason about where this client is in the watch
     * stream, but the client should use the snapshotVersion for its own
     * purposes.
     *
     * This is the same value as TargetChange.resume_token in the protos.
     */
    s, 
    /**
     * A sequence number representing the last time this query was
     * listened to, used for garbage collection purposes.
     *
     * Conventionally this would be a timestamp value, but device-local
     * clocks are unreliable and they must be able to create new listens
     * even while disconnected. Instead this should be a monotonically
     * increasing number that's incremented on each listen call.
     *
     * This is different from the queryId since the queryId is an
     * immutable identifier assigned to the Query on first use while
     * lastListenSequenceNumber is updated every time the query is
     * listened to.
     */
    i, 
    /**
     * Denotes the maximum snapshot version at which the associated query view
     * contained no limbo documents.  Undefined for data written prior to
     * schema version 9.
     */
    r, 
    /**
     * The query for this target.
     *
     * Because canonical ids are not unique we must store the actual query. We
     * use the proto to have an object we can persist without having to
     * duplicate translation logic to and from a `Query` object.
     */
    o) {
        this.targetId = t, this.canonicalId = e, this.readTime = n, this.resumeToken = s, 
        this.lastListenSequenceNumber = i, this.lastLimboFreeSnapshotVersion = r, this.query = o;
    }
}

pi.store = "targets", 
/** Keys are automatically assigned via the targetId property. */
pi.keyPath = "targetId", 
/** The name of the queryTargets index. */
pi.queryTargetsIndexName = "queryTargetsIndex", 
/**
 * The index of all canonicalIds to the targets that they match. This is not
 * a unique mapping because canonicalId does not promise a unique name for all
 * possible queries, so we append the targetId to make the mapping unique.
 */
pi.queryTargetsKeyPath = [ "canonicalId", "targetId" ];

/**
 * An object representing an association between a target and a document, or a
 * sentinel row marking the last sequence number at which a document was used.
 * Each document cached must have a corresponding sentinel row before lru
 * garbage collection is enabled.
 *
 * The target associations and sentinel rows are co-located so that orphaned
 * documents and their sequence numbers can be identified efficiently via a scan
 * of this store.
 */
class bi {
    constructor(
    /**
     * The targetId identifying a target or 0 for a sentinel row.
     */
    t, 
    /**
     * The path to the document, as encoded in the key.
     */
    e, 
    /**
     * If this is a sentinel row, this should be the sequence number of the last
     * time the document specified by `path` was used. Otherwise, it should be
     * `undefined`.
     */
    n) {
        this.targetId = t, this.path = e, this.sequenceNumber = n;
    }
}

/** Name of the IndexedDb object store.  */ bi.store = "targetDocuments", 
/** Keys are automatically assigned via the targetId, path properties. */
bi.keyPath = [ "targetId", "path" ], 
/** The index name for the reverse index. */
bi.documentTargetsIndex = "documentTargetsIndex", 
/** We also need to create the reverse index for these properties. */
bi.documentTargetsKeyPath = [ "path", "targetId" ];

/**
 * A record of global state tracked across all Targets, tracked separately
 * to avoid the need for extra indexes.
 *
 * This should be kept in-sync with the proto used in the iOS client.
 */
class vi {
    constructor(
    /**
     * The highest numbered target id across all targets.
     *
     * See DbTarget.targetId.
     */
    t, 
    /**
     * The highest numbered lastListenSequenceNumber across all targets.
     *
     * See DbTarget.lastListenSequenceNumber.
     */
    e, 
    /**
     * A global snapshot version representing the last consistent snapshot we
     * received from the backend. This is monotonically increasing and any
     * snapshots received from the backend prior to this version (e.g. for
     * targets resumed with a resumeToken) should be suppressed (buffered)
     * until the backend has caught up to this snapshot version again. This
     * prevents our cache from ever going backwards in time.
     */
    n, 
    /**
     * The number of targets persisted.
     */
    s) {
        this.highestTargetId = t, this.highestListenSequenceNumber = e, this.lastRemoteSnapshotVersion = n, 
        this.targetCount = s;
    }
}

/**
 * The key string used for the single object that exists in the
 * DbTargetGlobal store.
 */ vi.key = "targetGlobalKey", vi.store = "targetGlobal";

/**
 * An object representing an association between a Collection id (e.g. 'messages')
 * to a parent path (e.g. '/chats/123') that contains it as a (sub)collection.
 * This is used to efficiently find all collections to query when performing
 * a Collection Group query.
 */
class Si {
    constructor(
    /**
     * The collectionId (e.g. 'messages')
     */
    t, 
    /**
     * The path to the parent (either a document location or an empty path for
     * a root-level collection).
     */
    e) {
        this.collectionId = t, this.parent = e;
    }
}

/** Name of the IndexedDb object store. */ Si.store = "collectionParents", 
/** Keys are automatically assigned via the collectionId, parent properties. */
Si.keyPath = [ "collectionId", "parent" ];

/**
 * A record of the metadata state of each client.
 *
 * PORTING NOTE: This is used to synchronize multi-tab state and does not need
 * to be ported to iOS or Android.
 */
class Di {
    constructor(
    // Note: Previous schema versions included a field
    // "lastProcessedDocumentChangeId". Don't use anymore.
    /** The auto-generated client id assigned at client startup. */
    t, 
    /** The last time this state was updated. */
    e, 
    /** Whether the client's network connection is enabled. */
    n, 
    /** Whether this client is running in a foreground tab. */
    s) {
        this.clientId = t, this.updateTimeMs = e, this.networkEnabled = n, this.inForeground = s;
    }
}

/** Name of the IndexedDb object store. */ Di.store = "clientMetadata", 
/** Keys are automatically assigned via the clientId properties. */
Di.keyPath = "clientId";

/**
 * A object representing a bundle loaded by the SDK.
 */
class Ci {
    constructor(
    /** The ID of the loaded bundle. */
    t, 
    /** The create time of the loaded bundle. */
    e, 
    /** The schema version of the loaded bundle. */
    n) {
        this.bundleId = t, this.createTime = e, this.version = n;
    }
}

/** Name of the IndexedDb object store. */ Ci.store = "bundles", Ci.keyPath = "bundleId";

/**
 * A object representing a named query loaded by the SDK via a bundle.
 */
class xi {
    constructor(
    /** The name of the query. */
    t, 
    /** The read time of the results saved in the bundle from the named query. */
    e, 
    /** The query saved in the bundle. */
    n) {
        this.name = t, this.readTime = e, this.bundledQuery = n;
    }
}

/** Name of the IndexedDb object store. */ xi.store = "namedQueries", xi.keyPath = "name";

// Visible for testing
const Ni = [ ...[ ...[ ...[ ...[ mi.store, Ai.store, Ri.store, gi.store, pi.store, Ii.store, vi.store, bi.store ], Di.store ], Vi.store ], Si.store ], Ci.store, xi.store ];

// V2 is no longer usable (see comment at top of file)
// Visible for testing
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Fi extends Us {
    constructor(t, e) {
        super(), this.ro = t, this.oo = e;
    }
}

function Oi(t, e) {
    const n = V(t);
    return ps.As(n.ro, e);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Serializer for values stored in the LocalStore. */ class ki {
    constructor(t) {
        this.co = t;
    }
}

/** Decodes a remote document from storage locally to a Document. */ function Mi(t, e) {
    if (e.document) return ns(t.co, e.document, !!e.hasCommittedMutations);
    if (e.noDocument) {
        const t = _t.Ut(e.noDocument.path), n = Ui(e.noDocument.readTime);
        return new Ut(t, n, {
            hasCommittedMutations: !!e.hasCommittedMutations
        });
    }
    if (e.unknownDocument) {
        const t = _t.Ut(e.unknownDocument.path), n = Ui(e.unknownDocument.version);
        return new Qt(t, n);
    }
    return y();
}

/** Encodes a document for storage locally. */ function Li(t, e, n) {
    const s = $i(n), i = e.key.path.yt().Dt();
    if (e instanceof qt) {
        const n = function(t, e) {
            return {
                name: Hn(t, e.key),
                fields: e.Yt().mapValue.fields,
                updateTime: Qn(t, e.version.Tt())
            };
        }(t.co, e), r = e.hasCommittedMutations;
        return new gi(
        /* unknownDocument= */ null, 
        /* noDocument= */ null, n, r, s, i);
    }
    if (e instanceof Ut) {
        const t = e.key.path.Dt(), n = qi(e.version), r = e.hasCommittedMutations;
        return new gi(
        /* unknownDocument= */ null, new Pi(t, n), 
        /* document= */ null, r, s, i);
    }
    if (e instanceof Qt) {
        const t = e.key.path.Dt(), n = qi(e.version);
        return new gi(new yi(t, n), 
        /* noDocument= */ null, 
        /* document= */ null, 
        /* hasCommittedMutations= */ !0, s, i);
    }
    return y();
}

function $i(t) {
    const e = t.Tt();
    return [ e.seconds, e.nanoseconds ];
}

function Bi(t) {
    const e = new ot(t[0], t[1]);
    return ct.wt(e);
}

function qi(t) {
    const e = t.Tt();
    return new Ti(e.seconds, e.nanoseconds);
}

function Ui(t) {
    const e = new ot(t.seconds, t.nanoseconds);
    return ct.wt(e);
}

/** Encodes a batch of mutations into a DbMutationBatch for local storage. */
/** Decodes a DbMutationBatch into a MutationBatch */
function Qi(t, e) {
    const n = (e.baseMutations || []).map((e => os(t.co, e)));
    // Squash old transform mutations into existing patch or set mutations.
    // The replacement of representing `transforms` with `update_transforms`
    // on the SDK means that old `transform` mutations stored in IndexedDB need
    // to be updated to `update_transforms`.
    // TODO(b/174608374): Remove this code once we perform a schema migration.
        for (let t = e.mutations.length - 1; t >= 0; --t) {
        const n = e.mutations[t];
        if (void 0 !== (null == n ? void 0 : n.transform)) {
            e.mutations[t - 1].updateTransforms = n.transform.fieldTransforms, e.mutations.splice(t, 1), 
            --t;
        }
    }
    const s = e.mutations.map((e => os(t.co, e))), i = ot.fromMillis(e.localWriteTimeMs);
    return new zs(e.batchId, i, n, s);
}

/** Decodes a DbTarget into TargetData */ function Ki(t) {
    const e = Ui(t.readTime), n = void 0 !== t.lastLimboFreeSnapshotVersion ? Ui(t.lastLimboFreeSnapshotVersion) : ct.min();
    let s;
    var i;
    return void 0 !== t.query.documents ? (g(1 === (i = t.query).documents.length), 
    s = Re(de(Xn(i.documents[0])))) : s = function(t) {
        return Re(hs(t));
    }(t.query), new Se(s, t.targetId, 0 /* Listen */ , t.lastListenSequenceNumber, e, n, C.fromBase64String(t.resumeToken));
}

/** Encodes TargetData into a DbTarget for storage locally. */ function Wi(t, e) {
    const n = qi(e.oe), s = qi(e.lastLimboFreeSnapshotVersion);
    let i;
    i = Ht(e.target) ? as(t.co, e.target) : us(t.co, e.target);
    // We can't store the resumeToken as a ByteString in IndexedDb, so we
    // convert it to a base64 string for storage.
        const r = e.resumeToken.toBase64();
    // lastListenSequenceNumber is always 0 until we do real GC.
        return new pi(e.targetId, jt(e.target), n, r, e.sequenceNumber, s, i);
}

/**
 * A helper function for figuring out what kind of query has been stored.
 */
/**
 * Encodes a `BundledQuery` from bundle proto to a Query object.
 *
 * This reconstructs the original query used to build the bundle being loaded,
 * including features exists only in SDKs (for example: limit-to-last).
 */
function ji(t) {
    const e = hs({
        parent: t.parent,
        structuredQuery: t.structuredQuery
    });
    return "LAST" === t.limitType ? Pe(e, e.limit, "L" /* Last */) : e;
}

/** Encodes a NamedQuery proto object to a NamedQuery model object. */
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Gi {
    ao(t, e) {
        return zi(t).get(e).next((t => {
            if (t) return {
                id: (e = t).bundleId,
                createTime: Ui(e.createTime),
                version: e.version
            };
            /** Encodes a DbBundle to a Bundle. */
            var e;
            /** Encodes a BundleMetadata to a DbBundle. */        }));
    }
    uo(t, e) {
        return zi(t).put({
            bundleId: (n = e).id,
            createTime: qi(jn(n.createTime)),
            version: n.version
        });
        var n;
        /** Encodes a DbNamedQuery to a NamedQuery. */    }
    ho(t, e) {
        return Hi(t).get(e).next((t => {
            if (t) return {
                name: (e = t).name,
                query: ji(e.bundledQuery),
                readTime: Ui(e.readTime)
            };
            var e;
            /** Encodes a NamedQuery from a bundle proto to a DbNamedQuery. */        }));
    }
    lo(t, e) {
        return Hi(t).put(function(t) {
            return {
                name: t.name,
                readTime: qi(jn(t.readTime)),
                bundledQuery: t.bundledQuery
            };
        }(e));
    }
}

/**
 * Helper to get a typed SimpleDbStore for the bundles object store.
 */ function zi(t) {
    return Oi(t, Ci.store);
}

/**
 * Helper to get a typed SimpleDbStore for the namedQueries object store.
 */ function Hi(t) {
    return Oi(t, xi.store);
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An in-memory implementation of IndexManager.
 */ class Ji {
    constructor() {
        this._o = new Yi;
    }
    fo(t, e) {
        return this._o.add(e), gs.resolve();
    }
    wo(t, e) {
        return gs.resolve(this._o.getEntries(e));
    }
}

/**
 * Internal implementation of the collection-parent index exposed by MemoryIndexManager.
 * Also used for in-memory caching by IndexedDbIndexManager and initial index population
 * in indexeddb_schema.ts
 */ class Yi {
    constructor() {
        this.index = {};
    }
    // Returns false if the entry already existed.
    add(t) {
        const e = t.Vt(), n = t.yt(), s = this.index[e] || new wn(ut.At), i = !s.has(n);
        return this.index[e] = s.add(n), i;
    }
    has(t) {
        const e = t.Vt(), n = t.yt(), s = this.index[e];
        return s && s.has(n);
    }
    getEntries(t) {
        return (this.index[t] || new wn(ut.At)).Dt();
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A persisted implementation of IndexManager.
 */ class Xi {
    constructor() {
        /**
         * An in-memory copy of the index entries we've already written since the SDK
         * launched. Used to avoid re-writing the same entry repeatedly.
         *
         * This is *NOT* a complete cache of what's in persistence and so can never be used to
         * satisfy reads.
         */
        this.Eo = new Yi;
    }
    /**
     * Adds a new entry to the collection parent index.
     *
     * Repeated calls for the same collectionPath should be avoided within a
     * transaction as IndexedDbIndexManager only caches writes once a transaction
     * has been committed.
     */    fo(t, e) {
        if (!this.Eo.has(e)) {
            const n = e.Vt(), s = e.yt();
            t.Oi((() => {
                // Add the collection to the in memory cache only if the transaction was
                // successfully committed.
                this.Eo.add(e);
            }));
            const i = {
                collectionId: n,
                parent: fi(s)
            };
            return Zi(t).put(i);
        }
        return gs.resolve();
    }
    wo(t, e) {
        const n = [], s = IDBKeyRange.bound([ e, "" ], [ D(e), "" ], 
        /*lowerOpen=*/ !1, 
        /*upperOpen=*/ !0);
        return Zi(t).Ds(s).next((t => {
            for (const s of t) {
                // This collectionId guard shouldn't be necessary (and isn't as long
                // as we're running in a real browser), but there's a bug in
                // indexeddbshim that breaks our range in our tests running in node:
                // https://github.com/axemclion/IndexedDBShim/issues/334
                if (s.collectionId !== e) break;
                n.push(Ei(s.parent));
            }
            return n;
        }));
    }
}

/**
 * Helper to get a typed SimpleDbStore for the collectionParents
 * document store.
 */ function Zi(t) {
    return Oi(t, Si.store);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Delete a mutation batch and the associated document mutations.
 * @returns A PersistencePromise of the document mutations that were removed.
 */ function tr(t, e, n) {
    const s = t.store(Ai.store), i = t.store(Ri.store), r = [], o = IDBKeyRange.only(n.batchId);
    let c = 0;
    const a = s.Fs({
        range: o
    }, ((t, e, n) => (c++, n.delete())));
    r.push(a.next((() => {
        g(1 === c);
    })));
    const u = [];
    for (const t of n.mutations) {
        const s = Ri.key(e, t.key.path, n.batchId);
        r.push(i.delete(s)), u.push(t.key);
    }
    return gs.hs(r).next((() => u));
}

/**
 * Returns an approximate size for the given document.
 */ function er(t) {
    let e;
    if (t.document) e = t.document; else if (t.unknownDocument) e = t.unknownDocument; else {
        if (!t.noDocument) throw y();
        e = t.noDocument;
    }
    return JSON.stringify(e).length;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** A mutation queue for a specific user, backed by IndexedDB. */ class nr {
    constructor(
    /**
     * The normalized userId (e.g. null UID => "" userId) used to store /
     * retrieve mutations.
     */
    t, e, n, s) {
        this.userId = t, this.fe = e, this.To = n, this.Io = s, 
        /**
         * Caches the document keys for pending mutation batches. If the mutation
         * has been removed from IndexedDb, the cached value may continue to
         * be used to retrieve the batch's document keys. To remove a cached value
         * locally, `removeCachedMutationKeys()` should be invoked either directly
         * or through `removeMutationBatches()`.
         *
         * With multi-tab, when the primary client acknowledges or rejects a mutation,
         * this cache is used by secondary clients to invalidate the local
         * view of the documents that were previously affected by the mutation.
         */
        // PORTING NOTE: Multi-tab only.
        this.mo = {};
    }
    /**
     * Creates a new mutation queue for the given user.
     * @param user - The user for which to create a mutation queue.
     * @param serializer - The serializer to use when persisting to IndexedDb.
     */    static Ao(t, e, n, s) {
        // TODO(mcg): Figure out what constraints there are on userIDs
        // In particular, are there any reserved characters? are empty ids allowed?
        // For the moment store these together in the same mutations table assuming
        // that empty userIDs aren't allowed.
        g("" !== t.uid);
        const i = t.T() ? t.uid : "";
        return new nr(i, e, n, s);
    }
    Ro(t) {
        let e = !0;
        const n = IDBKeyRange.bound([ this.userId, Number.NEGATIVE_INFINITY ], [ this.userId, Number.POSITIVE_INFINITY ]);
        return ir(t).Fs({
            index: Ai.userMutationsIndex,
            range: n
        }, ((t, n, s) => {
            e = !1, s.done();
        })).next((() => e));
    }
    Po(t, e, n, s) {
        const i = rr(t), r = ir(t);
        // The IndexedDb implementation in Chrome (and Firefox) does not handle
        // compound indices that include auto-generated keys correctly. To ensure
        // that the index entry is added correctly in all browsers, we perform two
        // writes: The first write is used to retrieve the next auto-generated Batch
        // ID, and the second write populates the index and stores the actual
        // mutation batch.
        // See: https://bugs.chromium.org/p/chromium/issues/detail?id=701972
        // We write an empty object to obtain key
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return r.add({}).next((o => {
            g("number" == typeof o);
            const c = new zs(o, e, n, s), a = function(t, e, n) {
                const s = n.baseMutations.map((e => rs(t.co, e))), i = n.mutations.map((e => rs(t.co, e)));
                return new Ai(e, n.batchId, n.er.toMillis(), s, i);
            }(this.fe, this.userId, c), u = [];
            let h = new wn(((t, e) => v(t.Ct(), e.Ct())));
            for (const t of s) {
                const e = Ri.key(this.userId, t.key.path, o);
                h = h.add(t.key.path.yt()), u.push(r.put(a)), u.push(i.put(e, Ri.PLACEHOLDER));
            }
            return h.forEach((e => {
                u.push(this.To.fo(t, e));
            })), t.Oi((() => {
                this.mo[o] = c.keys();
            })), gs.hs(u).next((() => c));
        }));
    }
    yo(t, e) {
        return ir(t).get(e).next((t => t ? (g(t.userId === this.userId), Qi(this.fe, t)) : null));
    }
    /**
     * Returns the document keys for the mutation batch with the given batchId.
     * For primary clients, this method returns `null` after
     * `removeMutationBatches()` has been called. Secondary clients return a
     * cached result until `removeCachedMutationKeys()` is invoked.
     */
    // PORTING NOTE: Multi-tab only.
    Vo(t, e) {
        return this.mo[e] ? gs.resolve(this.mo[e]) : this.yo(t, e).next((t => {
            if (t) {
                const n = t.keys();
                return this.mo[e] = n, n;
            }
            return null;
        }));
    }
    po(t, e) {
        const n = e + 1, s = IDBKeyRange.lowerBound([ this.userId, n ]);
        let i = null;
        return ir(t).Fs({
            index: Ai.userMutationsIndex,
            range: s
        }, ((t, e, s) => {
            e.userId === this.userId && (g(e.batchId >= n), i = Qi(this.fe, e)), s.done();
        })).next((() => i));
    }
    bo(t) {
        const e = IDBKeyRange.upperBound([ this.userId, Number.POSITIVE_INFINITY ]);
        let n = -1;
        return ir(t).Fs({
            index: Ai.userMutationsIndex,
            range: e,
            reverse: !0
        }, ((t, e, s) => {
            n = e.batchId, s.done();
        })).next((() => n));
    }
    vo(t) {
        const e = IDBKeyRange.bound([ this.userId, -1 ], [ this.userId, Number.POSITIVE_INFINITY ]);
        return ir(t).Ds(Ai.userMutationsIndex, e).next((t => t.map((t => Qi(this.fe, t)))));
    }
    So(t, e) {
        // Scan the document-mutation index starting with a prefix starting with
        // the given documentKey.
        const n = Ri.prefixForPath(this.userId, e.path), s = IDBKeyRange.lowerBound(n), i = [];
        return rr(t).Fs({
            range: s
        }, ((n, s, r) => {
            const [o, c, a] = n, u = Ei(c);
            // Only consider rows matching exactly the specific key of
            // interest. Note that because we order by path first, and we
            // order terminators before path separators, we'll encounter all
            // the index rows for documentKey contiguously. In particular, all
            // the rows for documentKey will occur before any rows for
            // documents nested in a subcollection beneath documentKey so we
            // can stop as soon as we hit any such row.
                        if (o === this.userId && e.path.isEqual(u)) 
            // Look up the mutation batch in the store.
            return ir(t).get(a).next((t => {
                if (!t) throw y();
                g(t.userId === this.userId), i.push(Qi(this.fe, t));
            }));
            r.done();
        })).next((() => i));
    }
    Do(t, e) {
        let n = new wn(v);
        const s = [];
        return e.forEach((e => {
            const i = Ri.prefixForPath(this.userId, e.path), r = IDBKeyRange.lowerBound(i), o = rr(t).Fs({
                range: r
            }, ((t, s, i) => {
                const [r, o, c] = t, a = Ei(o);
                // Only consider rows matching exactly the specific key of
                // interest. Note that because we order by path first, and we
                // order terminators before path separators, we'll encounter all
                // the index rows for documentKey contiguously. In particular, all
                // the rows for documentKey will occur before any rows for
                // documents nested in a subcollection beneath documentKey so we
                // can stop as soon as we hit any such row.
                                r === this.userId && e.path.isEqual(a) ? n = n.add(c) : i.done();
            }));
            s.push(o);
        })), gs.hs(s).next((() => this.Co(t, n)));
    }
    xo(t, e) {
        const n = e.path, s = n.length + 1, i = Ri.prefixForPath(this.userId, n), r = IDBKeyRange.lowerBound(i);
        // Collect up unique batchIDs encountered during a scan of the index. Use a
        // SortedSet to accumulate batch IDs so they can be traversed in order in a
        // scan of the main table.
        let o = new wn(v);
        return rr(t).Fs({
            range: r
        }, ((t, e, i) => {
            const [r, c, a] = t, u = Ei(c);
            r === this.userId && n.vt(u) ? 
            // Rows with document keys more than one segment longer than the
            // query path can't be matches. For example, a query on 'rooms'
            // can't match the document /rooms/abc/messages/xyx.
            // TODO(mcg): we'll need a different scanner when we implement
            // ancestor queries.
            u.length === s && (o = o.add(a)) : i.done();
        })).next((() => this.Co(t, o)));
    }
    Co(t, e) {
        const n = [], s = [];
        // TODO(rockwood): Implement this using iterate.
        return e.forEach((e => {
            s.push(ir(t).get(e).next((t => {
                if (null === t) throw y();
                g(t.userId === this.userId), n.push(Qi(this.fe, t));
            })));
        })), gs.hs(s).next((() => n));
    }
    No(t, e) {
        return tr(t.ro, this.userId, e).next((n => (t.Oi((() => {
            this.Fo(e.batchId);
        })), gs.forEach(n, (e => this.Io.Oo(t, e))))));
    }
    /**
     * Clears the cached keys for a mutation batch. This method should be
     * called by secondary clients after they process mutation updates.
     *
     * Note that this method does not have to be called from primary clients as
     * the corresponding cache entries are cleared when an acknowledged or
     * rejected batch is removed from the mutation queue.
     */
    // PORTING NOTE: Multi-tab only
    Fo(t) {
        delete this.mo[t];
    }
    ko(t) {
        return this.Ro(t).next((e => {
            if (!e) return gs.resolve();
            // Verify that there are no entries in the documentMutations index if
            // the queue is empty.
                        const n = IDBKeyRange.lowerBound(Ri.prefixForUser(this.userId)), s = [];
            return rr(t).Fs({
                range: n
            }, ((t, e, n) => {
                if (t[0] === this.userId) {
                    const e = Ei(t[1]);
                    s.push(e);
                } else n.done();
            })).next((() => {
                g(0 === s.length);
            }));
        }));
    }
    Mo(t, e) {
        return sr(t, this.userId, e);
    }
    // PORTING NOTE: Multi-tab only (state is held in memory in other clients).
    /** Returns the mutation queue's metadata from IndexedDb. */
    Lo(t) {
        return or(t).get(this.userId).next((t => t || new mi(this.userId, -1, 
        /*lastStreamToken=*/ "")));
    }
}

/**
 * @returns true if the mutation queue for the given user contains a pending
 *         mutation for the given key.
 */ function sr(t, e, n) {
    const s = Ri.prefixForPath(e, n.path), i = s[1], r = IDBKeyRange.lowerBound(s);
    let o = !1;
    return rr(t).Fs({
        range: r,
        Ns: !0
    }, ((t, n, s) => {
        const [r, c, /*batchID*/ a] = t;
        r === e && c === i && (o = !0), s.done();
    })).next((() => o));
}

/** Returns true if any mutation queue contains the given document. */
/**
 * Helper to get a typed SimpleDbStore for the mutations object store.
 */
function ir(t) {
    return Oi(t, Ai.store);
}

/**
 * Helper to get a typed SimpleDbStore for the mutationQueues object store.
 */ function rr(t) {
    return Oi(t, Ri.store);
}

/**
 * Helper to get a typed SimpleDbStore for the mutationQueues object store.
 */ function or(t) {
    return Oi(t, mi.store);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Offset to ensure non-overlapping target ids. */
/**
 * Generates monotonically increasing target IDs for sending targets to the
 * watch stream.
 *
 * The client constructs two generators, one for the target cache, and one for
 * for the sync engine (to generate limbo documents targets). These
 * generators produce non-overlapping IDs (by using even and odd IDs
 * respectively).
 *
 * By separating the target ID space, the query cache can generate target IDs
 * that persist across client restarts, while sync engine can independently
 * generate in-memory target IDs that are transient and can be reused after a
 * restart.
 */
class cr {
    constructor(t) {
        this.$o = t;
    }
    next() {
        return this.$o += 2, this.$o;
    }
    static Bo() {
        // The target cache generator must return '2' in its first call to `next()`
        // as there is no differentiation in the protocol layer between an unset
        // number and the number '0'. If we were to sent a target with target ID
        // '0', the backend would consider it unset and replace it with its own ID.
        return new cr(0);
    }
    static qo() {
        // Sync engine assigns target IDs for limbo document detection.
        return new cr(-1);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class ar {
    constructor(t, e) {
        this.Io = t, this.fe = e;
    }
    // PORTING NOTE: We don't cache global metadata for the target cache, since
    // some of it (in particular `highestTargetId`) can be modified by secondary
    // tabs. We could perhaps be more granular (and e.g. still cache
    // `lastRemoteSnapshotVersion` in memory) but for simplicity we currently go
    // to IndexedDb whenever we need to read metadata. We can revisit if it turns
    // out to have a meaningful performance impact.
    Uo(t) {
        return this.Qo(t).next((e => {
            const n = new cr(e.highestTargetId);
            return e.highestTargetId = n.next(), this.Ko(t, e).next((() => e.highestTargetId));
        }));
    }
    Wo(t) {
        return this.Qo(t).next((t => ct.wt(new ot(t.lastRemoteSnapshotVersion.seconds, t.lastRemoteSnapshotVersion.nanoseconds))));
    }
    jo(t) {
        return this.Qo(t).next((t => t.highestListenSequenceNumber));
    }
    Go(t, e, n) {
        return this.Qo(t).next((s => (s.highestListenSequenceNumber = e, n && (s.lastRemoteSnapshotVersion = n.Tt()), 
        e > s.highestListenSequenceNumber && (s.highestListenSequenceNumber = e), this.Ko(t, s))));
    }
    zo(t, e) {
        return this.Ho(t, e).next((() => this.Qo(t).next((n => (n.targetCount += 1, this.Jo(e, n), 
        this.Ko(t, n))))));
    }
    Yo(t, e) {
        return this.Ho(t, e);
    }
    Xo(t, e) {
        return this.Zo(t, e.targetId).next((() => ur(t).delete(e.targetId))).next((() => this.Qo(t))).next((e => (g(e.targetCount > 0), 
        e.targetCount -= 1, this.Ko(t, e))));
    }
    /**
     * Drops any targets with sequence number less than or equal to the upper bound, excepting those
     * present in `activeTargetIds`. Document associations for the removed targets are also removed.
     * Returns the number of targets removed.
     */    Br(t, e, n) {
        let s = 0;
        const i = [];
        return ur(t).Fs(((r, o) => {
            const c = Ki(o);
            c.sequenceNumber <= e && null === n.get(c.targetId) && (s++, i.push(this.Xo(t, c)));
        })).next((() => gs.hs(i))).next((() => s));
    }
    /**
     * Call provided function with each `TargetData` that we have cached.
     */    Un(t, e) {
        return ur(t).Fs(((t, n) => {
            const s = Ki(n);
            e(s);
        }));
    }
    Qo(t) {
        return hr(t).get(vi.key).next((t => (g(null !== t), t)));
    }
    Ko(t, e) {
        return hr(t).put(vi.key, e);
    }
    Ho(t, e) {
        return ur(t).put(Wi(this.fe, e));
    }
    /**
     * In-place updates the provided metadata to account for values in the given
     * TargetData. Saving is done separately. Returns true if there were any
     * changes to the metadata.
     */    Jo(t, e) {
        let n = !1;
        return t.targetId > e.highestTargetId && (e.highestTargetId = t.targetId, n = !0), 
        t.sequenceNumber > e.highestListenSequenceNumber && (e.highestListenSequenceNumber = t.sequenceNumber, 
        n = !0), n;
    }
    tc(t) {
        return this.Qo(t).next((t => t.targetCount));
    }
    ec(t, e) {
        // Iterating by the canonicalId may yield more than one result because
        // canonicalId values are not required to be unique per target. This query
        // depends on the queryTargets index to be efficient.
        const n = jt(e), s = IDBKeyRange.bound([ n, Number.NEGATIVE_INFINITY ], [ n, Number.POSITIVE_INFINITY ]);
        let i = null;
        return ur(t).Fs({
            range: s,
            index: pi.queryTargetsIndexName
        }, ((t, n, s) => {
            const r = Ki(n);
            // After finding a potential match, check that the target is
            // actually equal to the requested target.
                        zt(e, r.target) && (i = r, s.done());
        })).next((() => i));
    }
    nc(t, e, n) {
        // PORTING NOTE: The reverse index (documentsTargets) is maintained by
        // IndexedDb.
        const s = [], i = lr(t);
        return e.forEach((e => {
            const r = fi(e.path);
            s.push(i.put(new bi(n, r))), s.push(this.Io.sc(t, n, e));
        })), gs.hs(s);
    }
    ic(t, e, n) {
        // PORTING NOTE: The reverse index (documentsTargets) is maintained by
        // IndexedDb.
        const s = lr(t);
        return gs.forEach(e, (e => {
            const i = fi(e.path);
            return gs.hs([ s.delete([ n, i ]), this.Io.rc(t, n, e) ]);
        }));
    }
    Zo(t, e) {
        const n = lr(t), s = IDBKeyRange.bound([ e ], [ e + 1 ], 
        /*lowerOpen=*/ !1, 
        /*upperOpen=*/ !0);
        return n.delete(s);
    }
    oc(t, e) {
        const n = IDBKeyRange.bound([ e ], [ e + 1 ], 
        /*lowerOpen=*/ !1, 
        /*upperOpen=*/ !0), s = lr(t);
        let i = Vn();
        return s.Fs({
            range: n,
            Ns: !0
        }, ((t, e, n) => {
            const s = Ei(t[1]), r = new _t(s);
            i = i.add(r);
        })).next((() => i));
    }
    Mo(t, e) {
        const n = fi(e.path), s = IDBKeyRange.bound([ n ], [ D(n) ], 
        /*lowerOpen=*/ !1, 
        /*upperOpen=*/ !0);
        let i = 0;
        return lr(t).Fs({
            index: bi.documentTargetsIndex,
            Ns: !0,
            range: s
        }, (([t, e], n, s) => {
            // Having a sentinel row for a document does not count as containing that document;
            // For the target cache, containing the document means the document is part of some
            // target.
            0 !== t && (i++, s.done());
        })).next((() => i > 0));
    }
    /**
     * Looks up a TargetData entry by target ID.
     *
     * @param targetId - The target ID of the TargetData entry to look up.
     * @returns The cached TargetData entry, or null if the cache has no entry for
     * the target.
     */
    // PORTING NOTE: Multi-tab only.
    Zn(t, e) {
        return ur(t).get(e).next((t => t ? Ki(t) : null));
    }
}

/**
 * Helper to get a typed SimpleDbStore for the queries object store.
 */ function ur(t) {
    return Oi(t, pi.store);
}

/**
 * Helper to get a typed SimpleDbStore for the target globals object store.
 */ function hr(t) {
    return Oi(t, vi.store);
}

/**
 * Helper to get a typed SimpleDbStore for the document target object store.
 */ function lr(t) {
    return Oi(t, bi.store);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Provides LRU functionality for IndexedDB persistence. */ class _r {
    constructor(t, e) {
        this.db = t, this.vr = function(t, e) {
            return new ei(t, e);
        }(this, e);
    }
    Mr(t) {
        const e = this.cc(t);
        return this.db.ac().tc(t).next((t => e.next((e => t + e))));
    }
    cc(t) {
        let e = 0;
        return this.$r(t, (t => {
            e++;
        })).next((() => e));
    }
    Un(t, e) {
        return this.db.ac().Un(t, e);
    }
    $r(t, e) {
        return this.uc(t, ((t, n) => e(n)));
    }
    sc(t, e, n) {
        return fr(t, n);
    }
    rc(t, e, n) {
        return fr(t, n);
    }
    Br(t, e, n) {
        return this.db.ac().Br(t, e, n);
    }
    Oo(t, e) {
        return fr(t, e);
    }
    /**
     * Returns true if anything would prevent this document from being garbage
     * collected, given that the document in question is not present in any
     * targets and has a sequence number less than or equal to the upper bound for
     * the collection run.
     */    hc(t, e) {
        return function(t, e) {
            let n = !1;
            return or(t).Os((s => sr(t, s, e).next((t => (t && (n = !0), gs.resolve(!t)))))).next((() => n));
        }(t, e);
    }
    qr(t, e) {
        const n = this.db._c().lc(), s = [];
        let i = 0;
        return this.uc(t, ((r, o) => {
            if (o <= e) {
                const e = this.hc(t, r).next((e => {
                    if (!e) 
                    // Our size accounting requires us to read all documents before
                    // removing them.
                    return i++, n.Ar(t, r).next((() => (n.mr(r), lr(t).delete([ 0, fi(r.path) ]))));
                }));
                s.push(e);
            }
        })).next((() => gs.hs(s))).next((() => n.apply(t))).next((() => i));
    }
    removeTarget(t, e) {
        const n = e.ce(t.oo);
        return this.db.ac().Yo(t, n);
    }
    fc(t, e) {
        return fr(t, e);
    }
    /**
     * Call provided function for each document in the cache that is 'orphaned'. Orphaned
     * means not a part of any target, so the only entry in the target-document index for
     * that document will be the sentinel row (targetId 0), which will also have the sequence
     * number for the last time the document was accessed.
     */    uc(t, e) {
        const n = lr(t);
        let s, i = Ws.Zi;
        return n.Fs({
            index: bi.documentTargetsIndex
        }, (([t, n], {path: r, sequenceNumber: o}) => {
            0 === t ? (
            // if nextToReport is valid, report it, this is a new key so the
            // last one must not be a member of any targets.
            i !== Ws.Zi && e(new _t(Ei(s)), i), 
            // set nextToReport to be this sequence number. It's the next one we
            // might report, if we don't find any targets for this document.
            // Note that the sequence number must be defined when the targetId
            // is 0.
            i = o, s = r) : 
            // set nextToReport to be invalid, we know we don't need to report
            // this one since we found a target for it.
            i = Ws.Zi;
        })).next((() => {
            // Since we report sequence numbers after getting to the next key, we
            // need to check if the last key we iterated over was an orphaned
            // document and report it.
            i !== Ws.Zi && e(new _t(Ei(s)), i);
        }));
    }
    Qr(t) {
        return this.db._c().dc(t);
    }
}

function fr(t, e) {
    return lr(t).put(
    /**
 * @returns A value suitable for writing a sentinel row in the target-document
 * store.
 */
    function(t, e) {
        return new bi(0, fi(t.path), e);
    }(e, t.oo));
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * The RemoteDocumentCache for IndexedDb. To construct, invoke
 * `newIndexedDbRemoteDocumentCache()`.
 */ class dr {
    /**
     * @param serializer - The document serializer.
     * @param indexManager - The query indexes that need to be maintained.
     */
    constructor(t, e) {
        this.fe = t, this.To = e;
    }
    /**
     * Adds the supplied entries to the cache.
     *
     * All calls of `addEntry` are required to go through the RemoteDocumentChangeBuffer
     * returned by `newChangeBuffer()` to ensure proper accounting of metadata.
     */    Er(t, e, n) {
        return Tr(t).put(Ir(e), n);
    }
    /**
     * Removes a document from the cache.
     *
     * All calls of `removeEntry`  are required to go through the RemoteDocumentChangeBuffer
     * returned by `newChangeBuffer()` to ensure proper accounting of metadata.
     */    mr(t, e) {
        const n = Tr(t), s = Ir(e);
        return n.delete(s);
    }
    /**
     * Updates the current cache size.
     *
     * Callers to `addEntry()` and `removeEntry()` *must* call this afterwards to update the
     * cache's metadata.
     */    updateMetadata(t, e) {
        return this.getMetadata(t).next((n => (n.byteSize += e, this.wc(t, n))));
    }
    Ar(t, e) {
        return Tr(t).get(Ir(e)).next((t => this.Ec(t)));
    }
    /**
     * Looks up an entry in the cache.
     *
     * @param documentKey - The key of the entry to look up.
     * @returns The cached MaybeDocument entry and its size, or null if we have
     * nothing cached.
     */    Tc(t, e) {
        return Tr(t).get(Ir(e)).next((t => {
            const e = this.Ec(t);
            return e ? {
                Ir: e,
                size: er(t)
            } : null;
        }));
    }
    getEntries(t, e) {
        let n = mn();
        return this.Ic(t, e, ((t, e) => {
            const s = this.Ec(e);
            n = n.Ie(t, s);
        })).next((() => n));
    }
    /**
     * Looks up several entries in the cache.
     *
     * @param documentKeys - The set of keys entries to look up.
     * @returns A map of MaybeDocuments indexed by key (if a document cannot be
     *     found, the key will be mapped to null) and a map of sizes indexed by
     *     key (zero if the key cannot be found).
     */    mc(t, e) {
        let n = mn(), s = new _n(_t.At);
        return this.Ic(t, e, ((t, e) => {
            const i = this.Ec(e);
            i ? (n = n.Ie(t, i), s = s.Ie(t, er(e))) : (n = n.Ie(t, null), s = s.Ie(t, 0));
        })).next((() => ({
            Ac: n,
            Rc: s
        })));
    }
    Ic(t, e, n) {
        if (e.bt()) return gs.resolve();
        const s = IDBKeyRange.bound(e.first().path.Dt(), e.last().path.Dt()), i = e.ge();
        let r = i.De();
        return Tr(t).Fs({
            range: s
        }, ((t, e, s) => {
            const o = _t.Ut(t);
            // Go through keys not found in cache.
                        for (;r && _t.At(r, o) < 0; ) n(r, null), r = i.De();
            r && r.isEqual(o) && (
            // Key found in cache.
            n(r, e), r = i.Ce() ? i.De() : null), 
            // Skip to the next key (if there is one).
            r ? s.Ss(r.path.Dt()) : s.done();
        })).next((() => {
            // The rest of the keys are not in the cache. One case where `iterate`
            // above won't go through them is when the cache is empty.
            for (;r; ) n(r, null), r = i.Ce() ? i.De() : null;
        }));
    }
    Pc(t, e, n) {
        let s = Rn();
        const i = e.path.length + 1, r = {};
        if (n.isEqual(ct.min())) {
            // Documents are ordered by key, so we can use a prefix scan to narrow
            // down the documents we need to match the query against.
            const t = e.path.Dt();
            r.range = IDBKeyRange.lowerBound(t);
        } else {
            // Execute an index-free query and filter by read time. This is safe
            // since all document changes to queries that have a
            // lastLimboFreeSnapshotVersion (`sinceReadTime`) have a read time set.
            const t = e.path.Dt(), s = $i(n);
            r.range = IDBKeyRange.lowerBound([ t, s ], 
            /* open= */ !0), r.index = gi.collectionReadTimeIndex;
        }
        return Tr(t).Fs(r, ((t, n, r) => {
            // The query is actually returning any path that starts with the query
            // path prefix which may include documents in subcollections. For
            // example, a query on 'rooms' will return rooms/abc/messages/xyx but we
            // shouldn't match it. Fix this by discarding rows with document keys
            // more than one segment longer than the query path.
            if (t.length !== i) return;
            const o = Mi(this.fe, n);
            e.path.vt(o.key.path) ? o instanceof qt && pe(e, o) && (s = s.Ie(o.key, o)) : r.done();
        })).next((() => s));
    }
    lc(t) {
        return new wr(this, !!t && t.yc);
    }
    dc(t) {
        return this.getMetadata(t).next((t => t.byteSize));
    }
    getMetadata(t) {
        return Er(t).get(Vi.key).next((t => (g(!!t), t)));
    }
    wc(t, e) {
        return Er(t).put(Vi.key, e);
    }
    /**
     * Decodes `remoteDoc` and returns the document (or null, if the document
     * corresponds to the format used for sentinel deletes).
     */    Ec(t) {
        if (t) {
            const e = Mi(this.fe, t);
            return e instanceof Ut && e.version.isEqual(ct.min()) ? null : e;
        }
        return null;
    }
}

/**
 * Creates a new IndexedDbRemoteDocumentCache.
 *
 * @param serializer - The document serializer.
 * @param indexManager - The query indexes that need to be maintained.
 */
/**
 * Handles the details of adding and updating documents in the IndexedDbRemoteDocumentCache.
 *
 * Unlike the MemoryRemoteDocumentChangeBuffer, the IndexedDb implementation computes the size
 * delta for all submitted changes. This avoids having to re-read all documents from IndexedDb
 * when we apply the changes.
 */
class wr extends Ys {
    /**
     * @param documentCache - The IndexedDbRemoteDocumentCache to apply the changes to.
     * @param trackRemovals - Whether to create sentinel deletes that can be tracked by
     * `getNewDocumentChanges()`.
     */
    constructor(t, e) {
        super(), this.gc = t, this.yc = e, 
        // A map of document sizes prior to applying the changes in this buffer.
        this.Vc = new Js((t => t.toString()), ((t, e) => t.isEqual(e)));
    }
    yr(t) {
        const e = [];
        let n = 0, s = new wn(((t, e) => v(t.Ct(), e.Ct())));
        return this._r.forEach(((i, r) => {
            const o = this.Vc.get(i);
            if (r.Ir) {
                const c = Li(this.gc.fe, r.Ir, this.wr(i));
                s = s.add(i.path.yt());
                const a = er(c);
                n += a - o, e.push(this.gc.Er(t, i, c));
            } else if (n -= o, this.yc) {
                // In order to track removals, we store a "sentinel delete" in the
                // RemoteDocumentCache. This entry is represented by a NoDocument
                // with a version of 0 and ignored by `maybeDecodeDocument()` but
                // preserved in `getNewDocumentChanges()`.
                const n = Li(this.gc.fe, new Ut(i, ct.min()), this.wr(i));
                e.push(this.gc.Er(t, i, n));
            } else e.push(this.gc.mr(t, i));
        })), s.forEach((n => {
            e.push(this.gc.To.fo(t, n));
        })), e.push(this.gc.updateMetadata(t, n)), gs.hs(e);
    }
    Rr(t, e) {
        // Record the size of everything we load from the cache so we can compute a delta later.
        return this.gc.Tc(t, e).next((t => null === t ? (this.Vc.set(e, 0), null) : (this.Vc.set(e, t.size), 
        t.Ir)));
    }
    Pr(t, e) {
        // Record the size of everything we load from the cache so we can compute
        // a delta later.
        return this.gc.mc(t, e).next((({Ac: t, Rc: e}) => (
        // Note: `getAllFromCache` returns two maps instead of a single map from
        // keys to `DocumentSizeEntry`s. This is to allow returning the
        // `NullableMaybeDocumentMap` directly, without a conversion.
        e.forEach(((t, e) => {
            this.Vc.set(t, e);
        })), t)));
    }
}

function Er(t) {
    return Oi(t, Vi.store);
}

/**
 * Helper to get a typed SimpleDbStore for the remoteDocuments object store.
 */ function Tr(t) {
    return Oi(t, gi.store);
}

function Ir(t) {
    return t.path.Dt();
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** Performs database creation and schema upgrades. */ class mr {
    constructor(t) {
        this.fe = t;
    }
    /**
     * Performs database creation and schema upgrades.
     *
     * Note that in production, this method is only ever used to upgrade the schema
     * to SCHEMA_VERSION. Different values of toVersion are only used for testing
     * and local feature development.
     */    Ps(t, e, n, s) {
        g(n < s && n >= 0 && s <= 11);
        const i = new Vs("createOrUpgrade", e);
        n < 1 && s >= 1 && (function(t) {
            t.createObjectStore(Ii.store);
        }(t), function(t) {
            t.createObjectStore(mi.store, {
                keyPath: mi.keyPath
            });
            t.createObjectStore(Ai.store, {
                keyPath: Ai.keyPath,
                autoIncrement: !0
            }).createIndex(Ai.userMutationsIndex, Ai.userMutationsKeyPath, {
                unique: !0
            }), t.createObjectStore(Ri.store);
        }
        /**
 * Upgrade function to migrate the 'mutations' store from V1 to V3. Loads
 * and rewrites all data.
 */ (t), Ar(t), function(t) {
            t.createObjectStore(gi.store);
        }(t));
        // Migration 2 to populate the targetGlobal object no longer needed since
        // migration 3 unconditionally clears it.
                let r = gs.resolve();
        return n < 3 && s >= 3 && (
        // Brand new clients don't need to drop and recreate--only clients that
        // potentially have corrupt data.
        0 !== n && (!function(t) {
            t.deleteObjectStore(bi.store), t.deleteObjectStore(pi.store), t.deleteObjectStore(vi.store);
        }(t), Ar(t)), r = r.next((() => 
        /**
 * Creates the target global singleton row.
 *
 * @param txn - The version upgrade transaction for indexeddb
 */
        function(t) {
            const e = t.store(vi.store), n = new vi(
            /*highestTargetId=*/ 0, 
            /*lastListenSequenceNumber=*/ 0, ct.min().Tt(), 
            /*targetCount=*/ 0);
            return e.put(vi.key, n);
        }
        /**
 * Creates indices on the RemoteDocuments store used for both multi-tab
 * and Index-Free queries.
 */ (i)))), n < 4 && s >= 4 && (0 !== n && (
        // Schema version 3 uses auto-generated keys to generate globally unique
        // mutation batch IDs (this was previously ensured internally by the
        // client). To migrate to the new schema, we have to read all mutations
        // and write them back out. We preserve the existing batch IDs to guarantee
        // consistency with other object stores. Any further mutation batch IDs will
        // be auto-generated.
        r = r.next((() => function(t, e) {
            return e.store(Ai.store).Ds().next((n => {
                t.deleteObjectStore(Ai.store);
                t.createObjectStore(Ai.store, {
                    keyPath: Ai.keyPath,
                    autoIncrement: !0
                }).createIndex(Ai.userMutationsIndex, Ai.userMutationsKeyPath, {
                    unique: !0
                });
                const s = e.store(Ai.store), i = n.map((t => s.put(t)));
                return gs.hs(i);
            }));
        }(t, i)))), r = r.next((() => {
            !function(t) {
                t.createObjectStore(Di.store, {
                    keyPath: Di.keyPath
                });
            }(t);
        }))), n < 5 && s >= 5 && (r = r.next((() => this.bc(i)))), n < 6 && s >= 6 && (r = r.next((() => (function(t) {
            t.createObjectStore(Vi.store);
        }(t), this.vc(i))))), n < 7 && s >= 7 && (r = r.next((() => this.Sc(i)))), n < 8 && s >= 8 && (r = r.next((() => this.Dc(t, i)))), 
        n < 9 && s >= 9 && (r = r.next((() => {
            // Multi-Tab used to manage its own changelog, but this has been moved
            // to the DbRemoteDocument object store itself. Since the previous change
            // log only contained transient data, we can drop its object store.
            !function(t) {
                t.objectStoreNames.contains("remoteDocumentChanges") && t.deleteObjectStore("remoteDocumentChanges");
            }(t), function(t) {
                const e = t.objectStore(gi.store);
                e.createIndex(gi.readTimeIndex, gi.readTimeIndexPath, {
                    unique: !1
                }), e.createIndex(gi.collectionReadTimeIndex, gi.collectionReadTimeIndexPath, {
                    unique: !1
                });
            }(e);
        }))), n < 10 && s >= 10 && (r = r.next((() => this.Cc(i)))), n < 11 && s >= 11 && (r = r.next((() => {
            !function(t) {
                t.createObjectStore(Ci.store, {
                    keyPath: Ci.keyPath
                });
            }(t), function(t) {
                t.createObjectStore(xi.store, {
                    keyPath: xi.keyPath
                });
            }
            /**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (t);
        }))), r;
    }
    vc(t) {
        let e = 0;
        return t.store(gi.store).Fs(((t, n) => {
            e += er(n);
        })).next((() => {
            const n = new Vi(e);
            return t.store(Vi.store).put(Vi.key, n);
        }));
    }
    bc(t) {
        const e = t.store(mi.store), n = t.store(Ai.store);
        return e.Ds().next((e => gs.forEach(e, (e => {
            const s = IDBKeyRange.bound([ e.userId, -1 ], [ e.userId, e.lastAcknowledgedBatchId ]);
            return n.Ds(Ai.userMutationsIndex, s).next((n => gs.forEach(n, (n => {
                g(n.userId === e.userId);
                const s = Qi(this.fe, n);
                return tr(t, e.userId, s).next((() => {}));
            }))));
        }))));
    }
    /**
     * Ensures that every document in the remote document cache has a corresponding sentinel row
     * with a sequence number. Missing rows are given the most recently used sequence number.
     */    Sc(t) {
        const e = t.store(bi.store), n = t.store(gi.store);
        return t.store(vi.store).get(vi.key).next((t => {
            const s = [];
            return n.Fs(((n, i) => {
                const r = new ut(n), o = function(t) {
                    return [ 0, fi(t) ];
                }(r);
                s.push(e.get(o).next((n => n ? gs.resolve() : (n => e.put(new bi(0, fi(n), t.highestListenSequenceNumber)))(r))));
            })).next((() => gs.hs(s)));
        }));
    }
    Dc(t, e) {
        // Create the index.
        t.createObjectStore(Si.store, {
            keyPath: Si.keyPath
        });
        const n = e.store(Si.store), s = new Yi, i = t => {
            if (s.add(t)) {
                const e = t.Vt(), s = t.yt();
                return n.put({
                    collectionId: e,
                    parent: fi(s)
                });
            }
        };
        // Helper to add an index entry iff we haven't already written it.
                // Index existing remote documents.
        return e.store(gi.store).Fs({
            Ns: !0
        }, ((t, e) => {
            const n = new ut(t);
            return i(n.yt());
        })).next((() => e.store(Ri.store).Fs({
            Ns: !0
        }, (([t, e, n], s) => {
            const r = Ei(e);
            return i(r.yt());
        }))));
    }
    Cc(t) {
        const e = t.store(pi.store);
        return e.Fs(((t, n) => {
            const s = Ki(n), i = Wi(this.fe, s);
            return e.put(i);
        }));
    }
}

function Ar(t) {
    t.createObjectStore(bi.store, {
        keyPath: bi.keyPath
    }).createIndex(bi.documentTargetsIndex, bi.documentTargetsKeyPath, {
        unique: !0
    });
    // NOTE: This is unique only because the TargetId is the suffix.
    t.createObjectStore(pi.store, {
        keyPath: pi.keyPath
    }).createIndex(pi.queryTargetsIndexName, pi.queryTargetsKeyPath, {
        unique: !0
    }), t.createObjectStore(vi.store);
}

const Rr = "Failed to obtain exclusive access to the persistence layer. To allow shared access, multi-tab synchronization has to be enabled in all tabs. If you are using `experimentalForceOwningTab:true`, make sure that only one tab has persistence enabled at any given time.";

/**
 * Oldest acceptable age in milliseconds for client metadata before the client
 * is considered inactive and its associated data is garbage collected.
 */
/**
 * An IndexedDB-backed instance of Persistence. Data is stored persistently
 * across sessions.
 *
 * On Web only, the Firestore SDKs support shared access to its persistence
 * layer. This allows multiple browser tabs to read and write to IndexedDb and
 * to synchronize state even without network connectivity. Shared access is
 * currently optional and not enabled unless all clients invoke
 * `enablePersistence()` with `{synchronizeTabs:true}`.
 *
 * In multi-tab mode, if multiple clients are active at the same time, the SDK
 * will designate one client as the “primary client”. An effort is made to pick
 * a visible, network-connected and active client, and this client is
 * responsible for letting other clients know about its presence. The primary
 * client writes a unique client-generated identifier (the client ID) to
 * IndexedDb’s “owner” store every 4 seconds. If the primary client fails to
 * update this entry, another client can acquire the lease and take over as
 * primary.
 *
 * Some persistence operations in the SDK are designated as primary-client only
 * operations. This includes the acknowledgment of mutations and all updates of
 * remote documents. The effects of these operations are written to persistence
 * and then broadcast to other tabs via LocalStorage (see
 * `WebStorageSharedClientState`), which then refresh their state from
 * persistence.
 *
 * Similarly, the primary client listens to notifications sent by secondary
 * clients to discover persistence changes written by secondary clients, such as
 * the addition of new mutations and query targets.
 *
 * If multi-tab is not enabled and another tab already obtained the primary
 * lease, IndexedDbPersistence enters a failed state and all subsequent
 * operations will automatically fail.
 *
 * Additionally, there is an optimization so that when a tab is closed, the
 * primary lease is released immediately (this is especially important to make
 * sure that a refreshed tab is able to immediately re-acquire the primary
 * lease). Unfortunately, IndexedDB cannot be reliably used in window.unload
 * since it is an asynchronous API. So in addition to attempting to give up the
 * lease, the leaseholder writes its client ID to a "zombiedClient" entry in
 * LocalStorage which acts as an indicator that another tab should go ahead and
 * take the primary lease immediately regardless of the current lease timestamp.
 *
 * TODO(b/114226234): Remove `synchronizeTabs` section when multi-tab is no
 * longer optional.
 */
class Pr {
    constructor(
    /**
     * Whether to synchronize the in-memory state of multiple tabs and share
     * access to local persistence.
     */
    t, e, n, s, i, r, o, c, a, 
    /**
     * If set to true, forcefully obtains database access. Existing tabs will
     * no longer be able to access IndexedDB.
     */
    u) {
        if (this.allowTabSynchronization = t, this.persistenceKey = e, this.clientId = n, 
        this.js = i, this.window = r, this.document = o, this.xc = a, this.Nc = u, this.Fc = null, 
        this.Oc = !1, this.isPrimary = !1, this.networkEnabled = !0, 
        /** Our window.unload handler, if registered. */
        this.kc = null, this.inForeground = !1, 
        /** Our 'visibilitychange' listener if registered. */
        this.Mc = null, 
        /** The client metadata refresh task. */
        this.Lc = null, 
        /** The last time we garbage collected the client metadata object store. */
        this.$c = Number.NEGATIVE_INFINITY, 
        /** A listener to notify on primary state changes. */
        this.Bc = t => Promise.resolve(), !Pr.G()) throw new N(x.UNIMPLEMENTED, "This platform is either missing IndexedDB or is known to have an incomplete implementation. Offline persistence has been disabled.");
        this.Io = new _r(this, s), this.qc = e + "main", this.fe = new ki(c), this.Uc = new ps(this.qc, 11, new mr(this.fe)), 
        this.Qc = new ar(this.Io, this.fe), this.To = new Xi, this.Kc = function(t, e) {
            return new dr(t, e);
        }
        /**
 * Returns the set of documents that have changed since the specified read
 * time.
 */
        // PORTING NOTE: This is only used for multi-tab synchronization.
        (this.fe, this.To), this.Wc = new Gi, this.window && this.window.localStorage ? this.jc = this.window.localStorage : (this.jc = null, 
        !1 === u && A("IndexedDbPersistence", "LocalStorage is unavailable. As a result, persistence may not work reliably. In particular enablePersistence() could fail immediately after refreshing the page."));
    }
    /**
     * Attempt to start IndexedDb persistence.
     *
     * @returns Whether persistence was enabled.
     */    start() {
        // NOTE: This is expected to fail sometimes (in the case of another tab
        // already having the persistence lock), so it's the first thing we should
        // do.
        return this.Gc().then((() => {
            if (!this.isPrimary && !this.allowTabSynchronization) 
            // Fail `start()` if `synchronizeTabs` is disabled and we cannot
            // obtain the primary lease.
            throw new N(x.FAILED_PRECONDITION, Rr);
            return this.zc(), this.Hc(), this.Jc(), this.runTransaction("getHighestListenSequenceNumber", "readonly", (t => this.Qc.jo(t)));
        })).then((t => {
            this.Fc = new Ws(t, this.xc);
        })).then((() => {
            this.Oc = !0;
        })).catch((t => (this.Uc && this.Uc.close(), Promise.reject(t))));
    }
    /**
     * Registers a listener that gets called when the primary state of the
     * instance changes. Upon registering, this listener is invoked immediately
     * with the current primary state.
     *
     * PORTING NOTE: This is only used for Web multi-tab.
     */    Yc(t) {
        return this.Bc = async e => {
            if (this.Nr) return t(e);
        }, t(this.isPrimary);
    }
    /**
     * Registers a listener that gets called when the database receives a
     * version change event indicating that it has deleted.
     *
     * PORTING NOTE: This is only used for Web multi-tab.
     */    Xc(t) {
        this.Uc.gs((async e => {
            // Check if an attempt is made to delete IndexedDB.
            null === e.newVersion && await t();
        }));
    }
    /**
     * Adjusts the current network state in the client's metadata, potentially
     * affecting the primary lease.
     *
     * PORTING NOTE: This is only used for Web multi-tab.
     */    Zc(t) {
        this.networkEnabled !== t && (this.networkEnabled = t, 
        // Schedule a primary lease refresh for immediate execution. The eventual
        // lease update will be propagated via `primaryStateListener`.
        this.js.Ws((async () => {
            this.Nr && await this.Gc();
        })));
    }
    /**
     * Updates the client metadata in IndexedDb and attempts to either obtain or
     * extend the primary lease for the local client. Asynchronously notifies the
     * primary state listener if the client either newly obtained or released its
     * primary lease.
     */    Gc() {
        return this.runTransaction("updateClientMetadataAndTryBecomePrimary", "readwrite", (t => gr(t).put(new Di(this.clientId, Date.now(), this.networkEnabled, this.inForeground)).next((() => {
            if (this.isPrimary) return this.ta(t).next((t => {
                t || (this.isPrimary = !1, this.js.ea((() => this.Bc(!1))));
            }));
        })).next((() => this.na(t))).next((e => this.isPrimary && !e ? this.sa(t).next((() => !1)) : !!e && this.ia(t).next((() => !0)))))).catch((t => {
            if (Ss(t)) 
            // Proceed with the existing state. Any subsequent access to
            // IndexedDB will verify the lease.
            return m("IndexedDbPersistence", "Failed to extend owner lease: ", t), this.isPrimary;
            if (!this.allowTabSynchronization) throw t;
            return m("IndexedDbPersistence", "Releasing owner lease after error during lease refresh", t), 
            /* isPrimary= */ !1;
        })).then((t => {
            this.isPrimary !== t && this.js.ea((() => this.Bc(t))), this.isPrimary = t;
        }));
    }
    ta(t) {
        return yr(t).get(Ii.key).next((t => gs.resolve(this.ra(t))));
    }
    oa(t) {
        return gr(t).delete(this.clientId);
    }
    /**
     * If the garbage collection threshold has passed, prunes the
     * RemoteDocumentChanges and the ClientMetadata store based on the last update
     * time of all clients.
     */    async ca() {
        if (this.isPrimary && !this.aa(this.$c, 18e5)) {
            this.$c = Date.now();
            const t = await this.runTransaction("maybeGarbageCollectMultiClientState", "readwrite-primary", (t => {
                const e = Oi(t, Di.store);
                return e.Ds().next((t => {
                    const n = this.ua(t, 18e5), s = t.filter((t => -1 === n.indexOf(t)));
                    // Delete metadata for clients that are no longer considered active.
                    return gs.forEach(s, (t => e.delete(t.clientId))).next((() => s));
                }));
            })).catch((() => []));
            // Delete potential leftover entries that may continue to mark the
            // inactive clients as zombied in LocalStorage.
            // Ideally we'd delete the IndexedDb and LocalStorage zombie entries for
            // the client atomically, but we can't. So we opt to delete the IndexedDb
            // entries first to avoid potentially reviving a zombied client.
                        if (this.jc) for (const e of t) this.jc.removeItem(this.ha(e.clientId));
        }
    }
    /**
     * Schedules a recurring timer to update the client metadata and to either
     * extend or acquire the primary lease if the client is eligible.
     */    Jc() {
        this.Lc = this.js.ni("client_metadata_refresh" /* ClientMetadataRefresh */ , 4e3, (() => this.Gc().then((() => this.ca())).then((() => this.Jc()))));
    }
    /** Checks whether `client` is the local client. */    ra(t) {
        return !!t && t.ownerId === this.clientId;
    }
    /**
     * Evaluate the state of all active clients and determine whether the local
     * client is or can act as the holder of the primary lease. Returns whether
     * the client is eligible for the lease, but does not actually acquire it.
     * May return 'false' even if there is no active leaseholder and another
     * (foreground) client should become leaseholder instead.
     */    na(t) {
        if (this.Nc) return gs.resolve(!0);
        return yr(t).get(Ii.key).next((e => {
            // A client is eligible for the primary lease if:
            // - its network is enabled and the client's tab is in the foreground.
            // - its network is enabled and no other client's tab is in the
            //   foreground.
            // - every clients network is disabled and the client's tab is in the
            //   foreground.
            // - every clients network is disabled and no other client's tab is in
            //   the foreground.
            // - the `forceOwningTab` setting was passed in.
            if (null !== e && this.aa(e.leaseTimestampMs, 5e3) && !this.la(e.ownerId)) {
                if (this.ra(e) && this.networkEnabled) return !0;
                if (!this.ra(e)) {
                    if (!e.allowTabSynchronization) 
                    // Fail the `canActAsPrimary` check if the current leaseholder has
                    // not opted into multi-tab synchronization. If this happens at
                    // client startup, we reject the Promise returned by
                    // `enablePersistence()` and the user can continue to use Firestore
                    // with in-memory persistence.
                    // If this fails during a lease refresh, we will instead block the
                    // AsyncQueue from executing further operations. Note that this is
                    // acceptable since mixing & matching different `synchronizeTabs`
                    // settings is not supported.
                    // TODO(b/114226234): Remove this check when `synchronizeTabs` can
                    // no longer be turned off.
                    throw new N(x.FAILED_PRECONDITION, Rr);
                    return !1;
                }
            }
            return !(!this.networkEnabled || !this.inForeground) || gr(t).Ds().next((t => void 0 === this.ua(t, 5e3).find((t => {
                if (this.clientId !== t.clientId) {
                    const e = !this.networkEnabled && t.networkEnabled, n = !this.inForeground && t.inForeground, s = this.networkEnabled === t.networkEnabled;
                    if (e || n && s) return !0;
                }
                return !1;
            }))));
        })).next((t => (this.isPrimary !== t && m("IndexedDbPersistence", `Client ${t ? "is" : "is not"} eligible for a primary lease.`), 
        t)));
    }
    async $() {
        // The shutdown() operations are idempotent and can be called even when
        // start() aborted (e.g. because it couldn't acquire the persistence lease).
        this.Oc = !1, this._a(), this.Lc && (this.Lc.cancel(), this.Lc = null), this.fa(), 
        this.da(), 
        // Use `SimpleDb.runTransaction` directly to avoid failing if another tab
        // has obtained the primary lease.
        await this.Uc.runTransaction("shutdown", "readwrite", [ Ii.store, Di.store ], (t => {
            const e = new Fi(t, Ws.Zi);
            return this.sa(e).next((() => this.oa(e)));
        })), this.Uc.close(), 
        // Remove the entry marking the client as zombied from LocalStorage since
        // we successfully deleted its metadata from IndexedDb.
        this.wa();
    }
    /**
     * Returns clients that are not zombied and have an updateTime within the
     * provided threshold.
     */    ua(t, e) {
        return t.filter((t => this.aa(t.updateTimeMs, e) && !this.la(t.clientId)));
    }
    /**
     * Returns the IDs of the clients that are currently active. If multi-tab
     * is not supported, returns an array that only contains the local client's
     * ID.
     *
     * PORTING NOTE: This is only used for Web multi-tab.
     */    Ea() {
        return this.runTransaction("getActiveClients", "readonly", (t => gr(t).Ds().next((t => this.ua(t, 18e5).map((t => t.clientId))))));
    }
    get Nr() {
        return this.Oc;
    }
    Ta(t) {
        return nr.Ao(t, this.fe, this.To, this.Io);
    }
    ac() {
        return this.Qc;
    }
    _c() {
        return this.Kc;
    }
    Ia() {
        return this.To;
    }
    ma() {
        return this.Wc;
    }
    runTransaction(t, e, n) {
        m("IndexedDbPersistence", "Starting transaction:", t);
        const s = "readonly" === e ? "readonly" : "readwrite";
        let i;
        // Do all transactions as readwrite against all object stores, since we
        // are the only reader/writer.
                return this.Uc.runTransaction(t, s, Ni, (s => (i = new Fi(s, this.Fc ? this.Fc.next() : Ws.Zi), 
        "readwrite-primary" === e ? this.ta(i).next((t => !!t || this.na(i))).next((e => {
            if (!e) throw A(`Failed to obtain primary lease for action '${t}'.`), this.isPrimary = !1, 
            this.js.ea((() => this.Bc(!1))), new N(x.FAILED_PRECONDITION, qs);
            return n(i);
        })).next((t => this.ia(i).next((() => t)))) : this.Aa(i).next((() => n(i)))))).then((t => (i.ki(), 
        t)));
    }
    /**
     * Verifies that the current tab is the primary leaseholder or alternatively
     * that the leaseholder has opted into multi-tab synchronization.
     */
    // TODO(b/114226234): Remove this check when `synchronizeTabs` can no longer
    // be turned off.
    Aa(t) {
        return yr(t).get(Ii.key).next((t => {
            if (null !== t && this.aa(t.leaseTimestampMs, 5e3) && !this.la(t.ownerId) && !this.ra(t) && !(this.Nc || this.allowTabSynchronization && t.allowTabSynchronization)) throw new N(x.FAILED_PRECONDITION, Rr);
        }));
    }
    /**
     * Obtains or extends the new primary lease for the local client. This
     * method does not verify that the client is eligible for this lease.
     */    ia(t) {
        const e = new Ii(this.clientId, this.allowTabSynchronization, Date.now());
        return yr(t).put(Ii.key, e);
    }
    static G() {
        return ps.G();
    }
    /** Checks the primary lease and removes it if we are the current primary. */    sa(t) {
        const e = yr(t);
        return e.get(Ii.key).next((t => this.ra(t) ? (m("IndexedDbPersistence", "Releasing primary lease."), 
        e.delete(Ii.key)) : gs.resolve()));
    }
    /** Verifies that `updateTimeMs` is within `maxAgeMs`. */    aa(t, e) {
        const n = Date.now();
        return !(t < n - e) && (!(t > n) || (A(`Detected an update time that is in the future: ${t} > ${n}`), 
        !1));
    }
    zc() {
        null !== this.document && "function" == typeof this.document.addEventListener && (this.Mc = () => {
            this.js.Ws((() => (this.inForeground = "visible" === this.document.visibilityState, 
            this.Gc())));
        }, this.document.addEventListener("visibilitychange", this.Mc), this.inForeground = "visible" === this.document.visibilityState);
    }
    fa() {
        this.Mc && (this.document.removeEventListener("visibilitychange", this.Mc), this.Mc = null);
    }
    /**
     * Attaches a window.unload handler that will synchronously write our
     * clientId to a "zombie client id" location in LocalStorage. This can be used
     * by tabs trying to acquire the primary lease to determine that the lease
     * is no longer valid even if the timestamp is recent. This is particularly
     * important for the refresh case (so the tab correctly re-acquires the
     * primary lease). LocalStorage is used for this rather than IndexedDb because
     * it is a synchronous API and so can be used reliably from  an unload
     * handler.
     */    Hc() {
        var t;
        "function" == typeof (null === (t = this.window) || void 0 === t ? void 0 : t.addEventListener) && (this.kc = () => {
            // Note: In theory, this should be scheduled on the AsyncQueue since it
            // accesses internal state. We execute this code directly during shutdown
            // to make sure it gets a chance to run.
            this._a(), this.js.Ws((() => this.$()));
        }, this.window.addEventListener("unload", this.kc));
    }
    da() {
        this.kc && (this.window.removeEventListener("unload", this.kc), this.kc = null);
    }
    /**
     * Returns whether a client is "zombied" based on its LocalStorage entry.
     * Clients become zombied when their tab closes without running all of the
     * cleanup logic in `shutdown()`.
     */    la(t) {
        var e;
        try {
            const n = null !== (null === (e = this.jc) || void 0 === e ? void 0 : e.getItem(this.ha(t)));
            return m("IndexedDbPersistence", `Client '${t}' ${n ? "is" : "is not"} zombied in LocalStorage`), 
            n;
        } catch (t) {
            // Gracefully handle if LocalStorage isn't working.
            return A("IndexedDbPersistence", "Failed to get zombied client id.", t), !1;
        }
    }
    /**
     * Record client as zombied (a client that had its tab closed). Zombied
     * clients are ignored during primary tab selection.
     */    _a() {
        if (this.jc) try {
            this.jc.setItem(this.ha(this.clientId), String(Date.now()));
        } catch (t) {
            // Gracefully handle if LocalStorage isn't available / working.
            A("Failed to set zombie client id.", t);
        }
    }
    /** Removes the zombied client entry if it exists. */    wa() {
        if (this.jc) try {
            this.jc.removeItem(this.ha(this.clientId));
        } catch (t) {
            // Ignore
        }
    }
    ha(t) {
        return `firestore_zombie_${this.persistenceKey}_${t}`;
    }
}

/**
 * Helper to get a typed SimpleDbStore for the primary client object store.
 */ function yr(t) {
    return Oi(t, Ii.store);
}

/**
 * Helper to get a typed SimpleDbStore for the client metadata object store.
 */ function gr(t) {
    return Oi(t, Di.store);
}

/**
 * Generates a string used as a prefix when storing data in IndexedDB and
 * LocalStorage.
 */ function Vr(t, e) {
    // Use two different prefix formats:
    //   * firestore / persistenceKey / projectID . databaseID / ...
    //   * firestore / persistenceKey / projectID / ...
    // projectIDs are DNS-compatible names and cannot contain dots
    // so there's no danger of collisions.
    let n = t.projectId;
    return t.M || (n += "." + t.database), "firestore/" + e + "/" + n + "/";
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A readonly view of the local state of all documents we're tracking (i.e. we
 * have a cached version in remoteDocumentCache or local mutations for the
 * document). The view is computed by applying the mutations in the
 * MutationQueue to the RemoteDocumentCache.
 */
class pr {
    constructor(t, e, n) {
        this.Kc = t, this.Ra = e, this.To = n;
    }
    /**
     * Get the local view of the document identified by `key`.
     *
     * @returns Local view of the document or null if we don't have any cached
     * state for it.
     */    Pa(t, e) {
        return this.Ra.So(t, e).next((n => this.ya(t, e, n)));
    }
    /** Internal version of `getDocument` that allows reusing batches. */    ya(t, e, n) {
        return this.Kc.Ar(t, e).next((t => {
            for (const s of n) t = s.ir(e, t);
            return t;
        }));
    }
    // Returns the view of the given `docs` as they would appear after applying
    // all mutations in the given `batches`.
    ga(t, e, n) {
        let s = mn();
        return e.forEach(((t, e) => {
            for (const s of n) e = s.ir(t, e);
            s = s.Ie(t, e);
        })), s;
    }
    /**
     * Gets the local view of the documents identified by `keys`.
     *
     * If we don't have cached state for a document in `keys`, a NoDocument will
     * be stored for that key in the resulting set.
     */    Va(t, e) {
        return this.Kc.getEntries(t, e).next((e => this.pa(t, e)));
    }
    /**
     * Similar to `getDocuments`, but creates the local view from the given
     * `baseDocs` without retrieving documents from the local store.
     */    pa(t, e) {
        return this.Ra.Do(t, e).next((n => {
            const s = this.ga(t, e, n);
            let i = In();
            return s.forEach(((t, e) => {
                // TODO(http://b/32275378): Don't conflate missing / deleted.
                e || (e = new Ut(t, ct.min())), i = i.Ie(t, e);
            })), i;
        }));
    }
    /**
     * Performs a query against the local view of all documents.
     *
     * @param transaction - The persistence transaction.
     * @param query - The query to match documents against.
     * @param sinceReadTime - If not set to SnapshotVersion.min(), return only
     *     documents that have been read since this snapshot version (exclusive).
     */    Pc(t, e, n) {
        /**
 * Returns whether the query matches a single document by path (rather than a
 * collection).
 */
        return function(t) {
            return _t.qt(t.path) && null === t.collectionGroup && 0 === t.filters.length;
        }(e) ? this.ba(t, e.path) : me(e) ? this.va(t, e, n) : this.Sa(t, e, n);
    }
    ba(t, e) {
        // Just do a simple document lookup.
        return this.Pa(t, new _t(e)).next((t => {
            let e = Rn();
            return t instanceof qt && (e = e.Ie(t.key, t)), e;
        }));
    }
    va(t, e, n) {
        const s = e.collectionGroup;
        let i = Rn();
        return this.To.wo(t, s).next((r => gs.forEach(r, (r => {
            const o = function(t, e) {
                return new _e(e, 
                /*collectionGroup=*/ null, t.ne.slice(), t.filters.slice(), t.limit, t.limitType, t.startAt, t.endAt);
            }
            /**
 * Returns true if this query does not specify any query constraints that
 * could remove results.
 */ (e, r.child(s));
            return this.Sa(t, o, n).next((t => {
                t.forEach(((t, e) => {
                    i = i.Ie(t, e);
                }));
            }));
        })).next((() => i))));
    }
    Sa(t, e, n) {
        // Query the remote documents and overlay mutations.
        let s, i;
        return this.Kc.Pc(t, e, n).next((n => (s = n, this.Ra.xo(t, e)))).next((e => (i = e, 
        this.Da(t, i, s).next((t => {
            s = t;
            for (const t of i) for (const e of t.mutations) {
                const n = e.key, i = s.get(n), r = Xe(e, i, i, t.er);
                s = r instanceof qt ? s.Ie(n, r) : s.remove(n);
            }
        }))))).next((() => (
        // Finally, filter out any documents that don't actually match
        // the query.
        s.forEach(((t, n) => {
            pe(e, n) || (s = s.remove(t));
        })), s)));
    }
    Da(t, e, n) {
        let s = Vn();
        for (const t of e) for (const e of t.mutations) e instanceof sn && null === n.get(e.key) && (s = s.add(e.key));
        let i = n;
        return this.Kc.getEntries(t, s).next((t => (t.forEach(((t, e) => {
            null !== e && e instanceof qt && (i = i.Ie(t, e));
        })), i)));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A set of changes to what documents are currently in view and out of view for
 * a given query. These changes are sent to the LocalStore by the View (via
 * the SyncEngine) and are used to pin / unpin documents as appropriate.
 */ class br {
    constructor(t, e, n, s) {
        this.targetId = t, this.fromCache = e, this.Ca = n, this.xa = s;
    }
    static Na(t, e) {
        let n = Vn(), s = Vn();
        for (const t of e.docChanges) switch (t.type) {
          case 0 /* Added */ :
            n = n.add(t.doc.key);
            break;

          case 1 /* Removed */ :
            s = s.add(t.doc.key);
 // do nothing
                }
        return new br(t, e.fromCache, n, s);
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A query engine that takes advantage of the target document mapping in the
 * QueryCache. Query execution is optimized by only reading the documents that
 * previously matched a query plus any documents that were edited after the
 * query was last listened to.
 *
 * There are some cases when this optimization is not guaranteed to produce
 * the same results as full collection scans. In these cases, query
 * processing falls back to full scans. These cases are:
 *
 * - Limit queries where a document that matched the query previously no longer
 *   matches the query.
 *
 * - Limit queries where a document edit may cause the document to sort below
 *   another document that is in the local cache.
 *
 * - Queries that have never been CURRENT or free of limbo documents.
 */ class vr {
    /** Sets the document view to query against. */
    Fa(t) {
        this.Oa = t;
    }
    /** Returns all local documents matching the specified query. */    Pc(t, n, s, i) {
        // Queries that match all documents don't benefit from using
        // key-based lookups. It is more efficient to scan all documents in a
        // collection, rather than to perform individual lookups.
        return function(t) {
            return 0 === t.filters.length && null === t.limit && null == t.startAt && null == t.endAt && (0 === t.ne.length || 1 === t.ne.length && t.ne[0].field.Ot());
        }(n) || s.isEqual(ct.min()) ? this.ka(t, n) : this.Oa.Va(t, i).next((r => {
            const o = this.Ma(n, r);
            return (we(n) || Ee(n)) && this.La(n.limitType, o, i, s) ? this.ka(t, n) : (I() <= e.DEBUG && m("QueryEngine", "Re-using previous result from %s to execute query: %s", s.toString(), Ve(n)), 
            this.Oa.Pc(t, n, s).next((t => (
            // We merge `previousResults` into `updateResults`, since
            // `updateResults` is already a DocumentMap. If a document is
            // contained in both lists, then its contents are the same.
            o.forEach((e => {
                t = t.Ie(e.key, e);
            })), t))));
        }));
        // Queries that have never seen a snapshot without limbo free documents
        // should also be run as a full collection scan.
        }
    /** Applies the query filter and sorting to the provided documents.  */    Ma(t, e) {
        // Sort the documents and re-apply the query filter since previously
        // matching documents do not necessarily still match the query.
        let n = new wn(be(t));
        return e.forEach(((e, s) => {
            s instanceof qt && pe(t, s) && (n = n.add(s));
        })), n;
    }
    /**
     * Determines if a limit query needs to be refilled from cache, making it
     * ineligible for index-free execution.
     *
     * @param sortedPreviousResults - The documents that matched the query when it
     * was last synchronized, sorted by the query's comparator.
     * @param remoteKeys - The document keys that matched the query at the last
     * snapshot.
     * @param limboFreeSnapshotVersion - The version of the snapshot when the
     * query was last synchronized.
     */    La(t, e, n, s) {
        // The query needs to be refilled if a previously matching document no
        // longer matches.
        if (n.size !== e.size) return !0;
        // Limit queries are not eligible for index-free query execution if there is
        // a potential that an older document from cache now sorts before a document
        // that was previously part of the limit. This, however, can only happen if
        // the document at the edge of the limit goes out of limit.
        // If a document that is not the limit boundary sorts differently,
        // the boundary of the limit itself did not change and documents from cache
        // will continue to be "rejected" by this boundary. Therefore, we can ignore
        // any modifications that don't affect the last document.
                const i = "F" /* First */ === t ? e.last() : e.first();
        return !!i && (i.hasPendingWrites || i.version.u(s) > 0);
    }
    ka(t, n) {
        return I() <= e.DEBUG && m("QueryEngine", "Using full collection scan to execute query:", Ve(n)), 
        this.Oa.Pc(t, n, ct.min());
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Implements `LocalStore` interface.
 *
 * Note: some field defined in this class might have public access level, but
 * the class is not exported so they are only accessible from this module.
 * This is useful to implement optional features (like bundles) in free
 * functions, such that they are tree-shakeable.
 */
class Sr {
    constructor(
    /** Manages our in-memory or durable persistence. */
    t, e, n, s) {
        this.persistence = t, this.$a = e, this.fe = s, 
        /**
         * Maps a targetID to data about its target.
         *
         * PORTING NOTE: We are using an immutable data structure on Web to make re-runs
         * of `applyRemoteEvent()` idempotent.
         */
        this.Ba = new _n(v), 
        /** Maps a target to its targetID. */
        // TODO(wuandy): Evaluate if TargetId can be part of Target.
        this.qa = new Js((t => jt(t)), zt), 
        /**
         * The read time of the last entry processed by `getNewDocumentChanges()`.
         *
         * PORTING NOTE: This is only used for multi-tab synchronization.
         */
        this.Ua = ct.min(), this.Ra = t.Ta(n), this.Qa = t._c(), this.Qc = t.ac(), this.Ka = new pr(this.Qa, this.Ra, this.persistence.Ia()), 
        this.Wc = t.ma(), this.$a.Fa(this.Ka);
    }
    Fr(t) {
        return this.persistence.runTransaction("Collect garbage", "readwrite-primary", (e => t.Ur(e, this.Ba)));
    }
}

function Dr(
/** Manages our in-memory or durable persistence. */
t, e, n, s) {
    return new Sr(t, e, n, s);
}

/**
 * Tells the LocalStore that the currently authenticated user has changed.
 *
 * In response the local store switches the mutation queue to the new user and
 * returns any resulting document changes.
 */
// PORTING NOTE: Android and iOS only return the documents affected by the
// change.
async function Cr(t, e) {
    const n = V(t);
    let s = n.Ra, i = n.Ka;
    const r = await n.persistence.runTransaction("Handle user change", "readonly", (t => {
        // Swap out the mutation queue, grabbing the pending mutation batches
        // before and after.
        let r;
        return n.Ra.vo(t).next((o => (r = o, s = n.persistence.Ta(e), 
        // Recreate our LocalDocumentsView using the new
        // MutationQueue.
        i = new pr(n.Qa, s, n.persistence.Ia()), s.vo(t)))).next((e => {
            const n = [], s = [];
            // Union the old/new changed keys.
            let o = Vn();
            for (const t of r) {
                n.push(t.batchId);
                for (const e of t.mutations) o = o.add(e.key);
            }
            for (const t of e) {
                s.push(t.batchId);
                for (const e of t.mutations) o = o.add(e.key);
            }
            // Return the set of all (potentially) changed documents and the list
            // of mutation batch IDs that were affected by change.
                        return i.Va(t, o).next((t => ({
                Wa: t,
                ja: n,
                Ga: s
            })));
        }));
    }));
    return n.Ra = s, n.Ka = i, n.$a.Fa(n.Ka), r;
}

/* Accepts locally generated Mutations and commit them to storage. */
/**
 * Acknowledges the given batch.
 *
 * On the happy path when a batch is acknowledged, the local store will
 *
 *  + remove the batch from the mutation queue;
 *  + apply the changes to the remote document cache;
 *  + recalculate the latency compensated view implied by those changes (there
 *    may be mutations in the queue that affect the documents but haven't been
 *    acknowledged yet); and
 *  + give the changed documents back the sync engine
 *
 * @returns The resulting (modified) documents.
 */
function xr(t, e) {
    const n = V(t);
    return n.persistence.runTransaction("Acknowledge batch", "readwrite-primary", (t => {
        const s = e.batch.keys(), i = n.Qa.lc({
            yc: !0
        });
        return function(t, e, n, s) {
            const i = n.batch, r = i.keys();
            let o = gs.resolve();
            return r.forEach((t => {
                o = o.next((() => s.Ar(e, t))).next((e => {
                    let r = e;
                    const o = n.ar.get(t);
                    g(null !== o), (!r || r.version.u(o) < 0) && (r = i.nr(t, r, n), r && 
                    // We use the commitVersion as the readTime rather than the
                    // document's updateTime since the updateTime is not advanced
                    // for updates that do not modify the underlying document.
                    s.Er(r, n.cr));
                }));
            })), o.next((() => t.Ra.No(e, i)));
        }
        /** Returns the local view of the documents affected by a mutation batch. */
        // PORTING NOTE: Multi-Tab only.
        (n, t, e, i).next((() => i.apply(t))).next((() => n.Ra.ko(t))).next((() => n.Ka.Va(t, s)));
    }));
}

/**
 * Removes mutations from the MutationQueue for the specified batch;
 * LocalDocuments will be recalculated.
 *
 * @returns The resulting modified documents.
 */
/**
 * Returns the last consistent snapshot processed (used by the RemoteStore to
 * determine whether to buffer incoming snapshots from the backend).
 */
function Nr(t) {
    const e = V(t);
    return e.persistence.runTransaction("Get last remote snapshot version", "readonly", (t => e.Qc.Wo(t)));
}

/**
 * Updates the "ground-state" (remote) documents. We assume that the remote
 * event reflects any write batches that have been acknowledged or rejected
 * (i.e. we do not re-apply local mutations to updates from this event).
 *
 * LocalDocuments are re-calculated if there are remaining mutations in the
 * queue.
 */ function Fr(t, e) {
    const n = V(t), s = e.oe;
    let i = n.Ba;
    return n.persistence.runTransaction("Apply remote event", "readwrite-primary", (t => {
        const r = n.Qa.lc({
            yc: !0
        });
        // Reset newTargetDataByTargetMap in case this transaction gets re-run.
                i = n.Ba;
        const o = [];
        e.rn.forEach(((e, r) => {
            const c = i.get(r);
            if (!c) return;
            // Only update the remote keys if the target is still active. This
            // ensures that we can persist the updated target data along with
            // the updated assignment.
                        o.push(n.Qc.ic(t, e.dn, r).next((() => n.Qc.nc(t, e._n, r))));
            const a = e.resumeToken;
            // Update the resume token if the change includes one.
                        if (a.o() > 0) {
                const u = c.ae(a, s).ce(t.oo);
                i = i.Ie(r, u), 
                // Update the target data if there are target changes (or if
                // sufficient time has passed since the last update).
                /**
 * Returns true if the newTargetData should be persisted during an update of
 * an active target. TargetData should always be persisted when a target is
 * being released and should not call this function.
 *
 * While the target is active, TargetData updates can be omitted when nothing
 * about the target has changed except metadata like the resume token or
 * snapshot version. Occasionally it's worth the extra write to prevent these
 * values from getting too stale after a crash, but this doesn't have to be
 * too frequent.
 */
                function(t, e, n) {
                    // Always persist target data if we don't already have a resume token.
                    if (g(e.resumeToken.o() > 0), 0 === t.resumeToken.o()) return !0;
                    // Don't allow resume token changes to be buffered indefinitely. This
                    // allows us to be reasonably up-to-date after a crash and avoids needing
                    // to loop over all active queries on shutdown. Especially in the browser
                    // we may not get time to do anything interesting while the current tab is
                    // closing.
                                        if (e.oe.Et() - t.oe.Et() >= 3e8) return !0;
                    // Otherwise if the only thing that has changed about a target is its resume
                    // token it's not worth persisting. Note that the RemoteStore keeps an
                    // in-memory view of the currently active targets which includes the current
                    // resume token, so stream failure or user changes will still use an
                    // up-to-date resume token regardless of what we do here.
                                        return n._n.size + n.fn.size + n.dn.size > 0;
                }
                /**
 * Notifies local store of the changed views to locally pin documents.
 */ (c, u, e) && o.push(n.Qc.Yo(t, u));
            }
        }));
        let c = In();
        // HACK: The only reason we allow a null snapshot version is so that we
        // can synthesize remote events when we get permission denied errors while
        // trying to resolve the state of a locally cached document that is in
        // limbo.
        if (e.cn.forEach(((s, i) => {
            e.an.has(s) && o.push(n.persistence.Io.fc(t, s));
        })), 
        // Each loop iteration only affects its "own" doc, so it's safe to get all the remote
        // documents in advance in a single call.
        o.push(Or(t, r, e.cn, s, void 0).next((t => {
            c = t;
        }))), !s.isEqual(ct.min())) {
            const e = n.Qc.Wo(t).next((e => n.Qc.Go(t, t.oo, s)));
            o.push(e);
        }
        return gs.hs(o).next((() => r.apply(t))).next((() => n.Ka.pa(t, c)));
    })).then((t => (n.Ba = i, t)));
}

/**
 * Populates document change buffer with documents from backend or a bundle.
 * Returns the document changes resulting from applying those documents.
 *
 * @param txn - Transaction to use to read existing documents from storage.
 * @param documentBuffer - Document buffer to collect the resulted changes to be
 *        applied to storage.
 * @param documents - Documents to be applied.
 * @param globalVersion - A `SnapshotVersion` representing the read time if all
 *        documents have the same read time.
 * @param documentVersions - A DocumentKey-to-SnapshotVersion map if documents
 *        have their own read time.
 *
 * Note: this function will use `documentVersions` if it is defined;
 * when it is not defined, resorts to `globalVersion`.
 */ function Or(t, e, n, s, 
// TODO(wuandy): We could add `readTime` to MaybeDocument instead to remove
// this parameter.
i) {
    let r = Vn();
    return n.forEach((t => r = r.add(t))), e.getEntries(t, r).next((t => {
        let r = In();
        return n.forEach(((n, o) => {
            const c = t.get(n), a = (null == i ? void 0 : i.get(n)) || s;
            // Note: The order of the steps below is important, since we want
            // to ensure that rejected limbo resolutions (which fabricate
            // NoDocuments with SnapshotVersion.min()) never add documents to
            // cache.
            o instanceof Ut && o.version.isEqual(ct.min()) ? (
            // NoDocuments with SnapshotVersion.min() are used in manufactured
            // events. We remove these documents from cache since we lost
            // access.
            e.mr(n, a), r = r.Ie(n, o)) : null == c || o.version.u(c.version) > 0 || 0 === o.version.u(c.version) && c.hasPendingWrites ? (e.Er(o, a), 
            r = r.Ie(n, o)) : m("LocalStore", "Ignoring outdated watch update for ", n, ". Current version:", c.version, " Watch version:", o.version);
        })), r;
    }));
}

/**
 * Gets the mutation batch after the passed in batchId in the mutation queue
 * or null if empty.
 * @param afterBatchId - If provided, the batch to search after.
 * @returns The next mutation or null if there wasn't one.
 */
function kr(t, e) {
    const n = V(t);
    return n.persistence.runTransaction("Get next mutation batch", "readonly", (t => (void 0 === e && (e = -1), 
    n.Ra.po(t, e))));
}

/**
 * Reads the current value of a Document with a given key or null if not
 * found - used for testing.
 */
/**
 * Assigns the given target an internal ID so that its results can be pinned so
 * they don't get GC'd. A target must be allocated in the local store before
 * the store can be used to manage its view.
 *
 * Allocating an already allocated `Target` will return the existing `TargetData`
 * for that `Target`.
 */
function Mr(t, e) {
    const n = V(t);
    return n.persistence.runTransaction("Allocate target", "readwrite", (t => {
        let s;
        return n.Qc.ec(t, e).next((i => i ? (
        // This target has been listened to previously, so reuse the
        // previous targetID.
        // TODO(mcg): freshen last accessed date?
        s = i, gs.resolve(s)) : n.Qc.Uo(t).next((i => (s = new Se(e, i, 0 /* Listen */ , t.oo), 
        n.Qc.zo(t, s).next((() => s)))))));
    })).then((t => {
        // If Multi-Tab is enabled, the existing target data may be newer than
        // the in-memory data
        const s = n.Ba.get(t.targetId);
        return (null === s || t.oe.u(s.oe) > 0) && (n.Ba = n.Ba.Ie(t.targetId, t), n.qa.set(e, t.targetId)), 
        t;
    }));
}

/**
 * Returns the TargetData as seen by the LocalStore, including updates that may
 * have not yet been persisted to the TargetCache.
 */
// Visible for testing.
/**
 * Unpins all the documents associated with the given target. If
 * `keepPersistedTargetData` is set to false and Eager GC enabled, the method
 * directly removes the associated target data from the target cache.
 *
 * Releasing a non-existing `Target` is a no-op.
 */
// PORTING NOTE: `keepPersistedTargetData` is multi-tab only.
async function Lr(t, e, n) {
    const s = V(t), i = s.Ba.get(e), r = n ? "readwrite" : "readwrite-primary";
    try {
        n || await s.persistence.runTransaction("Release target", r, (t => s.persistence.Io.removeTarget(t, i)));
    } catch (t) {
        if (!Ss(t)) throw t;
        // All `releaseTarget` does is record the final metadata state for the
        // target, but we've been recording this periodically during target
        // activity. If we lose this write this could cause a very slight
        // difference in the order of target deletion during GC, but we
        // don't define exact LRU semantics so this is acceptable.
        m("LocalStore", `Failed to update sequence numbers for target ${e}: ${t}`);
    }
    s.Ba = s.Ba.remove(e), s.qa.delete(i.target);
}

/**
 * Runs the specified query against the local store and returns the results,
 * potentially taking advantage of query data from previous executions (such
 * as the set of remote keys).
 *
 * @param usePreviousResults - Whether results from previous executions can
 * be used to optimize this query execution.
 */ function $r(t, e, n) {
    const s = V(t);
    let i = ct.min(), r = Vn();
    return s.persistence.runTransaction("Execute query", "readonly", (t => function(t, e, n) {
        const s = V(t), i = s.qa.get(n);
        return void 0 !== i ? gs.resolve(s.Ba.get(i)) : s.Qc.ec(e, n);
    }(s, t, Re(e)).next((e => {
        if (e) return i = e.lastLimboFreeSnapshotVersion, s.Qc.oc(t, e.targetId).next((t => {
            r = t;
        }));
    })).next((() => s.$a.Pc(t, e, n ? i : ct.min(), n ? r : Vn()))).next((t => ({
        documents: t,
        za: r
    })))));
}

// PORTING NOTE: Multi-Tab only.
function Br(t, e) {
    const n = V(t), s = V(n.Qc), i = n.Ba.get(e);
    return i ? Promise.resolve(i.target) : n.persistence.runTransaction("Get target data", "readonly", (t => s.Zn(t, e).next((t => t ? t.target : null))));
}

/**
 * Returns the set of documents that have been updated since the last call.
 * If this is the first call, returns the set of changes since client
 * initialization. Further invocations will return document that have changed
 * since the prior call.
 */
// PORTING NOTE: Multi-Tab only.
function qr(t) {
    const e = V(t);
    return e.persistence.runTransaction("Get new document changes", "readonly", (t => function(t, e, n) {
        const s = V(t);
        let i = In(), r = $i(n);
        const o = Tr(e), c = IDBKeyRange.lowerBound(r, !0);
        return o.Fs({
            index: gi.readTimeIndex,
            range: c
        }, ((t, e) => {
            // Unlike `getEntry()` and others, `getNewDocumentChanges()` parses
            // the documents directly since we want to keep sentinel deletes.
            const n = Mi(s.fe, e);
            i = i.Ie(n.key, n), r = e.readTime;
        })).next((() => ({
            tr: i,
            readTime: Bi(r)
        })));
    }
    /**
 * Returns the read time of the most recently read document in the cache, or
 * SnapshotVersion.min() if not available.
 */
    // PORTING NOTE: This is only used for multi-tab synchronization.
    (e.Qa, t, e.Ua))).then((({tr: t, readTime: n}) => (e.Ua = n, t)));
}

/**
 * Reads the newest document change from persistence and moves the internal
 * synchronization marker forward so that calls to `getNewDocumentChanges()`
 * only return changes that happened after client initialization.
 */
// PORTING NOTE: Multi-Tab only.
async function Ur(t) {
    const e = V(t);
    return e.persistence.runTransaction("Synchronize last document change read time", "readonly", (t => function(t) {
        const e = Tr(t);
        // If there are no existing entries, we return SnapshotVersion.min().
                let n = ct.min();
        return e.Fs({
            index: gi.readTimeIndex,
            reverse: !0
        }, ((t, e, s) => {
            e.readTime && (n = Bi(e.readTime)), s.done();
        })).next((() => n));
    }(t))).then((t => {
        e.Ua = t;
    }));
}

/**
 * Creates a new target using the given bundle name, which will be used to
 * hold the keys of all documents from the bundle in query-document mappings.
 * This ensures that the loaded documents do not get garbage collected
 * right away.
 */
/**
 * Applies the documents from a bundle to the "ground-state" (remote)
 * documents.
 *
 * LocalDocuments are re-calculated if there are remaining mutations in the
 * queue.
 */
async function Qr(t, e, n, s) {
    const i = V(t);
    let r = Vn(), o = In(), c = yn();
    for (const t of n) {
        const n = e.Ha(t.metadata.name);
        t.document && (r = r.add(n)), o = o.Ie(n, e.Ja(t)), c = c.Ie(n, e.Ya(t.metadata.readTime));
    }
    const a = i.Qa.lc({
        yc: !0
    }), u = await Mr(i, function(t) {
        // It is OK that the path used for the query is not valid, because this will
        // not be read and queried.
        return Re(de(ut.xt("__bundle__/docs/" + t)));
    }(s));
    // Allocates a target to hold all document keys from the bundle, such that
    // they will not get garbage collected right away.
        return i.persistence.runTransaction("Apply bundle documents", "readwrite", (t => Or(t, a, o, ct.min(), c).next((e => (a.apply(t), 
    e))).next((e => i.Qc.Zo(t, u.targetId).next((() => i.Qc.nc(t, r, u.targetId))).next((() => i.Ka.pa(t, e)))))));
}

/**
 * Returns a promise of a boolean to indicate if the given bundle has already
 * been loaded and the create time is newer than the current loading bundle.
 */
/**
 * Saves the given `NamedQuery` to local persistence.
 */
async function Kr(t, e, n = Vn()) {
    // Allocate a target for the named query such that it can be resumed
    // from associated read time if users use it to listen.
    // NOTE: this also means if no corresponding target exists, the new target
    // will remain active and will not get collected, unless users happen to
    // unlisten the query somehow.
    const s = await Mr(t, Re(ji(e.bundledQuery))), i = V(t);
    return i.persistence.runTransaction("Save named query", "readwrite", (t => {
        const r = jn(e.readTime);
        // Simply save the query itself if it is older than what the SDK already
        // has.
                if (s.oe.u(r) >= 0) return i.Wc.lo(t, e);
        // Update existing target data because the query from the bundle is newer.
                const o = s.ae(C.h, r);
        return i.Ba = i.Ba.Ie(o.targetId, o), i.Qc.Yo(t, o).next((() => i.Qc.Zo(t, s.targetId))).next((() => i.Qc.nc(t, n, s.targetId))).next((() => i.Wc.lo(t, e)));
    }));
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class Wr {
    constructor(t) {
        this.fe = t, this.Xa = new Map, this.Za = new Map;
    }
    ao(t, e) {
        return gs.resolve(this.Xa.get(e));
    }
    uo(t, e) {
        /** Encodes a BundleMetadata proto object to a Bundle model object. */
        var n;
        return this.Xa.set(e.id, {
            id: (n = e).id,
            version: n.version,
            createTime: jn(n.createTime)
        }), gs.resolve();
    }
    ho(t, e) {
        return gs.resolve(this.Za.get(e));
    }
    lo(t, e) {
        return this.Za.set(e.name, function(t) {
            return {
                name: t.name,
                query: ji(t.bundledQuery),
                readTime: jn(t.readTime)
            };
        }(e)), gs.resolve();
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A collection of references to a document from some kind of numbered entity
 * (either a target ID or batch ID). As references are added to or removed from
 * the set corresponding events are emitted to a registered garbage collector.
 *
 * Each reference is represented by a DocumentReference object. Each of them
 * contains enough information to uniquely identify the reference. They are all
 * stored primarily in a set sorted by key. A document is considered garbage if
 * there's no references in that set (this can be efficiently checked thanks to
 * sorting by key).
 *
 * ReferenceSet also keeps a secondary set that contains references sorted by
 * IDs. This one is used to efficiently implement removal of all references by
 * some target ID.
 */ class jr {
    constructor() {
        // A set of outstanding references to a document sorted by key.
        this.tu = new wn(Gr.eu), 
        // A set of outstanding references to a document sorted by target id.
        this.nu = new wn(Gr.su);
    }
    /** Returns true if the reference set contains no references. */    bt() {
        return this.tu.bt();
    }
    /** Adds a reference to the given document key for the given ID. */    sc(t, e) {
        const n = new Gr(t, e);
        this.tu = this.tu.add(n), this.nu = this.nu.add(n);
    }
    /** Add references to the given document keys for the given ID. */    iu(t, e) {
        t.forEach((t => this.sc(t, e)));
    }
    /**
     * Removes a reference to the given document key for the given
     * ID.
     */    rc(t, e) {
        this.ru(new Gr(t, e));
    }
    ou(t, e) {
        t.forEach((t => this.rc(t, e)));
    }
    /**
     * Clears all references with a given ID. Calls removeRef() for each key
     * removed.
     */    cu(t) {
        const e = new _t(new ut([])), n = new Gr(e, t), s = new Gr(e, t + 1), i = [];
        return this.nu.Qe([ n, s ], (t => {
            this.ru(t), i.push(t.key);
        })), i;
    }
    au() {
        this.tu.forEach((t => this.ru(t)));
    }
    ru(t) {
        this.tu = this.tu.delete(t), this.nu = this.nu.delete(t);
    }
    uu(t) {
        const e = new _t(new ut([])), n = new Gr(e, t), s = new Gr(e, t + 1);
        let i = Vn();
        return this.nu.Qe([ n, s ], (t => {
            i = i.add(t.key);
        })), i;
    }
    Mo(t) {
        const e = new Gr(t, 0), n = this.tu.We(e);
        return null !== n && t.isEqual(n.key);
    }
}

class Gr {
    constructor(t, e) {
        this.key = t, this.hu = e;
    }
    /** Compare by key then by ID */    static eu(t, e) {
        return _t.At(t.key, e.key) || v(t.hu, e.hu);
    }
    /** Compare by ID then by key */    static su(t, e) {
        return v(t.hu, e.hu) || _t.At(t.key, e.key);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class zr {
    constructor(t, e) {
        this.To = t, this.Io = e, 
        /**
         * The set of all mutations that have been sent but not yet been applied to
         * the backend.
         */
        this.Ra = [], 
        /** Next value to use when assigning sequential IDs to each mutation batch. */
        this.lu = 1, 
        /** An ordered mapping between documents and the mutations batch IDs. */
        this._u = new wn(Gr.eu);
    }
    Ro(t) {
        return gs.resolve(0 === this.Ra.length);
    }
    Po(t, e, n, s) {
        const i = this.lu;
        if (this.lu++, this.Ra.length > 0) {
            this.Ra[this.Ra.length - 1];
        }
        const r = new zs(i, e, n, s);
        this.Ra.push(r);
        // Track references by document key and index collection parents.
        for (const e of s) this._u = this._u.add(new Gr(e.key, i)), this.To.fo(t, e.key.path.yt());
        return gs.resolve(r);
    }
    yo(t, e) {
        return gs.resolve(this.fu(e));
    }
    po(t, e) {
        const n = e + 1, s = this.du(n), i = s < 0 ? 0 : s;
        // The requested batchId may still be out of range so normalize it to the
        // start of the queue.
                return gs.resolve(this.Ra.length > i ? this.Ra[i] : null);
    }
    bo() {
        return gs.resolve(0 === this.Ra.length ? -1 : this.lu - 1);
    }
    vo(t) {
        return gs.resolve(this.Ra.slice());
    }
    So(t, e) {
        const n = new Gr(e, 0), s = new Gr(e, Number.POSITIVE_INFINITY), i = [];
        return this._u.Qe([ n, s ], (t => {
            const e = this.fu(t.hu);
            i.push(e);
        })), gs.resolve(i);
    }
    Do(t, e) {
        let n = new wn(v);
        return e.forEach((t => {
            const e = new Gr(t, 0), s = new Gr(t, Number.POSITIVE_INFINITY);
            this._u.Qe([ e, s ], (t => {
                n = n.add(t.hu);
            }));
        })), gs.resolve(this.wu(n));
    }
    xo(t, e) {
        // Use the query path as a prefix for testing if a document matches the
        // query.
        const n = e.path, s = n.length + 1;
        // Construct a document reference for actually scanning the index. Unlike
        // the prefix the document key in this reference must have an even number of
        // segments. The empty segment can be used a suffix of the query path
        // because it precedes all other segments in an ordered traversal.
        let i = n;
        _t.qt(i) || (i = i.child(""));
        const r = new Gr(new _t(i), 0);
        // Find unique batchIDs referenced by all documents potentially matching the
        // query.
                let o = new wn(v);
        return this._u.Ke((t => {
            const e = t.key.path;
            return !!n.vt(e) && (
            // Rows with document keys more than one segment longer than the query
            // path can't be matches. For example, a query on 'rooms' can't match
            // the document /rooms/abc/messages/xyx.
            // TODO(mcg): we'll need a different scanner when we implement
            // ancestor queries.
            e.length === s && (o = o.add(t.hu)), !0);
        }), r), gs.resolve(this.wu(o));
    }
    wu(t) {
        // Construct an array of matching batches, sorted by batchID to ensure that
        // multiple mutations affecting the same document key are applied in order.
        const e = [];
        return t.forEach((t => {
            const n = this.fu(t);
            null !== n && e.push(n);
        })), e;
    }
    No(t, e) {
        g(0 === this.Eu(e.batchId, "removed")), this.Ra.shift();
        let n = this._u;
        return gs.forEach(e.mutations, (s => {
            const i = new Gr(s.key, e.batchId);
            return n = n.delete(i), this.Io.Oo(t, s.key);
        })).next((() => {
            this._u = n;
        }));
    }
    Fo(t) {
        // No-op since the memory mutation queue does not maintain a separate cache.
    }
    Mo(t, e) {
        const n = new Gr(e, 0), s = this._u.We(n);
        return gs.resolve(e.isEqual(s && s.key));
    }
    ko(t) {
        return this.Ra.length, gs.resolve();
    }
    /**
     * Finds the index of the given batchId in the mutation queue and asserts that
     * the resulting index is within the bounds of the queue.
     *
     * @param batchId - The batchId to search for
     * @param action - A description of what the caller is doing, phrased in passive
     * form (e.g. "acknowledged" in a routine that acknowledges batches).
     */    Eu(t, e) {
        return this.du(t);
    }
    /**
     * Finds the index of the given batchId in the mutation queue. This operation
     * is O(1).
     *
     * @returns The computed index of the batch with the given batchId, based on
     * the state of the queue. Note this index can be negative if the requested
     * batchId has already been remvoed from the queue or past the end of the
     * queue if the batchId is larger than the last added batch.
     */    du(t) {
        if (0 === this.Ra.length) 
        // As an index this is past the end of the queue
        return 0;
        // Examine the front of the queue to figure out the difference between the
        // batchId and indexes in the array. Note that since the queue is ordered
        // by batchId, if the first batch has a larger batchId then the requested
        // batchId doesn't exist in the queue.
                return t - this.Ra[0].batchId;
    }
    /**
     * A version of lookupMutationBatch that doesn't return a promise, this makes
     * other functions that uses this code easier to read and more efficent.
     */    fu(t) {
        const e = this.du(t);
        if (e < 0 || e >= this.Ra.length) return null;
        return this.Ra[e];
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * The memory-only RemoteDocumentCache for IndexedDb. To construct, invoke
 * `newMemoryRemoteDocumentCache()`.
 */
class Hr {
    /**
     * @param sizer - Used to assess the size of a document. For eager GC, this is
     * expected to just return 0 to avoid unnecessarily doing the work of
     * calculating the size.
     */
    constructor(t, e) {
        this.To = t, this.Tu = e, 
        /** Underlying cache of documents and their read times. */
        this.docs = new _n(_t.At), 
        /** Size of all cached documents. */
        this.size = 0;
    }
    /**
     * Adds the supplied entry to the cache and updates the cache size as appropriate.
     *
     * All calls of `addEntry`  are required to go through the RemoteDocumentChangeBuffer
     * returned by `newChangeBuffer()`.
     */    Er(t, e, n) {
        const s = e.key, i = this.docs.get(s), r = i ? i.size : 0, o = this.Tu(e);
        return this.docs = this.docs.Ie(s, {
            Ir: e,
            size: o,
            readTime: n
        }), this.size += o - r, this.To.fo(t, s.path.yt());
    }
    /**
     * Removes the specified entry from the cache and updates the cache size as appropriate.
     *
     * All calls of `removeEntry` are required to go through the RemoteDocumentChangeBuffer
     * returned by `newChangeBuffer()`.
     */    mr(t) {
        const e = this.docs.get(t);
        e && (this.docs = this.docs.remove(t), this.size -= e.size);
    }
    Ar(t, e) {
        const n = this.docs.get(e);
        return gs.resolve(n ? n.Ir : null);
    }
    getEntries(t, e) {
        let n = mn();
        return e.forEach((t => {
            const e = this.docs.get(t);
            n = n.Ie(t, e ? e.Ir : null);
        })), gs.resolve(n);
    }
    Pc(t, e, n) {
        let s = Rn();
        // Documents are ordered by key, so we can use a prefix scan to narrow down
        // the documents we need to match the query against.
                const i = new _t(e.path.child("")), r = this.docs.Ve(i);
        for (;r.Ce(); ) {
            const {key: t, value: {Ir: i, readTime: o}} = r.De();
            if (!e.path.vt(t.path)) break;
            o.u(n) <= 0 || i instanceof qt && pe(e, i) && (s = s.Ie(i.key, i));
        }
        return gs.resolve(s);
    }
    Iu(t, e) {
        return gs.forEach(this.docs, (t => e(t)));
    }
    lc(t) {
        // `trackRemovals` is ignores since the MemoryRemoteDocumentCache keeps
        // a separate changelog and does not need special handling for removals.
        return new Jr(this);
    }
    dc(t) {
        return gs.resolve(this.size);
    }
}

/**
 * Creates a new memory-only RemoteDocumentCache.
 *
 * @param indexManager - A class that manages collection group indices.
 * @param sizer - Used to assess the size of a document. For eager GC, this is
 * expected to just return 0 to avoid unnecessarily doing the work of
 * calculating the size.
 */
/**
 * Handles the details of adding and updating documents in the MemoryRemoteDocumentCache.
 */
class Jr extends Ys {
    constructor(t) {
        super(), this.gc = t;
    }
    yr(t) {
        const e = [];
        return this._r.forEach(((n, s) => {
            s && s.Ir ? e.push(this.gc.Er(t, s.Ir, this.wr(n))) : this.gc.mr(n);
        })), gs.hs(e);
    }
    Rr(t, e) {
        return this.gc.Ar(t, e);
    }
    Pr(t, e) {
        return this.gc.getEntries(t, e);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class Yr {
    constructor(t) {
        this.persistence = t, 
        /**
         * Maps a target to the data about that target
         */
        this.mu = new Js((t => jt(t)), zt), 
        /** The last received snapshot version. */
        this.lastRemoteSnapshotVersion = ct.min(), 
        /** The highest numbered target ID encountered. */
        this.highestTargetId = 0, 
        /** The highest sequence number encountered. */
        this.Au = 0, 
        /**
         * A ordered bidirectional mapping between documents and the remote target
         * IDs.
         */
        this.Ru = new jr, this.targetCount = 0, this.Pu = cr.Bo();
    }
    Un(t, e) {
        return this.mu.forEach(((t, n) => e(n))), gs.resolve();
    }
    Wo(t) {
        return gs.resolve(this.lastRemoteSnapshotVersion);
    }
    jo(t) {
        return gs.resolve(this.Au);
    }
    Uo(t) {
        return this.highestTargetId = this.Pu.next(), gs.resolve(this.highestTargetId);
    }
    Go(t, e, n) {
        return n && (this.lastRemoteSnapshotVersion = n), e > this.Au && (this.Au = e), 
        gs.resolve();
    }
    Ho(t) {
        this.mu.set(t.target, t);
        const e = t.targetId;
        e > this.highestTargetId && (this.Pu = new cr(e), this.highestTargetId = e), t.sequenceNumber > this.Au && (this.Au = t.sequenceNumber);
    }
    zo(t, e) {
        return this.Ho(e), this.targetCount += 1, gs.resolve();
    }
    Yo(t, e) {
        return this.Ho(e), gs.resolve();
    }
    Xo(t, e) {
        return this.mu.delete(e.target), this.Ru.cu(e.targetId), this.targetCount -= 1, 
        gs.resolve();
    }
    Br(t, e, n) {
        let s = 0;
        const i = [];
        return this.mu.forEach(((r, o) => {
            o.sequenceNumber <= e && null === n.get(o.targetId) && (this.mu.delete(r), i.push(this.Zo(t, o.targetId)), 
            s++);
        })), gs.hs(i).next((() => s));
    }
    tc(t) {
        return gs.resolve(this.targetCount);
    }
    ec(t, e) {
        const n = this.mu.get(e) || null;
        return gs.resolve(n);
    }
    nc(t, e, n) {
        return this.Ru.iu(e, n), gs.resolve();
    }
    ic(t, e, n) {
        this.Ru.ou(e, n);
        const s = this.persistence.Io, i = [];
        return s && e.forEach((e => {
            i.push(s.Oo(t, e));
        })), gs.hs(i);
    }
    Zo(t, e) {
        return this.Ru.cu(e), gs.resolve();
    }
    oc(t, e) {
        const n = this.Ru.uu(e);
        return gs.resolve(n);
    }
    Mo(t, e) {
        return gs.resolve(this.Ru.Mo(e));
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A memory-backed instance of Persistence. Data is stored only in RAM and
 * not persisted across sessions.
 */
class Xr {
    /**
     * The constructor accepts a factory for creating a reference delegate. This
     * allows both the delegate and this instance to have strong references to
     * each other without having nullable fields that would then need to be
     * checked or asserted on every access.
     */
    constructor(t, e) {
        this.yu = {}, this.Fc = new Ws(0), this.Oc = !1, this.Oc = !0, this.Io = t(this), 
        this.Qc = new Yr(this);
        this.To = new Ji, this.Kc = function(t, e) {
            return new Hr(t, e);
        }(this.To, (t => this.Io.gu(t))), this.fe = new ki(e), this.Wc = new Wr(this.fe);
    }
    start() {
        return Promise.resolve();
    }
    $() {
        // No durable state to ensure is closed on shutdown.
        return this.Oc = !1, Promise.resolve();
    }
    get Nr() {
        return this.Oc;
    }
    Xc() {
        // No op.
    }
    Zc() {
        // No op.
    }
    Ia() {
        return this.To;
    }
    Ta(t) {
        let e = this.yu[t.I()];
        return e || (e = new zr(this.To, this.Io), this.yu[t.I()] = e), e;
    }
    ac() {
        return this.Qc;
    }
    _c() {
        return this.Kc;
    }
    ma() {
        return this.Wc;
    }
    runTransaction(t, e, n) {
        m("MemoryPersistence", "Starting transaction:", t);
        const s = new Zr(this.Fc.next());
        return this.Io.Vu(), n(s).next((t => this.Io.pu(s).next((() => t)))).cs().then((t => (s.ki(), 
        t)));
    }
    bu(t, e) {
        return gs.ls(Object.values(this.yu).map((n => () => n.Mo(t, e))));
    }
}

/**
 * Memory persistence is not actually transactional, but future implementations
 * may have transaction-scoped state.
 */ class Zr extends Us {
    constructor(t) {
        super(), this.oo = t;
    }
}

class to {
    constructor(t) {
        this.persistence = t, 
        /** Tracks all documents that are active in Query views. */
        this.vu = new jr, 
        /** The list of documents that are potentially GCed after each transaction. */
        this.Su = null;
    }
    static Du(t) {
        return new to(t);
    }
    get Cu() {
        if (this.Su) return this.Su;
        throw y();
    }
    sc(t, e, n) {
        return this.vu.sc(n, e), this.Cu.delete(n.toString()), gs.resolve();
    }
    rc(t, e, n) {
        return this.vu.rc(n, e), this.Cu.add(n.toString()), gs.resolve();
    }
    Oo(t, e) {
        return this.Cu.add(e.toString()), gs.resolve();
    }
    removeTarget(t, e) {
        this.vu.cu(e.targetId).forEach((t => this.Cu.add(t.toString())));
        const n = this.persistence.ac();
        return n.oc(t, e.targetId).next((t => {
            t.forEach((t => this.Cu.add(t.toString())));
        })).next((() => n.Xo(t, e)));
    }
    Vu() {
        this.Su = new Set;
    }
    pu(t) {
        // Remove newly orphaned documents.
        const e = this.persistence._c().lc();
        return gs.forEach(this.Cu, (n => {
            const s = _t.Lt(n);
            return this.xu(t, s).next((t => {
                t || e.mr(s);
            }));
        })).next((() => (this.Su = null, e.apply(t))));
    }
    fc(t, e) {
        return this.xu(t, e).next((t => {
            t ? this.Cu.delete(e.toString()) : this.Cu.add(e.toString());
        }));
    }
    gu(t) {
        // For eager GC, we don't care about the document size, there are no size thresholds.
        return 0;
    }
    xu(t, e) {
        return gs.ls([ () => gs.resolve(this.vu.Mo(e)), () => this.persistence.ac().Mo(t, e), () => this.persistence.bu(t, e) ]);
    }
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// The format of the LocalStorage key that stores the client state is:
//     firestore_clients_<persistence_prefix>_<instance_key>
/** Assembles the key for a client state in WebStorage */
function eo(t, e) {
    return `firestore_clients_${t}_${e}`;
}

// The format of the WebStorage key that stores the mutation state is:
//     firestore_mutations_<persistence_prefix>_<batch_id>
//     (for unauthenticated users)
// or: firestore_mutations_<persistence_prefix>_<batch_id>_<user_uid>

// 'user_uid' is last to avoid needing to escape '_' characters that it might
// contain.
/** Assembles the key for a mutation batch in WebStorage */
function no(t, e, n) {
    let s = `firestore_mutations_${t}_${n}`;
    return e.T() && (s += "_" + e.uid), s;
}

// The format of the WebStorage key that stores a query target's metadata is:
//     firestore_targets_<persistence_prefix>_<target_id>
/** Assembles the key for a query state in WebStorage */
function so(t, e) {
    return `firestore_targets_${t}_${e}`;
}

// The WebStorage prefix that stores the primary tab's online state. The
// format of the key is:
//     firestore_online_state_<persistence_prefix>
/**
 * Holds the state of a mutation batch, including its user ID, batch ID and
 * whether the batch is 'pending', 'acknowledged' or 'rejected'.
 */
// Visible for testing
class io {
    constructor(t, e, n, s) {
        this.user = t, this.batchId = e, this.state = n, this.error = s;
    }
    /**
     * Parses a MutationMetadata from its JSON representation in WebStorage.
     * Logs a warning and returns null if the format of the data is not valid.
     */    static Nu(t, e, n) {
        const s = JSON.parse(n);
        let i = "object" == typeof s && -1 !== [ "pending", "acknowledged", "rejected" ].indexOf(s.state) && (void 0 === s.error || "object" == typeof s.error), r = void 0;
        return i && s.error && (i = "string" == typeof s.error.message && "string" == typeof s.error.code, 
        i && (r = new N(s.error.code, s.error.message))), i ? new io(t, e, s.state, r) : (A("SharedClientState", `Failed to parse mutation state for ID '${e}': ${n}`), 
        null);
    }
    Fu() {
        const t = {
            state: this.state,
            updateTimeMs: Date.now()
        };
        return this.error && (t.error = {
            code: this.error.code,
            message: this.error.message
        }), JSON.stringify(t);
    }
}

/**
 * Holds the state of a query target, including its target ID and whether the
 * target is 'not-current', 'current' or 'rejected'.
 */
// Visible for testing
class ro {
    constructor(t, e, n) {
        this.targetId = t, this.state = e, this.error = n;
    }
    /**
     * Parses a QueryTargetMetadata from its JSON representation in WebStorage.
     * Logs a warning and returns null if the format of the data is not valid.
     */    static Nu(t, e) {
        const n = JSON.parse(e);
        let s = "object" == typeof n && -1 !== [ "not-current", "current", "rejected" ].indexOf(n.state) && (void 0 === n.error || "object" == typeof n.error), i = void 0;
        return s && n.error && (s = "string" == typeof n.error.message && "string" == typeof n.error.code, 
        s && (i = new N(n.error.code, n.error.message))), s ? new ro(t, n.state, i) : (A("SharedClientState", `Failed to parse target state for ID '${t}': ${e}`), 
        null);
    }
    Fu() {
        const t = {
            state: this.state,
            updateTimeMs: Date.now()
        };
        return this.error && (t.error = {
            code: this.error.code,
            message: this.error.message
        }), JSON.stringify(t);
    }
}

/**
 * This class represents the immutable ClientState for a client read from
 * WebStorage, containing the list of active query targets.
 */ class oo {
    constructor(t, e) {
        this.clientId = t, this.activeTargetIds = e;
    }
    /**
     * Parses a RemoteClientState from the JSON representation in WebStorage.
     * Logs a warning and returns null if the format of the data is not valid.
     */    static Nu(t, e) {
        const n = JSON.parse(e);
        let s = "object" == typeof n && n.activeTargetIds instanceof Array, i = bn();
        for (let t = 0; s && t < n.activeTargetIds.length; ++t) s = Y(n.activeTargetIds[t]), 
        i = i.add(n.activeTargetIds[t]);
        return s ? new oo(t, i) : (A("SharedClientState", `Failed to parse client data for instance '${t}': ${e}`), 
        null);
    }
}

/**
 * This class represents the online state for all clients participating in
 * multi-tab. The online state is only written to by the primary client, and
 * used in secondary clients to update their query views.
 */ class co {
    constructor(t, e) {
        this.clientId = t, this.onlineState = e;
    }
    /**
     * Parses a SharedOnlineState from its JSON representation in WebStorage.
     * Logs a warning and returns null if the format of the data is not valid.
     */    static Nu(t) {
        const e = JSON.parse(t);
        return "object" == typeof e && -1 !== [ "Unknown", "Online", "Offline" ].indexOf(e.onlineState) && "string" == typeof e.clientId ? new co(e.clientId, e.onlineState) : (A("SharedClientState", "Failed to parse online state: " + t), 
        null);
    }
}

/**
 * Metadata state of the local client. Unlike `RemoteClientState`, this class is
 * mutable and keeps track of all pending mutations, which allows us to
 * update the range of pending mutation batch IDs as new mutations are added or
 * removed.
 *
 * The data in `LocalClientState` is not read from WebStorage and instead
 * updated via its instance methods. The updated state can be serialized via
 * `toWebStorageJSON()`.
 */
// Visible for testing.
class ao {
    constructor() {
        this.activeTargetIds = bn();
    }
    Ou(t) {
        this.activeTargetIds = this.activeTargetIds.add(t);
    }
    ku(t) {
        this.activeTargetIds = this.activeTargetIds.delete(t);
    }
    /**
     * Converts this entry into a JSON-encoded format we can use for WebStorage.
     * Does not encode `clientId` as it is part of the key in WebStorage.
     */    Fu() {
        const t = {
            activeTargetIds: this.activeTargetIds.Dt(),
            updateTimeMs: Date.now()
        };
        return JSON.stringify(t);
    }
}

/**
 * `WebStorageSharedClientState` uses WebStorage (window.localStorage) as the
 * backing store for the SharedClientState. It keeps track of all active
 * clients and supports modifications of the local client's data.
 */ class uo {
    constructor(t, e, n, s, i) {
        this.window = t, this.js = e, this.persistenceKey = n, this.Mu = s, this.Lu = null, 
        this.$u = null, this.Hi = null, this.Bu = this.qu.bind(this), this.Uu = new _n(v), 
        this.Nr = !1, 
        /**
         * Captures WebStorage events that occur before `start()` is called. These
         * events are replayed once `WebStorageSharedClientState` is started.
         */
        this.Qu = [];
        // Escape the special characters mentioned here:
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
        const r = n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        this.storage = this.window.localStorage, this.currentUser = i, this.Ku = eo(this.persistenceKey, this.Mu), 
        this.Wu = 
        /** Assembles the key for the current sequence number. */
        function(t) {
            return "firestore_sequence_number_" + t;
        }
        /**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (this.persistenceKey), this.Uu = this.Uu.Ie(this.Mu, new ao), this.ju = new RegExp(`^firestore_clients_${r}_([^_]*)$`), 
        this.Gu = new RegExp(`^firestore_mutations_${r}_(\\d+)(?:_(.*))?$`), this.zu = new RegExp(`^firestore_targets_${r}_(\\d+)$`), 
        this.Hu = 
        /** Assembles the key for the online state of the primary tab. */
        function(t) {
            return "firestore_online_state_" + t;
        }
        // The WebStorage prefix that plays as a event to indicate the remote documents
        // might have changed due to some secondary tabs loading a bundle.
        // format of the key is:
        //     firestore_bundle_loaded_<persistenceKey>
        (this.persistenceKey), this.Ju = function(t) {
            return "firestore_bundle_loaded_" + t;
        }
        // The WebStorage key prefix for the key that stores the last sequence number allocated. The key
        // looks like 'firestore_sequence_number_<persistence_prefix>'.
        (this.persistenceKey), 
        // Rather than adding the storage observer during start(), we add the
        // storage observer during initialization. This ensures that we collect
        // events before other components populate their initial state (during their
        // respective start() calls). Otherwise, we might for example miss a
        // mutation that is added after LocalStore's start() processed the existing
        // mutations but before we observe WebStorage events.
        this.window.addEventListener("storage", this.Bu);
    }
    /** Returns 'true' if WebStorage is available in the current environment. */    static G(t) {
        return !(!t || !t.localStorage);
    }
    async start() {
        // Retrieve the list of existing clients to backfill the data in
        // SharedClientState.
        const t = await this.Lu.Ea();
        for (const e of t) {
            if (e === this.Mu) continue;
            const t = this.getItem(eo(this.persistenceKey, e));
            if (t) {
                const n = oo.Nu(e, t);
                n && (this.Uu = this.Uu.Ie(n.clientId, n));
            }
        }
        this.Yu();
        // Check if there is an existing online state and call the callback handler
        // if applicable.
        const e = this.storage.getItem(this.Hu);
        if (e) {
            const t = this.Xu(e);
            t && this.Zu(t);
        }
        for (const t of this.Qu) this.qu(t);
        this.Qu = [], 
        // Register a window unload hook to remove the client metadata entry from
        // WebStorage even if `shutdown()` was not called.
        this.window.addEventListener("unload", (() => this.$())), this.Nr = !0;
    }
    Xi(t) {
        this.setItem(this.Wu, JSON.stringify(t));
    }
    th() {
        return this.eh(this.Uu);
    }
    nh(t) {
        let e = !1;
        return this.Uu.forEach(((n, s) => {
            s.activeTargetIds.has(t) && (e = !0);
        })), e;
    }
    sh(t) {
        this.ih(t, "pending");
    }
    rh(t, e, n) {
        this.ih(t, e, n), 
        // Once a final mutation result is observed by other clients, they no longer
        // access the mutation's metadata entry. Since WebStorage replays events
        // in order, it is safe to delete the entry right after updating it.
        this.oh(t);
    }
    ah(t) {
        let e = "not-current";
        // Lookup an existing query state if the target ID was already registered
        // by another tab
                if (this.nh(t)) {
            const n = this.storage.getItem(so(this.persistenceKey, t));
            if (n) {
                const s = ro.Nu(t, n);
                s && (e = s.state);
            }
        }
        return this.uh.Ou(t), this.Yu(), e;
    }
    hh(t) {
        this.uh.ku(t), this.Yu();
    }
    lh(t) {
        return this.uh.activeTargetIds.has(t);
    }
    _h(t) {
        this.removeItem(so(this.persistenceKey, t));
    }
    fh(t, e, n) {
        this.dh(t, e, n);
    }
    wh(t, e, n) {
        e.forEach((t => {
            this.oh(t);
        })), this.currentUser = t, n.forEach((t => {
            this.sh(t);
        }));
    }
    Eh(t) {
        this.Th(t);
    }
    Ih() {
        this.mh();
    }
    $() {
        this.Nr && (this.window.removeEventListener("storage", this.Bu), this.removeItem(this.Ku), 
        this.Nr = !1);
    }
    getItem(t) {
        const e = this.storage.getItem(t);
        return m("SharedClientState", "READ", t, e), e;
    }
    setItem(t, e) {
        m("SharedClientState", "SET", t, e), this.storage.setItem(t, e);
    }
    removeItem(t) {
        m("SharedClientState", "REMOVE", t), this.storage.removeItem(t);
    }
    qu(t) {
        // Note: The function is typed to take Event to be interface-compatible with
        // `Window.addEventListener`.
        const e = t;
        if (e.storageArea === this.storage) {
            if (m("SharedClientState", "EVENT", e.key, e.newValue), e.key === this.Ku) return void A("Received WebStorage notification for local change. Another client might have garbage-collected our state");
            this.js.ea((async () => {
                if (this.Nr) {
                    if (null !== e.key) if (this.ju.test(e.key)) {
                        if (null == e.newValue) {
                            const t = this.Ah(e.key);
                            return this.Rh(t, null);
                        }
                        {
                            const t = this.Ph(e.key, e.newValue);
                            if (t) return this.Rh(t.clientId, t);
                        }
                    } else if (this.Gu.test(e.key)) {
                        if (null !== e.newValue) {
                            const t = this.yh(e.key, e.newValue);
                            if (t) return this.gh(t);
                        }
                    } else if (this.zu.test(e.key)) {
                        if (null !== e.newValue) {
                            const t = this.Vh(e.key, e.newValue);
                            if (t) return this.ph(t);
                        }
                    } else if (e.key === this.Hu) {
                        if (null !== e.newValue) {
                            const t = this.Xu(e.newValue);
                            if (t) return this.Zu(t);
                        }
                    } else if (e.key === this.Wu) {
                        const t = function(t) {
                            let e = Ws.Zi;
                            if (null != t) try {
                                const n = JSON.parse(t);
                                g("number" == typeof n), e = n;
                            } catch (t) {
                                A("SharedClientState", "Failed to read sequence number from WebStorage", t);
                            }
                            return e;
                        }
                        /**
 * `MemorySharedClientState` is a simple implementation of SharedClientState for
 * clients using memory persistence. The state in this class remains fully
 * isolated and no synchronization is performed.
 */ (e.newValue);
                        t !== Ws.Zi && this.Hi(t);
                    } else if (e.key === this.Ju) return this.Lu.bh();
                } else this.Qu.push(e);
            }));
        }
    }
    get uh() {
        return this.Uu.get(this.Mu);
    }
    Yu() {
        this.setItem(this.Ku, this.uh.Fu());
    }
    ih(t, e, n) {
        const s = new io(this.currentUser, t, e, n), i = no(this.persistenceKey, this.currentUser, t);
        this.setItem(i, s.Fu());
    }
    oh(t) {
        const e = no(this.persistenceKey, this.currentUser, t);
        this.removeItem(e);
    }
    Th(t) {
        const e = {
            clientId: this.Mu,
            onlineState: t
        };
        this.storage.setItem(this.Hu, JSON.stringify(e));
    }
    dh(t, e, n) {
        const s = so(this.persistenceKey, t), i = new ro(t, e, n);
        this.setItem(s, i.Fu());
    }
    mh() {
        this.setItem(this.Ju, "value-not-used");
    }
    /**
     * Parses a client state key in WebStorage. Returns null if the key does not
     * match the expected key format.
     */    Ah(t) {
        const e = this.ju.exec(t);
        return e ? e[1] : null;
    }
    /**
     * Parses a client state in WebStorage. Returns 'null' if the value could not
     * be parsed.
     */    Ph(t, e) {
        const n = this.Ah(t);
        return oo.Nu(n, e);
    }
    /**
     * Parses a mutation batch state in WebStorage. Returns 'null' if the value
     * could not be parsed.
     */    yh(t, e) {
        const n = this.Gu.exec(t), s = Number(n[1]), i = void 0 !== n[2] ? n[2] : null;
        return io.Nu(new $(i), s, e);
    }
    /**
     * Parses a query target state from WebStorage. Returns 'null' if the value
     * could not be parsed.
     */    Vh(t, e) {
        const n = this.zu.exec(t), s = Number(n[1]);
        return ro.Nu(s, e);
    }
    /**
     * Parses an online state from WebStorage. Returns 'null' if the value
     * could not be parsed.
     */    Xu(t) {
        return co.Nu(t);
    }
    async gh(t) {
        if (t.user.uid === this.currentUser.uid) return this.Lu.Sh(t.batchId, t.state, t.error);
        m("SharedClientState", "Ignoring mutation for non-active user " + t.user.uid);
    }
    ph(t) {
        return this.Lu.Dh(t.targetId, t.state, t.error);
    }
    Rh(t, e) {
        const n = e ? this.Uu.Ie(t, e) : this.Uu.remove(t), s = this.eh(this.Uu), i = this.eh(n), r = [], o = [];
        return i.forEach((t => {
            s.has(t) || r.push(t);
        })), s.forEach((t => {
            i.has(t) || o.push(t);
        })), this.Lu.Ch(r, o).then((() => {
            this.Uu = n;
        }));
    }
    Zu(t) {
        // We check whether the client that wrote this online state is still active
        // by comparing its client ID to the list of clients kept active in
        // IndexedDb. If a client does not update their IndexedDb client state
        // within 5 seconds, it is considered inactive and we don't emit an online
        // state event.
        this.Uu.get(t.clientId) && this.$u(t.onlineState);
    }
    eh(t) {
        let e = bn();
        return t.forEach(((t, n) => {
            e = e.je(n.activeTargetIds);
        })), e;
    }
}

class ho {
    constructor() {
        this.xh = new ao, this.Nh = {}, this.$u = null, this.Hi = null;
    }
    sh(t) {
        // No op.
    }
    rh(t, e, n) {
        // No op.
    }
    ah(t) {
        return this.xh.Ou(t), this.Nh[t] || "not-current";
    }
    fh(t, e, n) {
        this.Nh[t] = e;
    }
    hh(t) {
        this.xh.ku(t);
    }
    lh(t) {
        return this.xh.activeTargetIds.has(t);
    }
    _h(t) {
        delete this.Nh[t];
    }
    th() {
        return this.xh.activeTargetIds;
    }
    nh(t) {
        return this.xh.activeTargetIds.has(t);
    }
    start() {
        return this.xh = new ao, Promise.resolve();
    }
    wh(t, e, n) {
        // No op.
    }
    Eh(t) {
        // No op.
    }
    $() {}
    Xi(t) {}
    Ih() {
        // No op.
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** The Platform's 'window' implementation or null if not available. */ function lo() {
    // `window` is not always available, e.g. in ReactNative and WebWorkers.
    // eslint-disable-next-line no-restricted-globals
    return "undefined" != typeof window ? window : null;
}

/** The Platform's 'document' implementation or null if not available. */ function _o() {
    // `document` is not always available, e.g. in ReactNative and WebWorkers.
    // eslint-disable-next-line no-restricted-globals
    return "undefined" != typeof document ? document : null;
}

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A component used by the RemoteStore to track the OnlineState (that is,
 * whether or not the client as a whole should be considered to be online or
 * offline), implementing the appropriate heuristics.
 *
 * In particular, when the client is trying to connect to the backend, we
 * allow up to MAX_WATCH_STREAM_FAILURES within ONLINE_STATE_TIMEOUT_MS for
 * a connection to succeed. If we have too many failures or the timeout elapses,
 * then we set the OnlineState to Offline, and the client will behave as if
 * it is offline (get()s will return cached data, etc.).
 */
class fo {
    constructor(t, e) {
        this.ks = t, this.$u = e, 
        /** The current OnlineState. */
        this.state = "Unknown" /* Unknown */ , 
        /**
         * A count of consecutive failures to open the stream. If it reaches the
         * maximum defined by MAX_WATCH_STREAM_FAILURES, we'll set the OnlineState to
         * Offline.
         */
        this.Fh = 0, 
        /**
         * A timer that elapses after ONLINE_STATE_TIMEOUT_MS, at which point we
         * transition from OnlineState.Unknown to OnlineState.Offline without waiting
         * for the stream to actually fail (MAX_WATCH_STREAM_FAILURES times).
         */
        this.Oh = null, 
        /**
         * Whether the client should log a warning message if it fails to connect to
         * the backend (initially true, cleared after a successful stream, or if we've
         * logged the message already).
         */
        this.kh = !0;
    }
    /**
     * Called by RemoteStore when a watch stream is started (including on each
     * backoff attempt).
     *
     * If this is the first attempt, it sets the OnlineState to Unknown and starts
     * the onlineStateTimer.
     */    Mh() {
        0 === this.Fh && (this.Lh("Unknown" /* Unknown */), this.Oh = this.ks.ni("online_state_timeout" /* OnlineStateTimeout */ , 1e4, (() => (this.Oh = null, 
        this.$h("Backend didn't respond within 10 seconds."), this.Lh("Offline" /* Offline */), 
        Promise.resolve()))));
    }
    /**
     * Updates our OnlineState as appropriate after the watch stream reports a
     * failure. The first failure moves us to the 'Unknown' state. We then may
     * allow multiple failures (based on MAX_WATCH_STREAM_FAILURES) before we
     * actually transition to the 'Offline' state.
     */    Bh(t) {
        "Online" /* Online */ === this.state ? this.Lh("Unknown" /* Unknown */) : (this.Fh++, 
        this.Fh >= 1 && (this.qh(), this.$h("Connection failed 1 times. Most recent error: " + t.toString()), 
        this.Lh("Offline" /* Offline */)));
    }
    /**
     * Explicitly sets the OnlineState to the specified state.
     *
     * Note that this resets our timers / failure counters, etc. used by our
     * Offline heuristics, so must not be used in place of
     * handleWatchStreamStart() and handleWatchStreamFailure().
     */    set(t) {
        this.qh(), this.Fh = 0, "Online" /* Online */ === t && (
        // We've connected to watch at least once. Don't warn the developer
        // about being offline going forward.
        this.kh = !1), this.Lh(t);
    }
    Lh(t) {
        t !== this.state && (this.state = t, this.$u(t));
    }
    $h(t) {
        const e = `Could not reach Cloud Firestore backend. ${t}\nThis typically indicates that your device does not have a healthy Internet connection at the moment. The client will operate in offline mode until it is able to successfully connect to the backend.`;
        this.kh ? (A(e), this.kh = !1) : m("OnlineStateTracker", e);
    }
    qh() {
        null !== this.Oh && (this.Oh.cancel(), this.Oh = null);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class wo {
    constructor(
    /**
     * The local store, used to fill the write pipeline with outbound mutations.
     */
    t, 
    /** The client-side proxy for interacting with the backend. */
    e, n, s, i) {
        this.Uh = t, this.Qh = e, this.ks = n, this.Kh = {}, 
        /**
         * A list of up to MAX_PENDING_WRITES writes that we have fetched from the
         * LocalStore via fillWritePipeline() and have or will send to the write
         * stream.
         *
         * Whenever writePipeline.length > 0 the RemoteStore will attempt to start or
         * restart the write stream. When the stream is established the writes in the
         * pipeline will be sent in order.
         *
         * Writes remain in writePipeline until they are acknowledged by the backend
         * and thus will automatically be re-sent if the stream is interrupted /
         * restarted before they're acknowledged.
         *
         * Write responses from the backend are linked to their originating request
         * purely based on order, and so we can just shift() writes from the front of
         * the writePipeline as we receive responses.
         */
        this.Wh = [], 
        /**
         * A mapping of watched targets that the client cares about tracking and the
         * user has explicitly called a 'listen' for this target.
         *
         * These targets may or may not have been sent to or acknowledged by the
         * server. On re-establishing the listen stream, these targets should be sent
         * to the server. The targets removed with unlistens are removed eagerly
         * without waiting for confirmation from the listen stream.
         */
        this.jh = new Map, 
        /**
         * A set of reasons for why the RemoteStore may be offline. If empty, the
         * RemoteStore may start its network connections.
         */
        this.Gh = new Set, 
        /**
         * Event handlers that get called when the network is disabled or enabled.
         *
         * PORTING NOTE: These functions are used on the Web client to create the
         * underlying streams (to support tree-shakeable streams). On Android and iOS,
         * the streams are created during construction of RemoteStore.
         */
        this.zh = [], this.Hh = i, this.Hh.L((t => {
            n.Ws((async () => {
                // Porting Note: Unlike iOS, `restartNetwork()` is called even when the
                // network becomes unreachable as we don't have any other way to tear
                // down our streams.
                go(this) && (m("RemoteStore", "Restarting streams for network reachability change."), 
                await async function(t) {
                    const e = V(t);
                    e.Gh.add(4 /* ConnectivityChange */), await To(e), e.Jh.set("Unknown" /* Unknown */), 
                    e.Gh.delete(4 /* ConnectivityChange */), await Eo(e);
                }(this));
            }));
        })), this.Jh = new fo(n, s);
    }
}

async function Eo(t) {
    if (go(t)) for (const e of t.zh) await e(/* enabled= */ !0);
}

/**
 * Temporarily disables the network. The network can be re-enabled using
 * enableNetwork().
 */ async function To(t) {
    for (const e of t.zh) await e(/* enabled= */ !1);
}

/**
 * Starts new listen for the given target. Uses resume token if provided. It
 * is a no-op if the target of given `TargetData` is already being listened to.
 */
function Io(t, e) {
    const n = V(t);
    n.jh.has(e.targetId) || (
    // Mark this as something the client is currently listening for.
    n.jh.set(e.targetId, e), yo(n) ? 
    // The listen will be sent in onWatchStreamOpen
    Po(n) : qo(n).li() && Ao(n, e));
}

/**
 * Removes the listen from server. It is a no-op if the given target id is
 * not being listened to.
 */ function mo(t, e) {
    const n = V(t), s = qo(n);
    n.jh.delete(e), s.li() && Ro(n, e), 0 === n.jh.size && (s.li() ? s.di() : go(n) && 
    // Revert to OnlineState.Unknown if the watch stream is not open and we
    // have no listeners, since without any listens to send we cannot
    // confirm if the stream is healthy and upgrade to OnlineState.Online.
    n.Jh.set("Unknown" /* Unknown */));
}

/**
 * We need to increment the the expected number of pending responses we're due
 * from watch so we wait for the ack to process any messages from this target.
 */ function Ao(t, e) {
    t.Yh.Dn(e.targetId), qo(t).gi(e);
}

/**
 * We need to increment the expected number of pending responses we're due
 * from watch so we wait for the removal on the server before we process any
 * messages from this target.
 */ function Ro(t, e) {
    t.Yh.Dn(e), qo(t).Vi(e);
}

function Po(t) {
    t.Yh = new Mn({
        Xn: e => t.Kh.Xn(e),
        Zn: e => t.jh.get(e) || null
    }), qo(t).start(), t.Jh.Mh();
}

/**
 * Returns whether the watch stream should be started because it's necessary
 * and has not yet been started.
 */ function yo(t) {
    return go(t) && !qo(t).hi() && t.jh.size > 0;
}

function go(t) {
    return 0 === V(t).Gh.size;
}

function Vo(t) {
    t.Yh = void 0;
}

async function po(t) {
    t.jh.forEach(((e, n) => {
        Ao(t, e);
    }));
}

async function bo(t, e) {
    Vo(t), 
    // If we still need the watch stream, retry the connection.
    yo(t) ? (t.Jh.Bh(e), Po(t)) : 
    // No need to restart watch stream because there are no active targets.
    // The online state is set to unknown because there is no active attempt
    // at establishing a connection
    t.Jh.set("Unknown" /* Unknown */);
}

async function vo(t, e, n) {
    if (
    // Mark the client as online since we got a message from the server
    t.Jh.set("Online" /* Online */), e instanceof On && 2 /* Removed */ === e.state && e.cause) 
    // There was an error on a target, don't wait for a consistent snapshot
    // to raise events
    try {
        await 
        /** Handles an error on a target */
        async function(t, e) {
            const n = e.cause;
            for (const s of e.targetIds) 
            // A watched target might have been removed already.
            t.jh.has(s) && (await t.Kh.Xh(s, n), t.jh.delete(s), t.Yh.removeTarget(s));
        }
        /**
 * Attempts to fill our write pipeline with writes from the LocalStore.
 *
 * Called internally to bootstrap or refill the write pipeline and by
 * SyncEngine whenever there are new mutations to process.
 *
 * Starts the write stream if necessary.
 */ (t, e);
    } catch (n) {
        m("RemoteStore", "Failed to remove targets %s: %s ", e.targetIds.join(","), n), 
        await So(t, n);
    } else if (e instanceof Nn ? t.Yh.Ln(e) : e instanceof Fn ? t.Yh.jn(e) : t.Yh.qn(e), 
    !n.isEqual(ct.min())) try {
        const e = await Nr(t.Uh);
        n.u(e) >= 0 && 
        // We have received a target change with a global snapshot if the snapshot
        // version is not equal to SnapshotVersion.min().
        await 
        /**
 * Takes a batch of changes from the Datastore, repackages them as a
 * RemoteEvent, and passes that on to the listener, which is typically the
 * SyncEngine.
 */
        function(t, e) {
            const n = t.Yh.Hn(e);
            // Update in-memory resume tokens. LocalStore will update the
            // persistent view of these when applying the completed RemoteEvent.
                        return n.rn.forEach(((n, s) => {
                if (n.resumeToken.o() > 0) {
                    const i = t.jh.get(s);
                    // A watched target might have been removed already.
                                        i && t.jh.set(s, i.ae(n.resumeToken, e));
                }
            })), 
            // Re-establish listens for the targets that have been invalidated by
            // existence filter mismatches.
            n.on.forEach((e => {
                const n = t.jh.get(e);
                if (!n) 
                // A watched target might have been removed already.
                return;
                // Clear the resume token for the target, since we're in a known mismatch
                // state.
                                t.jh.set(e, n.ae(C.h, n.oe)), 
                // Cause a hard reset by unwatching and rewatching immediately, but
                // deliberately don't send a resume token so that we get a full update.
                Ro(t, e);
                // Mark the target we send as being on behalf of an existence filter
                // mismatch, but don't actually retain that in listenTargets. This ensures
                // that we flag the first re-listen this way without impacting future
                // listens of this target (that might happen e.g. on reconnect).
                const s = new Se(n.target, e, 1 /* ExistenceFilterMismatch */ , n.sequenceNumber);
                Ao(t, s);
            })), t.Kh.Zh(n);
        }(t, n);
    } catch (e) {
        m("RemoteStore", "Failed to raise snapshot:", e), await So(t, e);
    }
}

/**
 * Recovery logic for IndexedDB errors that takes the network offline until
 * `op` succeeds. Retries are scheduled with backoff using
 * `enqueueRetryable()`. If `op()` is not provided, IndexedDB access is
 * validated via a generic operation.
 *
 * The returned Promise is resolved once the network is disabled and before
 * any retry attempt.
 */ async function So(t, e, n) {
    if (!Ss(e)) throw e;
    t.Gh.add(1 /* IndexedDbFailed */), 
    // Disable network and raise offline snapshots
    await To(t), t.Jh.set("Offline" /* Offline */), n || (
    // Use a simple read operation to determine if IndexedDB recovered.
    // Ideally, we would expose a health check directly on SimpleDb, but
    // RemoteStore only has access to persistence through LocalStore.
    n = () => Nr(t.Uh)), 
    // Probe IndexedDB periodically and re-enable network
    t.ks.ea((async () => {
        m("RemoteStore", "Retrying IndexedDB access"), await n(), t.Gh.delete(1 /* IndexedDbFailed */), 
        await Eo(t);
    }));
}

/**
 * Executes `op`. If `op` fails, takes the network offline until `op`
 * succeeds. Returns after the first attempt.
 */ function Do(t, e) {
    return e().catch((n => So(t, n, e)));
}

async function Co(t) {
    const e = V(t), n = Uo(e);
    let s = e.Wh.length > 0 ? e.Wh[e.Wh.length - 1].batchId : -1;
    for (;xo(e); ) try {
        const t = await kr(e.Uh, s);
        if (null === t) {
            0 === e.Wh.length && n.di();
            break;
        }
        s = t.batchId, No(e, t);
    } catch (t) {
        await So(e, t);
    }
    Fo(e) && Oo(e);
}

/**
 * Returns true if we can add to the write pipeline (i.e. the network is
 * enabled and the write pipeline is not full).
 */ function xo(t) {
    return go(t) && t.Wh.length < 10;
}

/**
 * Queues additional writes to be sent to the write stream, sending them
 * immediately if the write stream is established.
 */ function No(t, e) {
    t.Wh.push(e);
    const n = Uo(t);
    n.li() && n.bi && n.vi(e.mutations);
}

function Fo(t) {
    return go(t) && !Uo(t).hi() && t.Wh.length > 0;
}

function Oo(t) {
    Uo(t).start();
}

async function ko(t) {
    Uo(t).Ci();
}

async function Mo(t) {
    const e = Uo(t);
    // Send the write pipeline now that the stream is established.
        for (const n of t.Wh) e.vi(n.mutations);
}

async function Lo(t, e, n) {
    const s = t.Wh.shift(), i = Hs.from(s, e, n);
    await Do(t, (() => t.Kh.tl(i))), 
    // It's possible that with the completion of this mutation another
    // slot has freed up.
    await Co(t);
}

async function $o(t, e) {
    // If the write stream closed after the write handshake completes, a write
    // operation failed and we fail the pending operation.
    e && Uo(t).bi && 
    // This error affects the actual write.
    await async function(t, e) {
        // Only handle permanent errors here. If it's transient, just let the retry
        // logic kick in.
        if (n = e.code, et(n) && n !== x.ABORTED) {
            // This was a permanent error, the request itself was the problem
            // so it's not going to succeed if we resend it.
            const n = t.Wh.shift();
            // In this case it's also unlikely that the server itself is melting
            // down -- this was just a bad request so inhibit backoff on the next
            // restart.
                        Uo(t).fi(), await Do(t, (() => t.Kh.el(n.batchId, e))), 
            // It's possible that with the completion of this mutation
            // another slot has freed up.
            await Co(t);
        }
        var n;
    }(t, e), 
    // The write stream might have been started by refilling the write
    // pipeline for failed writes
    Fo(t) && Oo(t);
}

/**
 * Toggles the network state when the client gains or loses its primary lease.
 */
async function Bo(t, e) {
    const n = V(t);
    e ? (n.Gh.delete(2 /* IsSecondary */), await Eo(n)) : e || (n.Gh.add(2 /* IsSecondary */), 
    await To(n), n.Jh.set("Unknown" /* Unknown */));
}

/**
 * If not yet initialized, registers the WatchStream and its network state
 * callback with `remoteStoreImpl`. Returns the existing stream if one is
 * already available.
 *
 * PORTING NOTE: On iOS and Android, the WatchStream gets registered on startup.
 * This is not done on Web to allow it to be tree-shaken.
 */ function qo(t) {
    return t.nl || (
    // Create stream (but note that it is not started yet).
    t.nl = function(t, e, n) {
        const s = V(t);
        return s.Ni(), new Ls(e, s.ri, s.credentials, s.fe, n);
    }(t.Qh, t.ks, {
        Y: po.bind(null, t),
        Z: bo.bind(null, t),
        yi: vo.bind(null, t)
    }), t.zh.push((async e => {
        e ? (t.nl.fi(), yo(t) ? Po(t) : t.Jh.set("Unknown" /* Unknown */)) : (await t.nl.stop(), 
        Vo(t));
    }))), t.nl;
}

/**
 * If not yet initialized, registers the WriteStream and its network state
 * callback with `remoteStoreImpl`. Returns the existing stream if one is
 * already available.
 *
 * PORTING NOTE: On iOS and Android, the WriteStream gets registered on startup.
 * This is not done on Web to allow it to be tree-shaken.
 */ function Uo(t) {
    return t.sl || (
    // Create stream (but note that it is not started yet).
    t.sl = function(t, e, n) {
        const s = V(t);
        return s.Ni(), new $s(e, s.ri, s.credentials, s.fe, n);
    }(t.Qh, t.ks, {
        Y: ko.bind(null, t),
        Z: $o.bind(null, t),
        Di: Mo.bind(null, t),
        Si: Lo.bind(null, t)
    }), t.zh.push((async e => {
        e ? (t.sl.fi(), 
        // This will start the write stream if necessary.
        await Co(t)) : (await t.sl.stop(), t.Wh.length > 0 && (m("RemoteStore", `Stopping write stream with ${t.Wh.length} pending writes`), 
        t.Wh = []));
    }))), t.sl;
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Holds the listeners and the last received ViewSnapshot for a query being
 * tracked by EventManager.
 */ class Qo {
    constructor() {
        this.il = void 0, this.listeners = [];
    }
}

class Ko {
    constructor() {
        this.queries = new Js((t => ge(t)), ye), this.onlineState = "Unknown" /* Unknown */ , 
        this.rl = new Set;
    }
}

async function Wo(t, e) {
    const n = V(t), s = e.query;
    let i = !1, r = n.queries.get(s);
    if (r || (i = !0, r = new Qo), i) try {
        r.il = await n.ol(s);
    } catch (t) {
        const n = Os(t, `Initialization of query '${Ve(e.query)}' failed`);
        return void e.onError(n);
    }
    n.queries.set(s, r), r.listeners.push(e);
    // Run global snapshot listeners if a consistent snapshot has been emitted.
    e.cl(n.onlineState);
    if (r.il) {
        e.al(r.il) && Ho(n);
    }
}

async function jo(t, e) {
    const n = V(t), s = e.query;
    let i = !1;
    const r = n.queries.get(s);
    if (r) {
        const t = r.listeners.indexOf(e);
        t >= 0 && (r.listeners.splice(t, 1), i = 0 === r.listeners.length);
    }
    if (i) return n.queries.delete(s), n.ul(s);
}

function Go(t, e) {
    const n = V(t);
    let s = !1;
    for (const t of e) {
        const e = t.query, i = n.queries.get(e);
        if (i) {
            for (const e of i.listeners) e.al(t) && (s = !0);
            i.il = t;
        }
    }
    s && Ho(n);
}

function zo(t, e, n) {
    const s = V(t), i = s.queries.get(e);
    if (i) for (const t of i.listeners) t.onError(n);
    // Remove all listeners. NOTE: We don't need to call syncEngine.unlisten()
    // after an error.
        s.queries.delete(e);
}

// Call all global snapshot listeners that have been set.
function Ho(t) {
    t.rl.forEach((t => {
        t.next();
    }));
}

/**
 * QueryListener takes a series of internal view snapshots and determines
 * when to raise the event.
 *
 * It uses an Observer to dispatch events.
 */ class Jo {
    constructor(t, e, n) {
        this.query = t, this.hl = e, 
        /**
         * Initial snapshots (e.g. from cache) may not be propagated to the wrapped
         * observer. This flag is set to true once we've actually raised an event.
         */
        this.ll = !1, this._l = null, this.onlineState = "Unknown" /* Unknown */ , this.options = n || {};
    }
    /**
     * Applies the new ViewSnapshot to this listener, raising a user-facing event
     * if applicable (depending on what changed, whether the user has opted into
     * metadata-only changes, etc.). Returns true if a user-facing event was
     * indeed raised.
     */    al(t) {
        if (!this.options.includeMetadataChanges) {
            // Remove the metadata only changes.
            const e = [];
            for (const n of t.docChanges) 3 /* Metadata */ !== n.type && e.push(n);
            t = new Dn(t.query, t.docs, t.Ze, e, t.tn, t.fromCache, t.en, 
            /* excludesMetadataChanges= */ !0);
        }
        let e = !1;
        return this.ll ? this.fl(t) && (this.hl.next(t), e = !0) : this.dl(t, this.onlineState) && (this.wl(t), 
        e = !0), this._l = t, e;
    }
    onError(t) {
        this.hl.error(t);
    }
    /** Returns whether a snapshot was raised. */    cl(t) {
        this.onlineState = t;
        let e = !1;
        return this._l && !this.ll && this.dl(this._l, t) && (this.wl(this._l), e = !0), 
        e;
    }
    dl(t, e) {
        // Always raise the first event when we're synced
        if (!t.fromCache) return !0;
        // NOTE: We consider OnlineState.Unknown as online (it should become Offline
        // or Online if we wait long enough).
                const n = "Offline" /* Offline */ !== e;
        // Don't raise the event if we're online, aren't synced yet (checked
        // above) and are waiting for a sync.
                return (!this.options.El || !n) && (!t.docs.bt() || "Offline" /* Offline */ === e);
        // Raise data from cache if we have any documents or we are offline
        }
    fl(t) {
        // We don't need to handle includeDocumentMetadataChanges here because
        // the Metadata only changes have already been stripped out if needed.
        // At this point the only changes we will see are the ones we should
        // propagate.
        if (t.docChanges.length > 0) return !0;
        const e = this._l && this._l.hasPendingWrites !== t.hasPendingWrites;
        return !(!t.en && !e) && !0 === this.options.includeMetadataChanges;
        // Generally we should have hit one of the cases above, but it's possible
        // to get here if there were only metadata docChanges and they got
        // stripped out.
        }
    wl(t) {
        t = Dn.sn(t.query, t.docs, t.tn, t.fromCache), this.ll = !0, this.hl.next(t);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A complete element in the bundle stream, together with the byte length it
 * occupies in the stream.
 */ class Yo {
    constructor(t, 
    // How many bytes this element takes to store in the bundle.
    e) {
        this.payload = t, this.byteLength = e;
    }
    Tl() {
        return "metadata" in this.payload;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Helper to convert objects from bundles to model objects in the SDK.
 */ class Xo {
    constructor(t) {
        this.fe = t;
    }
    Ha(t) {
        return Jn(this.fe, t);
    }
    /**
     * Converts a BundleDocument to a MaybeDocument.
     */    Ja(t) {
        return t.metadata.exists ? ns(this.fe, t.document, !1) : new Ut(this.Ha(t.metadata.name), this.Ya(t.metadata.readTime));
    }
    Ya(t) {
        return jn(t);
    }
}

/**
 * A class to process the elements from a bundle, load them into local
 * storage and provide progress update while loading.
 */ class Zo {
    constructor(t, e, n) {
        this.Il = t, this.Uh = e, this.fe = n, 
        /** Batched queries to be saved into storage */
        this.queries = [], 
        /** Batched documents to be saved into storage */
        this.documents = [], this.progress = tc(t);
    }
    /**
     * Adds an element from the bundle to the loader.
     *
     * Returns a new progress if adding the element leads to a new progress,
     * otherwise returns null.
     */    ml(t) {
        this.progress.bytesLoaded += t.byteLength;
        let e = this.progress.documentsLoaded;
        return t.payload.namedQuery ? this.queries.push(t.payload.namedQuery) : t.payload.documentMetadata ? (this.documents.push({
            metadata: t.payload.documentMetadata
        }), t.payload.documentMetadata.exists || ++e) : t.payload.document && (this.documents[this.documents.length - 1].document = t.payload.document, 
        ++e), e !== this.progress.documentsLoaded ? (this.progress.documentsLoaded = e, 
        Object.assign({}, this.progress)) : null;
    }
    Al(t) {
        const e = new Map, n = new Xo(this.fe);
        for (const s of t) if (s.metadata.queries) {
            const t = n.Ha(s.metadata.name);
            for (const n of s.metadata.queries) {
                const s = (e.get(n) || Vn()).add(t);
                e.set(n, s);
            }
        }
        return e;
    }
    /**
     * Update the progress to 'Success' and return the updated progress.
     */    async complete() {
        const t = await Qr(this.Uh, new Xo(this.fe), this.documents, this.Il.id), e = this.Al(this.documents);
        for (const t of this.queries) await Kr(this.Uh, t, e.get(t.name));
        return this.progress.taskState = "Success", new Gs(Object.assign({}, this.progress), t);
    }
}

/**
 * Returns a `LoadBundleTaskProgress` representing the initial progress of
 * loading a bundle.
 */ function tc(t) {
    return {
        taskState: "Running",
        documentsLoaded: 0,
        bytesLoaded: 0,
        totalDocuments: t.totalDocuments,
        totalBytes: t.totalBytes
    };
}

/**
 * Returns a `LoadBundleTaskProgress` representing the progress that the loading
 * has succeeded.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class ec {
    constructor(t) {
        this.key = t;
    }
}

class nc {
    constructor(t) {
        this.key = t;
    }
}

/**
 * View is responsible for computing the final merged truth of what docs are in
 * a query. It gets notified of local and remote changes to docs, and applies
 * the query filters and limits to determine the most correct possible results.
 */ class sc {
    constructor(t, 
    /** Documents included in the remote target */
    e) {
        this.query = t, this.Rl = e, this.Pl = null, 
        /**
         * A flag whether the view is current with the backend. A view is considered
         * current after it has seen the current flag from the backend and did not
         * lose consistency within the watch stream (e.g. because of an existence
         * filter mismatch).
         */
        this.ln = !1, 
        /** Documents in the view but not in the remote target */
        this.yl = Vn(), 
        /** Document Keys that have local changes */
        this.tn = Vn(), this.gl = be(t), this.Vl = new vn(this.gl);
    }
    /**
     * The set of remote documents that the server has told us belongs to the target associated with
     * this view.
     */    get pl() {
        return this.Rl;
    }
    /**
     * Iterates over a set of doc changes, applies the query limit, and computes
     * what the new results should be, what the changes were, and whether we may
     * need to go back to the local cache for more results. Does not make any
     * changes to the view.
     * @param docChanges - The doc changes to apply to this view.
     * @param previousChanges - If this is being called with a refill, then start
     *        with this set of docs and changes instead of the current view.
     * @returns a new set of docs, changes, and refill flag.
     */    bl(t, e) {
        const n = e ? e.vl : new Sn, s = e ? e.Vl : this.Vl;
        let i = e ? e.tn : this.tn, r = s, o = !1;
        // Track the last doc in a (full) limit. This is necessary, because some
        // update (a delete, or an update moving a doc past the old limit) might
        // mean there is some other document in the local cache that either should
        // come (1) between the old last limit doc and the new last document, in the
        // case of updates, or (2) after the new last document, in the case of
        // deletes. So we keep this doc at the old limit to compare the updates to.
        // Note that this should never get used in a refill (when previousChanges is
        // set), because there will only be adds -- no deletes or updates.
        const c = we(this.query) && s.size === this.query.limit ? s.last() : null, a = Ee(this.query) && s.size === this.query.limit ? s.first() : null;
        // Drop documents out to meet limit/limitToLast requirement.
        if (t.Pe(((t, e) => {
            const u = s.get(t);
            let h = e instanceof qt ? e : null;
            h && (h = pe(this.query, h) ? h : null);
            const l = !!u && this.tn.has(u.key), _ = !!h && (h.Jt || 
            // We only consider committed mutations for documents that were
            // mutated during the lifetime of the view.
            this.tn.has(h.key) && h.hasCommittedMutations);
            let f = !1;
            // Calculate change
                        if (u && h) {
                u.data().isEqual(h.data()) ? l !== _ && (n.track({
                    type: 3 /* Metadata */ ,
                    doc: h
                }), f = !0) : this.Sl(u, h) || (n.track({
                    type: 2 /* Modified */ ,
                    doc: h
                }), f = !0, (c && this.gl(h, c) > 0 || a && this.gl(h, a) < 0) && (
                // This doc moved from inside the limit to outside the limit.
                // That means there may be some other doc in the local cache
                // that should be included instead.
                o = !0));
            } else !u && h ? (n.track({
                type: 0 /* Added */ ,
                doc: h
            }), f = !0) : u && !h && (n.track({
                type: 1 /* Removed */ ,
                doc: u
            }), f = !0, (c || a) && (
            // A doc was removed from a full limit query. We'll need to
            // requery from the local cache to see if we know about some other
            // doc that should be in the results.
            o = !0));
            f && (h ? (r = r.add(h), i = _ ? i.add(t) : i.delete(t)) : (r = r.delete(t), i = i.delete(t)));
        })), we(this.query) || Ee(this.query)) for (;r.size > this.query.limit; ) {
            const t = we(this.query) ? r.last() : r.first();
            r = r.delete(t.key), i = i.delete(t.key), n.track({
                type: 1 /* Removed */ ,
                doc: t
            });
        }
        return {
            Vl: r,
            vl: n,
            La: o,
            tn: i
        };
    }
    Sl(t, e) {
        // We suppress the initial change event for documents that were modified as
        // part of a write acknowledgment (e.g. when the value of a server transform
        // is applied) as Watch will send us the same document again.
        // By suppressing the event, we only raise two user visible events (one with
        // `hasPendingWrites` and the final state of the document) instead of three
        // (one with `hasPendingWrites`, the modified document with
        // `hasPendingWrites` and the final state of the document).
        return t.Jt && e.hasCommittedMutations && !e.Jt;
    }
    /**
     * Updates the view with the given ViewDocumentChanges and optionally updates
     * limbo docs and sync state from the provided target change.
     * @param docChanges - The set of changes to make to the view's docs.
     * @param updateLimboDocuments - Whether to update limbo documents based on
     *        this change.
     * @param targetChange - A target change to apply for computing limbo docs and
     *        sync state.
     * @returns A new ViewChange with the given docs, changes, and sync state.
     */
    // PORTING NOTE: The iOS/Android clients always compute limbo document changes.
    yr(t, e, n) {
        const s = this.Vl;
        this.Vl = t.Vl, this.tn = t.tn;
        // Sort changes based on type and query comparator
        const i = t.vl.Xe();
        i.sort(((t, e) => function(t, e) {
            const n = t => {
                switch (t) {
                  case 0 /* Added */ :
                    return 1;

                  case 2 /* Modified */ :
                  case 3 /* Metadata */ :
                    // A metadata change is converted to a modified change at the public
                    // api layer.  Since we sort by document key and then change type,
                    // metadata and modified changes must be sorted equivalently.
                    return 2;

                  case 1 /* Removed */ :
                    return 0;

                  default:
                    return y();
                }
            };
            return n(t) - n(e);
        }
        /**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (t.type, e.type) || this.gl(t.doc, e.doc))), this.Dl(n);
        const r = e ? this.Cl() : [], o = 0 === this.yl.size && this.ln ? 1 /* Synced */ : 0 /* Local */ , c = o !== this.Pl;
        if (this.Pl = o, 0 !== i.length || c) {
            return {
                snapshot: new Dn(this.query, t.Vl, s, i, t.tn, 0 /* Local */ === o, c, 
                /* excludesMetadataChanges= */ !1),
                xl: r
            };
        }
        // no changes
        return {
            xl: r
        };
    }
    /**
     * Applies an OnlineState change to the view, potentially generating a
     * ViewChange if the view's syncState changes as a result.
     */    cl(t) {
        return this.ln && "Offline" /* Offline */ === t ? (
        // If we're offline, set `current` to false and then call applyChanges()
        // to refresh our syncState and generate a ViewChange as appropriate. We
        // are guaranteed to get a new TargetChange that sets `current` back to
        // true once the client is back online.
        this.ln = !1, this.yr({
            Vl: this.Vl,
            vl: new Sn,
            tn: this.tn,
            La: !1
        }, 
        /* updateLimboDocuments= */ !1)) : {
            xl: []
        };
    }
    /**
     * Returns whether the doc for the given key should be in limbo.
     */    Nl(t) {
        // If the remote end says it's part of this query, it's not in limbo.
        return !this.Rl.has(t) && (
        // The local store doesn't think it's a result, so it shouldn't be in limbo.
        !!this.Vl.has(t) && !this.Vl.get(t).Jt);
    }
    /**
     * Updates syncedDocuments, current, and limbo docs based on the given change.
     * Returns the list of changes to which docs are in limbo.
     */    Dl(t) {
        t && (t._n.forEach((t => this.Rl = this.Rl.add(t))), t.fn.forEach((t => {})), t.dn.forEach((t => this.Rl = this.Rl.delete(t))), 
        this.ln = t.ln);
    }
    Cl() {
        // We can only determine limbo documents when we're in-sync with the server.
        if (!this.ln) return [];
        // TODO(klimt): Do this incrementally so that it's not quadratic when
        // updating many documents.
                const t = this.yl;
        this.yl = Vn(), this.Vl.forEach((t => {
            this.Nl(t.key) && (this.yl = this.yl.add(t.key));
        }));
        // Diff the new limbo docs with the old limbo docs.
        const e = [];
        return t.forEach((t => {
            this.yl.has(t) || e.push(new nc(t));
        })), this.yl.forEach((n => {
            t.has(n) || e.push(new ec(n));
        })), e;
    }
    /**
     * Update the in-memory state of the current view with the state read from
     * persistence.
     *
     * We update the query view whenever a client's primary status changes:
     * - When a client transitions from primary to secondary, it can miss
     *   LocalStorage updates and its query views may temporarily not be
     *   synchronized with the state on disk.
     * - For secondary to primary transitions, the client needs to update the list
     *   of `syncedDocuments` since secondary clients update their query views
     *   based purely on synthesized RemoteEvents.
     *
     * @param queryResult.documents - The documents that match the query according
     * to the LocalStore.
     * @param queryResult.remoteKeys - The keys of the documents that match the
     * query according to the backend.
     *
     * @returns The ViewChange that resulted from this synchronization.
     */
    // PORTING NOTE: Multi-tab only.
    Fl(t) {
        this.Rl = t.za, this.yl = Vn();
        const e = this.bl(t.documents);
        return this.yr(e, /*updateLimboDocuments=*/ !0);
    }
    /**
     * Returns a view snapshot as if this query was just listened to. Contains
     * a document add for every existing document and the `fromCache` and
     * `hasPendingWrites` status of the already established view.
     */
    // PORTING NOTE: Multi-tab only.
    Ol() {
        return Dn.sn(this.query, this.Vl, this.tn, 0 /* Local */ === this.Pl);
    }
}

/**
 * QueryView contains all of the data that SyncEngine needs to keep track of for
 * a particular query.
 */
class ic {
    constructor(
    /**
     * The query itself.
     */
    t, 
    /**
     * The target number created by the client that is used in the watch
     * stream to identify this query.
     */
    e, 
    /**
     * The view is responsible for computing the final merged truth of what
     * docs are in the query. It gets notified of local and remote changes,
     * and applies the query filters and limits to determine the most correct
     * possible results.
     */
    n) {
        this.query = t, this.targetId = e, this.view = n;
    }
}

/** Tracks a limbo resolution. */ class rc {
    constructor(t) {
        this.key = t, 
        /**
         * Set to true once we've received a document. This is used in
         * getRemoteKeysForTarget() and ultimately used by WatchChangeAggregator to
         * decide whether it needs to manufacture a delete event for the target once
         * the target is CURRENT.
         */
        this.kl = !1;
    }
}

/**
 * An implementation of `SyncEngine` coordinating with other parts of SDK.
 *
 * The parts of SyncEngine that act as a callback to RemoteStore need to be
 * registered individually. This is done in `syncEngineWrite()` and
 * `syncEngineListen()` (as well as `applyPrimaryState()`) as these methods
 * serve as entry points to RemoteStore's functionality.
 *
 * Note: some field defined in this class might have public access level, but
 * the class is not exported so they are only accessible from this module.
 * This is useful to implement optional features (like bundles) in free
 * functions, such that they are tree-shakeable.
 */ class oc {
    constructor(t, e, n, 
    // PORTING NOTE: Manages state synchronization in multi-tab environments.
    s, i, r) {
        this.Uh = t, this.Ml = e, this.Ll = n, this.$l = s, this.currentUser = i, this.Bl = r, 
        this.ql = {}, this.Ul = new Js((t => ge(t)), ye), this.Ql = new Map, 
        /**
         * The keys of documents that are in limbo for which we haven't yet started a
         * limbo resolution query.
         */
        this.Kl = [], 
        /**
         * Keeps track of the target ID for each document that is in limbo with an
         * active target.
         */
        this.Wl = new _n(_t.At), 
        /**
         * Keeps track of the information about an active limbo resolution for each
         * active target ID that was started for the purpose of limbo resolution.
         */
        this.jl = new Map, this.Gl = new jr, 
        /** Stores user completion handlers, indexed by User and BatchId. */
        this.zl = {}, 
        /** Stores user callbacks waiting for all pending writes to be acknowledged. */
        this.Hl = new Map, this.Jl = cr.qo(), this.onlineState = "Unknown" /* Unknown */ , 
        // The primary state is set to `true` or `false` immediately after Firestore
        // startup. In the interim, a client should only be considered primary if
        // `isPrimary` is true.
        this.Yl = void 0;
    }
    get Xl() {
        return !0 === this.Yl;
    }
}

/**
 * Initiates the new listen, resolves promise when listen enqueued to the
 * server. All the subsequent view snapshots or errors are sent to the
 * subscribed handlers. Returns the initial snapshot.
 */
async function cc(t, e) {
    const n = kc(t);
    let s, i;
    const r = n.Ul.get(e);
    if (r) 
    // PORTING NOTE: With Multi-Tab Web, it is possible that a query view
    // already exists when EventManager calls us for the first time. This
    // happens when the primary tab is already listening to this query on
    // behalf of another tab and the user of the primary also starts listening
    // to the query. EventManager will not have an assigned target ID in this
    // case and calls `listen` to obtain this ID.
    s = r.targetId, n.$l.ah(s), i = r.view.Ol(); else {
        const t = await Mr(n.Uh, Re(e)), r = n.$l.ah(t.targetId);
        s = t.targetId, i = await ac(n, e, s, "current" === r), n.Xl && Io(n.Ml, t);
    }
    return i;
}

/**
 * Registers a view for a previously unknown query and computes its initial
 * snapshot.
 */ async function ac(t, e, n, s) {
    // PORTING NOTE: On Web only, we inject the code that registers new Limbo
    // targets based on view changes. This allows us to only depend on Limbo
    // changes when user code includes queries.
    t.Zl = (e, n, s) => async function(t, e, n, s) {
        let i = e.view.bl(n);
        i.La && (
        // The query has a limit and some docs were removed, so we need
        // to re-run the query against the local store to make sure we
        // didn't lose any good docs that had been past the limit.
        i = await $r(t.Uh, e.query, 
        /* usePreviousResults= */ !1).then((({documents: t}) => e.view.bl(t, i))));
        const r = s && s.rn.get(e.targetId), o = e.view.yr(i, 
        /* updateLimboDocuments= */ t.Xl, r);
        return Rc(t, e.targetId, o.xl), o.snapshot;
    }(t, e, n, s);
    const i = await $r(t.Uh, e, 
    /* usePreviousResults= */ !0), r = new sc(e, i.za), o = r.bl(i.documents), c = xn.hn(n, s && "Offline" /* Offline */ !== t.onlineState), a = r.yr(o, 
    /* updateLimboDocuments= */ t.Xl, c);
    Rc(t, n, a.xl);
    const u = new ic(e, n, r);
    return t.Ul.set(e, u), t.Ql.has(n) ? t.Ql.get(n).push(e) : t.Ql.set(n, [ e ]), a.snapshot;
}

/** Stops listening to the query. */ async function uc(t, e) {
    const n = V(t), s = n.Ul.get(e), i = n.Ql.get(s.targetId);
    if (i.length > 1) return n.Ql.set(s.targetId, i.filter((t => !ye(t, e)))), void n.Ul.delete(e);
    // No other queries are mapped to the target, clean up the query and the target.
        if (n.Xl) {
        // We need to remove the local query target first to allow us to verify
        // whether any other client is still interested in this target.
        n.$l.hh(s.targetId);
        n.$l.nh(s.targetId) || await Lr(n.Uh, s.targetId, 
        /*keepPersistedTargetData=*/ !1).then((() => {
            n.$l._h(s.targetId), mo(n.Ml, s.targetId), mc(n, s.targetId);
        })).catch(js);
    } else mc(n, s.targetId), await Lr(n.Uh, s.targetId, 
    /*keepPersistedTargetData=*/ !0);
}

/**
 * Initiates the write of local mutation batch which involves adding the
 * writes to the mutation queue, notifying the remote store about new
 * mutations and raising events for any changes this write caused.
 *
 * The promise returned by this call is resolved when the above steps
 * have completed, *not* when the write was acked by the backend. The
 * userCallback is resolved once the write was acked/rejected by the
 * backend (or failed locally for any other reason).
 */ async function hc(t, e, n) {
    const s = Mc(t);
    try {
        const t = await function(t, e) {
            const n = V(t), s = ot.now(), i = e.reduce(((t, e) => t.add(e.key)), Vn());
            let r;
            return n.persistence.runTransaction("Locally write mutations", "readwrite", (t => n.Ka.Va(t, i).next((i => {
                r = i;
                // For non-idempotent mutations (such as `FieldValue.increment()`),
                // we record the base state in a separate patch mutation. This is
                // later used to guarantee consistent values and prevents flicker
                // even if the backend sends us an update that already includes our
                // transform.
                const o = [];
                for (const t of e) {
                    const e = Ze(t, r.get(t.key));
                    null != e && 
                    // NOTE: The base state should only be applied if there's some
                    // existing document to override, so use a Precondition of
                    // exists=true
                    o.push(new sn(t.key, e, $t(e.proto.mapValue), ze.exists(!0)));
                }
                return n.Ra.Po(t, s, o, e);
            })))).then((t => {
                const e = t.rr(r);
                return {
                    batchId: t.batchId,
                    _r: e
                };
            }));
        }(s.Uh, e);
        s.$l.sh(t.batchId), function(t, e, n) {
            let s = t.zl[t.currentUser.I()];
            s || (s = new _n(v));
            s = s.Ie(e, n), t.zl[t.currentUser.I()] = s;
        }
        /**
 * Resolves or rejects the user callback for the given batch and then discards
 * it.
 */ (s, t.batchId, n), await gc(s, t._r), await Co(s.Ml);
    } catch (t) {
        // If we can't persist the mutation, we reject the user callback and
        // don't send the mutation. The user can then retry the write.
        const e = Os(t, "Failed to persist write");
        n.reject(e);
    }
}

/**
 * Applies one remote event to the sync engine, notifying any views of the
 * changes, and releasing any pending mutation batches that would become
 * visible because of the snapshot version the remote event contains.
 */ async function lc(t, e) {
    const n = V(t);
    try {
        const t = await Fr(n.Uh, e);
        // Update `receivedDocument` as appropriate for any limbo targets.
                e.rn.forEach(((t, e) => {
            const s = n.jl.get(e);
            s && (
            // Since this is a limbo resolution lookup, it's for a single document
            // and it could be added, modified, or removed, but not a combination.
            g(t._n.size + t.fn.size + t.dn.size <= 1), t._n.size > 0 ? s.kl = !0 : t.fn.size > 0 ? g(s.kl) : t.dn.size > 0 && (g(s.kl), 
            s.kl = !1));
        })), await gc(n, t, e);
    } catch (t) {
        await js(t);
    }
}

/**
 * Applies an OnlineState change to the sync engine and notifies any views of
 * the change.
 */ function _c(t, e, n) {
    const s = V(t);
    // If we are the secondary client, we explicitly ignore the remote store's
    // online state (the local client may go offline, even though the primary
    // tab remains online) and only apply the primary tab's online state from
    // SharedClientState.
        if (s.Xl && 0 /* RemoteStore */ === n || !s.Xl && 1 /* SharedClientState */ === n) {
        const t = [];
        s.Ul.forEach(((n, s) => {
            const i = s.view.cl(e);
            i.snapshot && t.push(i.snapshot);
        })), function(t, e) {
            const n = V(t);
            n.onlineState = e;
            let s = !1;
            n.queries.forEach(((t, n) => {
                for (const t of n.listeners) 
                // Run global snapshot listeners if a consistent snapshot has been emitted.
                t.cl(e) && (s = !0);
            })), s && Ho(n);
        }(s.Ll, e), t.length && s.ql.yi(t), s.onlineState = e, s.Xl && s.$l.Eh(e);
    }
}

/**
 * Rejects the listen for the given targetID. This can be triggered by the
 * backend for any active target.
 *
 * @param syncEngine - The sync engine implementation.
 * @param targetId - The targetID corresponds to one previously initiated by the
 * user as part of TargetData passed to listen() on RemoteStore.
 * @param err - A description of the condition that has forced the rejection.
 * Nearly always this will be an indication that the user is no longer
 * authorized to see the data matching the target.
 */ async function fc(t, e, n) {
    const s = V(t);
    // PORTING NOTE: Multi-tab only.
        s.$l.fh(e, "rejected", n);
    const i = s.jl.get(e), r = i && i.key;
    if (r) {
        // TODO(klimt): We really only should do the following on permission
        // denied errors, but we don't have the cause code here.
        // It's a limbo doc. Create a synthetic event saying it was deleted.
        // This is kind of a hack. Ideally, we would have a method in the local
        // store to purge a document. However, it would be tricky to keep all of
        // the local store's invariants with another method.
        let t = new _n(_t.At);
        t = t.Ie(r, new Ut(r, ct.min()));
        const n = Vn().add(r), i = new Cn(ct.min(), 
        /* targetChanges= */ new Map, 
        /* targetMismatches= */ new wn(v), t, n);
        await lc(s, i), 
        // Since this query failed, we won't want to manually unlisten to it.
        // We only remove it from bookkeeping after we successfully applied the
        // RemoteEvent. If `applyRemoteEvent()` throws, we want to re-listen to
        // this query when the RemoteStore restarts the Watch stream, which should
        // re-trigger the target failure.
        s.Wl = s.Wl.remove(r), s.jl.delete(e), yc(s);
    } else await Lr(s.Uh, e, 
    /* keepPersistedTargetData */ !1).then((() => mc(s, e, n))).catch(js);
}

async function dc(t, e) {
    const n = V(t), s = e.batch.batchId;
    try {
        const t = await xr(n.Uh, e);
        // The local store may or may not be able to apply the write result and
        // raise events immediately (depending on whether the watcher is caught
        // up), so we raise user callbacks first so that they consistently happen
        // before listen events.
                Ic(n, s, /*error=*/ null), Tc(n, s), n.$l.rh(s, "acknowledged"), await gc(n, t);
    } catch (t) {
        await js(t);
    }
}

async function wc(t, e, n) {
    const s = V(t);
    try {
        const t = await function(t, e) {
            const n = V(t);
            return n.persistence.runTransaction("Reject batch", "readwrite-primary", (t => {
                let s;
                return n.Ra.yo(t, e).next((e => (g(null !== e), s = e.keys(), n.Ra.No(t, e)))).next((() => n.Ra.ko(t))).next((() => n.Ka.Va(t, s)));
            }));
        }
        /**
 * Returns the largest (latest) batch id in mutation queue that is pending
 * server response.
 *
 * Returns `BATCHID_UNKNOWN` if the queue is empty.
 */ (s.Uh, e);
        // The local store may or may not be able to apply the write result and
        // raise events immediately (depending on whether the watcher is caught up),
        // so we raise user callbacks first so that they consistently happen before
        // listen events.
                Ic(s, e, n), Tc(s, e), s.$l.rh(e, "rejected", n), await gc(s, t);
    } catch (n) {
        await js(n);
    }
}

/**
 * Registers a user callback that resolves when all pending mutations at the moment of calling
 * are acknowledged .
 */ async function Ec(t, e) {
    const n = V(t);
    go(n.Ml) || m("SyncEngine", "The network is disabled. The task returned by 'awaitPendingWrites()' will not complete until the network is enabled.");
    try {
        const t = await function(t) {
            const e = V(t);
            return e.persistence.runTransaction("Get highest unacknowledged batch id", "readonly", (t => e.Ra.bo(t)));
        }(n.Uh);
        if (-1 === t) 
        // Trigger the callback right away if there is no pending writes at the moment.
        return void e.resolve();
        const s = n.Hl.get(t) || [];
        s.push(e), n.Hl.set(t, s);
    } catch (t) {
        const n = Os(t, "Initialization of waitForPendingWrites() operation failed");
        e.reject(n);
    }
}

/**
 * Triggers the callbacks that are waiting for this batch id to get acknowledged by server,
 * if there are any.
 */ function Tc(t, e) {
    (t.Hl.get(e) || []).forEach((t => {
        t.resolve();
    })), t.Hl.delete(e);
}

/** Reject all outstanding callbacks waiting for pending writes to complete. */ function Ic(t, e, n) {
    const s = V(t);
    let i = s.zl[s.currentUser.I()];
    // NOTE: Mutations restored from persistence won't have callbacks, so it's
    // okay for there to be no callback for this ID.
        if (i) {
        const t = i.get(e);
        t && (n ? t.reject(n) : t.resolve(), i = i.remove(e)), s.zl[s.currentUser.I()] = i;
    }
}

function mc(t, e, n = null) {
    t.$l.hh(e);
    for (const s of t.Ql.get(e)) t.Ul.delete(s), n && t.ql.t_(s, n);
    if (t.Ql.delete(e), t.Xl) {
        t.Gl.cu(e).forEach((e => {
            t.Gl.Mo(e) || 
            // We removed the last reference for this key
            Ac(t, e);
        }));
    }
}

function Ac(t, e) {
    // It's possible that the target already got removed because the query failed. In that case,
    // the key won't exist in `limboTargetsByKey`. Only do the cleanup if we still have the target.
    const n = t.Wl.get(e);
    null !== n && (mo(t.Ml, n), t.Wl = t.Wl.remove(e), t.jl.delete(n), yc(t));
}

function Rc(t, e, n) {
    for (const s of n) if (s instanceof ec) t.Gl.sc(s.key, e), Pc(t, s); else if (s instanceof nc) {
        m("SyncEngine", "Document no longer in limbo: " + s.key), t.Gl.rc(s.key, e);
        t.Gl.Mo(s.key) || 
        // We removed the last reference for this key
        Ac(t, s.key);
    } else y();
}

function Pc(t, e) {
    const n = e.key;
    t.Wl.get(n) || (m("SyncEngine", "New document in limbo: " + n), t.Kl.push(n), yc(t));
}

/**
 * Starts listens for documents in limbo that are enqueued for resolution,
 * subject to a maximum number of concurrent resolutions.
 *
 * Without bounding the number of concurrent resolutions, the server can fail
 * with "resource exhausted" errors which can lead to pathological client
 * behavior as seen in https://github.com/firebase/firebase-js-sdk/issues/2683.
 */ function yc(t) {
    for (;t.Kl.length > 0 && t.Wl.size < t.Bl; ) {
        const e = t.Kl.shift(), n = t.Jl.next();
        t.jl.set(n, new rc(e)), t.Wl = t.Wl.Ie(e, n), Io(t.Ml, new Se(Re(de(e.path)), n, 2 /* LimboResolution */ , Ws.Zi));
    }
}

async function gc(t, e, n) {
    const s = V(t), i = [], r = [], o = [];
    s.Ul.bt() || (s.Ul.forEach(((t, c) => {
        o.push(s.Zl(c, e, n).then((t => {
            if (t) {
                s.Xl && s.$l.fh(c.targetId, t.fromCache ? "not-current" : "current"), i.push(t);
                const e = br.Na(c.targetId, t);
                r.push(e);
            }
        })));
    })), await Promise.all(o), s.ql.yi(i), await async function(t, e) {
        const n = V(t);
        try {
            await n.persistence.runTransaction("notifyLocalViewChanges", "readwrite", (t => gs.forEach(e, (e => gs.forEach(e.Ca, (s => n.persistence.Io.sc(t, e.targetId, s))).next((() => gs.forEach(e.xa, (s => n.persistence.Io.rc(t, e.targetId, s)))))))));
        } catch (t) {
            if (!Ss(t)) throw t;
            // If `notifyLocalViewChanges` fails, we did not advance the sequence
            // number for the documents that were included in this transaction.
            // This might trigger them to be deleted earlier than they otherwise
            // would have, but it should not invalidate the integrity of the data.
            m("LocalStore", "Failed to update sequence numbers: " + t);
        }
        for (const t of e) {
            const e = t.targetId;
            if (!t.fromCache) {
                const t = n.Ba.get(e), s = t.oe, i = t.ue(s);
                // Advance the last limbo free snapshot version
                                n.Ba = n.Ba.Ie(e, i);
            }
        }
    }(s.Uh, r));
}

async function Vc(t, e) {
    const n = V(t);
    if (!n.currentUser.isEqual(e)) {
        m("SyncEngine", "User change. New user:", e.I());
        const t = await Cr(n.Uh, e);
        n.currentUser = e, 
        // Fails tasks waiting for pending writes requested by previous user.
        function(t, e) {
            t.Hl.forEach((t => {
                t.forEach((t => {
                    t.reject(new N(x.CANCELLED, e));
                }));
            })), t.Hl.clear();
        }(n, "'waitForPendingWrites' promise is rejected due to a user change."), 
        // TODO(b/114226417): Consider calling this only in the primary tab.
        n.$l.wh(e, t.ja, t.Ga), await gc(n, t.Wa);
    }
}

function pc(t, e) {
    const n = V(t), s = n.jl.get(e);
    if (s && s.kl) return Vn().add(s.key);
    {
        let t = Vn();
        const s = n.Ql.get(e);
        if (!s) return t;
        for (const e of s) {
            const s = n.Ul.get(e);
            t = t.je(s.view.pl);
        }
        return t;
    }
}

/**
 * Reconcile the list of synced documents in an existing view with those
 * from persistence.
 */ async function bc(t, e) {
    const n = V(t), s = await $r(n.Uh, e.query, 
    /* usePreviousResults= */ !0), i = e.view.Fl(s);
    return n.Xl && Rc(n, e.targetId, i.xl), i;
}

/**
 * Retrieves newly changed documents from remote document cache and raises
 * snapshots if needed.
 */
// PORTING NOTE: Multi-Tab only.
async function vc(t) {
    const e = V(t);
    return qr(e.Uh).then((t => gc(e, t)));
}

/** Applies a mutation state to an existing batch.  */
// PORTING NOTE: Multi-Tab only.
async function Sc(t, e, n, s) {
    const i = V(t), r = await function(t, e) {
        const n = V(t), s = V(n.Ra);
        return n.persistence.runTransaction("Lookup mutation documents", "readonly", (t => s.Vo(t, e).next((e => e ? n.Ka.Va(t, e) : gs.resolve(null)))));
    }
    // PORTING NOTE: Multi-Tab only.
    (i.Uh, e);
    null !== r ? ("pending" === n ? 
    // If we are the primary client, we need to send this write to the
    // backend. Secondary clients will ignore these writes since their remote
    // connection is disabled.
    await Co(i.Ml) : "acknowledged" === n || "rejected" === n ? (
    // NOTE: Both these methods are no-ops for batches that originated from
    // other clients.
    Ic(i, e, s || null), Tc(i, e), function(t, e) {
        V(V(t).Ra).Fo(e);
    }
    // PORTING NOTE: Multi-Tab only.
    (i.Uh, e)) : y(), await gc(i, r)) : 
    // A throttled tab may not have seen the mutation before it was completed
    // and removed from the mutation queue, in which case we won't have cached
    // the affected documents. In this case we can safely ignore the update
    // since that means we didn't apply the mutation locally at all (if we
    // had, we would have cached the affected documents), and so we will just
    // see any resulting document changes via normal remote document updates
    // as applicable.
    m("SyncEngine", "Cannot apply mutation batch with id: " + e);
}

/** Applies a query target change from a different tab. */
// PORTING NOTE: Multi-Tab only.
async function Dc(t, e) {
    const n = V(t);
    if (kc(n), Mc(n), !0 === e && !0 !== n.Yl) {
        // Secondary tabs only maintain Views for their local listeners and the
        // Views internal state may not be 100% populated (in particular
        // secondary tabs don't track syncedDocuments, the set of documents the
        // server considers to be in the target). So when a secondary becomes
        // primary, we need to need to make sure that all views for all targets
        // match the state on disk.
        const t = n.$l.th(), e = await Cc(n, t.Dt());
        n.Yl = !0, await Bo(n.Ml, !0);
        for (const t of e) Io(n.Ml, t);
    } else if (!1 === e && !1 !== n.Yl) {
        const t = [];
        let e = Promise.resolve();
        n.Ql.forEach(((s, i) => {
            n.$l.lh(i) ? t.push(i) : e = e.then((() => (mc(n, i), Lr(n.Uh, i, 
            /*keepPersistedTargetData=*/ !0)))), mo(n.Ml, i);
        })), await e, await Cc(n, t), 
        // PORTING NOTE: Multi-Tab only.
        function(t) {
            const e = V(t);
            e.jl.forEach(((t, n) => {
                mo(e.Ml, n);
            })), e.Gl.au(), e.jl = new Map, e.Wl = new _n(_t.At);
        }
        /**
 * Reconcile the query views of the provided query targets with the state from
 * persistence. Raises snapshots for any changes that affect the local
 * client and returns the updated state of all target's query data.
 *
 * @param syncEngine - The sync engine implementation
 * @param targets - the list of targets with views that need to be recomputed
 * @param transitionToPrimary - `true` iff the tab transitions from a secondary
 * tab to a primary tab
 */
        // PORTING NOTE: Multi-Tab only.
        (n), n.Yl = !1, await Bo(n.Ml, !1);
    }
}

async function Cc(t, e, n) {
    const s = V(t), i = [], r = [];
    for (const t of e) {
        let e;
        const n = s.Ql.get(t);
        if (n && 0 !== n.length) {
            // For queries that have a local View, we fetch their current state
            // from LocalStore (as the resume token and the snapshot version
            // might have changed) and reconcile their views with the persisted
            // state (the list of syncedDocuments may have gotten out of sync).
            e = await Mr(s.Uh, Re(n[0]));
            for (const t of n) {
                const e = s.Ul.get(t), n = await bc(s, e);
                n.snapshot && r.push(n.snapshot);
            }
        } else {
            // For queries that never executed on this client, we need to
            // allocate the target in LocalStore and initialize a new View.
            const n = await Br(s.Uh, t);
            e = await Mr(s.Uh, n), await ac(s, xc(n), t, 
            /*current=*/ !1);
        }
        i.push(e);
    }
    return s.ql.yi(r), i;
}

/**
 * Creates a `Query` object from the specified `Target`. There is no way to
 * obtain the original `Query`, so we synthesize a `Query` from the `Target`
 * object.
 *
 * The synthesized result might be different from the original `Query`, but
 * since the synthesized `Query` should return the same results as the
 * original one (only the presentation of results might differ), the potential
 * difference will not cause issues.
 */
// PORTING NOTE: Multi-Tab only.
function xc(t) {
    return fe(t.path, t.collectionGroup, t.orderBy, t.filters, t.limit, "F" /* First */ , t.startAt, t.endAt);
}

/** Returns the IDs of the clients that are currently active. */
// PORTING NOTE: Multi-Tab only.
function Nc(t) {
    const e = V(t);
    return V(V(e.Uh).persistence).Ea();
}

/** Applies a query target change from a different tab. */
// PORTING NOTE: Multi-Tab only.
async function Fc(t, e, n, s) {
    const i = V(t);
    if (i.Yl) 
    // If we receive a target state notification via WebStorage, we are
    // either already secondary or another tab has taken the primary lease.
    m("SyncEngine", "Ignoring unexpected query state notification."); else if (i.Ql.has(e)) switch (n) {
      case "current":
      case "not-current":
        {
            const t = await qr(i.Uh), s = Cn.un(e, "current" === n);
            await gc(i, t, s);
            break;
        }

      case "rejected":
        await Lr(i.Uh, e, 
        /* keepPersistedTargetData */ !0), mc(i, e, s);
        break;

      default:
        y();
    }
}

/** Adds or removes Watch targets for queries from different tabs. */ async function Oc(t, e, n) {
    const s = kc(t);
    if (s.Yl) {
        for (const t of e) {
            if (s.Ql.has(t)) {
                // A target might have been added in a previous attempt
                m("SyncEngine", "Adding an already active target " + t);
                continue;
            }
            const e = await Br(s.Uh, t), n = await Mr(s.Uh, e);
            await ac(s, xc(e), n.targetId, 
            /*current=*/ !1), Io(s.Ml, n);
        }
        for (const t of n) 
        // Check that the target is still active since the target might have been
        // removed if it has been rejected by the backend.
        s.Ql.has(t) && 
        // Release queries that are still active.
        await Lr(s.Uh, t, 
        /* keepPersistedTargetData */ !1).then((() => {
            mo(s.Ml, t), mc(s, t);
        })).catch(js);
    }
}

function kc(t) {
    const e = V(t);
    return e.Ml.Kh.Zh = lc.bind(null, e), e.Ml.Kh.Xn = pc.bind(null, e), e.Ml.Kh.Xh = fc.bind(null, e), 
    e.ql.yi = Go.bind(null, e.Ll), e.ql.t_ = zo.bind(null, e.Ll), e;
}

function Mc(t) {
    const e = V(t);
    return e.Ml.Kh.tl = dc.bind(null, e), e.Ml.Kh.el = wc.bind(null, e), e;
}

/**
 * Loads a Firestore bundle into the SDK. The returned promise resolves when
 * the bundle finished loading.
 *
 * @param bundleReader - Bundle to load into the SDK.
 * @param task - LoadBundleTask used to update the loading progress to public API.
 */ function Lc(t, e, n) {
    const s = V(t);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (async function(t, e, n) {
        try {
            const s = await e.getMetadata();
            if (await function(t, e) {
                const n = V(t), s = jn(e.createTime);
                return n.persistence.runTransaction("hasNewerBundle", "readonly", (t => n.Wc.ao(t, e.id))).then((t => !!t && t.createTime.u(s) >= 0));
            }
            /**
 * Saves the given `BundleMetadata` to local persistence.
 */ (t.Uh, s)) return await e.close(), void n.e_(function(t) {
                return {
                    taskState: "Success",
                    documentsLoaded: t.totalDocuments,
                    bytesLoaded: t.totalBytes,
                    totalDocuments: t.totalDocuments,
                    totalBytes: t.totalBytes
                };
            }(s));
            n.n_(tc(s));
            const i = new Zo(s, t.Uh, e.fe);
            let r = await e.s_();
            for (;r; ) {
                const t = await i.ml(r);
                t && n.n_(t), r = await e.s_();
            }
            const o = await i.complete();
            // TODO(b/160876443): This currently raises snapshots with
            // `fromCache=false` if users already listen to some queries and bundles
            // has newer version.
                        await gc(t, o.tr, 
            /* remoteEvent */ void 0), 
            // Save metadata, so loading the same bundle will skip.
            await function(t, e) {
                const n = V(t);
                return n.persistence.runTransaction("Save bundle", "readwrite", (t => n.Wc.uo(t, e)));
            }
            /**
 * Returns a promise of a `NamedQuery` associated with given query name. Promise
 * resolves to undefined if no persisted data can be found.
 */ (t.Uh, s), n.e_(o.progress);
        } catch (t) {
            R("SyncEngine", "Loading bundle failed with " + t), n.i_(t);
        }
    }
    /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
    /**
 * Provides all components needed for Firestore with in-memory persistence.
 * Uses EagerGC garbage collection.
 */)(s, e, n).then((() => {
        s.$l.Ih();
    }));
}

class $c {
    constructor() {
        this.synchronizeTabs = !1;
    }
    async initialize(t) {
        this.fe = ys(t.rt.O), this.$l = this.r_(t), this.persistence = this.o_(t), await this.persistence.start(), 
        this.c_ = this.a_(t), this.Uh = this.u_(t);
    }
    a_(t) {
        return null;
    }
    u_(t) {
        return Dr(this.persistence, new vr, t.h_, this.fe);
    }
    o_(t) {
        return new Xr(to.Du, this.fe);
    }
    r_(t) {
        return new ho;
    }
    async terminate() {
        this.c_ && this.c_.stop(), await this.$l.$(), await this.persistence.$();
    }
}

/**
 * Provides all components needed for Firestore with IndexedDB persistence.
 */ class Bc extends $c {
    constructor(t, e, n) {
        super(), this.l_ = t, this.cacheSizeBytes = e, this.forceOwnership = n, this.synchronizeTabs = !1;
    }
    async initialize(t) {
        await super.initialize(t), await Ur(this.Uh), await this.l_.initialize(this, t), 
        // Enqueue writes from a previous session
        await Mc(this.l_.Lu), await Co(this.l_.Ml);
    }
    u_(t) {
        return Dr(this.persistence, new vr, t.h_, this.fe);
    }
    a_(t) {
        const e = this.persistence.Io.vr;
        return new ti(e, t.ks);
    }
    o_(t) {
        const e = Vr(t.rt.O, t.rt.persistenceKey), n = void 0 !== this.cacheSizeBytes ? Ks.Ki(this.cacheSizeBytes) : Ks.Gi;
        return new Pr(this.synchronizeTabs, e, t.clientId, n, t.ks, lo(), _o(), this.fe, this.$l, !!this.forceOwnership);
    }
    r_(t) {
        return new ho;
    }
}

/**
 * Provides all components needed for Firestore with multi-tab IndexedDB
 * persistence.
 *
 * In the legacy client, this provider is used to provide both multi-tab and
 * non-multi-tab persistence since we cannot tell at build time whether
 * `synchronizeTabs` will be enabled.
 */ class qc extends Bc {
    constructor(t, e) {
        super(t, e, /* forceOwnership= */ !1), this.l_ = t, this.cacheSizeBytes = e, this.synchronizeTabs = !0;
    }
    async initialize(t) {
        await super.initialize(t);
        const e = this.l_.Lu;
        this.$l instanceof uo && (this.$l.Lu = {
            Sh: Sc.bind(null, e),
            Dh: Fc.bind(null, e),
            Ch: Oc.bind(null, e),
            Ea: Nc.bind(null, e),
            bh: vc.bind(null, e)
        }, await this.$l.start()), 
        // NOTE: This will immediately call the listener, so we make sure to
        // set it after localStore / remoteStore are started.
        await this.persistence.Yc((async t => {
            await Dc(this.l_.Lu, t), this.c_ && (t && !this.c_.Nr ? this.c_.start(this.Uh) : t || this.c_.stop());
        }));
    }
    r_(t) {
        const e = lo();
        if (!uo.G(e)) throw new N(x.UNIMPLEMENTED, "IndexedDB persistence is only available on platforms that support LocalStorage.");
        const n = Vr(t.rt.O, t.rt.persistenceKey);
        return new uo(e, t.ks, n, t.clientId, t.h_);
    }
}

/**
 * Initializes and wires the components that are needed to interface with the
 * network.
 */ class Uc {
    async initialize(t, e) {
        this.Uh || (this.Uh = t.Uh, this.$l = t.$l, this.Qh = this.__(e), this.Ml = this.f_(e), 
        this.Ll = this.d_(e), this.Lu = this.w_(e, 
        /* startAsPrimary=*/ !t.synchronizeTabs), this.$l.$u = t => _c(this.Lu, t, 1 /* SharedClientState */), 
        this.Ml.Kh.E_ = Vc.bind(null, this.Lu), await Bo(this.Ml, this.Lu.Xl));
    }
    d_(t) {
        return new Ko;
    }
    __(t) {
        const e = ys(t.rt.O), n = (s = t.rt, new rt(s));
        var s;
        /** Return the Platform-specific connectivity monitor. */        return function(t, e, n) {
            return new Bs(t, e, n);
        }(t.credentials, n, e);
    }
    f_(t) {
        return e = this.Uh, n = this.Qh, s = t.ks, i = t => _c(this.Lu, t, 0 /* RemoteStore */), 
        r = z.G() ? new z : new G, new wo(e, n, s, i, r);
        var e, n, s, i, r;
        /** Re-enables the network. Idempotent. */    }
    w_(t, e) {
        return function(t, e, n, 
        // PORTING NOTE: Manages state synchronization in multi-tab environments.
        s, i, r, o) {
            const c = new oc(t, e, n, s, i, r);
            return o && (c.Yl = !0), c;
        }(this.Uh, this.Ml, this.Ll, this.$l, t.h_, t.Bl, e);
    }
    terminate() {
        return async function(t) {
            const e = V(t);
            m("RemoteStore", "RemoteStore shutting down."), e.Gh.add(5 /* Shutdown */), await To(e), 
            e.Hh.$(), 
            // Set the OnlineState to Unknown (rather than Offline) to avoid potentially
            // triggering spurious listener events with cached data, etc.
            e.Jh.set("Unknown" /* Unknown */);
        }(this.Ml);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * How many bytes to read each time when `ReadableStreamReader.read()` is
 * called. Only applicable for byte streams that we control (e.g. those backed
 * by an UInt8Array).
 */
/**
 * Builds a `ByteStreamReader` from a UInt8Array.
 * @param source - The data source to use.
 * @param bytesPerRead - How many bytes each `read()` from the returned reader
 *        will read.
 */
function Qc(t, e = 10240) {
    let n = 0;
    return {
        async read() {
            if (n < t.byteLength) {
                const s = {
                    value: t.slice(n, n + e),
                    done: !1
                };
                return n += e, s;
            }
            // TypesScript's lib.dom.d.ts doesn't have proper typings of
            // ReadableStreamReadResult yet.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        return {
                done: !0
            };
        },
        async cancel() {},
        releaseLock() {}
    };
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * On web, a `ReadableStream` is wrapped around by a `ByteStreamReader`.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/*
 * A wrapper implementation of Observer<T> that will dispatch events
 * asynchronously. To allow immediate silencing, a mute call is added which
 * causes events scheduled to no longer be raised.
 */
class Kc {
    constructor(t) {
        this.observer = t, 
        /**
         * When set to true, will not raise future events. Necessary to deal with
         * async detachment of listener.
         */
        this.muted = !1;
    }
    next(t) {
        this.observer.next && this.T_(this.observer.next, t);
    }
    error(t) {
        this.observer.error ? this.T_(this.observer.error, t) : console.error("Uncaught Error in snapshot listener:", t);
    }
    I_() {
        this.muted = !0;
    }
    T_(t, e) {
        this.muted || setTimeout((() => {
            this.muted || t(e);
        }), 0);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A class representing a bundle.
 *
 * Takes a bundle stream or buffer, and presents abstractions to read bundled
 * elements out of the underlying content.
 */ class Wc {
    constructor(
    /** The reader to read from underlying binary bundle data source. */
    t, e) {
        this.m_ = t, this.fe = e, 
        /** Cached bundle metadata. */
        this.metadata = new it, 
        /**
         * Internal buffer to hold bundle content, accumulating incomplete element
         * content.
         */
        this.buffer = new Uint8Array, this.A_ = new TextDecoder("utf-8"), 
        // Read the metadata (which is the first element).
        this.R_().then((t => {
            t && t.Tl() ? this.metadata.resolve(t.payload.metadata) : this.metadata.reject(new Error("The first element of the bundle is not a metadata, it is\n             " + JSON.stringify(null == t ? void 0 : t.payload)));
        }), (t => this.metadata.reject(t)));
    }
    close() {
        return this.m_.cancel();
    }
    async getMetadata() {
        return this.metadata.promise;
    }
    async s_() {
        // Makes sure metadata is read before proceeding.
        return await this.getMetadata(), this.R_();
    }
    /**
     * Reads from the head of internal buffer, and pulling more data from
     * underlying stream if a complete element cannot be found, until an
     * element(including the prefixed length and the JSON string) is found.
     *
     * Once a complete element is read, it is dropped from internal buffer.
     *
     * Returns either the bundled element, or null if we have reached the end of
     * the stream.
     */    async R_() {
        const t = await this.P_();
        if (null === t) return null;
        const e = this.A_.decode(t), n = Number(e);
        isNaN(n) && this.y_(`length string (${e}) is not valid number`);
        const s = await this.g_(n);
        return new Yo(JSON.parse(s), t.length + n);
    }
    /** First index of '{' from the underlying buffer. */    V_() {
        return this.buffer.findIndex((t => t === "{".charCodeAt(0)));
    }
    /**
     * Reads from the beginning of the internal buffer, until the first '{', and
     * return the content.
     *
     * If reached end of the stream, returns a null.
     */    async P_() {
        for (;this.V_() < 0; ) {
            if (await this.p_()) break;
        }
        // Broke out of the loop because underlying stream is closed, and there
        // happens to be no more data to process.
                if (0 === this.buffer.length) return null;
        const t = this.V_();
        // Broke out of the loop because underlying stream is closed, but still
        // cannot find an open bracket.
                t < 0 && this.y_("Reached the end of bundle when a length string is expected.");
        const e = this.buffer.slice(0, t);
        // Update the internal buffer to drop the read length.
                return this.buffer = this.buffer.slice(t), e;
    }
    /**
     * Reads from a specified position from the internal buffer, for a specified
     * number of bytes, pulling more data from the underlying stream if needed.
     *
     * Returns a string decoded from the read bytes.
     */    async g_(t) {
        for (;this.buffer.length < t; ) {
            await this.p_() && this.y_("Reached the end of bundle when more is expected.");
        }
        const e = this.A_.decode(this.buffer.slice(0, t));
        // Update the internal buffer to drop the read json string.
                return this.buffer = this.buffer.slice(t), e;
    }
    y_(t) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        throw this.m_.cancel(), new Error("Invalid bundle format: " + t);
    }
    /**
     * Pulls more data from underlying stream to internal buffer.
     * Returns a boolean indicating whether the stream is finished.
     */    async p_() {
        const t = await this.m_.read();
        if (!t.done) {
            const e = new Uint8Array(this.buffer.length + t.value.length);
            e.set(this.buffer), e.set(t.value, this.buffer.length), this.buffer = e;
        }
        return t.done;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A `FieldPath` refers to a field in a document. The path may consist of a
 * single field name (referring to a top-level field in the document), or a
 * list of field names (referring to a nested field in the document).
 *
 * Create a `FieldPath` by providing field names. If more than one field
 * name is provided, the path will point to a nested field in a document.
 */
class jc {
    /**
     * Creates a FieldPath from the provided field names. If more than one field
     * name is provided, the path will point to a nested field in a document.
     *
     * @param fieldNames - A list of field names.
     */
    constructor(...t) {
        for (let e = 0; e < t.length; ++e) if (0 === t[e].length) throw new N(x.INVALID_ARGUMENT, "Invalid field name at argument $(i + 1). Field names must not be empty.");
        this.b_ = new lt(t);
    }
    /**
     * Returns true if this `FieldPath` is equal to the provided one.
     *
     * @param other - The `FieldPath` to compare against.
     * @returns true if this `FieldPath` is equal to the provided one.
     */    isEqual(t) {
        return this.b_.isEqual(t.b_);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Sentinel values that can be used when writing document fields with `set()`
 * or `update()`.
 */ class Gc {
    /**
     * @param _methodName - The public API endpoint that returns this class.
     */
    constructor(t) {
        this._methodName = t;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A `DocumentReference` refers to a document location in a Firestore database
 * and can be used to write, read, or listen to the location. The document at
 * the referenced location may or may not exist.
 */ class zc {
    /** @hideconstructor */
    constructor(t, e, n) {
        this.v_ = e, this.S_ = n, 
        /** The type of this Firestore reference. */
        this.type = "document", this.firestore = t;
    }
    get D_() {
        return this.S_.path;
    }
    /**
     * The document's identifier within its collection.
     */    get id() {
        return this.S_.path.Vt();
    }
    /**
     * A string representing the path of the referenced document (relative
     * to the root of the database).
     */    get path() {
        return this.S_.path.Ct();
    }
    /**
     * The collection this `DocumentReference` belongs to.
     */    get parent() {
        return new Jc(this.firestore, this.v_, this.S_.path.yt());
    }
    /**
     * Applies a custom data converter to this `DocumentReference`, allowing you
     * to use your own custom model objects with Firestore. When you call {@link
     * setDoc}, {@link getDoc}, etc. with the returned `DocumentReference`
     * instance, the provided converter will convert between Firestore data and
     * your custom type `U`.
     *
     * @param converter - Converts objects to and from Firestore.
     * @returns A `DocumentReference<U>` that uses the provided converter.
     */    withConverter(t) {
        return new zc(this.firestore, t, this.S_);
    }
}

/**
 * A `Query` refers to a Query which you can read or listen to. You can also
 * construct refined `Query` objects by adding filters and ordering.
 */ class Hc {
    // This is the lite version of the Query class in the main SDK.
    /** @hideconstructor protected */
    constructor(t, e, n) {
        this.v_ = e, this.C_ = n, 
        /** The type of this Firestore reference. */
        this.type = "query", this.firestore = t;
    }
    /**
     * Applies a custom data converter to this query, allowing you to use your own
     * custom model objects with Firestore. When you call {@link getDocs} with
     * the returned query, the provided converter will convert between Firestore
     * data and your custom type `U`.
     *
     * @param converter - Converts objects to and from Firestore.
     * @returns A `Query<U>` that uses the provided converter.
     */    withConverter(t) {
        return new Hc(this.firestore, t, this.C_);
    }
}

/**
 * A `CollectionReference` object can be used for adding documents, getting
 * document references, and querying for documents (using {@link query}).
 */ class Jc extends Hc {
    /** @hideconstructor */
    constructor(t, e, n) {
        super(t, e, de(n)), this.firestore = t, this.D_ = n, this.type = "collection";
    }
    /** The collection's identifier. */    get id() {
        return this.C_.path.Vt();
    }
    /**
     * A string representing the path of the referenced collection (relative
     * to the root of the database).
     */    get path() {
        return this.C_.path.Ct();
    }
    /**
     * A reference to the containing `DocumentReference` if this is a
     * subcollection. If this isn't a subcollection, the reference is null.
     */    get parent() {
        const t = this.D_.yt();
        return t.bt() ? null : new zc(this.firestore, 
        /* converter= */ null, new _t(t));
    }
    /**
     * Applies a custom data converter to this CollectionReference, allowing you
     * to use your own custom model objects with Firestore. When you call {@link
     * addDoc} with the returned `CollectionReference` instance, the provided
     * converter will convert between Firestore data and your custom type `U`.
     *
     * @param converter - Converts objects to and from Firestore.
     * @returns A `CollectionReference<U>` that uses the provided converter.
     */    withConverter(t) {
        return new Jc(this.firestore, t, this.D_);
    }
}

function Yc(t, e, ...n) {
    if (t instanceof O && (t = t._), ni("collection", "path", e), t instanceof _i) {
        const s = ut.xt(e, ...n);
        return oi(s), new Jc(t, /* converter= */ null, s);
    }
    {
        if (!(t instanceof zc || t instanceof Jc)) throw new N(x.INVALID_ARGUMENT, "Expected first argument to collection() to be a CollectionReference, a DocumentReference or FirebaseFirestore");
        const s = ut.xt(t.path, ...n).child(ut.xt(e));
        return oi(s), new Jc(t.firestore, 
        /* converter= */ null, s);
    }
}

// TODO(firestorelite): Consider using ErrorFactory -
// https://github.com/firebase/firebase-js-sdk/blob/0131e1f/packages/util/src/errors.ts#L106
/**
 * Creates and returns a new `Query` instance that includes all documents in the
 * database that are contained in a collection or subcollection with the
 * given `collectionId`.
 *
 * @param firestore - A reference to the root Firestore instance.
 * @param collectionId - Identifies the collections to query over. Every
 * collection or subcollection with this ID as the last segment of its path
 * will be included. Cannot contain a slash.
 * @returns The created `Query`.
 */ function Xc(t, e) {
    if (t = ai(t, _i), ni("collectionGroup", "collection id", e), e.indexOf("/") >= 0) throw new N(x.INVALID_ARGUMENT, `Invalid collection ID '${e}' passed to function collectionGroup(). Collection IDs must not contain '/'.`);
    return new Hc(t, 
    /* converter= */ null, 
    /**
 * Creates a new Query for a collection group query that matches all documents
 * within the provided collection group.
 */
    function(t) {
        return new _e(ut.Nt(), t);
    }(e));
}

function Zc(t, e, ...n) {
    if (t instanceof O && (t = t._), 
    // We allow omission of 'pathString' but explicitly prohibit passing in both
    // 'undefined' and 'null'.
    1 === arguments.length && (e = b.t()), ni("doc", "path", e), t instanceof _i) {
        const s = ut.xt(e, ...n);
        return ri(s), new zc(t, 
        /* converter= */ null, new _t(s));
    }
    {
        if (!(t instanceof zc || t instanceof Jc)) throw new N(x.INVALID_ARGUMENT, "Expected first argument to collection() to be a CollectionReference, a DocumentReference or FirebaseFirestore");
        const s = t.D_.child(ut.xt(e, ...n));
        return ri(s), new zc(t.firestore, t instanceof Jc ? t.v_ : null, new _t(s));
    }
}

/**
 * Returns true if the provided references are equal.
 *
 * @param left - A reference to compare.
 * @param right - A reference to compare.
 * @returns true if the references point to the same location in the same
 * Firestore database.
 */ function ta(t, e) {
    return t instanceof O && (t = t._), e instanceof O && (e = e._), (t instanceof zc || t instanceof Jc) && (e instanceof zc || e instanceof Jc) && (t.firestore === e.firestore && t.path === e.path && t.v_ === e.v_);
}

/**
 * Returns true if the provided queries point to the same collection and apply
 * the same constraints.
 *
 * @param left - A `Query` to compare.
 * @param right - A `Query` to compare.
 * @returns true if the references point to the same location in the same
 * Firestore database.
 */ function ea(t, e) {
    return t instanceof O && (t = t._), e instanceof O && (e = e._), t instanceof Hc && e instanceof Hc && (t.firestore === e.firestore && ye(t.C_, e.C_) && t.v_ === e.v_);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * An immutable object representing a geographic location in Firestore. The
 * location is represented as latitude/longitude pair.
 *
 * Latitude values are in the range of [-90, 90].
 * Longitude values are in the range of [-180, 180].
 */ class na {
    /**
     * Creates a new immutable `GeoPoint` object with the provided latitude and
     * longitude values.
     * @param latitude - The latitude as number between -90 and 90.
     * @param longitude - The longitude as number between -180 and 180.
     */
    constructor(t, e) {
        if (!isFinite(t) || t < -90 || t > 90) throw new N(x.INVALID_ARGUMENT, "Latitude must be a number between -90 and 90, but was: " + t);
        if (!isFinite(e) || e < -180 || e > 180) throw new N(x.INVALID_ARGUMENT, "Longitude must be a number between -180 and 180, but was: " + e);
        this.x_ = t, this.N_ = e;
    }
    /**
     * The latitude of this `GeoPoint` instance.
     */    get latitude() {
        return this.x_;
    }
    /**
     * The longitude of this `GeoPoint` instance.
     */    get longitude() {
        return this.N_;
    }
    /**
     * Returns true if this `GeoPoint` is equal to the provided one.
     *
     * @param other - The `GeoPoint` to compare against.
     * @returns true if this `GeoPoint` is equal to the provided one.
     */    isEqual(t) {
        return this.x_ === t.x_ && this.N_ === t.N_;
    }
    toJSON() {
        return {
            latitude: this.x_,
            longitude: this.N_
        };
    }
    /**
     * Actually private to JS consumers of our API, so this function is prefixed
     * with an underscore.
     */    dt(t) {
        return v(this.x_, t.x_) || v(this.N_, t.N_);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ const sa = /^__.*__$/;

/** The result of parsing document data (e.g. for a setData call). */ class ia {
    constructor(t, e, n) {
        this.data = t, this.Te = e, this.fieldTransforms = n;
    }
    F_(t, e) {
        return null !== this.Te ? new sn(t, this.data, this.Te, e, this.fieldTransforms) : new nn(t, this.data, e, this.fieldTransforms);
    }
}

/** The result of parsing "update" data (i.e. for an updateData call). */ class ra {
    constructor(t, 
    // The fieldMask does not include document transforms.
    e, n) {
        this.data = t, this.Te = e, this.fieldTransforms = n;
    }
    F_(t, e) {
        return new sn(t, this.data, this.Te, e, this.fieldTransforms);
    }
}

function oa(t) {
    switch (t) {
      case 0 /* Set */ :
 // fall through
              case 2 /* MergeSet */ :
 // fall through
              case 1 /* Update */ :
        return !0;

      case 3 /* Argument */ :
      case 4 /* ArrayArgument */ :
        return !1;

      default:
        throw y();
    }
}

/** A "context" object passed around while parsing user data. */ class ca {
    /**
     * Initializes a ParseContext with the given source and path.
     *
     * @param settings - The settings for the parser.
     * @param databaseId - The database ID of the Firestore instance.
     * @param serializer - The serializer to use to generate the Value proto.
     * @param ignoreUndefinedProperties - Whether to ignore undefined properties
     * rather than throw.
     * @param fieldTransforms - A mutable list of field transforms encountered
     * while parsing the data.
     * @param fieldMask - A mutable list of field paths encountered while parsing
     * the data.
     *
     * TODO(b/34871131): We don't support array paths right now, so path can be
     * null to indicate the context represents any location within an array (in
     * which case certain features will not work and errors will be somewhat
     * compromised).
     */
    constructor(t, e, n, s, i, r) {
        this.settings = t, this.O = e, this.fe = n, this.ignoreUndefinedProperties = s, 
        // Minor hack: If fieldTransforms is undefined, we assume this is an
        // external call and we need to validate the entire path.
        void 0 === i && this.O_(), this.fieldTransforms = i || [], this.Te = r || [];
    }
    get path() {
        return this.settings.path;
    }
    get k_() {
        return this.settings.k_;
    }
    /** Returns a new context with the specified settings overwritten. */    M_(t) {
        return new ca(Object.assign(Object.assign({}, this.settings), t), this.O, this.fe, this.ignoreUndefinedProperties, this.fieldTransforms, this.Te);
    }
    L_(t) {
        var e;
        const n = null === (e = this.path) || void 0 === e ? void 0 : e.child(t), s = this.M_({
            path: n,
            B_: !1
        });
        return s.q_(t), s;
    }
    U_(t) {
        var e;
        const n = null === (e = this.path) || void 0 === e ? void 0 : e.child(t), s = this.M_({
            path: n,
            B_: !1
        });
        return s.O_(), s;
    }
    Q_(t) {
        // TODO(b/34871131): We don't support array paths right now; so make path
        // undefined.
        return this.M_({
            path: void 0,
            B_: !0
        });
    }
    K_(t) {
        return ba(t, this.settings.methodName, this.settings.W_ || !1, this.path, this.settings.j_);
    }
    /** Returns 'true' if 'fieldPath' was traversed when creating this context. */    contains(t) {
        return void 0 !== this.Te.find((e => t.vt(e))) || void 0 !== this.fieldTransforms.find((e => t.vt(e.field)));
    }
    O_() {
        // TODO(b/34871131): Remove null check once we have proper paths for fields
        // within arrays.
        if (this.path) for (let t = 0; t < this.path.length; t++) this.q_(this.path.get(t));
    }
    q_(t) {
        if (0 === t.length) throw this.K_("Document fields must not be empty");
        if (oa(this.k_) && sa.test(t)) throw this.K_('Document fields cannot begin and end with "__"');
    }
}

/**
 * Helper for parsing raw user input (provided via the API) into internal model
 * classes.
 */ class aa {
    constructor(t, e, n) {
        this.O = t, this.ignoreUndefinedProperties = e, this.fe = n || ys(t);
    }
    /** Creates a new top-level parse context. */    G_(t, e, n, s = !1) {
        return new ca({
            k_: t,
            methodName: e,
            j_: n,
            path: lt.Nt(),
            B_: !1,
            W_: s
        }, this.O, this.fe, this.ignoreUndefinedProperties);
    }
}

function ua(t) {
    const e = t.so(), n = ys(t.zr);
    return new aa(t.zr, !!e.ignoreUndefinedProperties, n);
}

/** Parse document data from a set() call. */ function ha(t, e, n, s, i, r = {}) {
    const o = t.G_(r.merge || r.mergeFields ? 2 /* MergeSet */ : 0 /* Set */ , e, n, i);
    ya("Data must be an object, but it was:", o, s);
    const c = Ra(s, o);
    let a, u;
    if (r.merge) a = new Et(o.Te), u = o.fieldTransforms; else if (r.mergeFields) {
        const t = [];
        for (const s of r.mergeFields) {
            const i = ga(e, s, n);
            if (!o.contains(i)) throw new N(x.INVALID_ARGUMENT, `Field '${i}' is specified in your field mask but missing from your input data.`);
            va(t, i) || t.push(i);
        }
        a = new Et(t), u = o.fieldTransforms.filter((t => a.Qt(t.field)));
    } else a = null, u = o.fieldTransforms;
    return new ia(new Mt(c), a, u);
}

class la extends Gc {
    z_(t) {
        if (2 /* MergeSet */ !== t.k_) throw 1 /* Update */ === t.k_ ? t.K_(this._methodName + "() can only appear at the top level of your update data") : t.K_(this._methodName + "() cannot be used with set() unless you pass {merge:true}");
        // No transform to add for a delete, but we need to add it to our
        // fieldMask so it gets deleted.
        return t.Te.push(t.path), null;
    }
    isEqual(t) {
        return t instanceof la;
    }
}

/**
 * Creates a child context for parsing SerializableFieldValues.
 *
 * This is different than calling `ParseContext.contextWith` because it keeps
 * the fieldTransforms and fieldMask separate.
 *
 * The created context has its `dataSource` set to `UserDataSource.Argument`.
 * Although these values are used with writes, any elements in these FieldValues
 * are not considered writes since they cannot contain any FieldValue sentinels,
 * etc.
 *
 * @param fieldValue - The sentinel FieldValue for which to create a child
 *     context.
 * @param context - The parent context.
 * @param arrayElement - Whether or not the FieldValue has an array.
 */ function _a(t, e, n) {
    return new ca({
        k_: 3 /* Argument */ ,
        j_: e.settings.j_,
        methodName: t._methodName,
        B_: n
    }, e.O, e.fe, e.ignoreUndefinedProperties);
}

class fa extends Gc {
    z_(t) {
        return new We(t.path, new Me);
    }
    isEqual(t) {
        return t instanceof fa;
    }
}

class da extends Gc {
    constructor(t, e) {
        super(t), this.H_ = e;
    }
    z_(t) {
        const e = _a(this, t, 
        /*array=*/ !0), n = this.H_.map((t => Aa(t, e))), s = new Le(n);
        return new We(t.path, s);
    }
    isEqual(t) {
        // TODO(mrschmidt): Implement isEquals
        return this === t;
    }
}

class wa extends Gc {
    constructor(t, e) {
        super(t), this.H_ = e;
    }
    z_(t) {
        const e = _a(this, t, 
        /*array=*/ !0), n = this.H_.map((t => Aa(t, e))), s = new Be(n);
        return new We(t.path, s);
    }
    isEqual(t) {
        // TODO(mrschmidt): Implement isEquals
        return this === t;
    }
}

class Ea extends Gc {
    constructor(t, e) {
        super(t), this.J_ = e;
    }
    z_(t) {
        const e = new Ue(t.fe, xe(t.fe, this.J_));
        return new We(t.path, e);
    }
    isEqual(t) {
        // TODO(mrschmidt): Implement isEquals
        return this === t;
    }
}

/** Parse update data from an update() call. */ function Ta(t, e, n, s) {
    const i = t.G_(1 /* Update */ , e, n);
    ya("Data must be an object, but it was:", i, s);
    const r = [], o = new Lt;
    dt(s, ((t, s) => {
        const c = pa(e, t, n);
        // For Compat types, we have to "extract" the underlying types before
        // performing validation.
                s instanceof O && (s = s._);
        const a = i.U_(c);
        if (s instanceof la) 
        // Add it to the field mask, but don't add anything to updateData.
        r.push(c); else {
            const t = Aa(s, a);
            null != t && (r.push(c), o.set(c, t));
        }
    }));
    const c = new Et(r);
    return new ra(o.Gt(), c, i.fieldTransforms);
}

/** Parse update data from a list of field/value arguments. */ function Ia(t, e, n, s, i, r) {
    const o = t.G_(1 /* Update */ , e, n), c = [ ga(e, s, n) ], a = [ i ];
    if (r.length % 2 != 0) throw new N(x.INVALID_ARGUMENT, `Function ${e}() needs to be called with an even number of arguments that alternate between field names and values.`);
    for (let t = 0; t < r.length; t += 2) c.push(ga(e, r[t])), a.push(r[t + 1]);
    const u = [], h = new Lt;
    // We iterate in reverse order to pick the last value for a field if the
    // user specified the field multiple times.
    for (let t = c.length - 1; t >= 0; --t) if (!va(u, c[t])) {
        const e = c[t];
        let n = a[t];
        // For Compat types, we have to "extract" the underlying types before
        // performing validation.
                n instanceof O && (n = n._);
        const s = o.U_(e);
        if (n instanceof la) 
        // Add it to the field mask, but don't add anything to updateData.
        u.push(e); else {
            const t = Aa(n, s);
            null != t && (u.push(e), h.set(e, t));
        }
    }
    const l = new Et(u);
    return new ra(h.Gt(), l, o.fieldTransforms);
}

/**
 * Parse a "query value" (e.g. value in a where filter or a value in a cursor
 * bound).
 *
 * @param allowArrays - Whether the query value is an array that may directly
 * contain additional arrays (e.g. the operand of an `in` query).
 */ function ma(t, e, n, s = !1) {
    return Aa(n, t.G_(s ? 4 /* ArrayArgument */ : 3 /* Argument */ , e));
}

/**
 * Parses user data to Protobuf Values.
 *
 * @param input - Data to be parsed.
 * @param context - A context object representing the current path being parsed,
 * the source of the data being parsed, etc.
 * @returns The parsed value, or null if the value was a FieldValue sentinel
 * that should not be included in the resulting parsed data.
 */ function Aa(t, e) {
    if (
    // Unwrap the API type from the Compat SDK. This will return the API type
    // from firestore-exp.
    t instanceof O && (t = t._), Pa(t)) return ya("Unsupported field value:", e, t), 
    Ra(t, e);
    if (t instanceof Gc) 
    // FieldValues usually parse into transforms (except FieldValue.delete())
    // in which case we do not want to include this field in our parsed data
    // (as doing so will overwrite the field directly prior to the transform
    // trying to transform it). So we don't add this location to
    // context.fieldMask and we return null as our parsing result.
    /**
 * "Parses" the provided FieldValueImpl, adding any necessary transforms to
 * context.fieldTransforms.
 */
    return function(t, e) {
        // Sentinels are only supported with writes, and not within arrays.
        if (!oa(e.k_)) throw e.K_(t._methodName + "() can only be used with update() and set()");
        if (!e.path) throw e.K_(t._methodName + "() is not currently supported inside arrays");
        const n = t.z_(e);
        n && e.fieldTransforms.push(n);
    }
    /**
 * Helper to parse a scalar value (i.e. not an Object, Array, or FieldValue)
 *
 * @returns The parsed value
 */ (t, e), null;
    if (
    // If context.path is null we are inside an array and we don't support
    // field mask paths more granular than the top-level array.
    e.path && e.Te.push(e.path), t instanceof Array) {
        // TODO(b/34871131): Include the path containing the array in the error
        // message.
        // In the case of IN queries, the parsed data is an array (representing
        // the set of values to be included for the IN query) that may directly
        // contain additional arrays (each representing an individual field
        // value), so we disable this validation.
        if (e.settings.B_ && 4 /* ArrayArgument */ !== e.k_) throw e.K_("Nested arrays are not supported");
        return function(t, e) {
            const n = [];
            let s = 0;
            for (const i of t) {
                let t = Aa(i, e.Q_(s));
                null == t && (
                // Just include nulls in the array for fields being replaced with a
                // sentinel.
                t = {
                    nullValue: "NULL_VALUE"
                }), n.push(t), s++;
            }
            return {
                arrayValue: {
                    values: n
                }
            };
        }(t, e);
    }
    return function(t, e) {
        t instanceof O && (t = t._);
        if (null === t) return {
            nullValue: "NULL_VALUE"
        };
        if ("number" == typeof t) return xe(e.fe, t);
        if ("boolean" == typeof t) return {
            booleanValue: t
        };
        if ("string" == typeof t) return {
            stringValue: t
        };
        if (t instanceof Date) {
            const n = ot.fromDate(t);
            return {
                timestampValue: Qn(e.fe, n)
            };
        }
        if (t instanceof ot) {
            // Firestore backend truncates precision down to microseconds. To ensure
            // offline mode works the same with regards to truncation, perform the
            // truncation immediately without waiting for the backend to do that.
            const n = new ot(t.seconds, 1e3 * Math.floor(t.nanoseconds / 1e3));
            return {
                timestampValue: Qn(e.fe, n)
            };
        }
        if (t instanceof na) return {
            geoPointValue: {
                latitude: t.latitude,
                longitude: t.longitude
            }
        };
        if (t instanceof F) return {
            bytesValue: Kn(e.fe, t.l)
        };
        if (t instanceof zc) {
            const n = e.O, s = t.firestore.zr;
            if (!s.isEqual(n)) throw e.K_(`Document reference is for database ${s.projectId}/${s.database} but should be for database ${n.projectId}/${n.database}`);
            return {
                referenceValue: Gn(t.firestore.zr || e.O, t.S_.path)
            };
        }
        if (void 0 === t && e.ignoreUndefinedProperties) return null;
        throw e.K_("Unsupported field value: " + ci(t));
    }
    /**
 * Checks whether an object looks like a JSON object that should be converted
 * into a struct. Normal class/prototype instances are considered to look like
 * JSON objects since they should be converted to a struct value. Arrays, Dates,
 * GeoPoints, etc. are not considered to look like JSON objects since they map
 * to specific FieldValue types other than ObjectValue.
 */ (t, e);
}

function Ra(t, e) {
    const n = {};
    return wt(t) ? 
    // If we encounter an empty object, we explicitly add it to the update
    // mask to ensure that the server creates a map entry.
    e.path && e.path.length > 0 && e.Te.push(e.path) : dt(t, ((t, s) => {
        const i = Aa(s, e.L_(t));
        null != i && (n[t] = i);
    })), {
        mapValue: {
            fields: n
        }
    };
}

function Pa(t) {
    return !("object" != typeof t || null === t || t instanceof Array || t instanceof Date || t instanceof ot || t instanceof na || t instanceof F || t instanceof zc || t instanceof Gc);
}

function ya(t, e, n) {
    if (!Pa(n) || !function(t) {
        return "object" == typeof t && null !== t && (Object.getPrototypeOf(t) === Object.prototype || null === Object.getPrototypeOf(t));
    }(n)) {
        const s = ci(n);
        throw "an object" === s ? e.K_(t + " a custom object") : e.K_(t + " " + s);
    }
}

/**
 * Helper that calls fromDotSeparatedString() but wraps any error thrown.
 */ function ga(t, e, n) {
    if (
    // If required, replace the FieldPath Compat class with with the firestore-exp
    // FieldPath.
    e instanceof O && (e = e._), e instanceof jc) return e.b_;
    if ("string" == typeof e) return pa(t, e);
    throw ba("Field path arguments must be of type string or FieldPath.", t, 
    /* hasConverter= */ !1, 
    /* path= */ void 0, n);
}

/**
 * Matches any characters in a field path string that are reserved.
 */ const Va = new RegExp("[~\\*/\\[\\]]");

/**
 * Wraps fromDotSeparatedString with an error message about the method that
 * was thrown.
 * @param methodName - The publicly visible method name
 * @param path - The dot-separated string form of a field path which will be
 * split on dots.
 * @param targetDoc - The document against which the field path will be
 * evaluated.
 */ function pa(t, e, n) {
    if (e.search(Va) >= 0) throw ba(`Invalid field path (${e}). Paths must not contain '~', '*', '/', '[', or ']'`, t, 
    /* hasConverter= */ !1, 
    /* path= */ void 0, n);
    try {
        return new jc(...e.split(".")).b_;
    } catch (s) {
        throw ba(`Invalid field path (${e}). Paths must not be empty, begin with '.', end with '.', or contain '..'`, t, 
        /* hasConverter= */ !1, 
        /* path= */ void 0, n);
    }
}

function ba(t, e, n, s, i) {
    const r = s && !s.bt(), o = void 0 !== i;
    let c = `Function ${e}() called with invalid data`;
    n && (c += " (via `toFirestore()`)"), c += ". ";
    let a = "";
    return (r || o) && (a += " (found", r && (a += " in field " + s), o && (a += " in document " + i), 
    a += ")"), new N(x.INVALID_ARGUMENT, c + t + a);
}

/** Checks `haystack` if FieldPath `needle` is present. Runs in O(n). */ function va(t, e) {
    return t.some((t => t.isEqual(e)));
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Internal transaction object responsible for accumulating the mutations to
 * perform and the base versions for any documents read.
 */ class Sa {
    constructor(t) {
        this.Qh = t, 
        // The version of each document that was read during this transaction.
        this.Y_ = new Map, this.mutations = [], this.X_ = !1, 
        /**
         * A deferred usage error that occurred previously in this transaction that
         * will cause the transaction to fail once it actually commits.
         */
        this.Z_ = null, 
        /**
         * Set of documents that have been written in the transaction.
         *
         * When there's more than one write to the same key in a transaction, any
         * writes after the first are handled differently.
         */
        this.tf = new Set;
    }
    async ef(t) {
        if (this.nf(), this.mutations.length > 0) throw new N(x.INVALID_ARGUMENT, "Firestore transactions require all reads to be executed before all writes.");
        const e = await async function(t, e) {
            const n = V(t), s = Zn(n.fe) + "/documents", i = {
                documents: e.map((t => Hn(n.fe, t)))
            }, r = await n._t("BatchGetDocuments", s, i), o = new Map;
            r.forEach((t => {
                const e = ss(n.fe, t);
                o.set(e.key.toString(), e);
            }));
            const c = [];
            return e.forEach((t => {
                const e = o.get(t.toString());
                g(!!e), c.push(e);
            })), c;
        }(this.Qh, t);
        return e.forEach((t => {
            t instanceof Ut || t instanceof qt ? this.sf(t) : y();
        })), e;
    }
    set(t, e) {
        this.write(e.F_(t, this.Ee(t))), this.tf.add(t.toString());
    }
    update(t, e) {
        try {
            this.write(e.F_(t, this.rf(t)));
        } catch (t) {
            this.Z_ = t;
        }
        this.tf.add(t.toString());
    }
    delete(t) {
        this.write(new un(t, this.Ee(t))), this.tf.add(t.toString());
    }
    async commit() {
        if (this.nf(), this.Z_) throw this.Z_;
        const t = this.Y_;
        // For each mutation, note that the doc was written.
                this.mutations.forEach((e => {
            t.delete(e.key.toString());
        })), 
        // For each document that was read but not written to, we want to perform
        // a `verify` operation.
        t.forEach(((t, e) => {
            const n = _t.Lt(e);
            this.mutations.push(new hn(n, this.Ee(n)));
        })), await async function(t, e) {
            const n = V(t), s = Zn(n.fe) + "/documents", i = {
                writes: e.map((t => rs(n.fe, t)))
            };
            await n.at("Commit", s, i);
        }(this.Qh, this.mutations), this.X_ = !0;
    }
    sf(t) {
        let e;
        if (t instanceof qt) e = t.version; else {
            if (!(t instanceof Ut)) throw y();
            // For deleted docs, we must use baseVersion 0 when we overwrite them.
            e = ct.min();
        }
        const n = this.Y_.get(t.key.toString());
        if (n) {
            if (!e.isEqual(n)) 
            // This transaction will fail no matter what.
            throw new N(x.ABORTED, "Document version changed between two reads.");
        } else this.Y_.set(t.key.toString(), e);
    }
    /**
     * Returns the version of this document when it was read in this transaction,
     * as a precondition, or no precondition if it was not read.
     */    Ee(t) {
        const e = this.Y_.get(t.toString());
        return !this.tf.has(t.toString()) && e ? ze.updateTime(e) : ze.de();
    }
    /**
     * Returns the precondition for a document if the operation is an update.
     */    rf(t) {
        const e = this.Y_.get(t.toString());
        // The first time a document is written, we want to take into account the
        // read time and existence
                if (!this.tf.has(t.toString()) && e) {
            if (e.isEqual(ct.min())) 
            // The document doesn't exist, so fail the transaction.
            // This has to be validated locally because you can't send a
            // precondition that a document does not exist without changing the
            // semantics of the backend write to be an insert. This is the reverse
            // of what we want, since we want to assert that the document doesn't
            // exist but then send the update and have it fail. Since we can't
            // express that to the backend, we have to validate locally.
            // Note: this can change once we can send separate verify writes in the
            // transaction.
            throw new N(x.INVALID_ARGUMENT, "Can't update a document that doesn't exist.");
            // Document exists, base precondition on document update time.
                        return ze.updateTime(e);
        }
        // Document was not read, so we just use the preconditions for a blind
        // update.
        return ze.exists(!0);
    }
    write(t) {
        this.nf(), this.mutations.push(t);
    }
    nf() {}
}

/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * TransactionRunner encapsulates the logic needed to run and retry transactions
 * with backoff.
 */
class Da {
    constructor(t, e, n, s) {
        this.ks = t, this.Qh = e, this.updateFunction = n, this.Bs = s, this.cf = 5, this.ui = new ks(this.ks, "transaction_retry" /* TransactionRetry */);
    }
    /** Runs the transaction and sets the result on deferred. */    run() {
        this.af();
    }
    af() {
        this.ui.ti((async () => {
            const t = new Sa(this.Qh), e = this.uf(t);
            e && e.then((e => {
                this.ks.Ws((() => t.commit().then((() => {
                    this.Bs.resolve(e);
                })).catch((t => {
                    this.hf(t);
                }))));
            })).catch((t => {
                this.hf(t);
            }));
        }));
    }
    uf(t) {
        try {
            const e = this.updateFunction(t);
            return !H(e) && e.catch && e.then ? e : (this.Bs.reject(Error("Transaction callback must return a Promise")), 
            null);
        } catch (t) {
            // Do not retry errors thrown by user provided updateFunction.
            return this.Bs.reject(t), null;
        }
    }
    hf(t) {
        this.cf > 0 && this.lf(t) ? (this.cf -= 1, this.ks.Ws((() => (this.af(), Promise.resolve())))) : this.Bs.reject(t);
    }
    lf(t) {
        if ("FirebaseError" === t.name) {
            // In transactions, the backend will fail outdated reads with FAILED_PRECONDITION and
            // non-matching document versions with ABORTED. These errors should be retried.
            const e = t.code;
            return "aborted" === e || "failed-precondition" === e || !et(e);
        }
        return !1;
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * FirestoreClient is a top-level class that constructs and owns all of the
 * pieces of the client SDK architecture. It is responsible for creating the
 * async queue that is shared by all of the other components in the system.
 */
class Ca {
    constructor(t, 
    /**
     * Asynchronous queue responsible for all of our internal processing. When
     * we get incoming work from the user (via public API) or the network
     * (incoming GRPC messages), we should always schedule onto this queue.
     * This ensures all of our work is properly serialized (e.g. we don't
     * start processing a new operation while the previous one is waiting for
     * an async I/O to complete).
     */
    e, n) {
        this.credentials = t, this.ks = e, this.rt = n, this.user = $.UNAUTHENTICATED, this.clientId = b.t(), 
        this._f = () => {}, this.S = new it, this.credentials.V((t => {
            m("FirestoreClient", "Received user=", t.uid), this.user.isEqual(t) || (this.user = t, 
            this._f(t)), this.S.resolve();
        }));
    }
    async getConfiguration() {
        return await this.S.promise, {
            ks: this.ks,
            rt: this.rt,
            clientId: this.clientId,
            credentials: this.credentials,
            h_: this.user,
            Bl: 100
        };
    }
    ff(t) {
        this._f = t, 
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.S.promise.then((() => this._f(this.user)));
    }
    /**
     * Checks that the client has not been terminated. Ensures that other methods on
     * this class cannot be called after the client is terminated.
     */    df() {
        if (this.ks.wf) throw new N(x.FAILED_PRECONDITION, "The client has already been terminated.");
    }
    terminate() {
        this.ks.Ef();
        const t = new it;
        return this.ks.Tf((async () => {
            try {
                this.If && await this.If.terminate(), this.mf && await this.mf.terminate(), 
                // `removeChangeListener` must be called after shutting down the
                // RemoteStore as it will prevent the RemoteStore from retrieving
                // auth tokens.
                this.credentials.p(), t.resolve();
            } catch (e) {
                const n = Os(e, "Failed to shutdown persistence");
                t.reject(n);
            }
        })), t.promise;
    }
}

async function xa(t, e) {
    t.ks.Af(), m("FirestoreClient", "Initializing OfflineComponentProvider");
    const n = await t.getConfiguration();
    await e.initialize(n), t.ff((n => t.ks.ea((async () => {
        await Cr(e.Uh, n);
    })))), 
    // When a user calls clearPersistence() in one client, all other clients
    // need to be terminated to allow the delete to succeed.
    e.persistence.Xc((() => t.terminate())), t.mf = e;
}

async function Na(t, e) {
    t.ks.Af();
    const n = await Fa(t);
    m("FirestoreClient", "Initializing OnlineComponentProvider");
    const s = await t.getConfiguration();
    await e.initialize(n, s), 
    // The CredentialChangeListener of the online component provider takes
    // precedence over the offline component provider.
    t.ff((n => t.ks.ea((() => async function(t, e) {
        const n = V(t);
        n.ks.Af(), m("RemoteStore", "RemoteStore received new credentials");
        const s = go(n);
        // Tear down and re-create our network streams. This will ensure we get a
        // fresh auth token for the new user and re-fill the write pipeline with
        // new mutations from the LocalStore (since mutations are per-user).
                n.Gh.add(3 /* CredentialChange */), await To(n), s && 
        // Don't set the network status to Unknown if we are offline.
        n.Jh.set("Unknown" /* Unknown */), await n.Kh.E_(e), n.Gh.delete(3 /* CredentialChange */), 
        await Eo(n);
    }(e.Ml, n))))), t.If = e;
}

async function Fa(t) {
    return t.mf || (m("FirestoreClient", "Using default OfflineComponentProvider"), 
    await xa(t, new $c)), t.mf;
}

async function Oa(t) {
    return t.If || (m("FirestoreClient", "Using default OnlineComponentProvider"), await Na(t, new Uc)), 
    t.If;
}

function ka(t) {
    return Fa(t).then((t => t.persistence));
}

function Ma(t) {
    return Fa(t).then((t => t.Uh));
}

function La(t) {
    return Oa(t).then((t => t.Ml));
}

function $a(t) {
    return Oa(t).then((t => t.Lu));
}

async function Ba(t) {
    const e = await Oa(t), n = e.Ll;
    return n.ol = cc.bind(null, e.Lu), n.ul = uc.bind(null, e.Lu), n;
}

/** Enables the network connection and re-enqueues all pending operations. */ function qa(t) {
    return t.ks.enqueue((async () => {
        const e = await ka(t), n = await La(t);
        return e.Zc(!0), function(t) {
            const e = V(t);
            return e.Gh.delete(0 /* UserDisabled */), Eo(e);
        }(n);
    }));
}

/** Disables the network connection. Pending operations will not complete. */ function Ua(t) {
    return t.ks.enqueue((async () => {
        const e = await ka(t), n = await La(t);
        return e.Zc(!1), async function(t) {
            const e = V(t);
            e.Gh.add(0 /* UserDisabled */), await To(e), 
            // Set the OnlineState to Offline so get()s return from cache, etc.
            e.Jh.set("Offline" /* Offline */);
        }(n);
    }));
}

/**
 * Returns a Promise that resolves when all writes that were pending at the time
 * this method was called received server acknowledgement. An acknowledgement
 * can be either acceptance or rejection.
 */ function Qa(t, e) {
    const n = new it;
    return t.ks.Ws((async () => async function(t, e, n) {
        try {
            const s = await function(t, e) {
                const n = V(t);
                return n.persistence.runTransaction("read document", "readonly", (t => n.Ka.Pa(t, e)));
            }(t, e);
            s instanceof qt ? n.resolve(s) : s instanceof Ut ? n.resolve(null) : n.reject(new N(x.UNAVAILABLE, "Failed to get document from cache. (However, this document may exist on the server. Run again without setting 'source' in the GetOptions to attempt to retrieve the document from the server.)"));
        } catch (t) {
            const s = Os(t, `Failed to get document '${e} from cache`);
            n.reject(s);
        }
    }
    /**
 * Retrieves a latency-compensated document from the backend via a
 * SnapshotListener.
 */ (await Ma(t), e, n))), n.promise;
}

function Ka(t, e, n = {}) {
    const s = new it;
    return t.ks.Ws((async () => function(t, e, n, s, i) {
        const r = new Kc({
            next: r => {
                // Remove query first before passing event to user to avoid
                // user actions affecting the now stale query.
                e.Ws((() => jo(t, o)));
                const c = r.docs.has(n);
                !c && r.fromCache ? 
                // TODO(dimond): If we're online and the document doesn't
                // exist then we resolve with a doc.exists set to false. If
                // we're offline however, we reject the Promise in this
                // case. Two options: 1) Cache the negative response from
                // the server so we can deliver that even when you're
                // offline 2) Actually reject the Promise in the online case
                // if the document doesn't exist.
                i.reject(new N(x.UNAVAILABLE, "Failed to get document because the client is offline.")) : c && r.fromCache && s && "server" === s.source ? i.reject(new N(x.UNAVAILABLE, 'Failed to get document from server. (However, this document does exist in the local cache. Run again without setting source to "server" to retrieve the cached document.)')) : i.resolve(r);
            },
            error: t => i.reject(t)
        }), o = new Jo(de(n.path), r, {
            includeMetadataChanges: !0,
            El: !0
        });
        return Wo(t, o);
    }(await Ba(t), t.ks, e, n, s))), s.promise;
}

function Wa(t, e) {
    const n = new it;
    return t.ks.Ws((async () => async function(t, e, n) {
        try {
            const s = await $r(t, e, 
            /* usePreviousResults= */ !0), i = new sc(e, s.za), r = i.bl(s.documents), o = i.yr(r, 
            /* updateLimboDocuments= */ !1);
            n.resolve(o.snapshot);
        } catch (t) {
            const s = Os(t, `Failed to execute query '${e} against cache`);
            n.reject(s);
        }
    }
    /**
 * Retrieves a latency-compensated query snapshot from the backend via a
 * SnapshotListener.
 */ (await Ma(t), e, n))), n.promise;
}

function ja(t, e, n = {}) {
    const s = new it;
    return t.ks.Ws((async () => function(t, e, n, s, i) {
        const r = new Kc({
            next: n => {
                // Remove query first before passing event to user to avoid
                // user actions affecting the now stale query.
                e.Ws((() => jo(t, o))), n.fromCache && "server" === s.source ? i.reject(new N(x.UNAVAILABLE, 'Failed to get documents from server. (However, these documents may exist in the local cache. Run again without setting source to "server" to retrieve the cached documents.)')) : i.resolve(n);
            },
            error: t => i.reject(t)
        }), o = new Jo(n, r, {
            includeMetadataChanges: !0,
            El: !0
        });
        return Wo(t, o);
    }(await Ba(t), t.ks, e, n, s))), s.promise;
}

function Ga(t, e) {
    const n = new Kc(e);
    return t.ks.Ws((async () => function(t, e) {
        V(t).rl.add(e), 
        // Immediately fire an initial event, indicating all existing listeners
        // are in-sync.
        e.next();
    }(await Ba(t), n))), () => {
        n.I_(), t.ks.Ws((async () => function(t, e) {
            V(t).rl.delete(e);
        }(await Ba(t), n)));
    };
}

/**
 * Takes an updateFunction in which a set of reads and writes can be performed
 * atomically. In the updateFunction, the client can read and write values
 * using the supplied transaction object. After the updateFunction, all
 * changes will be committed. If a retryable error occurs (ex: some other
 * client has changed any of the data referenced), then the updateFunction
 * will be called again after a backoff. If the updateFunction still fails
 * after all retries, then the transaction will be rejected.
 *
 * The transaction object passed to the updateFunction contains methods for
 * accessing documents and collections. Unlike other datastore access, data
 * accessed with the transaction will not reflect local changes that have not
 * been committed. For this reason, it is required that all reads are
 * performed before any writes. Transactions must be performed while online.
 */ function za(t, e) {
    const n = new it;
    return t.ks.Ws((async () => {
        const s = await function(t) {
            return Oa(t).then((t => t.Qh));
        }(t);
        new Da(t.ks, s, e, n).run();
    })), n.promise;
}

function Ha(t, e, n, s) {
    const i = function(t, e) {
        let n;
        n = "string" == typeof t ? (new TextEncoder).encode(t) : t;
        return function(t, e) {
            return new Wc(t, e);
        }(function(t, e) {
            if (t instanceof Uint8Array) return Qc(t, e);
            if (t instanceof ArrayBuffer) return Qc(new Uint8Array(t), e);
            if (t instanceof ReadableStream) return t.getReader();
            throw new Error("Source of `toByteStreamReader` has to be a ArrayBuffer or ReadableStream");
        }(n), e);
    }
    /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ (n, ys(e));
    t.ks.Ws((async () => {
        Lc(await $a(t), i, s);
    }));
}

function Ja(t, e) {
    return t.ks.enqueue((async () => function(t, e) {
        const n = V(t);
        return n.persistence.runTransaction("Get named query", "readonly", (t => n.Wc.ho(t, e)));
    }(await Ma(t), e)));
}

class Ya {
    constructor() {
        // The last promise in the queue.
        this.Rf = Promise.resolve(), 
        // A list of retryable operations. Retryable operations are run in order and
        // retried with backoff.
        this.Pf = [], 
        // Is this AsyncQueue being shut down? Once it is set to true, it will not
        // be changed again.
        this.yf = !1, 
        // Operations scheduled to be queued in the future. Operations are
        // automatically removed after they are run or canceled.
        this.gf = [], 
        // visible for testing
        this.Vf = null, 
        // Flag set while there's an outstanding AsyncQueue operation, used for
        // assertion sanity-checks.
        this.pf = !1, 
        // List of TimerIds to fast-forward delays for.
        this.bf = [], 
        // Backoff timer used to schedule retries for retryable operations
        this.ui = new ks(this, "async_queue_retry" /* AsyncQueueRetry */), 
        // Visibility handler that triggers an immediate retry of all retryable
        // operations. Meant to speed up recovery when we regain file system access
        // after page comes into foreground.
        this.vf = () => {
            const t = _o();
            t && m("AsyncQueue", "Visibility state changed to " + t.visibilityState), this.ui.si();
        };
        const t = _o();
        t && "function" == typeof t.addEventListener && t.addEventListener("visibilitychange", this.vf);
    }
    get wf() {
        return this.yf;
    }
    /**
     * Adds a new operation to the queue without waiting for it to complete (i.e.
     * we ignore the Promise result).
     */    Ws(t) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.enqueue(t);
    }
    Tf(t) {
        this.Sf(), 
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.Df(t);
    }
    Ef() {
        if (!this.yf) {
            this.yf = !0;
            const t = _o();
            t && "function" == typeof t.removeEventListener && t.removeEventListener("visibilitychange", this.vf);
        }
    }
    enqueue(t) {
        return this.Sf(), this.yf ? new Promise((t => {})) : this.Df(t);
    }
    ea(t) {
        this.Ws((() => (this.Pf.push(t), this.Cf())));
    }
    /**
     * Runs the next operation from the retryable queue. If the operation fails,
     * reschedules with backoff.
     */    async Cf() {
        if (0 !== this.Pf.length) {
            try {
                await this.Pf[0](), this.Pf.shift(), this.ui.reset();
            } catch (t) {
                if (!Ss(t)) throw t;
 // Failure will be handled by AsyncQueue
                                m("AsyncQueue", "Operation failed with retryable error: " + t);
            }
            this.Pf.length > 0 && 
            // If there are additional operations, we re-schedule `retryNextOp()`.
            // This is necessary to run retryable operations that failed during
            // their initial attempt since we don't know whether they are already
            // enqueued. If, for example, `op1`, `op2`, `op3` are enqueued and `op1`
            // needs to  be re-run, we will run `op1`, `op1`, `op2` using the
            // already enqueued calls to `retryNextOp()`. `op3()` will then run in the
            // call scheduled here.
            // Since `backoffAndRun()` cancels an existing backoff and schedules a
            // new backoff on every call, there is only ever a single additional
            // operation in the queue.
            this.ui.ti((() => this.Cf()));
        }
    }
    Df(t) {
        const e = this.Rf.then((() => (this.pf = !0, t().catch((t => {
            this.Vf = t, this.pf = !1;
            // Re-throw the error so that this.tail becomes a rejected Promise and
            // all further attempts to chain (via .then) will just short-circuit
            // and return the rejected Promise.
            throw A("INTERNAL UNHANDLED ERROR: ", 
            /**
 * Chrome includes Error.message in Error.stack. Other browsers do not.
 * This returns expected output of message + stack when available.
 * @param error - Error or FirestoreError
 */
            function(t) {
                let e = t.message || "";
                t.stack && (e = t.stack.includes(t.message) ? t.stack : t.message + "\n" + t.stack);
                return e;
            }
            /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
            /** DOMException error code constants. */ (t)), t;
        })).then((t => (this.pf = !1, t))))));
        return this.Rf = e, e;
    }
    ni(t, e, n) {
        this.Sf(), 
        // Fast-forward delays for timerIds that have been overriden.
        this.bf.indexOf(t) > -1 && (e = 0);
        const s = Fs.qs(this, t, e, n, (t => this.xf(t)));
        return this.gf.push(s), s;
    }
    Sf() {
        this.Vf && y();
    }
    Af() {}
    /**
     * Waits until all currently queued tasks are finished executing. Delayed
     * operations are not run.
     */    async Nf() {
        // Operations in the queue prior to draining may have enqueued additional
        // operations. Keep draining the queue until the tail is no longer advanced,
        // which indicates that no more new operations were enqueued and that all
        // operations were executed.
        let t;
        do {
            t = this.Rf, await t;
        } while (t !== this.Rf);
    }
    /**
     * For Tests: Determine if a delayed operation with a particular TimerId
     * exists.
     */    Ff(t) {
        for (const e of this.gf) if (e.Ms === t) return !0;
        return !1;
    }
    /**
     * For Tests: Runs some or all delayed operations early.
     *
     * @param lastTimerId - Delayed operations up to and including this TimerId
     * will be drained. Pass TimerId.All to run all delayed operations.
     * @returns a Promise that resolves once all operations have been run.
     */    Of(t) {
        // Note that draining may generate more delayed ops, so we do that first.
        return this.Nf().then((() => {
            // Run ops in the same order they'd run if they ran naturally.
            this.gf.sort(((t, e) => t.Ls - e.Ls));
            for (const e of this.gf) if (e.Ks(), "all" /* All */ !== t && e.Ms === t) break;
            return this.Nf();
        }));
    }
    /**
     * For Tests: Skip all subsequent delays for a timer id.
     */    kf(t) {
        this.bf.push(t);
    }
    /** Called once a DelayedOperation is run or canceled. */    xf(t) {
        // NOTE: indexOf / slice are O(n), but delayedOperations is expected to be small.
        const e = this.gf.indexOf(t);
        this.gf.splice(e, 1);
    }
}

const Xa = -1;

/**
 * The Cloud Firestore service interface.
 *
 * Do not call this constructor directly. Instead, use {@link getFirestore}.
 */
class Za extends _i {
    /** @hideconstructor */
    constructor(t, e) {
        super(t, e), this.Mf = new Ya, this.Wr = "name" in t ? t.name : "[DEFAULT]";
    }
    io() {
        return this.Lf || 
        // The client must be initialized to ensure that all subsequent API
        // usage throws an exception.
        eu(this), this.Lf.terminate();
    }
}

function tu(t) {
    return t.Lf || eu(t), t.Lf.df(), t.Lf;
}

function eu(t) {
    const e = t.so(), n = function(t, e, n) {
        return new W(t, e, n.host, n.ssl, n.experimentalForceLongPolling, n.experimentalAutoDetectLongPolling);
    }(t.zr, t.Wr, e);
    t.Lf = new Ca(t.Hr, t.Mf, n);
}

/**
 * Attempts to enable persistent storage, if possible.
 *
 * Must be called before any other functions (other than
 * {@link initializeFirestore}, {@link getFirestore} or
 * {@link clearIndexedDbPersistence}.
 *
 * If this fails, `enableIndexedDbPersistence()` will reject the promise it
 * returns. Note that even after this failure, the `Firestore` instance will
 * remain usable, however offline persistence will be disabled.
 *
 * There are several reasons why this can fail, which can be identified by
 * the `code` on the error.
 *
 *   * failed-precondition: The app is already open in another browser tab.
 *   * unimplemented: The browser is incompatible with the offline
 *     persistence implementation.
 *
 * @param firestore - The `Firestore` instance to enable persistence for.
 * @param persistenceSettings - Optional settings object to configure
 * persistence.
 * @returns A promise that represents successfully enabling persistent storage.
 */
/**
 * Registers both the `OfflineComponentProvider` and `OnlineComponentProvider`.
 * If the operation fails with a recoverable error (see
 * `canRecoverFromIndexedDbError()` below), the returned Promise is rejected
 * but the client remains usable.
 */
function nu(t, e, n) {
    const s = new it;
    return t.ks.enqueue((async () => {
        try {
            await xa(t, n), await Na(t, e), s.resolve();
        } catch (t) {
            if (!
            /**
 * Decides whether the provided error allows us to gracefully disable
 * persistence (as opposed to crashing the client).
 */
            function(t) {
                if ("FirebaseError" === t.name) return t.code === x.FAILED_PRECONDITION || t.code === x.UNIMPLEMENTED;
                if ("undefined" != typeof DOMException && t instanceof DOMException) 
                // There are a few known circumstances where we can open IndexedDb but
                // trying to read/write will fail (e.g. quota exceeded). For
                // well-understood cases, we attempt to detect these and then gracefully
                // fall back to memory persistence.
                // NOTE: Rather than continue to add to this list, we could decide to
                // always fall back, with the risk that we might accidentally hide errors
                // representing actual SDK bugs.
                // When the browser is out of quota we could get either quota exceeded
                // or an aborted error depending on whether the error happened during
                // schema migration.
                return 22 === t.code || 20 === t.code || 
                // Firefox Private Browsing mode disables IndexedDb and returns
                // INVALID_STATE for any usage.
                11 === t.code;
                return !0;
            }
            /**
 * Clears the persistent storage. This includes pending writes and cached
 * documents.
 *
 * Must be called while the `Firestore` instance is not started (after the app is
 * terminated or when the app is first initialized). On startup, this function
 * must be called before other functions (other than {@link
 * initializeFirestore} or {@link getFirestore})). If the `Firestore`
 * instance is still running, the promise will be rejected with the error code
 * of `failed-precondition`.
 *
 * Note: `clearIndexedDbPersistence()` is primarily intended to help write
 * reliable tests that use Cloud Firestore. It uses an efficient mechanism for
 * dropping existing data but does not attempt to securely overwrite or
 * otherwise make cached data unrecoverable. For applications that are sensitive
 * to the disclosure of cached data in between user sessions, we strongly
 * recommend not enabling persistence at all.
 *
 * @param firestore - The `Firestore` instance to clear persistence for.
 * @returns A promise that is resolved when the persistent storage is
 * cleared. Otherwise, the promise is rejected with an error.
 */ (t)) throw t;
            console.warn("Error enabling offline persistence. Falling back to persistence disabled: " + t), 
            s.reject(t);
        }
    })).then((() => s.promise));
}

function su(t) {
    if (t.Yr && !t.Xr) throw new N(x.FAILED_PRECONDITION, "Persistence can only be cleared before a Firestore instance is initialized or after it is terminated.");
    const e = new it;
    return t.Mf.Tf((async () => {
        try {
            await async function(t) {
                if (!ps.G()) return Promise.resolve();
                const e = t + "main";
                await ps.delete(e);
            }(Vr(t.zr, t.Wr)), e.resolve();
        } catch (t) {
            e.reject(t);
        }
    })), e.promise;
}

/**
 * Waits until all currently pending writes for the active user have been
 * acknowledged by the backend.
 *
 * The returned Promise resolves immediately if there are no outstanding writes.
 * Otherwise, the Promise waits for all previously issued writes (including
 * those written in a previous app session), but it does not wait for writes
 * that were added after the function is called. If you want to wait for
 * additional writes, call `waitForPendingWrites()` again.
 *
 * Any outstanding `waitForPendingWrites()` Promises are rejected during user
 * changes.
 *
 * @returns A Promise which resolves when all currently pending writes have been
 * acknowledged by the backend.
 */ function iu(t) {
    return function(t) {
        const e = new it;
        return t.ks.Ws((async () => Ec(await $a(t), e))), e.promise;
    }(tu(t = ai(t, Za)));
}

/**
 * Re-enables use of the network for this Firestore instance after a prior
 * call to {@link disableNetwork}.
 *
 * @returns A promise that is resolved once the network has been enabled.
 */ function ru(t) {
    if (t.Yr || t.Xr) throw new N(x.FAILED_PRECONDITION, "Firestore has already been started and persistence can no longer be enabled. You can only enable persistence before calling any other methods on a Firestore object.");
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Converts Firestore's internal types to the JavaScript types that we expose
 * to the user.
 */ class ou {
    $f(t, e = "none") {
        switch (gt(t)) {
          case 0 /* NullValue */ :
            return null;

          case 1 /* BooleanValue */ :
            return t.booleanValue;

          case 2 /* NumberValue */ :
            return mt(t.integerValue || t.doubleValue);

          case 3 /* TimestampValue */ :
            return this.Bf(t.timestampValue);

          case 4 /* ServerTimestampValue */ :
            return this.qf(t, e);

          case 5 /* StringValue */ :
            return t.stringValue;

          case 6 /* BlobValue */ :
            return this.Uf(At(t.bytesValue));

          case 7 /* RefValue */ :
            return this.Qf(t.referenceValue);

          case 8 /* GeoPointValue */ :
            return this.Kf(t.geoPointValue);

          case 9 /* ArrayValue */ :
            return this.Wf(t.arrayValue, e);

          case 10 /* ObjectValue */ :
            return this.jf(t.mapValue, e);

          default:
            throw y();
        }
    }
    jf(t, e) {
        const n = {};
        return dt(t.fields || {}, ((t, s) => {
            n[t] = this.$f(s, e);
        })), n;
    }
    Kf(t) {
        return new na(mt(t.latitude), mt(t.longitude));
    }
    Wf(t, e) {
        return (t.values || []).map((t => this.$f(t, e)));
    }
    qf(t, e) {
        switch (e) {
          case "previous":
            const n = Pt(t);
            return null == n ? null : this.$f(n, e);

          case "estimate":
            return this.Bf(yt(t));

          default:
            return null;
        }
    }
    Bf(t) {
        const e = It(t);
        return new ot(e.seconds, e.nanos);
    }
    Gf(t, e) {
        const n = ut.xt(t);
        g(Ps(n));
        const s = new j(n.get(1), n.get(3)), i = new _t(n.Pt(5));
        return s.isEqual(e) || 
        // TODO(b/64130202): Somehow support foreign references.
        A(`Document ${i} contains a document reference within a different database (${s.projectId}/${s.database}) which is not supported. It will be treated as a reference in the current database (${e.projectId}/${e.database}) instead.`), 
        i;
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A `DocumentSnapshot` contains data read from a document in your Firestore
 * database. The data can be extracted with `.data()` or `.get(<field>)` to
 * get a specific field.
 *
 * For a `DocumentSnapshot` that points to a non-existing document, any data
 * access will return 'undefined'. You can use the `exists()` method to
 * explicitly verify a document's existence.
 */ class cu {
    // Note: This class is stripped down version of the DocumentSnapshot in
    // the legacy SDK. The changes are:
    // - No support for SnapshotMetadata.
    // - No support for SnapshotOptions.
    /** @hideconstructor protected */
    constructor(t, e, n, s, i) {
        this.zf = t, this.Hf = e, this.S_ = n, this.Jf = s, this.v_ = i;
    }
    /** Property of the `DocumentSnapshot` that provides the document's ID. */    get id() {
        return this.S_.path.Vt();
    }
    /**
     * The `DocumentReference` for the document included in the `DocumentSnapshot`.
     */    get ref() {
        return new zc(this.zf, this.v_, this.S_);
    }
    /**
     * Signals whether or not the document at the snapshot's location exists.
     *
     * @returns true if the document exists.
     */    exists() {
        return null !== this.Jf;
    }
    /**
     * Retrieves all fields in the document as an `Object`. Returns `undefined` if
     * the document doesn't exist.
     *
     * @returns An `Object` containing all fields in the document or `undefined`
     * if the document doesn't exist.
     */    data() {
        if (this.Jf) {
            if (this.v_) {
                // We only want to use the converter and create a new DocumentSnapshot
                // if a converter has been provided.
                const t = new au(this.zf, this.Hf, this.S_, this.Jf, 
                /* converter= */ null);
                return this.v_.fromFirestore(t);
            }
            return this.Hf.$f(this.Jf.Yt());
        }
    }
    /**
     * Retrieves the field specified by `fieldPath`. Returns `undefined` if the
     * document or field doesn't exist.
     *
     * @param fieldPath - The path (for example 'foo' or 'foo.bar') to a specific
     * field.
     * @returns The data at the specified field location or undefined if no such
     * field exists in the document.
     */
    // We are using `any` here to avoid an explicit cast by our users.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(t) {
        if (this.Jf) {
            const e = this.Jf.data().field(uu("DocumentSnapshot.get", t));
            if (null !== e) return this.Hf.$f(e);
        }
    }
}

/**
 * A `QueryDocumentSnapshot` contains data read from a document in your
 * Firestore database as part of a query. The document is guaranteed to exist
 * and its data can be extracted with `.data()` or `.get(<field>)` to get a
 * specific field.
 *
 * A `QueryDocumentSnapshot` offers the same API surface as a
 * `DocumentSnapshot`. Since query results contain only existing documents, the
 * `exists` property will always be true and `data()` will never return
 * 'undefined'.
 */ class au extends cu {
    /**
     * Retrieves all fields in the document as an `Object`.
     *
     * @override
     * @returns An `Object` containing all fields in the document.
     */
    data() {
        return super.data();
    }
}

/**
 * Helper that calls fromDotSeparatedString() but wraps any error thrown.
 */ function uu(t, e) {
    return "string" == typeof e ? pa(t, e) : e instanceof O ? e._.b_ : e.b_;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function hu(t) {
    if (Ee(t) && 0 === t.ne.length) throw new N(x.UNIMPLEMENTED, "limitToLast() queries require specifying at least one orderBy() clause");
}

/**
 * A `QueryConstraint` is used to narrow the set of documents returned by a
 * Firestore query. `QueryConstraint`s are created by invoking {@link where},
 * {@link orderBy}, {@link startAt}, {@link startAfter}, {@link
 * endBefore}, {@link endAt}, {@link limit} or {@link limitToLast} and
 * can then be passed to {@link query} to create a new query instance that
 * also contains this `QueryConstraint`.
 */ class lu {}

/**
 * Creates a new immutable instance of `query` that is extended to also include
 * additional query constraints.
 *
 * @param query - The query instance to use as a base for the new constraints.
 * @param queryConstraints - The list of `QueryConstraint`s to apply.
 * @throws if any of the provided query constraints cannot be combined with the
 * existing or new constraints.
 */ function _u(t, ...e) {
    for (const n of e) t = n.Yf(t);
    return t;
}

class fu extends lu {
    constructor(t, e, n) {
        super(), this.Xf = t, this.Zf = e, this.td = n, this.type = "where";
    }
    Yf(t) {
        const e = ua(t.firestore), n = function(t, e, n, s, i, r, o) {
            let c;
            if (i.Ot()) {
                if ("array-contains" /* ARRAY_CONTAINS */ === r || "array-contains-any" /* ARRAY_CONTAINS_ANY */ === r) throw new N(x.INVALID_ARGUMENT, `Invalid Query. You can't perform '${r}' queries on FieldPath.documentId().`);
                if ("in" /* IN */ === r || "not-in" /* NOT_IN */ === r) {
                    Au(o, r);
                    const e = [];
                    for (const n of o) e.push(mu(s, t, n));
                    c = {
                        arrayValue: {
                            values: e
                        }
                    };
                } else c = mu(s, t, o);
            } else "in" /* IN */ !== r && "not-in" /* NOT_IN */ !== r && "array-contains-any" /* ARRAY_CONTAINS_ANY */ !== r || Au(o, r), 
            c = ma(n, e, o, 
            /* allowArrays= */ "in" /* IN */ === r || "not-in" /* NOT_IN */ === r);
            const a = Jt.create(i, r, c);
            return function(t, e) {
                if (e.ee()) {
                    const n = Ie(t);
                    if (null !== n && !n.isEqual(e.field)) throw new N(x.INVALID_ARGUMENT, `Invalid query. All where filters with an inequality (<, <=, >, or >=) must be on the same field. But you have inequality filters on '${n.toString()}' and '${e.field.toString()}'`);
                    const s = Te(t);
                    null !== s && Ru(t, e.field, s);
                }
                const n = function(t, e) {
                    for (const n of t.filters) if (e.indexOf(n.op) >= 0) return n.op;
                    return null;
                }(t, 
                /**
 * Given an operator, returns the set of operators that cannot be used with it.
 *
 * Operators in a query must adhere to the following set of rules:
 * 1. Only one array operator is allowed.
 * 2. Only one disjunctive operator is allowed.
 * 3. NOT_EQUAL cannot be used with another NOT_EQUAL operator.
 * 4. NOT_IN cannot be used with array, disjunctive, or NOT_EQUAL operators.
 *
 * Array operators: ARRAY_CONTAINS, ARRAY_CONTAINS_ANY
 * Disjunctive operators: IN, ARRAY_CONTAINS_ANY, NOT_IN
 */
                function(t) {
                    switch (t) {
                      case "!=" /* NOT_EQUAL */ :
                        return [ "!=" /* NOT_EQUAL */ , "not-in" /* NOT_IN */ ];

                      case "array-contains" /* ARRAY_CONTAINS */ :
                        return [ "array-contains" /* ARRAY_CONTAINS */ , "array-contains-any" /* ARRAY_CONTAINS_ANY */ , "not-in" /* NOT_IN */ ];

                      case "in" /* IN */ :
                        return [ "array-contains-any" /* ARRAY_CONTAINS_ANY */ , "in" /* IN */ , "not-in" /* NOT_IN */ ];

                      case "array-contains-any" /* ARRAY_CONTAINS_ANY */ :
                        return [ "array-contains" /* ARRAY_CONTAINS */ , "array-contains-any" /* ARRAY_CONTAINS_ANY */ , "in" /* IN */ , "not-in" /* NOT_IN */ ];

                      case "not-in" /* NOT_IN */ :
                        return [ "array-contains" /* ARRAY_CONTAINS */ , "array-contains-any" /* ARRAY_CONTAINS_ANY */ , "in" /* IN */ , "not-in" /* NOT_IN */ , "!=" /* NOT_EQUAL */ ];

                      default:
                        return [];
                    }
                }(e.op));
                if (null !== n) 
                // Special case when it's a duplicate op to give a slightly clearer error message.
                throw n === e.op ? new N(x.INVALID_ARGUMENT, `Invalid query. You cannot use more than one '${e.op.toString()}' filter.`) : new N(x.INVALID_ARGUMENT, `Invalid query. You cannot use '${e.op.toString()}' filters with '${n.toString()}' filters.`);
            }(t, a), a;
        }(t.C_, "where", e, t.firestore.zr, this.Xf, this.Zf, this.td);
        return new Hc(t.firestore, t.v_, function(t, e) {
            const n = t.filters.concat([ e ]);
            return new _e(t.path, t.collectionGroup, t.ne.slice(), n, t.limit, t.limitType, t.startAt, t.endAt);
        }(t.C_, n));
    }
}

/**
 * Creates a `QueryConstraint` that enforces that documents must contain the
 * specified field and that the value should satisfy the relation constraint
 * provided.
 *
 * @param fieldPath - The path to compare
 * @param opStr - The operation string (e.g "&lt;", "&lt;=", "==", "&lt;",
 *   "&lt;=", "!=").
 * @param value - The value for comparison
 * @returns The created `Query`.
 */ class du extends lu {
    constructor(t, e) {
        super(), this.Xf = t, this.ed = e, this.type = "orderBy";
    }
    Yf(t) {
        const e = function(t, e, n) {
            if (null !== t.startAt) throw new N(x.INVALID_ARGUMENT, "Invalid query. You must not call startAt() or startAfter() before calling orderBy().");
            if (null !== t.endAt) throw new N(x.INVALID_ARGUMENT, "Invalid query. You must not call endAt() or endBefore() before calling orderBy().");
            const s = new ae(e, n);
            return function(t, e) {
                if (null === Te(t)) {
                    // This is the first order by. It must match any inequality.
                    const n = Ie(t);
                    null !== n && Ru(t, n, e.field);
                }
            }(t, s), s;
        }
        /**
 * Create a Bound from a query and a document.
 *
 * Note that the Bound will always include the key of the document
 * and so only the provided document will compare equal to the returned
 * position.
 *
 * Will throw if the document does not contain all fields of the order by
 * of the query or if any of the fields in the order by are an uncommitted
 * server timestamp.
 */ (t.C_, this.Xf, this.ed);
        return new Hc(t.firestore, t.v_, function(t, e) {
            // TODO(dimond): validate that orderBy does not list the same key twice.
            const n = t.ne.concat([ e ]);
            return new _e(t.path, t.collectionGroup, n, t.filters.slice(), t.limit, t.limitType, t.startAt, t.endAt);
        }(t.C_, e));
    }
}

/**
 * Creates a `QueryConstraint` that sorts the query result by the
 * specified field, optionally in descending order instead of ascending.
 *
 * @param fieldPath - The field to sort by.
 * @param directionStr - Optional direction to sort by ('asc' or 'desc'). If
 * not specified, order will be ascending.
 * @returns The created `Query`.
 */ class wu extends lu {
    constructor(t, e, n) {
        super(), this.type = t, this.nd = e, this.sd = n;
    }
    Yf(t) {
        return new Hc(t.firestore, t.v_, Pe(t.C_, this.nd, this.sd));
    }
}

/**
 * Creates a `QueryConstraint` that only returns the first matching documents.
 *
 * @param limit - The maximum number of items to return.
 * @returns The created `Query`.
 */ class Eu extends lu {
    constructor(t, e, n) {
        super(), this.type = t, this.rd = e, this.od = n;
    }
    Yf(t) {
        const e = Iu(t, this.type, this.rd, this.od);
        return new Hc(t.firestore, t.v_, function(t, e) {
            return new _e(t.path, t.collectionGroup, t.ne.slice(), t.filters.slice(), t.limit, t.limitType, e, t.endAt);
        }(t.C_, e));
    }
}

class Tu extends lu {
    constructor(t, e, n) {
        super(), this.type = t, this.rd = e, this.od = n;
    }
    Yf(t) {
        const e = Iu(t, this.type, this.rd, this.od);
        return new Hc(t.firestore, t.v_, function(t, e) {
            return new _e(t.path, t.collectionGroup, t.ne.slice(), t.filters.slice(), t.limit, t.limitType, t.startAt, e);
        }(t.C_, e));
    }
}

/** Helper function to create a bound from a document or fields */
function Iu(t, e, n, s) {
    if (n[0] instanceof O && (n[0] = n[0]._), n[0] instanceof cu) return function(t, e, n, s, i) {
        if (!s) throw new N(x.NOT_FOUND, "Can't use a DocumentSnapshot that doesn't exist for " + n + "().");
        const r = [];
        // Because people expect to continue/end a query at the exact document
        // provided, we need to use the implicit sort order rather than the explicit
        // sort order, because it's guaranteed to contain the document key. That way
        // the position becomes unambiguous and the query continues/ends exactly at
        // the provided document. Without the key (by using the explicit sort
        // orders), multiple documents could match the position, yielding duplicate
        // results.
                for (const n of Ae(t)) if (n.field.Ot()) r.push(Ct(e, s.key)); else {
            const t = s.field(n.field);
            if (Rt(t)) throw new N(x.INVALID_ARGUMENT, 'Invalid query. You are trying to start or end a query using a document for which the field "' + n.field + '" is an uncommitted server timestamp. (Since the value of this field is unknown, you cannot start/end a query with it.)');
            if (null === t) {
                const t = n.field.Ct();
                throw new N(x.INVALID_ARGUMENT, `Invalid query. You are trying to start or end a query using a document for which the field '${t}' (used as the orderBy) does not exist.`);
            }
            r.push(t);
        }
        return new oe(r, i);
    }
    /**
 * Converts a list of field values to a Bound for the given query.
 */ (t.C_, t.firestore.zr, e, n[0].Jf, s);
    {
        const i = ua(t.firestore);
        return function(t, e, n, s, i, r) {
            // Use explicit order by's because it has to match the query the user made
            const o = t.ne;
            if (i.length > o.length) throw new N(x.INVALID_ARGUMENT, `Too many arguments provided to ${s}(). The number of arguments must be less than or equal to the number of orderBy() clauses`);
            const c = [];
            for (let r = 0; r < i.length; r++) {
                const a = i[r];
                if (o[r].field.Ot()) {
                    if ("string" != typeof a) throw new N(x.INVALID_ARGUMENT, `Invalid query. Expected a string for document ID in ${s}(), but got a ${typeof a}`);
                    if (!me(t) && -1 !== a.indexOf("/")) throw new N(x.INVALID_ARGUMENT, `Invalid query. When querying a collection and ordering by FieldPath.documentId(), the value passed to ${s}() must be a plain document ID, but '${a}' contains a slash.`);
                    const n = t.path.child(ut.xt(a));
                    if (!_t.qt(n)) throw new N(x.INVALID_ARGUMENT, `Invalid query. When querying a collection group and ordering by FieldPath.documentId(), the value passed to ${s}() must result in a valid document path, but '${n}' is not because it contains an odd number of segments.`);
                    const i = new _t(n);
                    c.push(Ct(e, i));
                } else {
                    const t = ma(n, s, a);
                    c.push(t);
                }
            }
            return new oe(c, r);
        }
        /**
 * Parses the given documentIdValue into a ReferenceValue, throwing
 * appropriate errors if the value is anything other than a DocumentReference
 * or String, or if the string is malformed.
 */ (t.C_, t.firestore.zr, i, e, n, s);
    }
}

function mu(t, e, n) {
    if (n instanceof O && (n = n._), "string" == typeof n) {
        if ("" === n) throw new N(x.INVALID_ARGUMENT, "Invalid query. When querying with FieldPath.documentId(), you must provide a valid document ID, but it was an empty string.");
        if (!me(e) && -1 !== n.indexOf("/")) throw new N(x.INVALID_ARGUMENT, `Invalid query. When querying a collection by FieldPath.documentId(), you must provide a plain document ID, but '${n}' contains a '/' character.`);
        const s = e.path.child(ut.xt(n));
        if (!_t.qt(s)) throw new N(x.INVALID_ARGUMENT, `Invalid query. When querying a collection group by FieldPath.documentId(), the value provided must result in a valid document path, but '${s}' is not because it has an odd number of segments (${s.length}).`);
        return Ct(t, new _t(s));
    }
    if (n instanceof zc) return Ct(t, n.S_);
    throw new N(x.INVALID_ARGUMENT, "Invalid query. When querying with FieldPath.documentId(), you must provide a valid string or a DocumentReference, but it was: " + ci(n) + ".");
}

/**
 * Validates that the value passed into a disjunctive filter satisfies all
 * array requirements.
 */ function Au(t, e) {
    if (!Array.isArray(t) || 0 === t.length) throw new N(x.INVALID_ARGUMENT, `Invalid Query. A non-empty array is required for '${e.toString()}' filters.`);
    if (t.length > 10) throw new N(x.INVALID_ARGUMENT, `Invalid Query. '${e.toString()}' filters support a maximum of 10 elements in the value array.`);
}

function Ru(t, e, n) {
    if (!n.isEqual(e)) throw new N(x.INVALID_ARGUMENT, `Invalid query. You have a where filter with an inequality (<, <=, >, or >=) on field '${e.toString()}' and so you must also use '${e.toString()}' as your first argument to orderBy(), but your first orderBy() is on field '${n.toString()}' instead.`);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Converts custom model object of type T into DocumentData by applying the
 * converter if it exists.
 *
 * This function is used when converting user objects to DocumentData
 * because we want to provide the user with a more specific error message if
 * their set() or fails due to invalid data originating from a toFirestore()
 * call.
 */ function Pu(t, e, n) {
    let s;
    // Cast to `any` in order to satisfy the union type constraint on
    // toFirestore().
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return s = t ? n && (n.merge || n.mergeFields) ? t.toFirestore(e, n) : t.toFirestore(e) : e, 
    s;
}

class yu extends ou {
    constructor(t) {
        super(), this.firestore = t;
    }
    Uf(t) {
        return new F(t);
    }
    Qf(t) {
        const e = this.Gf(t, this.firestore.zr);
        return new zc(this.firestore, /* converter= */ null, e);
    }
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ function gu(t) {
    /**
 * Returns true if obj is an object and contains at least one of the specified
 * methods.
 */
    return function(t, e) {
        if ("object" != typeof t || null === t) return !1;
        const n = t;
        for (const t of e) if (t in n && "function" == typeof n[t]) return !0;
        return !1;
    }
    /**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
    /**
 * Metadata about a snapshot, describing the state of the snapshot.
 */ (t, [ "next", "error", "complete" ]);
}

class Vu {
    /** @hideconstructor */
    constructor(t, e) {
        this.hasPendingWrites = t, this.fromCache = e;
    }
    /**
     * Returns true if this `SnapshotMetadata` is equal to the provided one.
     *
     * @param other - The `SnapshotMetadata` to compare against.
     * @returns true if this `SnapshotMetadata` is equal to the provided one.
     */    isEqual(t) {
        return this.hasPendingWrites === t.hasPendingWrites && this.fromCache === t.fromCache;
    }
}

/**
 * A `DocumentSnapshot` contains data read from a document in your Firestore
 * database. The data can be extracted with `.data()` or `.get(<field>)` to
 * get a specific field.
 *
 * For a `DocumentSnapshot` that points to a non-existing document, any data
 * access will return 'undefined'. You can use the `exists()` method to
 * explicitly verify a document's existence.
 */ class pu extends cu {
    /** @hideconstructor protected */
    constructor(t, e, n, s, i, r) {
        super(t, e, n, s, r), this.zf = t, this.ad = t, this.metadata = i;
    }
    /**
     * Property of the `DocumentSnapshot` that signals whether or not the data
     * exists. True if the document exists.
     */    exists() {
        return super.exists();
    }
    /**
     * Retrieves all fields in the document as an `Object`. Returns `undefined` if
     * the document doesn't exist.
     *
     * By default, `FieldValue.serverTimestamp()` values that have not yet been
     * set to their final value will be returned as `null`. You can override
     * this by passing an options object.
     *
     * @param options - An options object to configure how data is retrieved from
     * the snapshot (for example the desired behavior for server timestamps that
     * have not yet been set to their final value).
     * @returns An `Object` containing all fields in the document or `undefined` if
     * the document doesn't exist.
     */    data(t = {}) {
        if (this.Jf) {
            if (this.v_) {
                // We only want to use the converter and create a new DocumentSnapshot
                // if a converter has been provided.
                const e = new bu(this.zf, this.Hf, this.S_, this.Jf, this.metadata, 
                /* converter= */ null);
                return this.v_.fromFirestore(e, t);
            }
            return this.Hf.$f(this.Jf.Yt(), t.serverTimestamps);
        }
    }
    /**
     * Retrieves the field specified by `fieldPath`. Returns `undefined` if the
     * document or field doesn't exist.
     *
     * By default, a `FieldValue.serverTimestamp()` that has not yet been set to
     * its final value will be returned as `null`. You can override this by
     * passing an options object.
     *
     * @param fieldPath - The path (for example 'foo' or 'foo.bar') to a specific
     * field.
     * @param options - An options object to configure how the field is retrieved
     * from the snapshot (for example the desired behavior for server timestamps
     * that have not yet been set to their final value).
     * @returns The data at the specified field location or undefined if no such
     * field exists in the document.
     */
    // We are using `any` here to avoid an explicit cast by our users.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(t, e = {}) {
        if (this.Jf) {
            const n = this.Jf.data().field(uu("DocumentSnapshot.get", t));
            if (null !== n) return this.Hf.$f(n, e.serverTimestamps);
        }
    }
}

/**
 * A `QueryDocumentSnapshot` contains data read from a document in your
 * Firestore database as part of a query. The document is guaranteed to exist
 * and its data can be extracted with `.data()` or `.get(<field>)` to get a
 * specific field.
 *
 * A `QueryDocumentSnapshot` offers the same API surface as a
 * `DocumentSnapshot`. Since query results contain only existing documents, the
 * `exists` property will always be true and `data()` will never return
 * 'undefined'.
 */ class bu extends pu {
    /**
     * Retrieves all fields in the document as an `Object`.
     *
     * By default, `FieldValue.serverTimestamp()` values that have not yet been
     * set to their final value will be returned as `null`. You can override
     * this by passing an options object.
     *
     * @override
     * @param options - An options object to configure how data is retrieved from
     * the snapshot (for example the desired behavior for server timestamps that
     * have not yet been set to their final value).
     * @returns An `Object` containing all fields in the document.
     */
    data(t = {}) {
        return super.data(t);
    }
}

/**
 * A `QuerySnapshot` contains zero or more `DocumentSnapshot` objects
 * representing the results of a query. The documents can be accessed as an
 * array via the `docs` property or enumerated using the `forEach` method. The
 * number of documents can be determined via the `empty` and `size`
 * properties.
 */ class vu {
    /** @hideconstructor */
    constructor(t, e, n, s) {
        this.zf = t, this.Hf = e, this.ud = s, this.metadata = new Vu(s.hasPendingWrites, s.fromCache), 
        this.query = n;
    }
    /** An array of all the documents in the `QuerySnapshot`. */    get docs() {
        const t = [];
        return this.forEach((e => t.push(e))), t;
    }
    /** The number of documents in the `QuerySnapshot`. */    get size() {
        return this.ud.docs.size;
    }
    /** True if there are no documents in the `QuerySnapshot`. */    get empty() {
        return 0 === this.size;
    }
    /**
     * Enumerates all of the documents in the `QuerySnapshot`.
     *
     * @param callback - A callback to be called with a `QueryDocumentSnapshot` for
     * each document in the snapshot.
     * @param thisArg - The `this` binding for the callback.
     */    forEach(t, e) {
        this.ud.docs.forEach((n => {
            t.call(e, new bu(this.zf, this.Hf, n.key, n, new Vu(this.ud.tn.has(n.key), this.ud.fromCache), this.query.v_));
        }));
    }
    /**
     * Returns an array of the documents changes since the last snapshot. If this
     * is the first snapshot, all documents will be in the list as 'added'
     * changes.
     *
     * @param options - `SnapshotListenOptions` that control whether metadata-only
     * changes (i.e. only `DocumentSnapshot.metadata` changed) should trigger
     * snapshot events.
     */    docChanges(t = {}) {
        const e = !!t.includeMetadataChanges;
        if (e && this.ud.nn) throw new N(x.INVALID_ARGUMENT, "To include metadata changes with your document changes, you must also pass { includeMetadataChanges:true } to onSnapshot().");
        return this.hd && this.ld === e || (this.hd = 
        /** Calculates the array of DocumentChanges for a given ViewSnapshot. */
        function(t, e) {
            if (t.ud.Ze.bt()) {
                // Special case the first snapshot because index calculation is easy and
                // fast
                let e, n = 0;
                return t.ud.docChanges.map((s => {
                    const i = new bu(t.zf, t.Hf, s.doc.key, s.doc, new Vu(t.ud.tn.has(s.doc.key), t.ud.fromCache), t.query.v_);
                    return e = s.doc, {
                        type: "added",
                        doc: i,
                        oldIndex: -1,
                        newIndex: n++
                    };
                }));
            }
            {
                // A DocumentSet that is updated incrementally as changes are applied to use
                // to lookup the index of a document.
                let n = t.ud.Ze;
                return t.ud.docChanges.filter((t => e || 3 /* Metadata */ !== t.type)).map((e => {
                    const s = new bu(t.zf, t.Hf, e.doc.key, e.doc, new Vu(t.ud.tn.has(e.doc.key), t.ud.fromCache), t.query.v_);
                    let i = -1, r = -1;
                    return 0 /* Added */ !== e.type && (i = n.indexOf(e.doc.key), n = n.delete(e.doc.key)), 
                    1 /* Removed */ !== e.type && (n = n.add(e.doc), r = n.indexOf(e.doc.key)), {
                        type: Su(e.type),
                        doc: s,
                        oldIndex: i,
                        newIndex: r
                    };
                }));
            }
        }(this, e), this.ld = e), this.hd;
    }
}

function Su(t) {
    switch (t) {
      case 0 /* Added */ :
        return "added";

      case 2 /* Modified */ :
      case 3 /* Metadata */ :
        return "modified";

      case 1 /* Removed */ :
        return "removed";

      default:
        return y();
    }
}

// TODO(firestoreexp): Add tests for snapshotEqual with different snapshot
// metadata
/**
 * Returns true if the provided snapshots are equal.
 *
 * @param left - A snapshot to compare.
 * @param right - A snapshot to compare.
 * @returns true if the snapshots are equal.
 */ function Du(t, e) {
    return t instanceof pu && e instanceof pu ? t.zf === e.zf && t.S_.isEqual(e.S_) && (null === t.Jf ? null === e.Jf : t.Jf.isEqual(e.Jf)) && t.v_ === e.v_ : t instanceof vu && e instanceof vu && (t.zf === e.zf && ea(t.query, e.query) && t.metadata.isEqual(e.metadata) && t.ud.isEqual(e.ud));
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Reads the document referred to by this `DocumentReference`.
 *
 * Note: `getDoc()` attempts to provide up-to-date data when possible by waiting
 * for data from the server, but it may return cached data or fail if you are
 * offline and the server cannot be reached. To specify this behavior, invoke
 * {@link getDocFromCache} or {@link getDocFromServer}.
 *
 * @param reference - The reference of the document to fetch.
 * @returns A Promise resolved with a `DocumentSnapshot` containing the
 * current document contents.
 */ class Cu extends ou {
    constructor(t) {
        super(), this.firestore = t;
    }
    Uf(t) {
        return new F(t);
    }
    Qf(t) {
        const e = this.Gf(t, this.firestore.zr);
        return new zc(this.firestore, /* converter= */ null, e);
    }
}

/**
 * Reads the document referred to by this `DocumentReference` from cache.
 * Returns an error if the document is not currently cached.
 *
 * @returns A Promise resolved with a `DocumentSnapshot` containing the
 * current document contents.
 */ function xu(t, e, n, ...s) {
    t = ai(t, zc);
    const i = ai(t.firestore, Za), r = ua(i);
    let o;
    // For Compat types, we have to "extract" the underlying types before
    // performing validation.
    e instanceof O && (e = e._), o = "string" == typeof e || e instanceof jc ? Ia(r, "updateDoc", t.S_, e, n, s) : Ta(r, "updateDoc", t.S_, e);
    return Fu(i, [ o.F_(t.S_, ze.exists(!0)) ]);
}

/**
 * Deletes the document referred to by the specified `DocumentReference`.
 *
 * @param reference - A reference to the document to delete.
 * @returns A Promise resolved once the document has been successfully
 * deleted from the backend (note that it won't resolve while you're offline).
 */ function Nu(t, ...e) {
    var n, s, i;
    t instanceof O && (t = t._);
    let r = {
        includeMetadataChanges: !1
    }, o = 0;
    "object" != typeof e[o] || gu(e[o]) || (r = e[o], o++);
    const c = {
        includeMetadataChanges: r.includeMetadataChanges
    };
    if (gu(e[o])) {
        const t = e[o];
        e[o] = null === (n = t.next) || void 0 === n ? void 0 : n.bind(t), e[o + 1] = null === (s = t.error) || void 0 === s ? void 0 : s.bind(t), 
        e[o + 2] = null === (i = t.complete) || void 0 === i ? void 0 : i.bind(t);
    }
    let a, u, h;
    if (t instanceof zc) u = ai(t.firestore, Za), h = de(t.S_.path), a = {
        next: n => {
            e[o] && e[o](Ou(u, t, n));
        },
        error: e[o + 1],
        complete: e[o + 2]
    }; else {
        const n = ai(t, Hc);
        u = ai(n.firestore, Za), h = n.C_;
        const s = new Cu(u);
        a = {
            next: t => {
                e[o] && e[o](new vu(u, s, n, t));
            },
            error: e[o + 1],
            complete: e[o + 2]
        }, hu(t.C_);
    }
    return function(t, e, n, s) {
        const i = new Kc(s), r = new Jo(e, i, n);
        return t.ks.Ws((async () => Wo(await Ba(t), r))), () => {
            i.I_(), t.ks.Ws((async () => jo(await Ba(t), r)));
        };
    }(tu(u), h, c, a);
}

/** Locally writes `mutations` on the async queue. */
function Fu(t, e) {
    return function(t, e) {
        const n = new it;
        return t.ks.Ws((async () => hc(await $a(t), e, n))), n.promise;
    }(tu(t), e);
}

/**
 * Converts a ViewSnapshot that contains the single document specified by `ref`
 * to a DocumentSnapshot.
 */ function Ou(t, e, n) {
    const s = n.docs.get(e.S_), i = new Cu(t);
    return new pu(t, i, e.S_, s, new Vu(n.hasPendingWrites, n.fromCache), e.v_);
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A write batch, used to perform multiple writes as a single atomic unit.
 *
 * A `WriteBatch` object can be acquired by calling {@link writeBatch}. It
 * provides methods for adding writes to the write batch. None of the writes
 * will be committed (or visible locally) until {@link WriteBatch#commit} is
 * called.
 */ class ku {
    /** @hideconstructor */
    constructor(t, e) {
        this.zf = t, this._d = e, this.fd = [], this.dd = !1, this.wd = ua(t);
    }
    set(t, e, n) {
        this.Ed();
        const s = Mu(t, this.zf), i = Pu(s.v_, e, n), r = ha(this.wd, "WriteBatch.set", s.S_, i, null !== s.v_, n);
        return this.fd.push(r.F_(s.S_, ze.de())), this;
    }
    update(t, e, n, ...s) {
        this.Ed();
        const i = Mu(t, this.zf);
        // For Compat types, we have to "extract" the underlying types before
        // performing validation.
                let r;
        return e instanceof O && (e = e._), r = "string" == typeof e || e instanceof jc ? Ia(this.wd, "WriteBatch.update", i.S_, e, n, s) : Ta(this.wd, "WriteBatch.update", i.S_, e), 
        this.fd.push(r.F_(i.S_, ze.exists(!0))), this;
    }
    /**
     * Deletes the document referred to by the provided {@link DocumentReference}.
     *
     * @param documentRef - A reference to the document to be deleted.
     * @returns This `WriteBatch` instance. Used for chaining method calls.
     */    delete(t) {
        this.Ed();
        const e = Mu(t, this.zf);
        return this.fd = this.fd.concat(new un(e.S_, ze.de())), this;
    }
    /**
     * Commits all of the writes in this write batch as a single atomic unit.
     *
     * The result of these writes will only be reflected in document reads that
     * occur after the returned Promise resolves. If the client is offline, the
     * write fails. If you would like to see local modifications or buffer writes
     * until the client is online, use the full Firestore SDK.
     *
     * @returns A Promise resolved once all of the writes in the batch have been
     * successfully written to the backend as an atomic unit (note that it won't
     * resolve while you're offline).
     */    commit() {
        return this.Ed(), this.dd = !0, this.fd.length > 0 ? this._d(this.fd) : Promise.resolve();
    }
    Ed() {
        if (this.dd) throw new N(x.FAILED_PRECONDITION, "A write batch can no longer be used after commit() has been called.");
    }
}

function Mu(t, e) {
    if (t instanceof O && (t = t._), t.firestore !== e) throw new N(x.INVALID_ARGUMENT, "Provided document reference is from a different Firestore instance.");
    return t;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// TODO(mrschmidt) Consider using `BaseTransaction` as the base class in the
// legacy SDK.
/**
 * A reference to a transaction.
 *
 * The `Transaction` object passed to a transaction's `updateFunction` provides
 * the methods to read and write data within the transaction context. See
 * {@link runTransaction}.
 */
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * A reference to a transaction.
 *
 * The `Transaction` object passed to a transaction's `updateFunction` provides
 * the methods to read and write data within the transaction context. See
 * {@link runTransaction}.
 */
class Lu extends class {
    /** @hideconstructor */
    constructor(t, e) {
        this.zf = t, this.Td = e, this.wd = ua(t);
    }
    /**
     * Reads the document referenced by the provided {@link DocumentReference}.
     *
     * @param documentRef - A reference to the document to be read.
     * @returns A `DocumentSnapshot` with the read data.
     */    get(t) {
        const e = Mu(t, this.zf), n = new yu(this.zf);
        return this.Td.ef([ e.S_ ]).then((t => {
            if (!t || 1 !== t.length) return y();
            const s = t[0];
            if (s instanceof Ut) return new cu(this.zf, n, e.S_, null, e.v_);
            if (s instanceof qt) return new cu(this.zf, n, s.key, s, e.v_);
            throw y();
        }));
    }
    set(t, e, n) {
        const s = Mu(t, this.zf), i = Pu(s.v_, e, n), r = ha(this.wd, "Transaction.set", s.S_, i, null !== s.v_, n);
        return this.Td.set(s.S_, r), this;
    }
    update(t, e, n, ...s) {
        const i = Mu(t, this.zf);
        // For Compat types, we have to "extract" the underlying types before
        // performing validation.
                let r;
        return e instanceof O && (e = e._), r = "string" == typeof e || e instanceof jc ? Ia(this.wd, "Transaction.update", i.S_, e, n, s) : Ta(this.wd, "Transaction.update", i.S_, e), 
        this.Td.update(i.S_, r), this;
    }
    /**
     * Deletes the document referred to by the provided {@link DocumentReference}.
     *
     * @param documentRef - A reference to the document to be deleted.
     * @returns This `Transaction` instance. Used for chaining method calls.
     */    delete(t) {
        const e = Mu(t, this.zf);
        return this.Td.delete(e.S_), this;
    }
} {
    // This class implements the same logic as the Transaction API in the Lite SDK
    // but is subclassed in order to return its own DocumentSnapshot types.
    /** @hideconstructor */
    constructor(t, e) {
        super(t, e), this.zf = t;
    }
    /**
     * Reads the document referenced by the provided {@link DocumentReference}.
     *
     * @param documentRef - A reference to the document to be read.
     * @returns A `DocumentSnapshot` with the read data.
     */    get(t) {
        const e = Mu(t, this.zf), n = new Cu(this.zf);
        return super.get(t).then((t => new pu(this.zf, n, e.S_, t.Jf, new Vu(
        /* hasPendingWrites= */ !1, 
        /* fromCache= */ !1), e.v_)));
    }
}

/**
 * Executes the given `updateFunction` and then attempts to commit the changes
 * applied within the transaction. If any document read within the transaction
 * has changed, Cloud Firestore retries the `updateFunction`. If it fails to
 * commit after 5 attempts, the transaction fails.
 *
 * The maximum number of writes allowed in a single transaction is 500.
 *
 * @param firestore - A reference to the Firestore database to run this
 * transaction against.
 * @param updateFunction - The function to execute within the transaction
 * context.
 * @returns If the transaction completed successfully or was explicitly aborted
 * (the `updateFunction` returned a failed promise), the promise returned by the
 * `updateFunction `is returned here. Otherwise, if the transaction failed, a
 * rejected promise with the corresponding failure error is returned.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const $u = "You are using the memory-only build of Firestore. Persistence support is only available via the @firebase/firestore bundle or the firebase-firestore.js build.";

/**
 * The persistence provider included with the memory-only SDK. This provider
 * errors for all attempts to access persistence.
 */ class Bu {
    enableIndexedDbPersistence(t, e) {
        throw new N(x.FAILED_PRECONDITION, $u);
    }
    enableMultiTabIndexedDbPersistence(t) {
        throw new N(x.FAILED_PRECONDITION, $u);
    }
    clearIndexedDbPersistence(t) {
        throw new N(x.FAILED_PRECONDITION, $u);
    }
}

/**
 * The persistence provider included with the full Firestore SDK.
 */ class qu {
    enableIndexedDbPersistence(t, e) {
        return function(t, e) {
            ru(t = ai(t, Za));
            const n = tu(t), s = t.so(), i = new Uc;
            return nu(n, i, new Bc(i, s.cacheSizeBytes, null == e ? void 0 : e.forceOwnership));
        }
        /**
 * Attempts to enable multi-tab persistent storage, if possible. If enabled
 * across all tabs, all operations share access to local persistence, including
 * shared execution of queries and latency-compensated local document updates
 * across all connected instances.
 *
 * If this fails, `enableMultiTabIndexedDbPersistence()` will reject the promise
 * it returns. Note that even after this failure, the `Firestore` instance will
 * remain usable, however offline persistence will be disabled.
 *
 * There are several reasons why this can fail, which can be identified by
 * the `code` on the error.
 *
 *   * failed-precondition: The app is already open in another browser tab and
 *     multi-tab is not enabled.
 *   * unimplemented: The browser is incompatible with the offline
 *     persistence implementation.
 *
 * @param firestore - The `Firestore` instance to enable persistence for.
 * @returns A promise that represents successfully enabling persistent
 * storage.
 */ (t._, {
            forceOwnership: e
        });
    }
    enableMultiTabIndexedDbPersistence(t) {
        return function(t) {
            ru(t = ai(t, Za));
            const e = tu(t), n = t.so(), s = new Uc;
            return nu(e, s, new qc(s, n.cacheSizeBytes));
        }(t._);
    }
    clearIndexedDbPersistence(t) {
        return su(t._);
    }
}

/**
 * Compat class for Firestore. Exposes Firestore Legacy API, but delegates
 * to the functional API of firestore-exp.
 */ class Uu extends O {
    constructor(t, e, n) {
        super(e), this.Id = n, this.INTERNAL = {
            delete: () => this.terminate()
        }, t instanceof j || (this.md = t);
    }
    get zr() {
        return this._.zr;
    }
    settings(t) {
        t.merge && 
        // Remove the property from the settings once the merge is completed
        delete (t = Object.assign(Object.assign({}, this._.no()), t)).merge, this._.eo(t);
    }
    useEmulator(t, e) {
        "firestore.googleapis.com" !== this._.no().host && R("Host has been set in both settings() and useEmulator(), emulator host will be used"), 
        this.settings({
            host: `${t}:${e}`,
            ssl: !1,
            merge: !0
        });
    }
    enableNetwork() {
        return qa(tu(ai(this._, Za)));
    }
    disableNetwork() {
        return Ua(tu(ai(this._, Za)));
    }
    enablePersistence(t) {
        let e = !1, n = !1;
        return t && (e = !!t.synchronizeTabs, n = !!t.experimentalForceOwningTab, ii("synchronizeTabs", e, "experimentalForceOwningTab", n)), 
        e ? this.Id.enableMultiTabIndexedDbPersistence(this) : this.Id.enableIndexedDbPersistence(this, n);
    }
    clearPersistence() {
        return this.Id.clearIndexedDbPersistence(this);
    }
    terminate() {
        return this.app._removeServiceInstance("firestore"), this.app._removeServiceInstance("firestore-exp"), 
        this._._delete();
    }
    waitForPendingWrites() {
        return iu(this._);
    }
    onSnapshotsInSync(t) {
        return function(t, e) {
            return Ga(tu(t = ai(t, Za)), gu(e) ? e : {
                next: e
            });
        }(this._, t);
    }
    get app() {
        if (!this.md) throw new N(x.FAILED_PRECONDITION, "Firestore was not initialized using the Firebase SDK. 'app' is not available");
        return this.md;
    }
    collection(t) {
        try {
            return new nh(this, Yc(this._, t));
        } catch (t) {
            throw zu(t, "collection()", "Firestore.collection()");
        }
    }
    doc(t) {
        try {
            return new Gu(this, Zc(this._, t));
        } catch (t) {
            throw zu(t, "doc()", "Firestore.doc()");
        }
    }
    collectionGroup(t) {
        try {
            return new Zu(this, Xc(this._, t));
        } catch (t) {
            throw zu(t, "collectionGroup()", "Firestore.collectionGroup()");
        }
    }
    runTransaction(t) {
        return function(t, e) {
            return za(tu(t), (n => e(new Lu(t, n))));
        }(this._, (e => t(new Wu(this, e))));
    }
    batch() {
        return tu(this._), new ju(new ku(this._, (t => Fu(this._, t))));
    }
    loadBundle(t) {
        throw new N(x.FAILED_PRECONDITION, '"loadBundle()" does not exist, have you imported "firebase/firestore/bundle"?');
    }
    namedQuery(t) {
        throw new N(x.FAILED_PRECONDITION, '"namedQuery()" does not exist, have you imported "firebase/firestore/bundle"?');
    }
}

class Qu extends ou {
    constructor(t) {
        super(), this.firestore = t;
    }
    Uf(t) {
        return new L(new F(t));
    }
    Qf(t) {
        const e = this.Gf(t, this.firestore.zr);
        return Gu.Ad(e, this.firestore, /* converter= */ null);
    }
}

function Ku(t) {
    var e;
    e = t, T.setLogLevel(e);
}

/**
 * A reference to a transaction.
 */ class Wu extends O {
    constructor(t, e) {
        super(e), this.zf = t;
    }
    get(t) {
        const e = sh(t);
        return this._.get(e).then((t => new Yu(this.zf, t)));
    }
    set(t, e, n) {
        const s = sh(t);
        return n ? (si("Transaction.set", n), this._.set(s, e, n)) : this._.set(s, e), this;
    }
    update(t, e, n, ...s) {
        const i = sh(t);
        return 2 === arguments.length ? this._.update(i, e) : this._.update(i, e, n, ...s), 
        this;
    }
    delete(t) {
        const e = sh(t);
        return this._.delete(e), this;
    }
}

class ju extends O {
    set(t, e, n) {
        const s = sh(t);
        return n ? (si("WriteBatch.set", n), this._.set(s, e, n)) : this._.set(s, e), this;
    }
    update(t, e, n, ...s) {
        const i = sh(t);
        return 2 === arguments.length ? this._.update(i, e) : this._.update(i, e, n, ...s), 
        this;
    }
    delete(t) {
        const e = sh(t);
        return this._.delete(e), this;
    }
    commit() {
        return this._.commit();
    }
}

/**
 * A reference to a particular document in a collection in the database.
 */ class Gu extends O {
    constructor(t, e) {
        super(e), this.firestore = t, this.Hf = new Qu(t);
    }
    static Rd(t, e, n) {
        if (t.length % 2 != 0) throw new N(x.INVALID_ARGUMENT, `Invalid document reference. Document references must have an even number of segments, but ${t.Ct()} has ${t.length}`);
        return new Gu(e, new zc(e._, n, new _t(t)));
    }
    static Ad(t, e, n) {
        return new Gu(e, new zc(e._, n, t));
    }
    get id() {
        return this._.id;
    }
    get parent() {
        return new nh(this.firestore, this._.parent);
    }
    get path() {
        return this._.path;
    }
    collection(t) {
        try {
            return new nh(this.firestore, Yc(this._, t));
        } catch (t) {
            throw zu(t, "collection()", "DocumentReference.collection()");
        }
    }
    isEqual(t) {
        return t instanceof O && (t = t._), t instanceof zc && ta(this._, t);
    }
    set(t, e) {
        e = si("DocumentReference.set", e);
        try {
            return function(t, e, n) {
                t = ai(t, zc);
                const s = ai(t.firestore, Za), i = Pu(t.v_, e, n);
                return Fu(s, [ ha(ua(s), "setDoc", t.S_, i, null !== t.v_, n).F_(t.S_, ze.de()) ]);
            }(this._, t, e);
        } catch (t) {
            throw zu(t, "setDoc()", "DocumentReference.set()");
        }
    }
    update(t, e, ...n) {
        try {
            return 1 === arguments.length ? xu(this._, t) : xu(this._, t, e, ...n);
        } catch (t) {
            throw zu(t, "updateDoc()", "DocumentReference.update()");
        }
    }
    delete() {
        return Fu(ai((t = this._).firestore, Za), [ new un(t.S_, ze.de()) ]);
        var t;
        /**
 * Add a new document to specified `CollectionReference` with the given data,
 * assigning it a document ID automatically.
 *
 * @param reference - A reference to the collection to add this document to.
 * @param data - An Object containing the data for the new document.
 * @returns A Promise resolved with a `DocumentReference` pointing to the
 * newly created document after it has been written to the backend (Note that it
 * won't resolve while you're offline).
 */    }
    onSnapshot(...t) {
        const e = Hu(t), n = Ju(t, (t => new Yu(this.firestore, new pu(this.firestore._, this.Hf, t.S_, t.Jf, t.metadata, this._.v_))));
        return Nu(this._, e, n);
    }
    get(t) {
        let e;
        return e = "cache" === (null == t ? void 0 : t.source) ? function(t) {
            t = ai(t, zc);
            const e = ai(t.firestore, Za), n = tu(e), s = new Cu(e);
            return Qa(n, t.S_).then((n => new pu(e, s, t.S_, n, new Vu(n instanceof qt && n.Jt, 
            /* fromCache= */ !0), t.v_)));
        }
        /**
 * Reads the document referred to by this `DocumentReference` from the server.
 * Returns an error if the network is not available.
 *
 * @returns A Promise resolved with a `DocumentSnapshot` containing the
 * current document contents.
 */ (this._) : "server" === (null == t ? void 0 : t.source) ? function(t) {
            t = ai(t, zc);
            const e = ai(t.firestore, Za);
            return Ka(tu(e), t.S_, {
                source: "server"
            }).then((n => Ou(e, t, n)));
        }
        /**
 * Executes the query and returns the results as a `QuerySnapshot`.
 *
 * Note: `getDocs()` attempts to provide up-to-date data when possible by
 * waiting for data from the server, but it may return cached data or fail if
 * you are offline and the server cannot be reached. To specify this behavior,
 * invoke {@link getDocsFromCache} or {@link getDocsFromServer}.
 *
 * @returns A Promise that will be resolved with the results of the query.
 */ (this._) : function(t) {
            t = ai(t, zc);
            const e = ai(t.firestore, Za);
            return Ka(tu(e), t.S_).then((n => Ou(e, t, n)));
        }(this._), e.then((t => new Yu(this.firestore, new pu(this.firestore._, this.Hf, t.S_, t.Jf, t.metadata, this._.v_))));
    }
    withConverter(t) {
        return new Gu(this.firestore, this._.withConverter(t));
    }
}

/**
 * Replaces the function name in an error thrown by the firestore-exp API
 * with the function names used in the classic API.
 */ function zu(t, e, n) {
    return t.message = t.message.replace(e, n), t;
}

/**
 * Iterates the list of arguments from an `onSnapshot` call and returns the
 * first argument that may be an `SnapshotListenOptions` object. Returns an
 * empty object if none is found.
 */ function Hu(t) {
    for (const e of t) if ("object" == typeof e && !gu(e)) return e;
    return {};
}

/**
 * Creates an observer that can be passed to the firestore-exp SDK. The
 * observer converts all observed values into the format expected by the classic
 * SDK.
 *
 * @param args - The list of arguments from an `onSnapshot` call.
 * @param wrapper - The function that converts the firestore-exp type into the
 * type used by this shim.
 */ function Ju(t, e) {
    var n, s;
    let i;
    return i = gu(t[0]) ? t[0] : gu(t[1]) ? t[1] : "function" == typeof t[0] ? {
        next: t[0],
        error: t[1],
        complete: t[2]
    } : {
        next: t[1],
        error: t[2],
        complete: t[3]
    }, {
        next: t => {
            i.next && i.next(e(t));
        },
        error: null === (n = i.error) || void 0 === n ? void 0 : n.bind(i),
        complete: null === (s = i.complete) || void 0 === s ? void 0 : s.bind(i)
    };
}

class Yu extends O {
    constructor(t, e) {
        super(e), this.zf = t;
    }
    get ref() {
        return new Gu(this.zf, this._.ref);
    }
    get id() {
        return this._.id;
    }
    get metadata() {
        return this._.metadata;
    }
    get exists() {
        return this._.exists();
    }
    data(t) {
        return this._.data(t);
    }
    get(t, e) {
        return this._.get(t, e);
    }
    isEqual(t) {
        return Du(this._, t._);
    }
}

class Xu extends Yu {
    data(t) {
        return this._.data(t);
    }
}

class Zu extends O {
    constructor(t, e) {
        super(e), this.firestore = t, this.Hf = new Qu(t);
    }
    where(t, e, n) {
        try {
            // The "as string" cast is a little bit of a hack. `where` accepts the
            // FieldPath Compat type as input, but is not typed as such in order to
            // not expose this via our public typings file.
            return new Zu(this.firestore, _u(this._, function(t, e, n) {
                const s = e, i = uu("where", t);
                return new fu(i, s, n);
            }(t, e, n)));
        } catch (t) {
            throw zu(t, /(orderBy|where)\(\)/, "Query.$1()");
        }
    }
    orderBy(t, e) {
        try {
            // The "as string" cast is a little bit of a hack. `orderBy` accepts the
            // FieldPath Compat type as input, but is not typed as such in order to
            // not expose this via our public typings file.
            return new Zu(this.firestore, _u(this._, function(t, e = "asc") {
                const n = e, s = uu("orderBy", t);
                return new du(s, n);
            }(t, e)));
        } catch (t) {
            throw zu(t, /(orderBy|where)\(\)/, "Query.$1()");
        }
    }
    limit(t) {
        try {
            return new Zu(this.firestore, _u(this._, function(t) {
                return ui("limit", t), new wu("limit", t, "F" /* First */);
            }
            /**
 * Creates a `QueryConstraint` that only returns the last matching documents.
 *
 * You must specify at least one `orderBy` clause for `limitToLast` queries,
 * otherwise an exception will be thrown during execution.
 *
 * @param limit - The maximum number of items to return.
 * @returns The created `Query`.
 */ (t)));
        } catch (t) {
            throw zu(t, "limit()", "Query.limit()");
        }
    }
    limitToLast(t) {
        try {
            return new Zu(this.firestore, _u(this._, function(t) {
                return ui("limitToLast", t), new wu("limitToLast", t, "L" /* Last */);
            }(t)));
        } catch (t) {
            throw zu(t, "limitToLast()", "Query.limitToLast()");
        }
    }
    startAt(...t) {
        try {
            return new Zu(this.firestore, _u(this._, function(...t) {
                return new Eu("startAt", t, /*before=*/ !0);
            }(...t)));
        } catch (t) {
            throw zu(t, "startAt()", "Query.startAt()");
        }
    }
    startAfter(...t) {
        try {
            return new Zu(this.firestore, _u(this._, function(...t) {
                return new Eu("startAfter", t, 
                /*before=*/ !1);
            }(...t)));
        } catch (t) {
            throw zu(t, "startAfter()", "Query.startAfter()");
        }
    }
    endBefore(...t) {
        try {
            return new Zu(this.firestore, _u(this._, function(...t) {
                return new Tu("endBefore", t, /*before=*/ !0);
            }(...t)));
        } catch (t) {
            throw zu(t, "endBefore()", "Query.endBefore()");
        }
    }
    endAt(...t) {
        try {
            return new Zu(this.firestore, _u(this._, function(...t) {
                return new Tu("endAt", t, /*before=*/ !1);
            }(...t)));
        } catch (t) {
            throw zu(t, "endAt()", "Query.endAt()");
        }
    }
    isEqual(t) {
        return ea(this._, t._);
    }
    get(t) {
        let e;
        return e = "cache" === (null == t ? void 0 : t.source) ? 
        /**
 * Executes the query and returns the results as a `QuerySnapshot` from cache.
 * Returns an error if the document is not currently cached.
 *
 * @returns A Promise that will be resolved with the results of the query.
 */
        function(t) {
            t = ai(t, Hc);
            const e = ai(t.firestore, Za), n = tu(e), s = new Cu(e);
            return Wa(n, t.C_).then((n => new vu(e, s, t, n)));
        }
        /**
 * Executes the query and returns the results as a `QuerySnapshot` from the
 * server. Returns an error if the network is not available.
 *
 * @returns A Promise that will be resolved with the results of the query.
 */ (this._) : "server" === (null == t ? void 0 : t.source) ? function(t) {
            t = ai(t, Hc);
            const e = ai(t.firestore, Za), n = tu(e), s = new Cu(e);
            return ja(n, t.C_, {
                source: "server"
            }).then((n => new vu(e, s, t, n)));
        }(this._) : function(t) {
            t = ai(t, Hc);
            const e = ai(t.firestore, Za), n = tu(e), s = new Cu(e);
            return hu(t.C_), ja(n, t.C_).then((n => new vu(e, s, t, n)));
        }(this._), e.then((t => new eh(this.firestore, new vu(this.firestore._, this.Hf, this._, t.ud))));
    }
    onSnapshot(...t) {
        const e = Hu(t), n = Ju(t, (t => new eh(this.firestore, new vu(this.firestore._, this.Hf, this._, t.ud))));
        return Nu(this._, e, n);
    }
    withConverter(t) {
        return new Zu(this.firestore, this._.withConverter(t));
    }
}

class th extends O {
    constructor(t, e) {
        super(e), this.zf = t;
    }
    get type() {
        return this._.type;
    }
    get doc() {
        return new Xu(this.zf, this._.doc);
    }
    get oldIndex() {
        return this._.oldIndex;
    }
    get newIndex() {
        return this._.newIndex;
    }
}

class eh extends O {
    constructor(t, e) {
        super(e), this.zf = t;
    }
    get query() {
        return new Zu(this.zf, this._.query);
    }
    get metadata() {
        return this._.metadata;
    }
    get size() {
        return this._.size;
    }
    get empty() {
        return this._.empty;
    }
    get docs() {
        return this._.docs.map((t => new Xu(this.zf, t)));
    }
    docChanges(t) {
        return this._.docChanges(t).map((t => new th(this.zf, t)));
    }
    forEach(t, e) {
        this._.forEach((n => {
            t.call(e, new Xu(this.zf, n));
        }));
    }
    isEqual(t) {
        return Du(this._, t._);
    }
}

class nh extends Zu {
    constructor(t, e) {
        super(t, e), this.firestore = t, this._ = e;
    }
    get id() {
        return this._.id;
    }
    get path() {
        return this._.path;
    }
    get parent() {
        const t = this._.parent;
        return t ? new Gu(this.firestore, t) : null;
    }
    doc(t) {
        try {
            return new Gu(this.firestore, void 0 === t ? Zc(this._) : Zc(this._, t));
        } catch (t) {
            throw zu(t, "doc()", "CollectionReference.doc()");
        }
    }
    add(t) {
        return function(t, e) {
            const n = ai(t.firestore, Za), s = Zc(t), i = Pu(t.v_, e);
            return Fu(n, [ ha(ua(t.firestore), "addDoc", s.S_, i, null !== t.v_, {}).F_(s.S_, ze.exists(!1)) ]).then((() => s));
        }(this._, t).then((t => new Gu(this.firestore, t)));
    }
    isEqual(t) {
        return ta(this._, t._);
    }
    withConverter(t) {
        return new nh(this.firestore, this._.withConverter(t));
    }
}

function sh(t) {
    return t instanceof O && (t = t._), ai(t, zc);
}

/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// The objects that are a part of this API are exposed to third-parties as
// compiled javascript so we want to flag our private members with a leading
// underscore to discourage their use.
/**
 * A `FieldPath` refers to a field in a document. The path may consist of a
 * single field name (referring to a top-level field in the document), or a list
 * of field names (referring to a nested field in the document).
 */ class ih extends O {
    /**
     * Creates a FieldPath from the provided field names. If more than one field
     * name is provided, the path will point to a nested field in a document.
     *
     * @param fieldNames - A list of field names.
     */
    constructor(...t) {
        super(new jc(...t));
    }
    static documentId() {
        /**
         * Internal Note: The backend doesn't technically support querying by
         * document ID. Instead it queries by the entire document name (full path
         * included), but in the cases we currently support documentId(), the net
         * effect is the same.
         */
        return new ih(lt.kt().Ct());
    }
    isEqual(t) {
        return t instanceof O && (t = t._), t instanceof jc && this._.b_.isEqual(t.b_);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Returns a sentinel for use with {@link updateDoc} or
 * {@link setDoc} with `{merge: true}` to mark a field for deletion.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class rh extends O {
    static serverTimestamp() {
        const t = new fa("serverTimestamp");
        return t._methodName = "FieldValue.serverTimestamp", new rh(t);
    }
    static delete() {
        const t = new la("deleteField");
        return t._methodName = "FieldValue.delete", new rh(t);
    }
    static arrayUnion(...t) {
        const e = 
        /**
 * Returns a special value that can be used with {@link setDoc} or {@link
 * updateDoc} that tells the server to union the given elements with any array
 * value that already exists on the server. Each specified element that doesn't
 * already exist in the array will be added to the end. If the field being
 * modified is not already an array it will be overwritten with an array
 * containing exactly the specified elements.
 *
 * @param elements - The elements to union into the array.
 * @returns The `FieldValue` sentinel for use in a call to `setDoc()` or
 * `updateDoc()`.
 */
        function(...t) {
            // NOTE: We don't actually parse the data until it's used in set() or
            // update() since we'd need the Firestore instance to do this.
            return new da("arrayUnion", t);
        }
        /**
 * Returns a special value that can be used with {@link (setDoc:1)} or {@link
 * updateDoc} that tells the server to remove the given elements from any
 * array value that already exists on the server. All instances of each element
 * specified will be removed from the array. If the field being modified is not
 * already an array it will be overwritten with an empty array.
 *
 * @param elements - The elements to remove from the array.
 * @returns The `FieldValue` sentinel for use in a call to `setDoc()` or
 * `updateDoc()`
 */ (...t);
        return e._methodName = "FieldValue.arrayUnion", new rh(e);
    }
    static arrayRemove(...t) {
        const e = function(...t) {
            // NOTE: We don't actually parse the data until it's used in set() or
            // update() since we'd need the Firestore instance to do this.
            return new wa("arrayRemove", t);
        }
        /**
 * Returns a special value that can be used with {@link setDoc} or {@link
 * updateDoc} that tells the server to increment the field's current value by
 * the given value.
 *
 * If either the operand or the current field value uses floating point
 * precision, all arithmetic follows IEEE 754 semantics. If both values are
 * integers, values outside of JavaScript's safe number range
 * (`Number.MIN_SAFE_INTEGER` to `Number.MAX_SAFE_INTEGER`) are also subject to
 * precision loss. Furthermore, once processed by the Firestore backend, all
 * integer operations are capped between -2^63 and 2^63-1.
 *
 * If the current field value is not of type `number`, or if the field does not
 * yet exist, the transformation sets the field to the given value.
 *
 * @param n - The value to increment by.
 * @returns The `FieldValue` sentinel for use in a call to `setDoc()` or
 * `updateDoc()`
 */ (...t);
        return e._methodName = "FieldValue.arrayRemove", new rh(e);
    }
    static increment(t) {
        const e = function(t) {
            return new Ea("increment", t);
        }(t);
        return e._methodName = "FieldValue.increment", new rh(e);
    }
    isEqual(t) {
        return this._.isEqual(t._);
    }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */ class oh {
    constructor() {
        this.Pd = {}, this.yd = new it, this.gd = {
            taskState: "Running",
            totalBytes: 0,
            totalDocuments: 0,
            bytesLoaded: 0,
            documentsLoaded: 0
        };
    }
    onProgress(t, e, n) {
        this.Pd = {
            next: t,
            error: e,
            complete: n
        };
    }
    catch(t) {
        return this.yd.promise.catch(t);
    }
    then(t, e) {
        return this.yd.promise.then(t, e);
    }
    /**
     * Notifies all observers that bundle loading has completed, with a provided
     * `LoadBundleTaskProgress` object.
     */    e_(t) {
        this.n_(t), this.Pd.complete && this.Pd.complete(), this.yd.resolve(t);
    }
    /**
     * Notifies all observers that bundle loading has failed, with a provided
     * `Error` as the reason.
     */    i_(t) {
        this.gd.taskState = "Error", this.Pd.next && this.Pd.next(this.gd), this.Pd.error && this.Pd.error(t), 
        this.yd.reject(t);
    }
    /**
     * Notifies a progress update of loading a bundle.
     * @param progress - The new progress.
     */    n_(t) {
        this.gd = t, this.Pd.next && this.Pd.next(t);
    }
}

function ch(t, e) {
    const n = new oh;
    return Ha(tu(t._), t.zr, e, n), n;
}

function ah(t, e) {
    return Ja(tu(t._), e).then((e => e ? new Zu(t, new Hc(t._, null, e.query)) : null));
}

export { L as Blob, Xa as CACHE_SIZE_UNLIMITED, nh as CollectionReference, Gu as DocumentReference, Yu as DocumentSnapshot, Za as ExpFirebaseFirestore, ih as FieldPath, rh as FieldValue, Uu as Firestore, na as GeoPoint, qu as IndexedDbPersistenceProvider, Bu as MemoryPersistenceProvider, Zu as Query, Xu as QueryDocumentSnapshot, eh as QuerySnapshot, ot as Timestamp, Wu as Transaction, ju as WriteBatch, ch as loadBundle, ah as namedQuery, Ku as setLogLevel };
//# sourceMappingURL=prebuilt.js.map
